import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  BarChart3,
  Bot,
  Briefcase,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardCheck,
  ClipboardList,
  CircleDollarSign,
  Clock,
  Compass,
  Copy,
  Database,
  Download,
  ExternalLink,
  FileText,
  Flame,
  FlaskConical,
  Gauge,
  Globe2,
  Megaphone,
  Home,
  Info,
  KeyRound,
  LayoutDashboard,
  Lightbulb,
  LineChart,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Mail,
  MapPinned,
  Menu,
  Newspaper,
  Plug,
  Plus,
  Radar,
  RefreshCw,
  Route,
  ScanSearch,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  Upload,
  UserPlus,
  UsersRound,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import type { IconBaseProps, IconType } from "react-icons";
import { FaLinkedin, FaSalesforce, FaSlack } from "react-icons/fa6";
import {
  SiFacebook,
  SiGoogleads,
  SiGoogleanalytics,
  SiHubspot,
  SiInstagram,
  SiMailchimp,
  SiShopify,
  SiStripe,
  SiYoutube,
  SiZoho,
} from "react-icons/si";
import {
  activateAgentNetwork,
  applyProposedTerritoryStructure,
  approveBusinessProfile,
  bootstrapWorkspace,
  completeOnboarding,
  createBillingPortal,
  createCreditCheckout,
  createPlanSelection,
  createSetupSession,
  createSubscriptionCheckout,
  approveCampaignPlan,
  approveMarketingStrategy,
  approveSalesTerritory,
  connectIntegrationWithApiKey,
  createCampaign,
  createCampaignPlan,
  createManualLead,
  decideContentDraft,
  decideLeadRecommendation,
  decidePerformanceRecommendation,
  decideRevenueRecommendation,
  decideSalesRecommendation,
  decideStrategyInitiative,
  disconnectIntegration,
  downloadUsageCsv,
  duplicateContentVariant,
  editCampaignPlan,
  editStrategyInitiative,
  generateCampaignPlan,
  generateContentDraft,
  generateMarketingStrategy,
  generateSalesRecommendations,
  getCampaign,
  getCampaignPlan,
  getContentDraft,
  importLeadsCsv,
  assignNurtureLeadTrack,
  decideBuyingIntentAlert,
  decideNurtureAlert,
  decideNurtureStepDraft,
  decideNurtureTrack,
  decideRevenueForecastAlert,
  explainBuyingIntentAccount,
  explainNurtureLead,
  generateNurtureStepDraft,
  loadAcceptedInitiatives,
  loadAudienceContext,
  loadBrandVoice,
  loadBusinessDiscoveryProfile,
  loadBuyingIntentAccountDetail,
  loadBuyingIntentAccountHistory,
  loadBuyingIntentAccountSignals,
  loadBuyingIntentAccounts,
  loadBuyingIntentAlerts,
  loadBuyingIntentOverview,
  loadCampaigns,
  loadCampaignPlans,
  loadContentDrafts,
  loadCreditPurchases,
  loadInvoices,
  loadMarketingStrategy,
  loadIntegrationsCatalog,
  loadLeadDetail,
  loadLeadIntelligenceOverview,
  loadLeadScoringConfig,
  loadLeads,
  loadModelLearning,
  loadNurtureAlerts,
  loadNurtureLeadDetail,
  loadNurtureLeadHistory,
  loadNurtureLeads,
  loadNurtureOverview,
  loadNurturePerformance,
  loadNurtureSegments,
  loadNurtureTracks,
  decideConversionExperiment,
  loadConversionAnalysis,
  loadConversionAnalysisHistory,
  loadConversionExperimentOutcomes,
  runConversionAnalysis,
  decideReputationAlert,
  loadReputationAnalysis,
  loadReputationAnalysisHistory,
  runReputationAnalysis,
  decideContentInitiative,
  loadContentStrategyAnalysis,
  loadContentStrategyAnalysisHistory,
  runContentStrategyAnalysis,
  generateOutreachDraft,
  loadOutreachDrafts,
  updateOutreachDraft,
  submitOutreachDraftForReview,
  decideOutreachDraft,
  loadMarketResearchBrief,
  narrateMarketResearchBrief,
  loadSocialPresenceBrief,
  loadPerformanceAnalysis,
  loadPerformanceAnalysisHistory,
  loadRecommendationOutcomes,
  loadPlatformReadiness,
  loadPublicPlans,
  loadRevenueForecast,
  loadRevenueForecastAccuracy,
  loadRevenueForecastAlerts,
  loadRevenueForecastHistory,
  loadRevenueOrchestrationOverview,
  loadSalesPlan,
  loadWorkspace,
  proposeNurtureTrack,
  reassignAccountTerritory,
  redeemCoupon,
  refreshLeadScores,
  regenerateCampaignSection,
  registerSessionRefresh,
  rescanWebsite,
  runBusinessDiscovery,
  runBuyingIntentAnalysis,
  runNurtureAnalysis,
  runPerformanceAnalysis,
  runRevenueForecast,
  runRevenueOrchestrationCycle,
  runRevenueScenario,
  saveBrandVoice,
  saveBusinessProfile,
  saveLeadIcpConfig,
  saveLeadScoringWeights,
  selectContentVariant,
  sendCampaignToContentGeneration,
  sendInitiativeToCampaignPlanning,
  setAgentActivation,
  startCampaignFromInitiative,
  startIntegrationConnect,
  submitDraftForReview,
  syncAllIntegrations,
  syncIntegration,
  createTeamInvite,
  type UsageRecordEntry,
  loadUsageRecords,
  type ApprovalItem,
  loadApprovals,
  loadPublicInvitePreview,
  loadTeamInvites,
  loadTeamMembers,
  removeTeamMember,
  resendTeamInviteEmail,
  revokeTeamInvite,
  updateTeamMemberRole,
  type ApiKey,
  loadApiKeys,
  createApiKey,
  revokeApiKey,
  transformContentVariant,
  undoContentVariant,
  updateAssetRequirementStatus,
  updateAutoRecharge,
  updateCompanyProfile,
  updateContentVariant,
  type AcceptedInitiativeSummary,
  type AccountIntent,
  type AccountPlanSummary,
  type AccountTier,
  type ActivityStep,
  type AgentActivityStatus,
  type AssetRequirementRecord,
  type AudienceSnapshot,
  type Bootstrap,
  type BrandVoiceProfile,
  type BusinessFact,
  type BusinessProfileRecord,
  type BusinessProfileSections,
  type BuyingIntentAccountDetail,
  type BuyingIntentAccountListQuery,
  type BuyingIntentAccountSnapshot,
  type BuyingIntentAlert,
  type BuyingIntentHistoryEntry,
  type BuyingIntentOverview,
  type BuyingIntentSignalTimelineEntry,
  type BuyingIntentTrend,
  type CampaignAssetStatus,
  type IntentAccountExplanation,
  type NurtureAlert,
  type NurtureClassification,
  type NurtureEngagementLevel,
  type NurtureEngagementTrend,
  type NurtureLeadExplanation,
  type NurtureLeadHistoryEntry,
  type NurtureLeadSnapshot,
  type NurtureOverview,
  type NurturePerformanceResult,
  type NurtureSegment,
  type NurtureTrack,
  type ForecastPeriodKind,
  type ForecastRisk,
  type RevenueForecastAccuracyResult,
  type RevenueForecastAlert,
  type RevenueForecastResult,
  type CampaignDetail,
  type CampaignPlanDetail,
  type CampaignPlanStatus,
  type CampaignPlanSummary,
  type CampaignRecommendation,
  type CampaignRegenerateSection,
  type CampaignSummary,
  type CommercialSummary,
  type CompanyProfileUpdate,
  type ConfidenceLevel,
  type ConnectorManifest,
  type ContentDraftRecord,
  type ContentRecommendation,
  type ContentStatus,
  type ContentType,
  type ContentVariantRecord,
  type CoverageGap,
  type CrossAgentInsight,
  type AgentFeedbackGroup,
  type DestinationAgent,
  type RecommendationCategory,
  type RecommendationOutcome,
  type FactStatus,
  type FactValue,
  type GapCertainty,
  type GapDriver,
  type Gap,
  type GapSeverity,
  type ReviewStatus,
  type IntentBand,
  type LeadBand,
  type EvidenceChecklistItem,
  type InitiativeRecord,
  type InitiativeStatus,
  type LeadDetail,
  type LeadIntelligenceOverview,
  type LeadListItem,
  type LeadListQuery,
  type LeadMappingSummary,
  type LeadRecommendation,
  type LeadSourceProvider,
  type ManualLeadRow,
  type MembershipRole,
  type ModelLearningStats,
  type OnboardingSubmission,
  type OrchestrationRecommendation,
  type OrchestrationSeverity,
  type OverallHealth,
  type ContentInitiativeType,
  type ContentStrategyAnalysisHistoryEntry,
  type ContentStrategyAnalysisResult,
  type OutreachChannel,
  type OutreachDraftRecord,
  type OutreachDraftStatus,
  type MarketResearchBrief,
  type SocialPresenceBrief,
  type SocialPresenceSourceCoverageEntry,
  type HypothesisOutcome,
  type RecommendedAction,
  type ReputationAlertType,
  type ReputationAnalysisHistoryEntry,
  type ReputationAnalysisResult,
  type ReputationSeverity,
  type ReputationSourceCategory,
  type ReputationTheme,
  type ReputationTrendState,
  type ConversionAnalysisHistoryEntry,
  type ConversionAnalysisResult,
  type ConversionExperimentOutcome,
  type ExperimentRecommendationType,
  type PriorityBucket,
  type PerformanceAnalysisHistoryEntry,
  type PerformanceAnalysisResult,
  type PerformanceRecommendation,
  type PublicPlan,
  type RevenueHealth,
  type RevenueOrchestrationOverview,
  type RevenueSnapshot,
  type RoadmapPhase,
  type SalesPlanResult,
  type ScannedPage,
  type ScenarioResult,
  type StrategyRecord,
  type TeamInvite,
  type TeamMember,
  type PublicInvitePreview,
  type TerritoryStatus,
  type TransformOperation,
  type VariantContentValue,
  type WebsiteAnalysisCategory,
  type WebsiteFinding,
  type WebsiteRecommendation,
  type WebsiteScan,
  type WebsiteSeverity,
  type WebsiteSubScores,
  type Workspace,
} from "./api";
import {
  getSession,
  isAuthConfigured,
  signIn,
  signOut,
  signUp,
  type AuthSession,
} from "./auth";
import { COUNTRIES, INDUSTRIES } from "./reference-data";

// Onboarding's own picker (Step 3) -- kept separate from, and narrower
// than, the full Settings > Integrations list below because it's validated
// against OnboardingSubmissionSchema.integrations' fixed zod enum
// (packages/contracts). Adding a provider to the Integration Hub doesn't
// automatically make it selectable during signup unless that enum (and
// this list) are updated too -- a deliberate, separate decision.
const onboardingIntegrationOptions = [
  ["hubspot", "HubSpot", "CRM and marketing"],
  ["salesforce", "Salesforce", "CRM and pipeline"],
  ["google-analytics", "Google Analytics", "Website performance"],
  ["linkedin", "LinkedIn", "Social and company signals"],
  ["mailchimp", "Mailchimp", "Email marketing"],
  ["stripe", "Stripe", "Recognised revenue"],
  ["slack", "Slack", "Approvals and alerts"],
] as const;

// The full Settings > Integrations catalogue -- every connector this app
// actually implements. Each entry's live status/auth-type comes from the
// fetched connectorCatalog manifest (IntegrationsView's `catalog` state);
// this array only supplies the display name/description and render order.
const integrationOptions = [
  ["hubspot", "HubSpot", "CRM and marketing"],
  ["salesforce", "Salesforce", "CRM and pipeline"],
  ["zoho", "Zoho CRM", "Leads, contacts and deals"],
  ["pipedrive", "Pipedrive", "Pipeline stages and deal activity"],
  ["dynamics", "Microsoft Dynamics 365", "Accounts, contacts and opportunities"],
  ["google-analytics", "Google Analytics", "Website performance"],
  ["linkedin", "LinkedIn", "Social and company signals"],
  ["facebook", "Facebook", "Page and campaign performance"],
  ["instagram", "Instagram", "Content and engagement performance"],
  ["youtube", "YouTube", "Video performance"],
  ["google-ads", "Google Ads", "Campaign spend and performance"],
  ["mailchimp", "Mailchimp", "Email marketing"],
  ["marketo", "Marketo", "Marketing lists and campaigns"],
  ["stripe", "Stripe", "Recognised revenue"],
  ["shopify", "Shopify", "Storefront and order data"],
  ["slack", "Slack", "Approvals and alerts"],
  ["teams", "Microsoft Teams", "Approvals and alerts"],
] as const;

// Hand-vectorised for the four providers with no maintained brand icon in
// either Simple Icons or Font Awesome Brands (Pipedrive, Marketo, and
// Dynamics/Teams -- the latter two excluded from every open icon set
// following a Microsoft trademark dispute). Flattened to a single colour
// each, matching how Simple Icons itself renders every other multi-tone
// mark here (Instagram, YouTube) as one flat brand colour rather than a
// gradient -- keeps every icon on this page visually consistent.
function PipedriveIcon({ size = 22, color = "currentColor" }: IconBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <rect x="5" y="7" width="3.6" height="15.5" rx="1" fill={color} />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.6 18.6c3.2 0 5.8-2.5 5.8-5.9s-2.6-5.9-5.8-5.9c-1.9 0-3.5.9-4.5 2.3v7.2c1 1.4 2.6 2.3 4.5 2.3Zm-.2-2.9c-1.9 0-3.4-1.4-3.4-3s1.5-3 3.4-3c1.9 0 3.3 1.4 3.3 3s-1.4 3-3.3 3Z"
        fill={color}
      />
    </svg>
  );
}
function MarketoIcon({ size = 22, color = "currentColor" }: IconBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <polygon points="4,20 4,13 7,11 7,20" fill={color} />
      <polygon points="10,20 10,8 13,6 13,20" fill={color} />
      <polygon points="16,20 16,3 20,1 20,20" fill={color} />
    </svg>
  );
}
function Dynamics365Icon({ size = 22, color = "currentColor" }: IconBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 3h10l4 6-4 3v9H6V3Z" fill={color} />
    </svg>
  );
}
function TeamsIcon({ size = 22, color = "currentColor" }: IconBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="9" cy="6" r="3.2" fill={color} />
      <circle cx="16.5" cy="7.2" r="2.2" fill={color} />
      <rect x="3" y="10" width="13" height="12" rx="3" fill={color} />
      <text x="9.5" y="19.3" fontSize="9" fontWeight="800" fill="#fff" textAnchor="middle" fontFamily="sans-serif">
        T
      </text>
    </svg>
  );
}

// Real brand marks (Simple Icons / Font Awesome Brands, plus the four
// hand-vectorised ones above) with each provider's official colour, used
// in place of the generic Plug icon on Settings > Integrations.
const PROVIDER_BRAND_ICONS: Partial<Record<string, { Icon: IconType; color: string }>> = {
  hubspot: { Icon: SiHubspot, color: "#FF7A59" },
  salesforce: { Icon: FaSalesforce, color: "#00A1E0" },
  zoho: { Icon: SiZoho, color: "#C8202F" },
  "google-analytics": { Icon: SiGoogleanalytics, color: "#E37400" },
  linkedin: { Icon: FaLinkedin, color: "#0A66C2" },
  facebook: { Icon: SiFacebook, color: "#1877F2" },
  instagram: { Icon: SiInstagram, color: "#E4405F" },
  youtube: { Icon: SiYoutube, color: "#FF0000" },
  "google-ads": { Icon: SiGoogleads, color: "#4285F4" },
  mailchimp: { Icon: SiMailchimp, color: "#FFE01B" },
  stripe: { Icon: SiStripe, color: "#635BFF" },
  shopify: { Icon: SiShopify, color: "#95BF47" },
  slack: { Icon: FaSlack, color: "#4A154B" },
  pipedrive: { Icon: PipedriveIcon, color: "#0E7A3D" },
  marketo: { Icon: MarketoIcon, color: "#5C4C9C" },
  dynamics: { Icon: Dynamics365Icon, color: "#0B53CE" },
  teams: { Icon: TeamsIcon, color: "#6264A7" },
};

// Settings > Integrations groups channels by the real category each
// connector's manifest reports (connectorCatalog's `category` field, e.g.
// "CRM", "Social") rather than a second hand-maintained taxonomy that could
// drift from it. This only controls display order/labels for those same
// category values.
const CATEGORY_ORDER = ["CRM", "Marketing", "Advertising", "Analytics", "Social", "Collaboration", "Commerce", "Revenue"];
const CATEGORY_LABELS: Record<string, string> = {
  CRM: "CRM",
  Marketing: "Marketing",
  Advertising: "Advertising",
  Analytics: "Analytics",
  Social: "Social media",
  Collaboration: "Collaboration & communication",
  Commerce: "Commerce",
  Revenue: "Revenue",
};

const money = (value: number, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);
const numberFrom = (value: unknown) => Number(value ?? 0);
const labelFor = (value: string) =>
  value.replace(/[-_]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

function Brand({ light = false }: { light?: boolean }) {
  return (
    <div className={`brand ${light ? "light" : ""}`}>
      <span className="brand-logo" aria-hidden="true">
        <img src="/clintralogo4200.svg" alt="" />
      </span>
      <span>CLINTRA</span>
    </div>
  );
}

function LoadingScreen({
  label = "Preparing your revenue workspace…",
}: {
  label?: string;
}) {
  return (
    <main className="loading-screen">
      <Brand />
      <LoaderCircle className="spin" size={34} />
      <h1>{label}</h1>
      <p>Securing your tenant and checking your data connections.</p>
    </main>
  );
}

function AuthPage({
  onAuthenticated,
  invitePreview,
}: {
  onAuthenticated: (session: AuthSession) => Promise<void>;
  invitePreview?: PublicInvitePreview | null;
}) {
  // A valid invite means a brand-new teammate joining an EXISTING
  // workspace -- they don't pick a plan or enter company details (that
  // workspace already has both), and their email must exactly match the
  // invited address for bootstrap() to actually consume the invite
  // (apps/api/src/workspace.ts), so it's locked rather than editable.
  const invite = invitePreview && invitePreview.valid ? invitePreview : null;
  const initialMode = invite
    ? "sign-up"
    : window.location.pathname.includes("sign-up") ||
        window.location.pathname.includes("signup")
      ? "sign-up"
      : "sign-in";
  const [mode, setMode] = useState<"sign-in" | "sign-up">(initialMode);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(invite?.email ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [countryCode, setCountryCode] = useState("GB");
  const [companySize, setCompanySize] = useState("1-10");
  const [primaryUseCase, setPrimaryUseCase] = useState("Revenue planning");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [billing, setBilling] = useState<"monthly" | "annual">(
    params.get("billing") === "annual" ? "annual" : "monthly",
  );
  const [plansLoading, setPlansLoading] = useState(initialMode === "sign-up" && !invite);
  const [planReload, setPlanReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  // invitePreview arrives asynchronously (App() fetches it after mount), so
  // it's very often still null on this component's first render -- mode and
  // email's useState initializers above only run once, at that first render.
  // Without this, a slow-to-resolve invite lands the user on the generic
  // sign-in view with an unprefilled email, and if they then switch to
  // sign-up by hand, the email field ends up locked (readOnly tracks the
  // now-resolved `invite`) but still empty, blocking submission entirely.
  useEffect(() => {
    if (!invite) return;
    setMode("sign-up");
    setEmail((current) => current || invite.email);
  }, [invite?.email]);
  useEffect(() => {
    if (mode !== "sign-up" || plans.length || invite) return;
    const requestedPlan = params.get("plan") ?? undefined;
    const requestedBilling = params.get("billing") ?? undefined;
    setPlansLoading(true);
    setError("");
    void (async () => {
      try {
        const result = await loadPublicPlans(requestedPlan, requestedBilling);
        setPlans(result.plans);
        if (result.selected) {
          setSelectedSlug(result.selected.plan.slug);
          setBilling(result.selected.price.interval);
        }
      } catch (cause) {
        try {
          const fallback = await loadPublicPlans();
          setPlans(fallback.plans);
          setSelectedSlug("");
          setError(
            cause instanceof Error
              ? `${cause.message}. Select one of the current plans below.`
              : "Select one of the current plans below.",
          );
        } catch (fallbackCause) {
          setPlans([]);
          setSelectedSlug("");
          setError(
            fallbackCause instanceof Error
              ? fallbackCause.message
              : "The Clintra plan catalogue is temporarily unavailable.",
          );
        }
      } finally {
        setPlansLoading(false);
      }
    })();
  }, [mode, params, planReload, plans.length, invite]);
  const selectedPlan = plans.find((plan) => plan.slug === selectedSlug) ?? null;
  const selectedPrice = selectedPlan?.prices[billing];
  const switchMode = (next: "sign-in" | "sign-up") => {
    setMode(next);
    setError("");
    setMessage("");
    window.history.pushState(
      {},
      "",
      next === "sign-up" ? "/signup" : "/sign-in",
    );
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (mode === "sign-up" && password !== confirm)
      return setError("Passwords do not match.");
    if (mode === "sign-up" && !invite && !selectedPlan)
      return setError("Select an active Clintra plan.");
    if (mode === "sign-up" && (!acceptedTerms || !acceptedPrivacy))
      return setError("Accept the Terms and Privacy Policy to create an account.");
    if (password.length < 8)
      return setError("Use at least 8 characters for your password.");
    setBusy(true);
    try {
      if (mode === "sign-up" && !invite && selectedPlan) {
        const attribution = Object.fromEntries(
          ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "ref", "partner"]
            .map((key) => [key, params.get(key)])
            .filter((entry): entry is [string, string] => Boolean(entry[1])),
        );
        const selection = await createPlanSelection({
          plan: selectedPlan.slug,
          billing,
          companyName,
          countryCode,
          companySize,
          primaryUseCase,
          attribution: {
            ...(attribution.utm_source ? { utmSource: attribution.utm_source } : {}),
            ...(attribution.utm_medium ? { utmMedium: attribution.utm_medium } : {}),
            ...(attribution.utm_campaign ? { utmCampaign: attribution.utm_campaign } : {}),
            ...(attribution.utm_term ? { utmTerm: attribution.utm_term } : {}),
            ...(attribution.utm_content ? { utmContent: attribution.utm_content } : {}),
            ...(attribution.ref ? { referralCode: attribution.ref } : {}),
            ...(attribution.partner ? { partnerId: attribution.partner } : {}),
          },
          acceptedTerms: true,
          acceptedPrivacy: true,
        });
        window.sessionStorage.setItem("clintra-plan-selection", selection.selectionToken);
      }
      const session =
        mode === "sign-up"
          ? await signUp(`${firstName.trim()} ${lastName.trim()}`.trim(), email, password)
          : await signIn(email, password);
      if (session) await onAuthenticated(session);
      else
        setMessage(
          "Your account was created. Check your email if verification is required, then sign in.",
        );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Authentication failed.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth-shell">
      <section className="auth-story">
        <Brand light />
        <div className="auth-story-copy">
          <span className="overline">AI-powered revenue optimisation</span>
          <h1>Turn every commercial signal into a clearer next move.</h1>
          <p>
            Connect sales, marketing, website and revenue data. Clintra’s
            specialist agents build an accountable view of where growth is
            coming from and what to improve next.
          </p>
        </div>
        <div className="auth-proof">
          <span>
            <ShieldCheck size={22} />
            <b>Human governed</b>
            <small>Recommendations stay reviewable.</small>
          </span>
          <span>
            <Radar size={22} />
            <b>One revenue view</b>
            <small>Targets, pipeline and signals connected.</small>
          </span>
          <span>
            <Sparkles size={22} />
            <b>Specialist agents</b>
            <small>Focused analysis, not generic chat.</small>
          </span>
        </div>
      </section>
      <section className="auth-panel">
        <div className="auth-card">
          <span className="auth-icon">
            <LockKeyhole size={24} />
          </span>
          <div>
            <span className="overline">Secure workspace access</span>
            <h2>
              {mode === "sign-up"
                ? invite
                  ? `Join ${invite.organisationName}`
                  : "Create your Clintra account"
                : "Welcome back"}
            </h2>
            <p>
              {mode === "sign-up"
                ? invite
                  ? `Set a password to accept your invite and join as ${ROLE_LABEL[invite.role]}.`
                  : "Start with a secure Neon Auth account, then configure your company."
                : "Sign in to continue to your revenue workspace."}
            </p>
          </div>
          {mode === "sign-up" && !invite && (
            <div className="signup-plan-summary">
              <div className="signup-plan-heading">
                <div>
                  <span className="overline">Start your 30-day free trial</span>
                  <h3>{selectedPlan?.name ?? "Select a plan"}</h3>
                </div>
                <span className="no-card-badge">No credit card required</span>
              </div>
              {plansLoading ? (
                <p>Loading the current plan catalogue…</p>
              ) : plans.length ? (
                <>
                  <div className="signup-plan-controls">
                    <div>
                      <strong>Choose your plan</strong>
                      <small>Entitlements are validated by the Clintra API.</small>
                    </div>
                    <label>
                      Billing after trial
                      <select value={billing} onChange={(event) => setBilling(event.target.value as "monthly" | "annual")}>
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                      </select>
                    </label>
                  </div>
                  <div className="signup-plan-grid" aria-label="Clintra plans">
                    {plans.map((plan) => {
                      const price = plan.prices[billing];
                      const selected = plan.slug === selectedSlug;
                      return (
                        <button
                          type="button"
                          key={plan.slug}
                          className={selected ? "selected" : ""}
                          aria-pressed={selected}
                          onClick={() => setSelectedSlug(plan.slug)}
                        >
                          <span className="overline">{plan.name}</span>
                          <strong>
                            {price?.amountMinor == null
                              ? "Custom"
                              : new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: price.currency,
                                  maximumFractionDigits: 0,
                                }).format(price.amountMinor / 100)}
                          </strong>
                          <small>{price?.amountMinor == null ? "Contact Clintra" : `${billing} after trial`}</small>
                          <span>{String(plan.entitlements.fullUsers ?? "Custom")} users · {String(plan.entitlements.activeAgents ?? "Custom")} agents</span>
                          <b>{selected ? "Selected" : "Choose plan"}</b>
                        </button>
                      );
                    })}
                  </div>
                  {selectedPlan ? (
                    <>
                      <div className="plan-price-line">
                        <strong>
                          {selectedPrice?.amountMinor == null
                            ? "Custom pricing"
                            : new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: selectedPrice.currency,
                                maximumFractionDigits: 0,
                              }).format(selectedPrice.amountMinor / 100)}
                        </strong>
                        <span>after trial · {billing}</span>
                      </div>
                      <div className="plan-facts">
                        <span>{String(selectedPlan.entitlements.fullUsers ?? "Custom")} full users</span>
                        <span>{String(selectedPlan.entitlements.activeAgents ?? "Custom")} active agents</span>
                        <span>{String(selectedPlan.entitlements.monthlyCredits ?? "Contracted")} AI credits</span>
                        <span>{String(selectedPlan.entitlements.activeLeads ?? "Contracted")} active leads</span>
                      </div>
                      <p>You will not be charged automatically unless you add a payment method and explicitly activate a paid subscription.</p>
                    </>
                  ) : (
                    <p>Select one of the current plans above to continue.</p>
                  )}
                </>
              ) : (
                <div className="plan-catalogue-error">
                  <p>The plan catalogue could not be loaded. Signup is paused so an invalid plan can never be assigned silently.</p>
                  <button type="button" className="button secondary" onClick={() => setPlanReload((value) => value + 1)}>
                    <RefreshCw size={15} />
                    Retry plan catalogue
                  </button>
                </div>
              )}
            </div>
          )}
          {!isAuthConfigured && (
            <div className="form-alert error">
              Neon Auth is not configured. Add <code>VITE_NEON_AUTH_URL</code>{" "}
              to the Pages build.
            </div>
          )}
          <form onSubmit={submit} className="auth-form">
            {mode === "sign-up" && (
              <div className="auth-name-grid">
                <label>
                  First name
                  <input autoComplete="given-name" value={firstName} onChange={(event) => setFirstName(event.target.value)} placeholder="Alex" required />
                </label>
                <label>
                  Last name
                  <input autoComplete="family-name" value={lastName} onChange={(event) => setLastName(event.target.value)} placeholder="Morgan" required />
                </label>
              </div>
            )}
            <label>
              Work email
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="alex@company.com"
                required
                readOnly={Boolean(invite) && mode === "sign-up"}
                title={invite && mode === "sign-up" ? "This invite is locked to this email address" : undefined}
              />
            </label>
            <label>
              Password
              <input
                type="password"
                autoComplete={
                  mode === "sign-up" ? "new-password" : "current-password"
                }
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
            </label>
            {mode === "sign-up" && (
              <label>
                Confirm password
                <input type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} placeholder="Repeat your password" required />
              </label>
            )}
            {mode === "sign-up" && !invite && (
              <>
                <label>
                  Company name
                  <input autoComplete="organization" value={companyName} onChange={(event) => setCompanyName(event.target.value)} placeholder="Acme Ltd" required />
                </label>
                <div className="auth-name-grid">
                  <label>
                    Country
                    <input value={countryCode} onChange={(event) => setCountryCode(event.target.value.toUpperCase())} maxLength={2} placeholder="GB" required />
                  </label>
                  <label>
                    Company size
                    <select value={companySize} onChange={(event) => setCompanySize(event.target.value)}>
                      {["1-10", "11-50", "51-200", "201-500", "501+"].map((size) => <option key={size}>{size}</option>)}
                    </select>
                  </label>
                </div>
                <label>
                  Primary use case
                  <select value={primaryUseCase} onChange={(event) => setPrimaryUseCase(event.target.value)}>
                    {["Revenue planning", "Marketing operations", "Sales operations", "Campaign management", "Lead intelligence", "Content and social", "Revenue forecasting"].map((useCase) => <option key={useCase}>{useCase}</option>)}
                  </select>
                </label>
              </>
            )}
            {mode === "sign-up" && (
              <>
                <label className="consent-check compact">
                  <input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} />
                  <span>I accept the Clintra Terms of Service.</span>
                </label>
                <label className="consent-check compact">
                  <input type="checkbox" checked={acceptedPrivacy} onChange={(event) => setAcceptedPrivacy(event.target.checked)} />
                  <span>I accept the Privacy Policy.</span>
                </label>
              </>
            )}
            {error && (
              <div className="form-alert error">
                <X size={17} />
                {error}
              </div>
            )}
            {message && (
              <div className="form-alert success">
                <Check size={17} />
                {message}
              </div>
            )}
            <button
              className="button primary large"
              disabled={busy || !isAuthConfigured || (mode === "sign-up" && plansLoading)}
            >
              {busy ? (
                <LoaderCircle className="spin" />
              ) : mode === "sign-up" ? (
                "Create account"
              ) : (
                "Sign in"
              )}
              <ArrowRight size={18} />
            </button>
          </form>
          <div className="auth-switch">
            {mode === "sign-up"
              ? "Already have an account?"
              : "New to Clintra?"}{" "}
            <button
              onClick={() =>
                switchMode(mode === "sign-up" ? "sign-in" : "sign-up")
              }
            >
              {mode === "sign-up" ? "Sign in" : "Create an account"}
            </button>
          </div>
          <small className="secure-note">
            <ShieldCheck size={14} />
            Authentication is managed by Neon Auth and Better Auth.
          </small>
        </div>
      </section>
    </main>
  );
}

const REVENUE_SOURCE_TYPES = [
  "product",
  "service",
  "subscription",
  "commerce",
  "partner",
  "other",
] as const;
const blankOnboarding: OnboardingSubmission = {
  company: {
    name: "",
    websiteUrl: "",
    industry: "",
    countryCode: "GB",
    employeeBand: "1-10",
    businessModel: "B2B services",
    valueProposition: "",
    targetMarkets: ["United Kingdom"],
    primaryGoal: "",
  },
  commercial: {
    annualRevenueTarget: 1_000_000,
    currentRevenue: 0,
    currentPipeline: 0,
    averageDealValue: 25_000,
    leadToOpportunityRate: 0.08,
    opportunityWinRate: 0.25,
    salesCycleDays: 60,
    currency: "GBP",
  },
  revenueSources: [
    { name: "Primary services", type: "service", annualValue: 0 },
  ],
  integrations: [],
  scanWebsite: true,
  externalAnalysisConsent: true,
};

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>
        {label}
        {required && (
          <span className="required-star" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function useCloseOnOutsideClick(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);
  return ref;
}

function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useCloseOnOutsideClick(open, () => setOpen(false));
  const selected = options.find((option) => option.value === value);
  const filtered = query
    ? options.filter((option) =>
        option.label.toLowerCase().includes(query.toLowerCase()),
      )
    : options;
  return (
    <div className="searchable-select" ref={ref}>
      <button
        type="button"
        className="searchable-select-trigger"
        onClick={() => {
          setOpen((current) => !current);
          setQuery("");
        }}
      >
        <span className={selected ? "" : "placeholder"}>
          {selected ? selected.label : placeholder || "Select..."}
        </span>
      </button>
      {open && (
        <div className="searchable-select-panel">
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search..."
          />
          <ul>
            {filtered.length === 0 && <li className="empty">No matches</li>}
            {filtered.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  className={option.value === value ? "selected" : ""}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SearchableMultiSelect({
  values,
  onChange,
  options,
  placeholder,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useCloseOnOutsideClick(open, () => setOpen(false));
  const filtered = options.filter(
    (option) =>
      !values.includes(option.value) &&
      (!query || option.label.toLowerCase().includes(query.toLowerCase())),
  );
  const add = (optionValue: string) => {
    onChange([...values, optionValue]);
    setQuery("");
  };
  const remove = (optionValue: string) =>
    onChange(values.filter((current) => current !== optionValue));
  return (
    <div className="searchable-select multi" ref={ref}>
      <div
        className="searchable-select-trigger chips"
        onClick={() => setOpen(true)}
      >
        {values.map((current) => {
          const option = options.find((item) => item.value === current);
          return (
            <span className="chip" key={current}>
              {option ? option.label : current}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  remove(current);
                }}
                aria-label={`Remove ${option ? option.label : current}`}
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={values.length ? "" : placeholder || "Search..."}
        />
      </div>
      {open && (
        <div className="searchable-select-panel">
          <ul>
            {filtered.length === 0 && <li className="empty">No matches</li>}
            {filtered.map((option) => (
              <li key={option.value}>
                <button type="button" onClick={() => add(option.value)}>
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Onboarding({
  session,
  bootstrap,
  onComplete,
  onSignOut,
}: {
  session: AuthSession;
  bootstrap: Bootstrap;
  onComplete: (workspace: Workspace) => void;
  onSignOut: () => void;
}) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(blankOnboarding);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [otherIndustry, setOtherIndustry] = useState(false);
  const steps = ["Company", "Revenue model", "Revenue sources", "Connections"];
  const countryCodeOptions = useMemo(
    () => COUNTRIES.map((country) => ({ value: country.code, label: country.name })),
    [],
  );
  const countryNameOptions = useMemo(
    () => COUNTRIES.map((country) => ({ value: country.name, label: country.name })),
    [],
  );
  const updateCompany = (
    key: keyof OnboardingSubmission["company"],
    value: string | string[],
  ) =>
    setForm((current) => ({
      ...current,
      company: { ...current.company, [key]: value },
    }));
  const updateCommercial = (
    key: keyof OnboardingSubmission["commercial"],
    value: number | string,
  ) =>
    setForm((current) => ({
      ...current,
      commercial: { ...current.commercial, [key]: value },
    }));
  const source = form.revenueSources;
  const next = () => {
    setError("");
    if (step === 0) {
      const missing: string[] = [];
      if (!form.company.name.trim()) missing.push("Company name");
      if (!form.company.websiteUrl.trim()) missing.push("Website");
      if (!form.company.industry.trim()) missing.push("Industry");
      if (form.company.countryCode.trim().length !== 2) missing.push("Country");
      if (!form.company.employeeBand) missing.push("Team size");
      if (!form.company.businessModel) missing.push("Business model");
      if (form.company.valueProposition.trim().length < 10)
        missing.push("Value proposition (min. 10 characters)");
      if (!form.company.primaryGoal.trim()) missing.push("Primary revenue goal");
      if (form.company.targetMarkets.length === 0) missing.push("Target markets");
      if (missing.length > 0)
        return setError(`Complete the required fields: ${missing.join(", ")}.`);
    }
    setStep((value) => Math.min(3, value + 1));
  };
  const finish = async () => {
    if (!consent)
      return setError(
        "Confirm that Clintra may analyse the website and connected business data.",
      );
    setBusy(true);
    setError("");
    try {
      onComplete(
        await completeOnboarding(
          session.session.token,
          bootstrap.tenantId,
          form,
        ),
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Unable to complete onboarding.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="onboarding-shell">
      <header className="onboarding-top">
        <Brand />
        <div>
          <span>{session.user.email}</span>
          <button onClick={onSignOut}>Sign out</button>
        </div>
      </header>
      <div className="onboarding-layout">
        <aside className="onboarding-side">
          <span className="overline">Workspace setup</span>
          <h1>Give Clintra the context to make useful decisions.</h1>
          <p>
            We’ll establish your commercial baseline, scan your website and
            prepare the right specialist agents.
          </p>
          <div className="step-list">
            {steps.map((name, index) => (
              <div
                className={`${index === step ? "active" : ""} ${index < step ? "done" : ""}`}
                key={name}
              >
                <span>{index < step ? <Check size={17} /> : index + 1}</span>
                <div>
                  <b>{name}</b>
                  <small>
                    {
                      [
                        "Who you are and who you serve",
                        "Targets, pipeline and conversion",
                        "How your company earns revenue",
                        "Data sources and website scan",
                      ][index]
                    }
                  </small>
                </div>
              </div>
            ))}
          </div>
        </aside>
        <section className="onboarding-card">
          <div className="onboarding-plan-context">
            <div>
              <span className="overline">Selected trial plan</span>
              <strong>{bootstrap.selectedPlan.name}</strong>
            </div>
            <span>
              {bootstrap.selectedPlan.status === "trialing" ? "30-day trial" : labelFor(bootstrap.selectedPlan.status)}
              {bootstrap.selectedPlan.trialEndsAt
                ? ` · ends ${new Date(bootstrap.selectedPlan.trialEndsAt).toLocaleDateString()}`
                : ""}
            </span>
          </div>
          <div className="progress">
            <span style={{ width: `${(step + 1) * 25}%` }} />
          </div>
          <div className="onboarding-heading">
            <span className="overline">Step {step + 1} of 4</span>
            <h2>
              {
                [
                  "Tell us about the company",
                  "Set the commercial baseline",
                  "Map your revenue sources",
                  "Connect the revenue system",
                ][step]
              }
            </h2>
            <p>
              {
                [
                  "This becomes the shared context used by every specialist agent.",
                  "Clintra uses transparent formulas to connect targets to required activity.",
                  "Separate products, services and recurring income so performance can be compared.",
                  "Choose the systems you use. OAuth connection can be completed from Integrations.",
                ][step]
              }
            </p>
          </div>
          {step === 0 && (
            <div className="form-grid onboarding-form">
              <Field label="Company name" required>
                <input
                  value={form.company.name}
                  onChange={(e) => updateCompany("name", e.target.value)}
                  placeholder="Acme Ltd"
                />
              </Field>
              <Field label="Website" required>
                <input
                  type="url"
                  value={form.company.websiteUrl}
                  onChange={(e) => updateCompany("websiteUrl", e.target.value)}
                  placeholder="https://example.com"
                />
              </Field>
              <Field label="Industry" required>
                <select
                  value={otherIndustry ? "Other" : form.company.industry}
                  onChange={(e) => {
                    const selected = e.target.value;
                    if (selected === "Other") {
                      setOtherIndustry(true);
                      updateCompany("industry", "");
                    } else {
                      setOtherIndustry(false);
                      updateCompany("industry", selected);
                    }
                  }}
                >
                  <option value="" disabled>
                    Select an industry
                  </option>
                  {INDUSTRIES.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
                {otherIndustry && (
                  <input
                    style={{ marginTop: 8 }}
                    value={form.company.industry}
                    onChange={(e) => updateCompany("industry", e.target.value)}
                    placeholder="Enter your industry"
                  />
                )}
              </Field>
              <Field label="Country" required>
                <SearchableSelect
                  value={form.company.countryCode}
                  onChange={(value) => updateCompany("countryCode", value)}
                  options={countryCodeOptions}
                  placeholder="Select a country"
                />
              </Field>
              <Field label="Team size" required>
                <select
                  value={form.company.employeeBand}
                  onChange={(e) =>
                    updateCompany("employeeBand", e.target.value)
                  }
                >
                  {["1-10", "11-50", "51-200", "201-500", "501+"].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </Field>
              <Field label="Business model" required>
                <select
                  value={form.company.businessModel}
                  onChange={(e) =>
                    updateCompany("businessModel", e.target.value)
                  }
                >
                  {[
                    "B2B services",
                    "B2B SaaS",
                    "B2C",
                    "Ecommerce",
                    "Marketplace",
                    "Mixed product and services",
                  ].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </Field>
              <Field
                label="Value proposition"
                required
                hint={`Minimum 10 characters (${form.company.valueProposition.trim().length}/10)`}
              >
                <textarea
                  value={form.company.valueProposition}
                  onChange={(e) =>
                    updateCompany("valueProposition", e.target.value)
                  }
                  placeholder="What value do you create, and for whom?"
                />
              </Field>
              <Field label="Primary revenue goal" required>
                <textarea
                  value={form.company.primaryGoal}
                  onChange={(e) => updateCompany("primaryGoal", e.target.value)}
                  placeholder="Grow recurring revenue by 30% while improving win rate."
                />
              </Field>
              <Field label="Target markets" required hint="Search and select one or more countries">
                <SearchableMultiSelect
                  values={form.company.targetMarkets}
                  onChange={(values) => updateCompany("targetMarkets", values)}
                  options={countryNameOptions}
                  placeholder="Search countries..."
                />
              </Field>
            </div>
          )}
          {step === 1 && (
            <div className="form-grid onboarding-form numeric">
              <Field label="Annual revenue target">
                <input
                  type="number"
                  value={form.commercial.annualRevenueTarget}
                  onChange={(e) =>
                    updateCommercial(
                      "annualRevenueTarget",
                      Number(e.target.value),
                    )
                  }
                />
              </Field>
              <Field label="Revenue achieved so far">
                <input
                  type="number"
                  value={form.commercial.currentRevenue}
                  onChange={(e) =>
                    updateCommercial("currentRevenue", Number(e.target.value))
                  }
                />
              </Field>
              <Field label="Current qualified pipeline">
                <input
                  type="number"
                  value={form.commercial.currentPipeline}
                  onChange={(e) =>
                    updateCommercial("currentPipeline", Number(e.target.value))
                  }
                />
              </Field>
              <Field label="Average deal value">
                <input
                  type="number"
                  value={form.commercial.averageDealValue}
                  onChange={(e) =>
                    updateCommercial("averageDealValue", Number(e.target.value))
                  }
                />
              </Field>
              <Field label="Lead to opportunity %">
                <input
                  type="number"
                  value={form.commercial.leadToOpportunityRate * 100}
                  onChange={(e) =>
                    updateCommercial(
                      "leadToOpportunityRate",
                      Number(e.target.value) / 100,
                    )
                  }
                />
              </Field>
              <Field label="Opportunity win rate %">
                <input
                  type="number"
                  value={form.commercial.opportunityWinRate * 100}
                  onChange={(e) =>
                    updateCommercial(
                      "opportunityWinRate",
                      Number(e.target.value) / 100,
                    )
                  }
                />
              </Field>
              <Field label="Average sales cycle (days)">
                <input
                  type="number"
                  value={form.commercial.salesCycleDays}
                  onChange={(e) =>
                    updateCommercial("salesCycleDays", Number(e.target.value))
                  }
                />
              </Field>
              <Field label="Currency">
                <select
                  value={form.commercial.currency}
                  onChange={(e) => updateCommercial("currency", e.target.value)}
                >
                  {["GBP", "USD", "EUR", "CAD", "AUD"].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </Field>
              <div className="formula-note">
                <ShieldCheck size={22} />
                <div>
                  <b>Transparent planning</b>
                  <p>
                    Targets are calculated deterministically. Agent
                    recommendations cannot silently change these assumptions.
                  </p>
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="sources-editor">
              {source.map((item, index) => (
                <div className="source-row" key={index}>
                  <Field label="Revenue source">
                    <input
                      value={item.name}
                      onChange={(e) =>
                        setForm((c) => ({
                          ...c,
                          revenueSources: c.revenueSources.map((s, i) =>
                            i === index ? { ...s, name: e.target.value } : s,
                          ),
                        }))
                      }
                    />
                  </Field>
                  <Field label="Type">
                    <select
                      value={item.type}
                      onChange={(e) =>
                        setForm((c) => ({
                          ...c,
                          revenueSources: c.revenueSources.map((s, i) =>
                            i === index
                              ? { ...s, type: e.target.value as typeof s.type }
                              : s,
                          ),
                        }))
                      }
                    >
                      {[
                        "product",
                        "service",
                        "subscription",
                        "commerce",
                        "partner",
                        "other",
                      ].map((x) => (
                        <option key={x} value={x}>
                          {labelFor(x)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Annual value">
                    <input
                      type="number"
                      value={item.annualValue}
                      onChange={(e) =>
                        setForm((c) => ({
                          ...c,
                          revenueSources: c.revenueSources.map((s, i) =>
                            i === index
                              ? { ...s, annualValue: Number(e.target.value) }
                              : s,
                          ),
                        }))
                      }
                    />
                  </Field>
                  <button
                    className="icon-action"
                    aria-label="Remove revenue source"
                    disabled={source.length === 1}
                    onClick={() =>
                      setForm((c) => ({
                        ...c,
                        revenueSources: c.revenueSources.filter(
                          (_, i) => i !== index,
                        ),
                      }))
                    }
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button
                className="button secondary"
                onClick={() =>
                  setForm((c) => ({
                    ...c,
                    revenueSources: [
                      ...c.revenueSources,
                      { name: "", type: "service", annualValue: 0 },
                    ],
                  }))
                }
              >
                <Plus size={17} />
                Add revenue source
              </button>
            </div>
          )}
          {step === 3 && (
            <div className="connections-step">
              <div className="integration-picker">
                {onboardingIntegrationOptions.map(([id, name, description]) => {
                  const selected = form.integrations.includes(id);
                  return (
                    <button
                      className={selected ? "selected" : ""}
                      key={id}
                      onClick={() =>
                        setForm((c) => ({
                          ...c,
                          integrations: selected
                            ? c.integrations.filter((x) => x !== id)
                            : [...c.integrations, id],
                        }))
                      }
                    >
                      <span>
                        {selected ? <Check size={18} /> : <Plug size={18} />}
                      </span>
                      <div>
                        <b>{name}</b>
                        <small>{description}</small>
                      </div>
                    </button>
                  );
                })}
              </div>
              <label className="scan-choice">
                <input
                  type="checkbox"
                  checked={form.scanWebsite}
                  onChange={(e) =>
                    setForm((c) => ({ ...c, scanWebsite: e.target.checked }))
                  }
                />
                <span>
                  <ScanSearch size={23} />
                </span>
                <div>
                  <b>Scan {form.company.websiteUrl || "your website"}</b>
                  <p>
                    Website Intelligence will inspect the public homepage for
                    messaging, metadata, conversion paths, forms and calls to
                    action.
                  </p>
                </div>
              </label>
              <label className="consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>
                  I authorise Clintra to analyse the public website and the
                  business data I choose to connect. I understand
                  recommendations require human review.
                </span>
              </label>
            </div>
          )}
          {error && (
            <div className="form-alert error">
              <X size={17} />
              {error}
            </div>
          )}
          <footer className="onboarding-actions">
            <button
              className="button secondary"
              disabled={step === 0 || busy}
              onClick={() => setStep((s) => s - 1)}
            >
              <ArrowLeft size={17} />
              Back
            </button>
            {step < 3 ? (
              <button className="button primary" onClick={next}>
                Continue
                <ArrowRight size={17} />
              </button>
            ) : (
              <button
                className="button primary"
                disabled={busy}
                onClick={finish}
              >
                {busy ? (
                  <>
                    <LoaderCircle className="spin" />
                    Scanning and preparing…
                  </>
                ) : (
                  <>
                    Complete setup
                    <Sparkles size={17} />
                  </>
                )}
              </button>
            )}
          </footer>
        </section>
      </div>
    </main>
  );
}

type View =
  | "Overview"
  | "Business Discovery"
  | "Revenue Orchestration"
  | "Revenue Plan"
  | "Website Intelligence"
  | "Lead Intelligence"
  | "Recommendations"
  | "Integrations"
  | "Agent Centre"
  | "Marketing Strategy"
  | "Campaign Planning"
  | "Sales Planning"
  | "Content Generation"
  | "Performance Optimisation"
  | "Buying Intent"
  | "Lead Nurturing"
  | "Revenue Forecasting"
  | "Conversion Optimisation"
  | "Reputation Intelligence"
  | "Content Strategy"
  | "Outreach Personalisation"
  | "Market Research"
  | "Social Presence"
  | "Plan & Billing"
  | "AI Credits & Usage"
  | "Platform Readiness"
  | "Settings"
  | "Team"
  | "API"
  | "Business Profile"
  | "Approvals"
  // Group headers only -- pure navigation groupings with no page of their
  // own, exactly like "Settings" already worked before this reorganisation
  // (clicking the label itself falls through to Generic's placeholder;
  // clicking the chevron expands the group). Grouped around the real
  // workflow areas -- Revenue, Marketing, Sales, Intelligence, Campaigns,
  // AI Agents, Optimisation -- instead of one flat list of every agent.
  | "Revenue"
  | "Marketing"
  | "Sales"
  | "Intelligence"
  | "Campaigns"
  | "AI Agents"
  | "Optimisation";
const navigation: Array<[View, typeof LayoutDashboard, string?]> = [
  ["Overview", LayoutDashboard],
  ["Revenue", CircleDollarSign],
  ["Marketing", Megaphone],
  ["Sales", Briefcase],
  ["Intelligence", Radar],
  ["Campaigns", Zap],
  ["AI Agents", Bot],
  ["Optimisation", Gauge],
  ["Approvals", ClipboardCheck],
  ["Integrations", Plug],
  ["Settings", Building2],
];
const NAV_CHILDREN: Partial<Record<View, Array<[View, typeof LayoutDashboard, string?]>>> = {
  Revenue: [
    ["Revenue Orchestration", Radar],
    ["Revenue Plan", Target],
    ["Revenue Forecasting", LineChart],
  ],
  Marketing: [
    ["Marketing Strategy", Route],
    ["Content Strategy", Newspaper],
    ["Content Generation", FileText],
  ],
  Sales: [
    ["Sales Planning", MapPinned],
    ["Outreach Personalisation", Send],
  ],
  Intelligence: [
    ["Business Discovery", Compass],
    ["Market Research", Search],
    ["Website Intelligence", Globe2],
    ["Lead Intelligence", UsersRound],
    ["Buying Intent", Flame],
    ["Reputation Intelligence", ShieldAlert],
    ["Social Presence", Activity],
  ],
  Campaigns: [
    ["Campaign Planning", ClipboardList],
    ["Lead Nurturing", Workflow],
  ],
  "AI Agents": [
    ["Agent Centre", LayoutDashboard, "Agents & Entitlements"],
    ["Recommendations", Lightbulb],
  ],
  Optimisation: [
    ["Performance Optimisation", TrendingUp],
    ["Conversion Optimisation", FlaskConical],
  ],
  Settings: [
    ["Business Profile", Briefcase, "Company & Revenue Profile"],
    ["Team", UserPlus, "Team & Roles"],
    ["API", KeyRound, "API Keys"],
    ["Plan & Billing", CircleDollarSign, "Trial, Plans & Billing"],
    ["AI Credits & Usage", Activity],
    ["Platform Readiness", ShieldCheck],
  ],
};
const NAV_PARENT_OF: Partial<Record<View, View>> = Object.fromEntries(
  Object.entries(NAV_CHILDREN).flatMap(([parent, children]) =>
    (children ?? []).map(([child]) => [child, parent as View]),
  ),
);
const familyRootOf = (view: View): View | null =>
  NAV_PARENT_OF[view] ?? (NAV_CHILDREN[view] ? view : null);
// Nav items visible only once their corresponding specialist agent is active.
const AGENT_GATED_VIEWS: Partial<Record<View, string>> = {
  "Business Discovery": "business-discovery",
  "Website Intelligence": "website-intelligence",
  "Lead Intelligence": "lead-intelligence",
  "Revenue Orchestration": "orchestrator",
  "Marketing Strategy": "marketing-strategy",
  "Campaign Planning": "campaign-planning",
  "Sales Planning": "sales-planning",
  "Content Generation": "content-generation",
  "Performance Optimisation": "performance-optimisation",
  "Buying Intent": "buying-intent",
  "Lead Nurturing": "lead-nurturing",
  "Revenue Forecasting": "revenue-forecasting",
  "Conversion Optimisation": "conversion-optimisation",
  "Reputation Intelligence": "reputation-intelligence",
  "Content Strategy": "content-strategy",
  "Outreach Personalisation": "outreach-personalisation",
  "Market Research": "market-research",
  "Social Presence": "social-presence",
};
// These agent pages render their own large page-title heading (agent-pill
// badge, status, h1, action button) -- showing the plain view name again in
// the global topbar next to it would just repeat the same title twice.
const VIEWS_WITH_OWN_PAGE_TITLE = new Set<View>(
  (Object.keys(AGENT_GATED_VIEWS) as View[]).filter((name) => name !== "Website Intelligence"),
);
// The URL each nav item pushes to on click -- pulled out of the sidebar's
// button handler so ApprovalsView's "Review" links can reuse the exact
// same path a sidebar click would produce, instead of duplicating it.
function pathForView(view: View): string {
  switch (view) {
    case "Plan & Billing": return "/settings/billing";
    case "AI Credits & Usage": return "/settings/usage";
    case "Agent Centre": return "/settings/agents";
    case "Integrations": return "/integrations";
    case "Team": return "/settings/team";
    case "API": return "/settings/api-keys";
    case "Business Profile": return "/settings/business-profile";
    case "Platform Readiness": return "/admin/platform-readiness";
    case "Approvals": return "/approvals";
    case "Website Intelligence": return "/website-intelligence";
    case "Lead Intelligence": return "/lead-intelligence";
    case "Revenue Orchestration": return "/revenue-orchestration";
    case "Business Discovery": return "/business-discovery";
    case "Marketing Strategy": return "/marketing-strategy";
    case "Campaign Planning": return "/campaign-planning";
    case "Sales Planning": return "/sales-planning";
    case "Content Generation": return "/content-generation";
    case "Performance Optimisation": return "/performance-optimisation";
    case "Buying Intent": return "/buying-intent";
    case "Lead Nurturing": return "/lead-nurturing";
    case "Revenue Forecasting": return "/revenue-forecasting";
    case "Conversion Optimisation": return "/conversion-optimisation";
    case "Reputation Intelligence": return "/reputation-intelligence";
    case "Content Strategy": return "/content-strategy";
    case "Outreach Personalisation": return "/outreach-personalisation";
    case "Market Research": return "/market-research";
    case "Social Presence": return "/social-presence";
    default: return "/";
  }
}
// Maps an ApprovalItem.agentId (the literal string each service writes
// into recommendations.agent_id, or the fixed id approvals.ts assigns for
// its own bespoke-table entity types) to the View that reviews it. This is
// a DIFFERENT id space from AGENT_GATED_VIEWS' activation ids above --
// e.g. Revenue Orchestration's recommendations rows are literally tagged
// 'revenue-orchestration', not the "orchestrator" id used to check
// whether that agent is active -- so the two maps can't be merged.
const APPROVAL_AGENT_VIEW: Record<string, View> = {
  "buying-intent": "Buying Intent",
  "content-strategy": "Content Strategy",
  "conversion-optimisation": "Conversion Optimisation",
  "performance-optimisation": "Performance Optimisation",
  "revenue-forecasting": "Revenue Forecasting",
  "reputation-intelligence": "Reputation Intelligence",
  "lead-nurturing": "Lead Nurturing",
  "revenue-orchestration": "Revenue Orchestration",
  "lead-intelligence": "Lead Intelligence",
  "sales-planning": "Sales Planning",
  "content-generation": "Content Generation",
  "outreach-personalisation": "Outreach Personalisation",
  "marketing-strategy": "Marketing Strategy",
  "campaign-planning": "Campaign Planning",
  "business-discovery": "Business Discovery",
};

function Panel({
  title,
  kicker,
  children,
  action,
}: {
  title: string;
  kicker?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="panel">
      <header>
        <div>
          {kicker && <span className="overline">{kicker}</span>}
          <h2>{title}</h2>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}
// Slide-in detail panel for evidence/audit-trail views that shouldn't lose
// page context by navigating away. No such pattern existed anywhere in this
// app before -- built fresh, portal-mounted the same way the sidebar's
// collapsed-rail flyout already is.
function Drawer({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer-panel" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-header">
          <h2>{title}</h2>
          <button className="drawer-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
function Metric({
  label,
  value,
  meta,
  icon: Icon,
  metaTone,
  tone,
  valueSize,
}: {
  label: string;
  value: string;
  meta: string;
  icon: typeof Activity;
  metaTone?: "success";
  tone?: "warning";
  valueSize?: "sm";
}) {
  return (
    <article className={`metric ${tone ?? ""}`}>
      <span>
        <Icon size={21} />
      </span>
      <small>{label}</small>
      <strong className={valueSize === "sm" ? "metric-value-sm" : undefined}>{value}</strong>
      <p className={metaTone === "success" ? "metric-meta-success" : ""}>{meta}</p>
    </article>
  );
}

function Overview({
  workspace,
  onNavigate,
}: {
  workspace: Workspace;
  onNavigate: (view: View) => void;
}) {
  const commercial = workspace.commercial,
    currency = String(commercial.currency ?? "GBP"),
    target = numberFrom(commercial.annualRevenueTarget),
    revenue = numberFrom(commercial.currentRevenue),
    pipeline = numberFrom(commercial.currentPipeline),
    deal = numberFrom(commercial.averageDealValue);
  const targetRun = workspace.agentRuns.find(
    (run) => run.agent === "revenue-forecasting",
  );
  const forecast = targetRun?.output ?? {};
  const attainment = target ? Math.round((revenue / target) * 100) : 0;

  const dealsNeeded =
    numberFrom(forecast.dealsNeeded) ||
    (deal ? Math.ceil(target / Math.max(1, deal)) : 0);
  const opportunitiesNeeded = numberFrom(forecast.opportunitiesNeeded);
  const leadsNeeded = numberFrom(forecast.leadsNeeded);
  const pipelineRequired = numberFrom(forecast.pipelineRequired);
  const pipelineGap = numberFrom(forecast.pipelineGap);

  const recordCountFor = (entityTypes: string[]) =>
    workspace.integrationRecordCounts
      .filter((entry) => entityTypes.includes(entry.entityType))
      .reduce((sum, entry) => sum + entry.count, 0);
  const currentLeads = recordCountFor(["contact", "lead"]);
  const currentOpportunities = recordCountFor(["deal", "opportunity"]);

  // Revenue Health -- a new, transparent score computed from real signals,
  // not a value that exists anywhere in the backend. Coverage is "how much
  // of the required pipeline do we actually have," separate from the
  // forecasting agent's own differently-defined coverageRatio field.
  const coverageRatio = pipelineRequired
    ? pipeline / pipelineRequired
    : target
      ? pipeline / target
      : null;
  const needsApprovalCount = workspace.agents.filter(
    (agent) => agent.status === "needs_approval",
  ).length;
  let healthScore: number | null = null;
  if (coverageRatio !== null) {
    healthScore = Math.max(0, Math.min(100, Math.round(coverageRatio * 100)));
    healthScore -= Math.min(20, needsApprovalCount * 10);
    if (workspace.websiteScanStatus === "failed") healthScore -= 10;
    if (workspace.billing.trial && workspace.billing.trial.daysRemaining <= 3)
      healthScore -= 15;
    healthScore = Math.max(0, Math.min(100, healthScore));
  }
  const healthLabel =
    healthScore === null
      ? "Not set up"
      : healthScore >= 70
        ? "Healthy"
        : healthScore >= 40
          ? "At Risk"
          : "Critical";

  type Alert = {
    severity: "critical" | "warning";
    message: string;
    source: string;
  };
  const alerts: Alert[] = [];
  if (workspace.billing.trial && workspace.billing.trial.daysRemaining <= 3) {
    const days = workspace.billing.trial.daysRemaining;
    alerts.push({
      severity: "critical",
      message: `Trial ends in ${days} day${days === 1 ? "" : "s"}.`,
      source: "Billing",
    });
  }
  workspace.agents
    .filter((agent) => agent.status === "needs_approval")
    .forEach((agent) =>
      alerts.push({
        severity: "warning",
        message: `${agent.name} has output waiting for your review.`,
        source: agent.name,
      }),
    );
  workspace.integrations
    .filter((item) => item.status === "connected")
    .forEach((item) => {
      const staleMs = item.lastSyncedAt
        ? Date.now() - new Date(item.lastSyncedAt).getTime()
        : Number.POSITIVE_INFINITY;
      if (staleMs > 24 * 60 * 60 * 1000)
        alerts.push({
          severity: "warning",
          message: `${labelFor(item.provider)} hasn't synced in over 24 hours.`,
          source: labelFor(item.provider),
        });
    });
  if (workspace.websiteScanStatus === "failed")
    alerts.push({
      severity: "critical",
      message: workspace.websiteScanError || "The last website scan failed.",
      source: "Website Intelligence",
    });

  type ActivityItem = { label: string; detail: string; timestamp: string };
  const activity: ActivityItem[] = [
    ...workspace.agentRuns.map((run) => ({
      label: `${labelFor(run.agent)} ${run.status === "completed" ? "completed analysis" : labelFor(run.status)}`,
      detail: run.summary || "",
      timestamp: run.startedAt,
    })),
    ...(workspace.websiteScan?.scanHistory ?? []).map((entry) => ({
      label: `Website scan completed (score ${entry.score}/100)`,
      detail: `${entry.pagesScanned} pages scanned`,
      timestamp: entry.scannedAt,
    })),
    ...workspace.integrations
      .filter((item) => item.lastSyncedAt !== null)
      .map((item) => ({
        label: `${labelFor(item.provider)} synchronised`,
        detail: "",
        timestamp: item.lastSyncedAt as string,
      })),
  ]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 8);

  const recommendations = workspace.websiteScan?.opportunities ?? [];
  const timelineMax = Math.max(target, revenue, pipeline, 1);
  const curatedAgents = workspace.agents
    .filter((agent) => agent.id !== "orchestrator")
    .slice()
    .sort((a, b) => {
      const rank = (agent: typeof a) =>
        agent.status === "needs_approval" ? 0 : agent.active ? 1 : 2;
      return rank(a) - rank(b);
    })
    .slice(0, 6);
  const connectedCount = workspace.integrations.filter(
    (item) => item.status === "connected",
  ).length;

  return (
    <div className="page-stack">
      <div className="welcome-banner">
        <div>
          <span>
            <Sparkles size={22} />
          </span>
          <div>
            <small>Revenue system initialised</small>
            <h2>{workspace.organisationName} is ready for analysis.</h2>
            <p>
              {
                workspace.agents.filter((agent) => agent.status === "completed")
                  .length
              }{" "}
              of {workspace.agents.length} specialist agents completed an
              initial task. Connect live systems to deepen the evidence.
            </p>
          </div>
        </div>
        <button className="button inverse" onClick={() => onNavigate("Agent Centre")}>
          Review agent activity
          <ArrowRight size={17} />
        </button>
      </div>
      <div className="metrics">
        <Metric
          label="Revenue target"
          value={money(target, currency)}
          meta={`${attainment}% achieved`}
          icon={Target}
        />
        <Metric
          label="Current revenue"
          value={money(revenue, currency)}
          meta={`${money(Math.max(0, target - revenue), currency)} remaining`}
          icon={CircleDollarSign}
        />
        <Metric
          label="Qualified pipeline"
          value={money(pipeline, currency)}
          meta={`${target ? (pipeline / target).toFixed(1) : "0.0"}× target coverage`}
          icon={Activity}
        />
        <Metric
          label="Revenue health"
          value={healthLabel}
          meta={healthScore === null ? "Set up your revenue plan to see this" : `${healthScore}/100`}
          icon={ShieldCheck}
        />
      </div>
      <div className="overview-grid">
        <Panel title="Revenue Timeline / Forecast" kicker="Where revenue stands today">
          {target || pipeline || revenue ? (
            <div className="revenue-timeline">
              <div className="revenue-timeline-row">
                <small>Target</small>
                <div className="revenue-timeline-track">
                  <span className="target" style={{ width: `${(target / timelineMax) * 100}%` }} />
                </div>
                <b>{money(target, currency)}</b>
              </div>
              <div className="revenue-timeline-row">
                <small>Current revenue</small>
                <div className="revenue-timeline-track">
                  <span className="actual" style={{ width: `${(revenue / timelineMax) * 100}%` }} />
                </div>
                <b>{money(revenue, currency)}</b>
              </div>
              <div className="revenue-timeline-row">
                <small>Qualified pipeline</small>
                <div className="revenue-timeline-track">
                  <span className="forecast" style={{ width: `${(pipeline / timelineMax) * 100}%` }} />
                </div>
                <b>{money(pipeline, currency)}</b>
              </div>
              <p className="revenue-timeline-note">
                {pipelineGap > 0
                  ? `${money(pipelineGap, currency)} more pipeline needed to be on track for target.`
                  : pipelineRequired
                    ? "Pipeline currently covers what's required for target."
                    : "Complete your revenue plan to see a coverage readout."}
              </p>
            </div>
          ) : (
            <Empty
              icon={Target}
              title="Forecast unavailable"
              text="Complete your revenue plan and connect pipeline data to generate a forecast."
            />
          )}
        </Panel>
        <Panel title="AI Recommendations" kicker="Human-governed">
          {recommendations.length ? (
            <div className="recommendation-list">
              {recommendations.slice(0, 3).map((item) => (
                <div key={item}>
                  <span>
                    <Lightbulb size={16} />
                  </span>
                  <div>
                    <small>Website Intelligence</small>
                    <p>{item}</p>
                  </div>
                  <button className="button secondary" onClick={() => onNavigate("Recommendations")}>
                    Review
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <Empty
              icon={Lightbulb}
              title="No recommendations yet"
              text="Connect your business systems and complete initial analysis to generate recommendations."
            />
          )}
        </Panel>
      </div>
      <Panel title="Commercial Requirements" kicker="What it takes to hit target">
        <div className="requirement-table">
          <div className="requirement-table-head">
            <span />
            <span>Current</span>
            <span>Required</span>
            <span>Gap</span>
          </div>
          <div className="requirement-table-row">
            <span>Leads</span>
            <b>{currentLeads || "—"}</b>
            <b>{leadsNeeded || "—"}</b>
            <b>{leadsNeeded ? Math.max(0, leadsNeeded - currentLeads) : "—"}</b>
          </div>
          <div className="requirement-table-row">
            <span>Opportunities</span>
            <b>{currentOpportunities || "—"}</b>
            <b>{opportunitiesNeeded || "—"}</b>
            <b>{opportunitiesNeeded ? Math.max(0, opportunitiesNeeded - currentOpportunities) : "—"}</b>
          </div>
          <div className="requirement-table-row">
            <span>Deals</span>
            <b>—</b>
            <b>{dealsNeeded || "—"}</b>
            <b>—</b>
          </div>
          <div className="requirement-table-row">
            <span>Pipeline</span>
            <b>{money(pipeline, currency)}</b>
            <b>{money(pipelineRequired, currency)}</b>
            <b>{money(Math.max(0, pipelineRequired - pipeline), currency)}</b>
          </div>
        </div>
      </Panel>
      <div className="overview-grid thirds">
        <Panel
          title="AI Agents"
          kicker="Specialist team"
          action={
            <button className="button secondary" onClick={() => onNavigate("Agent Centre")}>
              View all agents <ArrowRight size={14} />
            </button>
          }
        >
          {curatedAgents.length ? (
            <div className="source-list">
              {curatedAgents.map((agent) => (
                <div key={agent.id}>
                  <span>
                    <b>{agent.name}</b>
                    <small>{labelFor(agent.status)}</small>
                  </span>
                  <em
                    className={`status ${agent.status === "needs_approval" ? "pending" : agent.active ? "complete" : "neutral"}`}
                  >
                    {agent.status === "needs_approval" ? "Needs review" : agent.active ? "Active" : "Idle"}
                  </em>
                </div>
              ))}
            </div>
          ) : (
            <Empty icon={Bot} title="No active agents yet" text="Activate specialist agents to see their status here." />
          )}
        </Panel>
        <Panel title="Revenue Sources" kicker="Commercial mix">
          {workspace.revenueSources.length ? (
            <div className="source-list">
              {workspace.revenueSources.map((source) => (
                <div key={source.name}>
                  <span>
                    <b>{source.name}</b>
                    <small>{labelFor(source.type)}</small>
                  </span>
                  <strong>{money(source.annualValue, currency)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <Empty
              icon={CircleDollarSign}
              title="No revenue sources yet"
              text="Add revenue sources from your revenue plan to see your commercial mix."
            />
          )}
        </Panel>
        <Panel title="Connected Systems" kicker={`${connectedCount} of ${workspace.integrations.length} connected`}>
          {workspace.integrations.length ? (
            <div className="source-list">
              {workspace.integrations.map((item) => (
                <div key={item.provider}>
                  <span>
                    <b>{labelFor(item.provider)}</b>
                    <small>
                      {item.lastSyncedAt
                        ? `Synced ${new Date(item.lastSyncedAt).toLocaleString()}`
                        : "Not yet synced"}
                    </small>
                  </span>
                  <em className={`status ${item.status === "connected" ? "complete" : "pending"}`}>
                    {item.status === "connected" ? "Connected" : "Selected"}
                  </em>
                </div>
              ))}
            </div>
          ) : (
            <Empty
              icon={Plug}
              title="No systems connected"
              text="Connect your CRM, analytics and revenue systems from Integrations."
            />
          )}
        </Panel>
      </div>
      <div className="overview-grid">
        <Panel
          title="Website Intelligence"
          kicker="Public website scan"
          action={
            <span className={`status ${workspace.websiteScan ? "complete" : "pending"}`}>
              {workspace.websiteScan ? "Complete" : "Pending"}
            </span>
          }
        >
          {workspace.websiteScan ? (
            <div className="scan-summary">
              <div className="score-ring">
                <b>{workspace.websiteScan.score}</b>
                <small>/100</small>
              </div>
              <div>
                <h3>
                  {workspace.websiteScan.title || workspace.organisationName}
                </h3>
                <p>
                  {workspace.websiteScan.primaryHeading ||
                    "No primary heading detected"}
                </p>
                <ul>
                  {workspace.websiteScan.opportunities
                    .slice(0, 3)
                    .map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                </ul>
              </div>
            </div>
          ) : (
            <Empty
              icon={Globe2}
              title="No website result yet"
              text="Complete or retry the website scan to establish the digital baseline."
            />
          )}
        </Panel>
        <Panel title="Alerts" kicker={alerts.length ? `${alerts.length} require attention` : "All clear"}>
          {alerts.length ? (
            <div className="alert-list">
              {alerts.map((alert, index) => (
                <div key={index} className={`alert-row ${alert.severity}`}>
                  <b>{labelFor(alert.severity)}</b>
                  <p>{alert.message}</p>
                  <small>{alert.source}</small>
                </div>
              ))}
            </div>
          ) : (
            <Empty icon={ShieldCheck} title="No active alerts" text="Your revenue system is currently clear." />
          )}
        </Panel>
      </div>
      <Panel title="Recent Activity" kicker="Latest system events">
        {activity.length ? (
          <div className="activity-feed">
            {activity.map((item, index) => (
              <div key={index}>
                <span className="activity-dot" />
                <div>
                  <b>{item.label}</b>
                  {item.detail && <small>{item.detail}</small>}
                </div>
                <em>{new Date(item.timestamp).toLocaleString()}</em>
              </div>
            ))}
          </div>
        ) : (
          <Empty
            icon={Activity}
            title="No recent activity"
            text="Activity will appear as agents run, integrations sync, and scans complete."
          />
        )}
      </Panel>
    </div>
  );
}

function Empty({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof Globe2;
  title: string;
  text: string;
}) {
  return (
    <div className="empty">
      <span>
        <Icon size={27} className={Icon === LoaderCircle ? "spin" : undefined} />
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
type OpportunityTag = { impact: "high" | "medium"; category: string };

const OPPORTUNITY_TAGS: Array<{
  match: (text: string) => boolean;
  tag: OpportunityTag;
}> = [
  {
    match: (text) => text === "Add a clear, descriptive page title.",
    tag: { impact: "high", category: "SEO & Discovery" },
  },
  {
    match: (text) => text === "Add a search-friendly meta description.",
    tag: { impact: "medium", category: "SEO & Discovery" },
  },
  {
    match: (text) =>
      text === "Use one clear primary heading that explains the offer.",
    tag: { impact: "high", category: "Messaging" },
  },
  {
    match: (text) => text === "Add a prominent next-step call to action.",
    tag: { impact: "high", category: "Conversion" },
  },
  {
    match: (text) =>
      text === "Add a measurable lead-capture or enquiry path.",
    tag: { impact: "high", category: "Conversion" },
  },
  {
    match: (text) =>
      text ===
      "Strengthen the page with more useful buyer-focused evidence.",
    tag: { impact: "medium", category: "Content Depth" },
  },
  {
    match: (text) =>
      text.startsWith("This site appears to be a JavaScript-rendered application"),
    tag: { impact: "high", category: "Technical" },
  },
  {
    match: (text) => text.includes("returned an error when scanned"),
    tag: { impact: "medium", category: "Technical" },
  },
];

function tagOpportunity(text: string): OpportunityTag {
  return (
    OPPORTUNITY_TAGS.find((entry) => entry.match(text))?.tag ?? {
      impact: "medium",
      category: "General",
    }
  );
}

function priorityFor(score: number): { label: string; className: string } {
  if (score < 50) return { label: "High", className: "error" };
  if (score < 75) return { label: "Medium", className: "pending" };
  return { label: "Low", className: "complete" };
}

const TYPE_FROM_PATH: Array<{ match: RegExp; label: string }> = [
  { match: /^pricing/i, label: "Pricing" },
  { match: /^contact/i, label: "Contact" },
  { match: /^(blog|news)/i, label: "Blog" },
  { match: /^(products?|features)/i, label: "Product" },
  { match: /^services?/i, label: "Service" },
  { match: /^about/i, label: "About" },
];

function typeForPage(page: ScannedPage): string {
  if (page.aiCategory) return labelFor(page.aiCategory);
  try {
    const pathname = new URL(page.url).pathname.replace(/^\/+/, "");
    if (!pathname) return "Homepage";
    return TYPE_FROM_PATH.find((entry) => entry.match.test(pathname))?.label ?? "—";
  } catch {
    return "—";
  }
}

type HierarchyNode = {
  segment: string;
  label: string;
  url: string | undefined;
  children: HierarchyNode[];
};

function buildHierarchy(pages: ScannedPage[]): HierarchyNode {
  const homepage = pages[0];
  const root: HierarchyNode = {
    segment: "",
    label: homepage?.title || "Home",
    url: homepage?.url,
    children: [],
  };
  for (const page of pages.slice(1)) {
    let segments: string[];
    try {
      segments = new URL(page.url).pathname.split("/").filter(Boolean);
    } catch {
      continue;
    }
    if (!segments.length) continue;
    let cursor = root;
    segments.forEach((segment, index) => {
      let child = cursor.children.find((node) => node.segment === segment);
      if (!child) {
        child = { segment, label: labelFor(segment), url: undefined, children: [] };
        cursor.children.push(child);
      }
      if (index === segments.length - 1) {
        child.url = page.url;
        child.label = page.title || labelFor(segment);
      }
      cursor = child;
    });
  }
  return root;
}

function HierarchyNodeView({
  node,
  depth,
}: {
  node: HierarchyNode;
  depth: number;
}) {
  return (
    <li>
      <span style={{ paddingLeft: depth * 18 }}>
        {depth === 0 ? <Home size={14} /> : <FileText size={13} />}
        {node.label}
      </span>
      {node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <HierarchyNodeView
              key={child.segment}
              node={child}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function HierarchyTree({ pages }: { pages: ScannedPage[] }) {
  const root = useMemo(() => buildHierarchy(pages), [pages]);
  return (
    <Panel title="Website Hierarchy" kicker="Automatic sitemap">
      <ul className="hierarchy-tree">
        <HierarchyNodeView node={root} depth={0} />
      </ul>
    </Panel>
  );
}

function HealthGauge({
  score,
  criticalIssues,
  aiAnalyzed,
  pagesScanned,
}: {
  score: number;
  criticalIssues: number;
  aiAnalyzed: number;
  pagesScanned: number;
}) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(100, score)) / 100);
  const tone = score >= 80 ? "success" : score >= 60 ? "warning" : "danger";
  const label =
    score >= 80 ? "Optimized" : score >= 60 ? "Needs work" : "Needs attention";
  return (
    <Panel title="Website Health">
      <div className="health-gauge">
        <svg viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} className="health-gauge-track" />
          <circle
            cx="60"
            cy="60"
            r={radius}
            className={`health-gauge-value ${tone}`}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="health-gauge-label">
          <strong>{score}</strong>
          <small>{label}</small>
        </div>
      </div>
      <div className="health-gauge-stats">
        <div>
          <span>Critical issues</span>
          <b>{criticalIssues}</b>
        </div>
        <div>
          {aiAnalyzed > 0 ? (
            <>
              <span>AI pages analyzed</span>
              <b>
                {aiAnalyzed} / {pagesScanned}
              </b>
            </>
          ) : (
            <>
              <span>AI analysis</span>
              <b className="muted">Off</b>
            </>
          )}
        </div>
      </div>
    </Panel>
  );
}

const WEBSITE_CATEGORY_LABELS: Record<WebsiteAnalysisCategory, string> = {
  content: "Content",
  seo: "SEO",
  ux: "UX",
  conversion: "Conversion",
  technical: "Technical",
  accessibility: "Accessibility",
};

function WebsiteScoreBreakdown({ subScores }: { subScores: WebsiteSubScores | null }) {
  return (
    <Panel title="Website Score" kicker="By category">
      {subScores ? (
        <div className="score-breakdown">
          {(Object.keys(WEBSITE_CATEGORY_LABELS) as WebsiteAnalysisCategory[]).map((category) => {
            const value = subScores[category];
            return (
              <div className="score-breakdown-row" key={category}>
                <div className="score-breakdown-label">
                  <b>{WEBSITE_CATEGORY_LABELS[category]}</b>
                </div>
                <div className="score-breakdown-track">
                  <span className={value >= 75 ? "strong" : value >= 40 ? "partial" : "weak"} style={{ width: `${Math.min(100, value)}%` }} />
                </div>
                <div className="score-breakdown-points">{value}/100</div>
              </div>
            );
          })}
        </div>
      ) : (
        <Empty icon={ScanSearch} title="Not scored yet" text="Run a scan to see the category breakdown." />
      )}
    </Panel>
  );
}

const WEBSITE_SEVERITY_RANK: Record<WebsiteSeverity, number> = { critical: 4, high: 3, medium: 2, low: 1 };
const WEBSITE_SEVERITY_TONE: Record<WebsiteSeverity, "error" | "pending" | "neutral" | "complete"> = {
  critical: "error",
  high: "pending",
  medium: "neutral",
  low: "complete",
};
const WEBSITE_SEVERITY_LABEL: Record<WebsiteSeverity, string> = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };

function FindingsBySeverity({ findings }: { findings: WebsiteFinding[] }) {
  const sorted = [...findings].sort((a, b) => WEBSITE_SEVERITY_RANK[b.severity] - WEBSITE_SEVERITY_RANK[a.severity]);
  const counts = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const item of findings) counts[item.severity] += 1;
  return (
    <Panel title="Findings" kicker={`${counts.critical} critical · ${counts.high} high · ${counts.medium} medium · ${counts.low} low`}>
      {sorted.length ? (
        <div className="source-list">
          {sorted.map((item) => (
            <div key={item.id}>
              <span>
                <b>{item.title}</b>
                <small>{item.description}</small>
              </span>
              <em className={`status ${WEBSITE_SEVERITY_TONE[item.severity]}`}>{WEBSITE_SEVERITY_LABEL[item.severity]}</em>
            </div>
          ))}
        </div>
      ) : (
        <Empty icon={ShieldCheck} title="No findings" text="Nothing flagged in the last scan." />
      )}
    </Panel>
  );
}

function StructuredRecommendations({ recommendations }: { recommendations: WebsiteRecommendation[] }) {
  return (
    <Panel title="Recommendations" kicker={`${recommendations.length} from the last scan`}>
      {recommendations.length ? (
        <div className="website-recommendations">
          {recommendations.map((item) => (
            <div key={item.id} className="website-recommendation-card">
              <div className="page-title-action">
                <b>{item.what}</b>
                <em className={`status ${WEBSITE_SEVERITY_TONE[item.priority]}`}>{WEBSITE_SEVERITY_LABEL[item.priority]}</em>
              </div>
              <p>{item.why}</p>
              <p className="muted">
                <b>Impact: </b>
                {item.impact}
              </p>
              <p>
                <b>Suggested action: </b>
                {item.suggestedAction}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <Empty icon={Lightbulb} title="No recommendations" text="Nothing to recommend from the last scan." />
      )}
    </Panel>
  );
}

function IntelligenceLog({ scan }: { scan: WebsiteScan }) {
  const discoveryLabel =
    scan.discoveryMethod === "sitemap"
      ? "via sitemap"
      : scan.discoveryMethod === "homepage-links"
        ? "via homepage links"
        : "homepage only";
  const aiEnrichedCount = scan.pages.filter((page) => page.aiCategory).length;
  const steps = [
    `Discovered ${scan.pagesDiscovered} page${scan.pagesDiscovered === 1 ? "" : "s"} ${discoveryLabel}`,
    `Analyzed ${scan.pagesScanned} page${scan.pagesScanned === 1 ? "" : "s"}`,
    ...(aiEnrichedCount > 0
      ? [`AI-enriched ${aiEnrichedCount} page${aiEnrichedCount === 1 ? "" : "s"}`]
      : []),
    `Scan completed ${new Date(scan.scannedAt).toLocaleString()}`,
  ];
  return (
    <Panel title="Intelligence Log">
      <ul className="intel-log">
        {steps.map((step) => (
          <li key={step}>
            <Check size={13} />
            <span>{step}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function ConnectedAgentsPanel({
  workspace,
  onNavigateToAgents,
}: {
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const related = workspace.agents.filter(
    (agent) =>
      agent.id === "website-intelligence" ||
      agent.dependencies.includes("website-intelligence"),
  );
  return (
    <Panel
      title="Connected Agents"
      action={
        <button className="button secondary" onClick={onNavigateToAgents}>
          View all agents <ArrowRight size={14} />
        </button>
      }
    >
      {related.length ? (
        <div className="source-list">
          {related.map((agent) => (
            <div key={agent.id}>
              <span>
                <b>{agent.name}</b>
                <small>{agent.category}</small>
              </span>
              <em className={`status ${agent.active ? "complete" : "neutral"}`}>
                {agent.active ? "Synced" : "Not active"}
              </em>
            </div>
          ))}
        </div>
      ) : (
        <Empty
          icon={Bot}
          title="No connected agents"
          text="Activate agents from the Agents page to see them here."
        />
      )}
    </Panel>
  );
}

function BusinessContextPanel({ scan }: { scan: WebsiteScan }) {
  const hasContext =
    scan.brandPositioning || scan.websiteTone || scan.brandVoice || scan.aiRecommendation;
  return (
    <Panel title="Business Context" kicker="AI-derived brand insight">
      {hasContext ? (
        <>
          <div className="brand-context-tags">
            <div>
              <small>Brand Positioning</small>
              <b>{scan.brandPositioning || "—"}</b>
            </div>
            <div>
              <small>Website Tone</small>
              <b>{scan.websiteTone || "—"}</b>
            </div>
            <div>
              <small>Brand Voice</small>
              <b>{scan.brandVoice || "—"}</b>
            </div>
          </div>
          {scan.aiRecommendation && (
            <div className="brand-context-recommendation">
              <small>AI Recommendation</small>
              <p>&ldquo;{scan.aiRecommendation}&rdquo;</p>
            </div>
          )}
        </>
      ) : (
        <Empty
          icon={Sparkles}
          title="AI analysis not available"
          text="Enable AI analysis to see brand insights for this scan."
        />
      )}
    </Panel>
  );
}

const PAGE_INVENTORY_SIZE_OPTIONS = [5, 10, 20];

function PageInventoryTable({ pages }: { pages: ScannedPage[] }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(
    PAGE_INVENTORY_SIZE_OPTIONS[0] ?? 5,
  );
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pages;
    return pages.filter(
      (candidate) =>
        candidate.title.toLowerCase().includes(term) ||
        candidate.url.toLowerCase().includes(term) ||
        typeForPage(candidate).toLowerCase().includes(term),
    );
  }, [pages, search]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    setPage(1);
  }, [search, pages, pageSize]);
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);
  return (
    <Panel
      title="Page Inventory"
      kicker={`${pages.length} page${pages.length === 1 ? "" : "s"}`}
      action={
        <div className="table-search">
          <Search size={15} />
          <input
            type="search"
            placeholder="Search pages…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      }
    >
      <div className="page-inventory-table">
        <table>
          <thead>
            <tr>
              <th>Page name</th>
              <th>Type</th>
              <th>CTA</th>
              <th>Priority</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length ? (
              pageRows.map((pageRow) => {
                const priority = priorityFor(pageRow.score);
                const cta =
                  pageRow.ctaLabel ||
                  (pageRow.callsToAction > 0
                    ? `${pageRow.callsToAction} CTA${pageRow.callsToAction === 1 ? "" : "s"}`
                    : "");
                return (
                  <tr key={pageRow.url}>
                    <td>
                      <b>{pageRow.title || pageRow.url}</b>
                      <small>{pageRow.url}</small>
                    </td>
                    <td>{typeForPage(pageRow)}</td>
                    <td className={cta ? "" : "muted"}>{cta || "Missing"}</td>
                    <td>
                      <em className={`status ${priority.className}`}>
                        {priority.label}
                      </em>
                    </td>
                    <td>
                      <a
                        className="button secondary"
                        href={pageRow.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open <ExternalLink size={13} />
                      </a>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className="muted">
                  No pages match “{search}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {filtered.length > 0 && (
        <div className="table-pagination">
          <label className="table-page-size">
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
            >
              {PAGE_INVENTORY_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          {totalPages > 1 && (
            <div className="table-pagination-controls">
              <button
                className="button secondary"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              >
                <ChevronLeft size={15} /> Prev
              </button>
              <div className="table-pagination-pages">
                {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                  (number) => (
                    <button
                      key={number}
                      className={number === currentPage ? "active" : ""}
                      onClick={() => setPage(number)}
                    >
                      {number}
                    </button>
                  ),
                )}
              </div>
              <button
                className="button secondary"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next <ChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function OpportunityCards({ opportunities }: { opportunities: string[] }) {
  return (
    <Panel title="Prioritised Opportunities">
      {opportunities.length ? (
        <div className="opportunity-cards">
          {opportunities.map((item) => {
            const tag = tagOpportunity(item);
            return (
              <article key={item}>
                <em className={`status ${tag.impact === "high" ? "error" : "pending"}`}>
                  {tag.impact === "high" ? "High impact" : "Medium impact"}
                </em>
                <p>{item}</p>
                <small>{tag.category}</small>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="form-alert success">
          <Check size={17} />
          No basic issues detected across scanned pages.
        </div>
      )}
    </Panel>
  );
}

function HeroSummary({ workspace, scan }: { workspace: Workspace; scan: WebsiteScan }) {
  const tone = scan.score >= 80 ? "complete" : scan.score >= 60 ? "pending" : "error";
  return (
    <Panel title="Website Overview" kicker={workspace.organisationName}>
      <div className="hero-summary">
        <div className="hero-summary-main">
          <a href={scan.url} target="_blank" rel="noreferrer">
            {scan.url} <ExternalLink size={13} />
          </a>
          {scan.aiExecutiveSummary ? (
            <p>{scan.aiExecutiveSummary}</p>
          ) : (
            <p className="muted">
              Enable AI analysis to generate an executive summary of this
              website&apos;s revenue readiness.
            </p>
          )}
          {scan.websiteGoal ? (
            <div className="hero-summary-goal">
              <Target size={15} />
              <span>Primary goal: {scan.websiteGoal}</span>
            </div>
          ) : null}
        </div>
        <div className="hero-summary-score">
          <em className={`status ${tone}`}>{scan.score}/100</em>
          <small>Website score</small>
        </div>
      </div>
    </Panel>
  );
}

const CONTACT_METHOD_PATTERNS: Array<{ label: string; pattern: RegExp }> = [
  { label: "Email address", pattern: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i },
  { label: "Phone number", pattern: /(\+?\d[\d\s().-]{7,}\d)/ },
  { label: "Contact form", pattern: /contact us|get in touch|enquire|enquiry/i },
];

function BusinessUnderstanding({ workspace, scan }: { workspace: Workspace; scan: WebsiteScan }) {
  const pages = scan.pages;
  const primaryCtas = useMemo(() => {
    const counts = new Map<string, number>();
    for (const page of pages) {
      const label = page.ctaLabel.trim();
      if (!label) continue;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([label]) => label);
  }, [pages]);
  const keyLandingPages = useMemo(
    () => [...pages].sort((a, b) => b.score - a.score).slice(0, 5),
    [pages],
  );
  const resourceTopics = useMemo(
    () =>
      pages
        .filter((page) => typeForPage(page) === "Blog")
        .slice(0, 6)
        .map((page) => page.title || page.url),
    [pages],
  );
  const contactMethods = useMemo(() => {
    const combined = pages.map((page) => page.content).join(" ");
    return CONTACT_METHOD_PATTERNS.filter((entry) => entry.pattern.test(combined)).map(
      (entry) => entry.label,
    );
  }, [pages]);
  const company = workspace.company;
  return (
    <Panel title="Business Understanding" kicker="AI-derived brand insight">
      <div className="business-understanding-grid">
        <div>
          <small>Products / Services</small>
          <b>{scan.productsServices.length ? scan.productsServices.join(", ") : "—"}</b>
        </div>
        <div>
          <small>Industry</small>
          <b>{company.industry ? String(company.industry) : "—"}</b>
        </div>
        <div>
          <small>Target audience</small>
          <b>{scan.targetAudience || "—"}</b>
        </div>
        <div>
          <small>Value proposition</small>
          <b>{company.valueProposition ? String(company.valueProposition) : "—"}</b>
        </div>
        <div>
          <small>Primary CTAs</small>
          <b>{primaryCtas.length ? primaryCtas.join(", ") : "—"}</b>
        </div>
        <div>
          <small>Key landing pages</small>
          <b>
            {keyLandingPages.length
              ? keyLandingPages.map((page) => page.title || page.url).join(", ")
              : "—"}
          </b>
        </div>
        <div>
          <small>Resources / blog topics</small>
          <b>{resourceTopics.length ? resourceTopics.join(", ") : "—"}</b>
        </div>
        <div>
          <small>Contact methods</small>
          <b>{contactMethods.length ? contactMethods.join(", ") : "—"}</b>
        </div>
      </div>
    </Panel>
  );
}

function CtaIntelligence({ pages }: { pages: ScannedPage[] }) {
  const withCta = pages.filter((page) => page.callsToAction > 0 || page.ctaLabel);
  const coverage = pages.length ? Math.round((withCta.length / pages.length) * 100) : 0;
  const missing = pages.filter((page) => page.callsToAction === 0 && !page.ctaLabel);
  const topLabels = useMemo(() => {
    const counts = new Map<string, number>();
    for (const page of pages) {
      const label = page.ctaLabel.trim();
      if (!label) continue;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [pages]);
  return (
    <Panel title="CTA Intelligence" kicker={`${coverage}% coverage`}>
      <div className="stat-row-grid">
        <div>
          <span>Pages with a CTA</span>
          <b>
            {withCta.length} / {pages.length}
          </b>
        </div>
        <div>
          <span>Pages missing a CTA</span>
          <b>{missing.length}</b>
        </div>
      </div>
      {topLabels.length > 0 && (
        <div className="source-list">
          {topLabels.map(([label, count]) => (
            <div key={label}>
              <span>
                <b>{label}</b>
              </span>
              <em className="status neutral">{count}</em>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function ConversionFlowMap({ pages }: { pages: ScannedPage[] }) {
  const homepage = pages[0];
  const stages = useMemo(() => {
    const branches = new Map<string, ScannedPage[]>();
    for (const page of pages.slice(1)) {
      let segment: string | undefined;
      try {
        segment = new URL(page.url).pathname.split("/").filter(Boolean)[0];
      } catch {
        continue;
      }
      if (!segment) continue;
      const list = branches.get(segment) ?? [];
      list.push(page);
      branches.set(segment, list);
    }
    return [...branches.entries()]
      .map(([segment, branchPages]) => ({
        label: labelFor(segment),
        pageCount: branchPages.length,
        conversionPages: branchPages.filter(
          (page) => page.callsToAction > 0 || page.forms > 0,
        ).length,
      }))
      .sort((a, b) => b.pageCount - a.pageCount)
      .slice(0, 6);
  }, [pages]);
  const frictionPoints = useMemo(
    () =>
      pages
        .filter(
          (page) =>
            page !== homepage &&
            page.callsToAction === 0 &&
            page.forms === 0 &&
            typeForPage(page) !== "Blog",
        )
        .slice(0, 5)
        .map((page) => page.title || page.url),
    [pages, homepage],
  );
  return (
    <Panel title="Conversion Flow Map" kicker="Inferred from site structure">
      <div className="flow-map">
        <div className="flow-map-step">
          <Home size={16} />
          <div>
            <b>Home</b>
            <small>
              {homepage?.callsToAction ?? 0} CTA
              {(homepage?.callsToAction ?? 0) === 1 ? "" : "s"}
            </small>
          </div>
        </div>
        {stages.map((stage) => (
          <div className="flow-map-step" key={stage.label}>
            <ArrowRight size={14} className="flow-map-arrow" />
            <div>
              <b>{stage.label}</b>
              <small>
                {stage.pageCount} page{stage.pageCount === 1 ? "" : "s"} ·{" "}
                {stage.conversionPages} with a CTA/form
              </small>
            </div>
          </div>
        ))}
      </div>
      {frictionPoints.length > 0 ? (
        <div className="flow-map-friction">
          <small>Friction points — pages with no CTA and no form:</small>
          <ul>
            {frictionPoints.map((title) => (
              <li key={title}>{title}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="form-alert success">
          <Check size={17} />
          No obvious dead ends detected in the scanned pages.
        </div>
      )}
    </Panel>
  );
}

function OpportunityMatrix({ pages }: { pages: ScannedPage[] }) {
  const matrix = useMemo(() => {
    const counts = new Map<string, { high: number; medium: number }>();
    for (const page of pages) {
      for (const item of page.opportunities) {
        const tag = tagOpportunity(item);
        const entry = counts.get(tag.category) ?? { high: 0, medium: 0 };
        entry[tag.impact] += 1;
        counts.set(tag.category, entry);
      }
    }
    return [...counts.entries()].sort(
      (a, b) => b[1].high + b[1].medium - (a[1].high + a[1].medium),
    );
  }, [pages]);
  return (
    <Panel title="Opportunity Matrix" kicker="Issues by category, across all pages">
      {matrix.length ? (
        <div className="opportunity-matrix">
          {matrix.map(([category, counts]) => (
            <div key={category} className="opportunity-matrix-row">
              <span>{category}</span>
              <div>
                {counts.high > 0 && <em className="status error">{counts.high} high</em>}
                {counts.medium > 0 && (
                  <em className="status pending">{counts.medium} medium</em>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="form-alert success">
          <Check size={17} />
          No issues found across scanned pages.
        </div>
      )}
    </Panel>
  );
}

function ContentQuality({ pages }: { pages: ScannedPage[] }) {
  const avgWords = pages.length
    ? Math.round(pages.reduce((sum, page) => sum + page.wordCount, 0) / pages.length)
    : 0;
  const thin = pages.filter((page) => page.wordCount < 250).length;
  const missingHeading = pages.filter((page) => !page.primaryHeading).length;
  const missingDescription = pages.filter((page) => !page.description).length;
  return (
    <Panel title="Content Quality">
      <div className="stat-row-grid">
        <div>
          <span>Average word count</span>
          <b>{avgWords.toLocaleString()}</b>
        </div>
        <div>
          <span>Thin content (&lt;250 words)</span>
          <b>
            {thin} page{thin === 1 ? "" : "s"}
          </b>
        </div>
        <div>
          <span>Missing primary heading</span>
          <b>
            {missingHeading} page{missingHeading === 1 ? "" : "s"}
          </b>
        </div>
        <div>
          <span>Missing meta description</span>
          <b>
            {missingDescription} page{missingDescription === 1 ? "" : "s"}
          </b>
        </div>
      </div>
    </Panel>
  );
}

function SeoSnapshot({ pages }: { pages: ScannedPage[] }) {
  const avgTitleLength = pages.length
    ? Math.round(pages.reduce((sum, page) => sum + page.title.length, 0) / pages.length)
    : 0;
  const withTitle = pages.filter((page) => page.title).length;
  const withDescription = pages.filter((page) => page.description).length;
  const withHeading = pages.filter((page) => page.primaryHeading).length;
  return (
    <Panel title="SEO Snapshot">
      <div className="stat-row-grid">
        <div>
          <span>Pages with a title tag</span>
          <b>
            {withTitle} / {pages.length}
          </b>
        </div>
        <div>
          <span>Pages with a meta description</span>
          <b>
            {withDescription} / {pages.length}
          </b>
        </div>
        <div>
          <span>Pages with a primary heading</span>
          <b>
            {withHeading} / {pages.length}
          </b>
        </div>
        <div>
          <span>Average title length</span>
          <b>{avgTitleLength} chars</b>
        </div>
      </div>
    </Panel>
  );
}

function MissingContentChecklist({ pages }: { pages: ScannedPage[] }) {
  const checks = useMemo(() => {
    const definitions: Array<{ label: string; test: (page: ScannedPage) => boolean }> = [
      { label: "Missing a page title", test: (page) => !page.title },
      { label: "Missing a meta description", test: (page) => !page.description },
      { label: "Missing a primary heading", test: (page) => !page.primaryHeading },
      {
        label: "Missing a call to action",
        test: (page) => page.callsToAction === 0 && !page.ctaLabel,
      },
      { label: "Missing a lead-capture form", test: (page) => page.forms === 0 },
    ];
    return definitions
      .map((definition) => ({
        label: definition.label,
        count: pages.filter(definition.test).length,
      }))
      .filter((entry) => entry.count > 0);
  }, [pages]);
  return (
    <Panel
      title="Missing Content"
      kicker={`${checks.length} gap${checks.length === 1 ? "" : "s"} found`}
    >
      {checks.length ? (
        <div className="source-list">
          {checks.map((check) => (
            <div key={check.label}>
              <span>
                <b>{check.label}</b>
              </span>
              <em className="status pending">
                {check.count} page{check.count === 1 ? "" : "s"}
              </em>
            </div>
          ))}
        </div>
      ) : (
        <div className="form-alert success">
          <Check size={17} />
          No content gaps detected.
        </div>
      )}
    </Panel>
  );
}

const TRUST_SIGNAL_PATTERNS: Array<{ label: string; pattern: RegExp }> = [
  { label: "Customer testimonials or reviews", pattern: /testimonial|review|rating/i },
  {
    label: "Certifications or accreditations",
    pattern: /certified|accredited|iso\s?9001|award-winning/i,
  },
  {
    label: "Security or privacy assurance",
    pattern: /ssl|secure checkout|privacy policy|gdpr/i,
  },
  {
    label: "Money-back or satisfaction guarantee",
    pattern: /money-back|satisfaction guarantee|refund policy/i,
  },
  {
    label: "Case studies or client logos",
    pattern: /case stud(y|ies)|our clients|trusted by/i,
  },
];

function TrustSignals({ pages }: { pages: ScannedPage[] }) {
  const detected = useMemo(
    () =>
      TRUST_SIGNAL_PATTERNS.map((signal) => ({
        label: signal.label,
        pageCount: pages.filter((page) => signal.pattern.test(page.content)).length,
      })).filter((entry) => entry.pageCount > 0),
    [pages],
  );
  return (
    <Panel
      title="Trust Signals"
      kicker={`${detected.length} of ${TRUST_SIGNAL_PATTERNS.length} detected`}
    >
      {detected.length ? (
        <div className="source-list">
          {detected.map((entry) => (
            <div key={entry.label}>
              <span>
                <b>{entry.label}</b>
              </span>
              <em className="status complete">
                {entry.pageCount} page{entry.pageCount === 1 ? "" : "s"}
              </em>
            </div>
          ))}
        </div>
      ) : (
        <Empty
          icon={ShieldCheck}
          title="No trust signals detected"
          text="Consider adding testimonials, certifications, or security assurances to build buyer confidence."
        />
      )}
    </Panel>
  );
}

function ScanHistoryPanel({ scan }: { scan: WebsiteScan }) {
  const history = scan.scanHistory;
  return (
    <Panel
      title="Scan History"
      kicker={`${history.length} scan${history.length === 1 ? "" : "s"}`}
    >
      {history.length ? (
        <div className="source-list">
          {history.map((entry) => (
            <div key={entry.scannedAt}>
              <span>
                <b>{new Date(entry.scannedAt).toLocaleString()}</b>
                <small>
                  {entry.pagesScanned} page{entry.pagesScanned === 1 ? "" : "s"} scanned
                </small>
              </span>
              <em
                className={`status ${
                  entry.score >= 80 ? "complete" : entry.score >= 60 ? "pending" : "error"
                }`}
              >
                {entry.score}/100
              </em>
            </div>
          ))}
        </div>
      ) : (
        <Empty
          icon={Activity}
          title="No scan history yet"
          text="Past scan scores will appear here after you scan more than once."
        />
      )}
    </Panel>
  );
}

function WebsiteView({
  session,
  workspace,
  onUpdate,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onUpdate: (workspace: Workspace) => void;
  onNavigateToAgents: () => void;
}) {
  const scan = workspace.websiteScan;
  const scanning =
    workspace.websiteScanStatus === "queued" || workspace.websiteScanStatus === "running";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pageFilter, setPageFilter] = useState("");
  const rescan = async () => {
    setBusy(true);
    setError("");
    try {
      onUpdate(
        await rescanWebsite(session.session.token, workspace.tenantId, pageFilter),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Website scan failed.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!scanning) return;
    const interval = setInterval(() => {
      void loadWorkspace(session.session.token, workspace.tenantId)
        .then(onUpdate)
        .catch(() => {});
    }, 4000);
    return () => clearInterval(interval);
  }, [scanning, session.session.token, workspace.tenantId]);
  const discoveryLabel =
    scan?.discoveryMethod === "sitemap"
      ? "via sitemap"
      : scan?.discoveryMethod === "homepage-links"
        ? "via homepage links"
        : "homepage only";
  const pagesScanned = scan?.pagesScanned ?? 1;
  const pagesDiscovered = scan?.pagesDiscovered ?? pagesScanned;
  const pagesFailed = scan?.pagesFailed ?? [];
  const pages = scan?.pages ?? [];
  const criticalIssues = scan ? scan.findings.filter((item) => item.severity === "critical").length : 0;
  const aiAnalyzed = pages.filter((page) => page.aiCategory).length;
  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Website Intelligence Agent</span>
          <p>
            Clintra scans public pages only and turns observable conversion
            friction into reviewable opportunities.
          </p>
        </div>
        <div className="scan-controls">
          <input
            type="text"
            className="scan-page-filter"
            placeholder="Filter pages, e.g. course"
            aria-label="Filter which pages get scanned"
            value={pageFilter}
            disabled={busy || scanning}
            onChange={(event) => setPageFilter(event.target.value)}
          />
          <button
            className="button primary"
            disabled={busy || scanning}
            onClick={rescan}
          >
            {busy || scanning ? (
              <LoaderCircle className="spin" />
            ) : (
              <ScanSearch size={18} />
            )}
            {busy || scanning ? "Scanning…" : "Scan website"}
          </button>
        </div>
      </div>
      {scan?.pageFilter ? (
        <div className="form-alert info">
          <ScanSearch size={17} />
          Showing pages matching &quot;{scan.pageFilter}&quot;. Clear the filter and rescan to
          see all pages again.
        </div>
      ) : null}
      {error && (
        <div className="form-alert error">
          <X size={17} />
          {error}
        </div>
      )}
      {workspace.websiteScanStatus === "failed" && workspace.websiteScanError && (
        <div className="form-alert error">
          <X size={17} />
          {workspace.websiteScanError}
        </div>
      )}
      {scanning && (
        <div className="form-alert info">
          <LoaderCircle className="spin" size={17} />
          Scanning your website. Full-site scans can take up to a minute or
          two depending on how many pages are found.
        </div>
      )}
      {scan ? (
        <>
          <HeroSummary workspace={workspace} scan={scan} />
          <div className="metrics">
            <Metric
              label="Website score"
              value={`${scan.score}/100`}
              meta={`Average across ${pagesScanned} page${pagesScanned === 1 ? "" : "s"}`}
              icon={ScanSearch}
            />
            <Metric
              label="Pages scanned"
              value={
                pagesDiscovered > pagesScanned
                  ? `${pagesScanned} / ${pagesDiscovered}`
                  : String(pagesScanned)
              }
              meta={`Discovered ${discoveryLabel}`}
              icon={Globe2}
            />
            <Metric
              label="Words analysed"
              value={scan.wordCount.toLocaleString()}
              meta="Visible homepage content"
              icon={Database}
            />
            <Metric
              label="Calls to action"
              value={String(scan.callsToAction)}
              meta={`${scan.forms} forms detected on homepage`}
              icon={Target}
            />
          </div>
          <div className="website-grid">
            <div className="page-stack">
              <StructuredRecommendations recommendations={scan.recommendations} />
              <FindingsBySeverity findings={scan.findings} />
              <BusinessUnderstanding workspace={workspace} scan={scan} />
              <PageInventoryTable pages={pages} />
              <HierarchyTree pages={pages} />
              <ConversionFlowMap pages={pages} />
              <CtaIntelligence pages={pages} />
              <OpportunityMatrix pages={pages} />
              <OpportunityCards opportunities={scan.opportunities} />
              <ContentQuality pages={pages} />
              <SeoSnapshot pages={pages} />
              <MissingContentChecklist pages={pages} />
              <BusinessContextPanel scan={scan} />
              <TrustSignals pages={pages} />
            </div>
            <div className="page-stack">
              <WebsiteScoreBreakdown subScores={scan.subScores} />
              <HealthGauge
                score={scan.score}
                criticalIssues={criticalIssues}
                aiAnalyzed={aiAnalyzed}
                pagesScanned={pagesScanned}
              />
              <ScanHistoryPanel scan={scan} />
              <IntelligenceLog scan={scan} />
              <ConnectedAgentsPanel
                workspace={workspace}
                onNavigateToAgents={onNavigateToAgents}
              />
            </div>
          </div>
          {pagesFailed.length > 0 && (
            <Panel
              title="Pages that couldn't be scanned"
              kicker={`${pagesFailed.length} of ${pagesDiscovered} discovered`}
            >
              <div className="source-list">
                {pagesFailed.map((page) => (
                  <div key={page.url}>
                    <span>
                      <b>{page.url}</b>
                      <small>{page.reason}</small>
                    </span>
                    <em className="status error">Failed</em>
                  </div>
                ))}
              </div>
            </Panel>
          )}
        </>
      ) : (
        <Panel title="Website scan">
          <Empty
            icon={Globe2}
            title="Website scan unavailable"
            text="The initial scan could not complete. Check that the website is public and uses HTTP or HTTPS."
          />
        </Panel>
      )}
    </div>
  );
}
// ───────────────────────── Lead Intelligence ─────────────────────────

function leadBandTone(band: LeadBand | null): "complete" | "pending" | "neutral" {
  if (band === "high") return "complete";
  if (band === "medium") return "pending";
  return "neutral";
}
function leadBandLabel(band: LeadBand | null): string {
  if (!band) return "Unscored";
  return band[0]!.toUpperCase() + band.slice(1);
}
// Derived purely from the same High/Medium/Low counts shown on the metric
// cards -- the recommendation is never an independent AI judgment separate
// from the score, and the number updates automatically as scores change.
function leadRecommendationText(counts: LeadIntelligenceOverview["counts"], averageScore: number | null): string {
  const avgSuffix = averageScore !== null ? ` (average score ${averageScore})` : "";
  if (counts.high > 0)
    return `${counts.high} lead${counts.high === 1 ? "" : "s"} currently sit in the High Priority band${avgSuffix}. Review the Priority Queue to see the full breakdown and evidence behind each score.`;
  if (counts.medium > 0)
    return `No leads currently meet the High Priority threshold. ${counts.medium} lead${counts.medium === 1 ? "" : "s"} ${counts.medium === 1 ? "is" : "are"} currently in the Medium Priority band and should be reviewed based on their score and available evidence.`;
  if (counts.low > 0)
    return `No leads currently meet the High or Medium Priority threshold. ${counts.low} lead${counts.low === 1 ? "" : "s"} ${counts.low === 1 ? "is" : "are"} currently in the Low Priority band.`;
  return "No leads currently meet the High Priority threshold.";
}
function relativeDay(iso: string | null): string {
  if (!iso) return "No activity recorded";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

function LeadScoreGauge({ score, band }: { score: number | null; band: LeadBand | null }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const value = Math.max(0, Math.min(100, score ?? 0));
  const offset = circumference * (1 - value / 100);
  const tone = band === "high" ? "success" : band === "medium" ? "warning" : "danger";
  return (
    <div className="health-gauge">
      <svg viewBox="0 0 120 120" role="img" aria-label={score !== null ? `Lead score ${score} out of 100, ${leadBandLabel(band)} priority` : "Lead not yet scored"}>
        <circle cx="60" cy="60" r={radius} className="health-gauge-track" />
        {score !== null && (
          <circle cx="60" cy="60" r={radius} className={`health-gauge-value ${tone}`} strokeDasharray={circumference} strokeDashoffset={offset} />
        )}
      </svg>
      <div className="health-gauge-label">
        <strong>{score ?? "—"}</strong>
        <small>{leadBandLabel(band)}</small>
      </div>
    </div>
  );
}

function LeadIntelligenceView({
  session, workspace, onNavigateToAgents, onNavigateToIntegrations,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
  onNavigateToIntegrations: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "lead-intelligence");
  const [tab, setTab] = useState<"overview" | "leads" | "config" | "model">("overview");
  const [overview, setOverview] = useState<LeadIntelligenceOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const reloadOverview = () => {
    setOverviewLoading(true);
    void loadLeadIntelligenceOverview(session.session.token, workspace.tenantId)
      .then((result) => {
        setOverview(result);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Lead Intelligence."))
      .finally(() => setOverviewLoading(false));
  };
  useEffect(() => {
    if (agent?.active) reloadOverview();
  }, [agent?.active]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Lead Intelligence Agent</span>
          <h1>Lead Intelligence isn't active yet.</h1>
        </div>
        <Panel title="Activate Lead Intelligence">
          <Empty icon={UsersRound} title="Agent not active" text="Activate the Lead Intelligence agent from Agents & Entitlements to start scoring and enriching your leads." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  const refresh = () => {
    setRefreshing(true);
    void refreshLeadScores(session.session.token, workspace.tenantId)
      .then(reloadOverview)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not refresh scores."))
      .finally(() => setRefreshing(false));
  };

  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">Lead Intelligence Agent</span>
        <h1>
          Lead Intelligence
          <span className="info-tooltip">
            <Info size={13} />
            <span className="info-tooltip-content">AI-powered lead scoring, enrichment, and prioritisation against your ideal customer profile.</span>
          </span>
        </h1>
      </div>
      <p className="lead-ai-note">
        <Sparkles size={13} /> Lead Intelligence continuously evaluates new lead and engagement signals. AI recommends, you approve, action proceeds. Clintra never contacts leads or changes your CRM without explicit approval.
      </p>
      {error && (
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      )}
      <div className="lead-tabs" role="tablist">
        {(
          [
            ["overview", "Overview"],
            ["leads", "Priority Queue"],
            ["config", "ICP & Scoring"],
            ["model", "Model Learning"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            className={tab === key ? "active" : ""}
            onClick={() => {
              setTab(key);
              setSelectedLeadId(null);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "overview" &&
        (overview ? (
          <LeadOverviewTab
            overview={overview}
            refreshing={refreshing}
            onRefresh={refresh}
            onNavigateToIntegrations={onNavigateToIntegrations}
            onOpenQueue={() => setTab("leads")}
          />
        ) : (
          <Panel title="Intelligence Overview">
            <Empty icon={LoaderCircle} title="Loading" text={overviewLoading ? "Loading your lead intelligence overview…" : "Nothing to show yet."} />
          </Panel>
        ))}
      {tab === "leads" && !selectedLeadId && <LeadQueueTab session={session} workspace={workspace} onOpenLead={setSelectedLeadId} />}
      {tab === "leads" && selectedLeadId && (
        <LeadDetailPanel session={session} workspace={workspace} leadId={selectedLeadId} onBack={() => setSelectedLeadId(null)} />
      )}
      {tab === "config" && <LeadConfigTab session={session} workspace={workspace} onSaved={reloadOverview} />}
      {tab === "model" && <LeadModelLearningTab session={session} workspace={workspace} />}
    </div>
  );
}

function LeadOverviewTab({
  overview, refreshing, onRefresh, onNavigateToIntegrations, onOpenQueue,
}: {
  overview: LeadIntelligenceOverview;
  refreshing: boolean;
  onRefresh: () => void;
  onNavigateToIntegrations: () => void;
  onOpenQueue: () => void;
}) {
  if (!overview.crmConnected)
    return (
      <Panel title="Connect your CRM">
        <Empty icon={Plug} title="No CRM connected" text="Lead Intelligence needs a connected CRM before it can score or enrich leads. Connect HubSpot or Salesforce to begin." />
        <div className="page-title-action">
          <button className="button primary" onClick={onNavigateToIntegrations}>
            Connect a CRM <ArrowRight size={15} />
          </button>
        </div>
      </Panel>
    );

  return (
    <>
      <div className="metrics metrics-5">
        <Metric
          icon={UsersRound}
          label="Total Leads"
          value={String(overview.counts.leads)}
          meta={`${overview.counts.scored} scored`}
          {...(overview.counts.leads > 0 && overview.counts.scored === overview.counts.leads ? { metaTone: "success" as const } : {})}
        />
        <Metric icon={Radar} label="High Priority" value={String(overview.counts.high)} meta="Strongest fit &amp; signals" />
        <Metric icon={Activity} label="Medium Priority" value={String(overview.counts.medium)} meta="Worth monitoring" />
        <Metric icon={Gauge} label="Low Priority" value={String(overview.counts.low)} meta="Lower fit or fewer signals" />
        <Metric icon={Database} label="Enrichment Coverage" value={overview.counts.leads ? `${Math.round((overview.counts.scored / overview.counts.leads) * 100)}%` : "0%"} meta={`${overview.counts.unscored} awaiting scoring`} />
      </div>
      <Panel
        title="Data Sources"
        kicker="Connected Systems"
        action={
          <button className="button secondary" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Refresh Intelligence
          </button>
        }
      >
        {overview.dataSources.length ? (
          <div className="source-list">
            {overview.dataSources.map((source) => (
              <div key={source.provider}>
                <span>
                  <b>{source.provider === "hubspot" ? "HubSpot" : "Salesforce"}</b>
                  <small>
                    {source.behavioralSupported
                      ? source.behavioralSynced
                        ? "Behavioural signals enabled"
                        : "Reconnect to enable behavioural signals"
                      : "Behavioural signals not yet available for this source"}
                  </small>
                </span>
                <em className="status complete">Connected</em>
              </div>
            ))}
          </div>
        ) : (
          <Empty icon={Plug} title="No data sources connected" text="Lead Intelligence draws on your connected CRM. Connect one to begin." />
        )}
      </Panel>
      {!overview.icpConfigured && (
        <div className="form-alert info">
          <Target size={16} />
          Define your Ideal Customer Profile in ICP &amp; Scoring so Clintra can explain how well each lead matches it.
        </div>
      )}
      <Panel
        title="AI Recommendation"
        kicker="Prioritisation signals — not guaranteed purchasing behaviour"
        action={
          <button className="button primary" onClick={onOpenQueue}>
            Review Priority Queue <ArrowRight size={15} />
          </button>
        }
      >
        {overview.counts.scored ? (
          <div className="ai-callout">
            <p>{leadRecommendationText(overview.counts, overview.averageScore)}</p>
          </div>
        ) : (
          <Empty icon={UsersRound} title="No leads scored yet" text="Once your CRM sync completes, scored and prioritised leads will appear here." />
        )}
      </Panel>
      {overview.lastScoringRun && (
        <Panel title="Agent Activity" kicker="Last scoring run">
          <div className="source-list">
            <div>
              <span>
                <b>{new Date(overview.lastScoringRun.startedAt).toLocaleString()}</b>
                <small>
                  {overview.lastScoringRun.scoresWritten} score change{overview.lastScoringRun.scoresWritten === 1 ? "" : "s"} written · {overview.lastScoringRun.insufficientData} lead
                  {overview.lastScoringRun.insufficientData === 1 ? "" : "s"} still awaiting enough data
                </small>
              </span>
              <em className="status neutral">{overview.lastScoringRun.leadsScored} evaluated</em>
            </div>
          </div>
        </Panel>
      )}
    </>
  );
}

// Split around the Source column, which sits between Company and Last
// Activity in the requested column order and isn't itself sortable.
const LEAD_SORTS_BEFORE_SOURCE: Array<[NonNullable<LeadListQuery["sort"]>, string]> = [
  ["score", "Score"], ["name", "Lead"], ["company", "Company"],
];
const LEAD_SORTS_AFTER_SOURCE: Array<[NonNullable<LeadListQuery["sort"]>, string]> = [
  ["lastActivity", "Last Activity"], ["stage", "Stage"],
];

// Only the providers Clintra actually ingests leads from -- provenance
// display only, never a scoring factor and never expanded to unsupported
// integrations just to look more complete.
function leadSourceLabel(provider: LeadSourceProvider | string | null): string {
  if (provider === "hubspot") return "HubSpot";
  if (provider === "salesforce") return "Salesforce";
  if (provider === "zoho") return "Zoho CRM";
  if (provider === "pipedrive") return "Pipedrive";
  if (provider === "dynamics") return "Dynamics 365";
  if (provider === "manual") return "Manual Entry";
  if (provider === "csv") return "CSV Import";
  return "Source not available";
}

// A small hand-rolled CSV parser (quoted fields, embedded commas/quotes) --
// no CSV library exists anywhere in this repo, and a full RFC4180 parser
// isn't warranted for what's expected to be simple lead-export files.
// Embedded newlines inside a quoted field are not supported.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i]!;
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += char;
    } else if (char === '"') inQuotes = true;
    else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = [];
    } else field += char;
  }
  if (field || row.length) row.push(field);
  if (row.some((cell) => cell.trim())) rows.push(row);
  return rows;
}

const CSV_HEADER_ALIASES: Record<string, keyof ManualLeadRow> = {
  firstname: "firstName", "first name": "firstName", first: "firstName",
  lastname: "lastName", "last name": "lastName", last: "lastName",
  email: "email", "email address": "email",
  company: "company", organisation: "company", organization: "company",
  title: "title", jobtitle: "title", "job title": "title",
  industry: "industry",
  employeecount: "employeeCount", "employee count": "employeeCount", employees: "employeeCount",
  revenue: "revenue", "annual revenue": "revenue",
  country: "country",
  source: "source",
};

// Rows with no recognisable email column/value are dropped before the
// request is even sent -- reported back as a count, not silently vanished.
function rowsFromCsv(text: string): { rows: ManualLeadRow[]; skippedRows: number } {
  const table = parseCsv(text);
  if (table.length < 2) return { rows: [], skippedRows: 0 };
  const [headerRow, ...dataRows] = table;
  const fields = headerRow!.map((header) => CSV_HEADER_ALIASES[header.trim().toLowerCase()] ?? null);
  const rows: ManualLeadRow[] = [];
  let skippedRows = 0;
  for (const dataRow of dataRows) {
    const row: Partial<Record<keyof ManualLeadRow, string | number>> = {};
    fields.forEach((field, index) => {
      if (!field) return;
      const raw = dataRow[index]?.trim();
      if (!raw) return;
      row[field] = field === "employeeCount" || field === "revenue" ? Number(raw) : raw;
    });
    if (row.email) rows.push(row as ManualLeadRow);
    else skippedRows++;
  }
  return { rows, skippedRows };
}

function LeadQueueTab({ session, workspace, onOpenLead }: { session: AuthSession; workspace: Workspace; onOpenLead: (id: string) => void }) {
  const [data, setData] = useState<LeadListItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<NonNullable<LeadListQuery["sort"]>>("score");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [band, setBand] = useState<LeadBand | "">("");
  const [source, setSource] = useState<LeadSourceProvider | "">("");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  const [showAddLead, setShowAddLead] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({ name: "", email: "", company: "", title: "", industry: "", employeeCount: "", country: "", source: "" });
  const [addLeadBusy, setAddLeadBusy] = useState(false);
  const [addLeadError, setAddLeadError] = useState("");
  const [addLeadResult, setAddLeadResult] = useState<LeadMappingSummary | null>(null);

  const [showImport, setShowImport] = useState(false);
  const [importRows, setImportRows] = useState<ManualLeadRow[]>([]);
  const [importSkippedRows, setImportSkippedRows] = useState(0);
  const [importSource, setImportSource] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState<LeadMappingSummary | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchDraft), 300);
    return () => window.clearTimeout(timeout);
  }, [searchDraft]);

  useEffect(() => {
    setLoading(true);
    void loadLeads(session.session.token, workspace.tenantId, {
      page, pageSize: 25, sort, direction, ...(band ? { band } : {}), ...(search ? { search } : {}), ...(source ? { source } : {}),
    })
      .then((result) => {
        setData(result.data);
        setTotalPages(result.totalPages);
        setTotal(result.total);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load leads."))
      .finally(() => setLoading(false));
  }, [page, sort, direction, band, search, source, reloadToken]);

  const toggleSort = (key: NonNullable<LeadListQuery["sort"]>) => {
    if (sort === key) setDirection(direction === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      setDirection("desc");
    }
    setPage(1);
  };

  const submitAddLead = async () => {
    if (!addLeadForm.email.trim()) {
      setAddLeadError("Email is required.");
      return;
    }
    setAddLeadBusy(true);
    setAddLeadError("");
    const [firstName, ...rest] = addLeadForm.name.trim().split(/\s+/).filter(Boolean);
    const lastName = rest.join(" ");
    try {
      const summary = await createManualLead(session.session.token, workspace.tenantId, {
        ...(firstName ? { firstName } : {}), ...(lastName ? { lastName } : {}),
        email: addLeadForm.email.trim(),
        ...(addLeadForm.company ? { company: addLeadForm.company } : {}),
        ...(addLeadForm.title ? { title: addLeadForm.title } : {}),
        ...(addLeadForm.industry ? { industry: addLeadForm.industry } : {}),
        ...(addLeadForm.employeeCount ? { employeeCount: Number(addLeadForm.employeeCount) } : {}),
        ...(addLeadForm.country ? { country: addLeadForm.country } : {}),
        ...(addLeadForm.source ? { source: addLeadForm.source } : {}),
      });
      setAddLeadResult(summary);
      setAddLeadForm({ name: "", email: "", company: "", title: "", industry: "", employeeCount: "", country: "", source: "" });
      setReloadToken((token) => token + 1);
    } catch (cause) {
      setAddLeadError(cause instanceof Error ? cause.message : "Could not add lead.");
    } finally {
      setAddLeadBusy(false);
    }
  };

  const handleCsvFile = async (file: File) => {
    setImportResult(null);
    const text = await file.text();
    const { rows, skippedRows } = rowsFromCsv(text);
    setImportRows(rows);
    setImportSkippedRows(skippedRows);
    setImportError(rows.length ? "" : "No rows with a valid email column were found.");
  };

  const submitImport = async () => {
    if (!importRows.length) return;
    setImportBusy(true);
    setImportError("");
    try {
      const summary = await importLeadsCsv(session.session.token, workspace.tenantId, importRows, importSource.trim() || undefined);
      setImportResult(summary);
      setImportRows([]);
      setReloadToken((token) => token + 1);
    } catch (cause) {
      setImportError(cause instanceof Error ? cause.message : "Could not import leads.");
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <Panel
      title="Priority Lead Queue"
      kicker={`${total} lead${total === 1 ? "" : "s"}`}
      action={
        <div className="page-title-action">
          <button
            className="button secondary"
            onClick={() => {
              setShowImport((value) => !value);
              setShowAddLead(false);
            }}
          >
            <Upload size={15} /> Import CSV
          </button>
          <button
            className="button primary"
            onClick={() => {
              setShowAddLead((value) => !value);
              setShowImport(false);
            }}
          >
            <Plus size={15} /> Add Lead
          </button>
        </div>
      }
    >
      {showAddLead && (
        <div className="lead-config-grid">
          <label>
            Name
            <input value={addLeadForm.name} onChange={(event) => setAddLeadForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Ada Lovelace" />
          </label>
          <label>
            Email*
            <input value={addLeadForm.email} onChange={(event) => setAddLeadForm((prev) => ({ ...prev, email: event.target.value }))} placeholder="ada@acme.io" />
          </label>
          <label>
            Company
            <input value={addLeadForm.company} onChange={(event) => setAddLeadForm((prev) => ({ ...prev, company: event.target.value }))} />
          </label>
          <label>
            Title
            <input value={addLeadForm.title} onChange={(event) => setAddLeadForm((prev) => ({ ...prev, title: event.target.value }))} />
          </label>
          <label>
            Industry
            <input value={addLeadForm.industry} onChange={(event) => setAddLeadForm((prev) => ({ ...prev, industry: event.target.value }))} />
          </label>
          <label>
            Employee count
            <input value={addLeadForm.employeeCount} onChange={(event) => setAddLeadForm((prev) => ({ ...prev, employeeCount: event.target.value }))} type="number" min="0" />
          </label>
          <label>
            Country (ISO-2)
            <input value={addLeadForm.country} onChange={(event) => setAddLeadForm((prev) => ({ ...prev, country: event.target.value }))} placeholder="e.g. US" />
          </label>
          <label>
            Source (optional label, e.g. Email, Marketing Campaign)
            <input value={addLeadForm.source} onChange={(event) => setAddLeadForm((prev) => ({ ...prev, source: event.target.value }))} />
          </label>
          {addLeadError && (
            <div className="form-alert error">
              <X size={16} />
              {addLeadError}
            </div>
          )}
          {addLeadResult && (
            <div className="form-alert success">
              <Check size={16} />
              Added. {addLeadResult.leadsCreated} lead{addLeadResult.leadsCreated === 1 ? "" : "s"} created, {addLeadResult.leadsUpdated} updated.
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={addLeadBusy} onClick={() => void submitAddLead()}>
              {addLeadBusy ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save lead
            </button>
          </div>
        </div>
      )}
      {showImport && (
        <div className="lead-config-grid">
          <label>
            CSV file
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleCsvFile(file);
              }}
            />
          </label>
          <label>
            Default source (optional label, e.g. Email, Marketing Campaign)
            <input value={importSource} onChange={(event) => setImportSource(event.target.value)} />
          </label>
          {importRows.length > 0 && (
            <p className="muted">
              {importRows.length} row{importRows.length === 1 ? "" : "s"} ready to import
              {importSkippedRows > 0 ? ` (${importSkippedRows} row${importSkippedRows === 1 ? "" : "s"} skipped -- no email column found)` : ""}.
            </p>
          )}
          {importError && (
            <div className="form-alert error">
              <X size={16} />
              {importError}
            </div>
          )}
          {importResult && (
            <div className="form-alert success">
              <Check size={16} />
              Imported. {importResult.leadsCreated} created, {importResult.leadsUpdated} updated, {importResult.skipped.length} skipped.
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={importBusy || !importRows.length} onClick={() => void submitImport()}>
              {importBusy ? <LoaderCircle className="spin" size={15} /> : <Upload size={15} />} Import {importRows.length || ""} lead{importRows.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}
      <div className="lead-filter-bar">
        <div className="table-search">
          <Search size={15} />
          <input
            placeholder="Search leads or companies"
            value={searchDraft}
            onChange={(event) => {
              setSearchDraft(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <select
          value={band}
          onChange={(event) => {
            setBand(event.target.value as LeadBand | "");
            setPage(1);
          }}
        >
          <option value="">All priorities</option>
          <option value="high">High Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="low">Low Priority</option>
        </select>
        <select
          value={source}
          onChange={(event) => {
            setSource(event.target.value as LeadSourceProvider | "");
            setPage(1);
          }}
        >
          <option value="">All Sources</option>
          <option value="hubspot">HubSpot</option>
          <option value="salesforce">Salesforce</option>
          <option value="zoho">Zoho CRM</option>
          <option value="pipedrive">Pipedrive</option>
          <option value="dynamics">Dynamics 365</option>
          <option value="manual">Manual Entry</option>
          <option value="csv">CSV Import</option>
        </select>
      </div>
      {error && (
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      )}
      {data.length ? (
        <div className={`page-inventory-table lead-table ${loading ? "reloading" : ""}`}>
          <table>
            <thead>
              <tr>
                <th>Priority</th>
                {LEAD_SORTS_BEFORE_SOURCE.map(([key, label]) => (
                  <th key={key}>
                    <button className="sort-header" onClick={() => toggleSort(key)}>
                      {label} {sort === key && (direction === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                    </button>
                  </th>
                ))}
                <th>Source</th>
                {LEAD_SORTS_AFTER_SOURCE.map(([key, label]) => (
                  <th key={key}>
                    <button className="sort-header" onClick={() => toggleSort(key)}>
                      {label} {sort === key && (direction === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                    </button>
                  </th>
                ))}
                <th>Industry</th>
              </tr>
            </thead>
            <tbody>
              {data.map((lead) => (
                <tr key={lead.id} className="clickable" onClick={() => onOpenLead(lead.id)}>
                  <td>
                    <em className={`status ${leadBandTone(lead.band)}`}>{leadBandLabel(lead.band)}</em>
                  </td>
                  <td>
                    <b>{lead.score ?? ""}</b>
                  </td>
                  <td>{lead.name}</td>
                  <td>{lead.company}</td>
                  <td>
                    {lead.source.primary ? (
                      <>
                        <em className="status neutral">{leadSourceLabel(lead.source.primary)}</em>
                        {lead.source.additional.length > 0 && <small>+{lead.source.additional.map(leadSourceLabel).join(", ")}</small>}
                      </>
                    ) : (
                      <span className="muted">Source not available</span>
                    )}
                  </td>
                  <td>{relativeDay(lead.lastActivityAt)}</td>
                  <td>
                    <em className="status neutral">{lead.stage}</em>
                  </td>
                  <td>{lead.industry}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty icon={UsersRound} title="No leads scored yet" text="Once your CRM sync completes, scored and prioritised leads will appear here." />
      )}
      {totalPages > 1 && (
        <div className="table-pagination">
          <div className="table-pagination-controls">
            <button className="button secondary" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
              <ChevronLeft size={15} /> Prev
            </button>
            <button className="button secondary" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>
              Next <ChevronRight size={15} />
            </button>
          </div>
          <small>
            Page {page} of {totalPages}
          </small>
        </div>
      )}
    </Panel>
  );
}

function LeadDetailPanel({ session, workspace, leadId, onBack }: { session: AuthSession; workspace: Workspace; leadId: string; onBack: () => void }) {
  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [error, setError] = useState("");
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState("");

  useEffect(() => {
    setDetail(null);
    void loadLeadDetail(session.session.token, workspace.tenantId, leadId)
      .then(setDetail)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load this lead."));
  }, [leadId]);

  // Approving/rejecting only records the decision -- it never itself
  // contacts the lead or changes CRM data. Updates the recommendation in
  // place rather than re-fetching the whole lead (which would also
  // re-trigger AI narration for no reason).
  const decide = async (recommendationId: string, decision: "approved" | "rejected") => {
    setDecidingId(recommendationId);
    setDecisionError("");
    try {
      const result = await decideLeadRecommendation(session.session.token, workspace.tenantId, leadId, recommendationId, decision);
      setDetail((current) =>
        current
          ? { ...current, recommendations: current.recommendations.map((item) => (item.id === recommendationId ? { ...item, status: result.status as LeadRecommendation["status"], decidedBy: result.decidedBy, decidedAt: result.decidedAt } : item)) }
          : current,
      );
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingId(null);
    }
  };

  if (error)
    return (
      <Panel title="Lead">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
        <button className="button secondary" onClick={onBack}>
          <ArrowLeft size={15} /> Back to Priority Queue
        </button>
      </Panel>
    );
  if (!detail)
    return (
      <Panel title="Lead">
        <Empty icon={LoaderCircle} title="Loading" text="Loading this lead's score breakdown…" />
      </Panel>
    );

  // icp_fit gets its own dedicated "ICP Match" panel below instead of being
  // one row among the others -- everything else stays in Score Breakdown.
  const componentOrder: Array<[string, string]> = [
    ["firmographic", "Firmographic Fit"], ["behavioral", "Behavioral Engagement"],
    ["historical", "Historical Conversion Likelihood"], ["recent_activity", "Recent Activity"],
  ];
  const icpFitComponent = detail.score.components.icp_fit;

  return (
    <div className="page-stack">
      <button className="lead-detail-back" onClick={onBack}>
        <ArrowLeft size={15} /> Back to Priority Queue
      </button>
      <div className="website-grid">
        <div className="page-stack">
          <Panel title={detail.lead.name} kicker={detail.lead.company ?? "No company on record"}>
            <div className="lead-score-hero">
              <LeadScoreGauge score={detail.score.score} band={detail.score.band} />
              <div>
                <p>
                  {detail.lead.title ? `${detail.lead.title} at ` : ""}
                  {detail.lead.company ?? "an unlinked company"}. Stage: <em className="status neutral">{detail.lead.stage}</em>
                </p>
                {detail.lead.disqualifiedReason && (
                  <div className="form-alert error">
                    <X size={16} /> {detail.lead.disqualifiedReason}
                  </div>
                )}
                {detail.score.status === "insufficient_data" && (
                  <div className="form-alert info">
                    <Info size={16} /> Not enough data yet to compute a full score for this lead.
                  </div>
                )}
              </div>
            </div>
          </Panel>
          <Panel title="Score Breakdown" kicker="Why this score?">
            {/* Deterministic and reproducible -- computed the same way every
                time from the components below, never an independent AI
                judgment. The AI Summary just narrates these same reasons in
                prose; it never adds a reason that isn't already listed here. */}
            {detail.explanationNarrative ? (
              <div className="ai-callout">
                <p>
                  <Sparkles size={13} /> <b>AI Summary</b>
                  <br />
                  {detail.explanationNarrative}
                </p>
              </div>
            ) : (
              <p className="muted">AI Summary not available -- connect an OpenAI-entitled plan to enable a narrated summary of the reasons below.</p>
            )}
            {detail.score.explanation.length > 0 && (
              <ul className="score-why">
                {detail.score.explanation.map((line, index) => (
                  <li key={index}>{line}</li>
                ))}
              </ul>
            )}
            <div className="score-breakdown">
              {componentOrder.map(([id, label]) => {
                const component = detail.score.components[id];
                if (!component) return null;
                const pct = component.available ? Math.round(((component.points ?? 0) / Math.max(component.contributionCeiling ?? 1, 1)) * 100) : 0;
                return (
                  <div className="score-breakdown-row" key={id}>
                    <div className="score-breakdown-label">
                      <b>{label}</b>
                      <small>Weight {component.weight}%</small>
                    </div>
                    {component.available ? (
                      <>
                        <div className="score-breakdown-track">
                          <span className={pct >= 75 ? "strong" : pct >= 40 ? "partial" : "weak"} style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <div className="score-breakdown-points">{component.points ?? 0} pts</div>
                      </>
                    ) : (
                      <div className="score-breakdown-note muted">{component.unavailableReason ?? "No data available"}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </Panel>
          <Panel title="ICP Match" kicker="How well this lead fits your Ideal Customer Profile">
            {icpFitComponent?.available ? (
              <>
                <p className="ai-callout">
                  {Math.round(((icpFitComponent.points ?? 0) / Math.max(icpFitComponent.contributionCeiling ?? 1, 1)) * 100)}% match, worth {icpFitComponent.points ?? 0} of the score.
                </p>
                <ul className="score-why">
                  {icpFitComponent.subScores.map((sub) => (
                    <li key={sub.id}>
                      {sub.evaluable ? <Check size={13} /> : <X size={13} />} <b>{sub.label}:</b> {sub.note}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <Empty icon={Target} title="ICP not evaluable" text={icpFitComponent?.unavailableReason ?? "Define your Ideal Customer Profile in ICP & Scoring to see a match breakdown here."} />
            )}
          </Panel>
          {detail.scoreChange && (
            <Panel title="Why did this score change?" kicker={`${detail.scoreChange.from.score} → ${detail.scoreChange.to.score}`}>
              <p className="ai-callout">{detail.scoreChange.headline}</p>
              <ul className="score-why">
                {detail.scoreChange.components
                  .filter((component) => Math.abs(component.pointsDelta) >= 0.1)
                  .map((component) => (
                    <li key={component.id}>
                      <span className={`score-delta ${component.pointsDelta > 0 ? "up" : "down"}`}>{component.pointsDelta > 0 ? "+" : ""}{component.pointsDelta}</span> {component.summary}
                    </li>
                  ))}
              </ul>
            </Panel>
          )}
          <Panel title="Recent Behavioral Signals">
            {detail.activityTimeline.length ? (
              <div className="activity-feed">
                {detail.activityTimeline.map((activity, index) => (
                  <div key={index}>
                    <span className="activity-dot" />
                    <div>
                      <b>{activity.kind.replace(/_/g, " ")}</b>
                      <small>{activity.source} · strength {activity.strength.toFixed(2)}</small>
                    </div>
                    <em>{new Date(activity.occurredAt).toLocaleDateString()}</em>
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon={Radar} title="No behavioural signals yet" text="Connect a supported marketing or analytics source, or wait for the next sync, to see activity here." />
            )}
          </Panel>
        </div>
        <div className="page-stack">
          <Panel title="Lead Source" kicker="How this lead entered Clintra">
            {detail.source.primary ? (
              <div className="source-list">
                <div>
                  <span>
                    <b>Source</b>
                    {detail.source.additional.length > 0 && <small>Additional: {detail.source.additional.map(leadSourceLabel).join(", ")}</small>}
                  </span>
                  <em className="status neutral">{leadSourceLabel(detail.source.primary)}</em>
                </div>
                <div>
                  <span>
                    <b>First captured</b>
                  </span>
                  <em className="status neutral">{detail.source.firstCapturedAt ? new Date(detail.source.firstCapturedAt).toLocaleDateString() : "Not available"}</em>
                </div>
                <div>
                  <span>
                    <b>Last synced</b>
                  </span>
                  <em className="status neutral">{detail.source.lastSyncedAt ? relativeDay(detail.source.lastSyncedAt) : "Not available"}</em>
                </div>
                <div>
                  <span>
                    <b>Source status</b>
                  </span>
                  <em className={`status ${detail.source.sourceStatus === "connected" ? "complete" : "neutral"}`}>
                    {detail.source.sourceStatus === "connected" ? "Connected" : detail.source.sourceStatus ?? "Not available"}
                  </em>
                </div>
                {detail.source.originalSource && (
                  <div>
                    <span>
                      <b>Original source</b>
                      <small>Marketing/lead-source attribution reported by the CRM</small>
                    </span>
                    <em className="status neutral">{detail.source.originalSource}</em>
                  </div>
                )}
              </div>
            ) : (
              <Empty icon={Plug} title="Source not available" text="This lead has no recorded ingestion source. Clintra never fabricates provenance data." />
            )}
          </Panel>
          <Panel title="Recommended Action" kicker="Human-governed -- approving never contacts the lead or changes CRM data">
            {decisionError && (
              <div className="form-alert error">
                <X size={16} />
                {decisionError}
              </div>
            )}
            {detail.recommendations.length ? (
              <div className="website-recommendations">
                {detail.recommendations.map((recommendation) => (
                  <div key={recommendation.id} className="website-recommendation-card">
                    <div className="page-title-action">
                      <b>
                        <Lightbulb size={15} /> {recommendation.title}
                      </b>
                    </div>
                    <p>{recommendation.rationale}</p>
                    {recommendation.status === "pending" ? (
                      <div className="page-title-action">
                        <button className="button secondary" disabled={decidingId === recommendation.id} onClick={() => void decide(recommendation.id, "rejected")}>
                          Reject
                        </button>
                        <button className="button primary" disabled={decidingId === recommendation.id} onClick={() => void decide(recommendation.id, "approved")}>
                          {decidingId === recommendation.id ? <LoaderCircle className="spin" size={15} /> : "Approve"}
                        </button>
                      </div>
                    ) : (
                      <p className="muted">
                        {recommendation.status === "approved" ? "Approved" : "Rejected"}
                        {recommendation.decidedAt ? ` on ${new Date(recommendation.decidedAt).toLocaleDateString()}` : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon={Lightbulb} title="No recommendations yet" text="Clintra generates a recommendation here when this lead's score crosses a meaningful threshold." />
            )}
          </Panel>
          <Panel title="Enrichment" kicker={detail.lead.accountSource ? `Enriched from ${leadSourceLabel(detail.lead.accountSource)}` : "Firmographic data"}>
            <div className="source-list">
              <div>
                <span>
                  <b>Industry</b>
                </span>
                <em className="status neutral">{detail.lead.industry ?? "Unknown"}</em>
              </div>
              <div>
                <span>
                  <b>Company Size</b>
                </span>
                <em className="status neutral">{detail.lead.employeeBand ?? "Unknown"}</em>
              </div>
              <div>
                <span>
                  <b>Revenue</b>
                </span>
                <em className="status neutral">{detail.lead.revenueBand ?? "Unknown"}</em>
              </div>
              <div>
                <span>
                  <b>Geography</b>
                </span>
                <em className="status neutral">{detail.lead.countryCode ?? "Unknown"}</em>
              </div>
              <div>
                <span>
                  <b>Consent</b>
                </span>
                <em className="status neutral">{detail.lead.consentStatus}</em>
              </div>
            </div>
          </Panel>
          <Panel title="Score History">
            {detail.scoreHistory.length > 1 ? (
              <div className="source-list">
                {detail.scoreHistory.map((entry) => (
                  <div key={entry.id}>
                    <span>
                      <b>{entry.score}</b>
                      <small>{new Date(entry.calculatedAt).toLocaleString()}</small>
                    </span>
                    <em className={`status ${leadBandTone(entry.band)}`}>{leadBandLabel(entry.band)}</em>
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon={Activity} title="Not enough scoring history" text="Score history appears after this lead has been scored more than once." />
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function LeadConfigTab({ session, workspace, onSaved }: { session: AuthSession; workspace: Workspace; onSaved: () => void }) {
  const [config, setConfig] = useState<Awaited<ReturnType<typeof loadLeadScoringConfig>> | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const [industries, setIndustries] = useState("");
  const [employeeBands, setEmployeeBands] = useState("");
  const [revenueBands, setRevenueBands] = useState("");
  const [countries, setCountries] = useState("");
  const [titleKeywords, setTitleKeywords] = useState("");
  const [weights, setWeights] = useState({ icpFit: 35, firmographic: 20, behavioral: 20, historical: 15, recentActivity: 10 });

  useEffect(() => {
    void loadLeadScoringConfig(session.session.token, workspace.tenantId)
      .then((result) => {
        setConfig(result);
        setIndustries(result.companyProfile.industries.join(", "));
        setEmployeeBands(result.companyProfile.employeeBands.join(", "));
        setRevenueBands(result.companyProfile.revenueBands.join(", "));
        setCountries(result.companyProfile.countries.join(", "));
        setTitleKeywords(result.buyerProfile.titleKeywords.join(", "));
        setWeights({
          icpFit: result.weights.icp_fit, firmographic: result.weights.firmographic, behavioral: result.weights.behavioral,
          historical: result.weights.historical, recentActivity: result.weights.recent_activity,
        });
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load configuration."));
  }, []);

  const splitList = (value: string) => value.split(",").map((entry) => entry.trim()).filter(Boolean);
  const weightTotal = weights.icpFit + weights.firmographic + weights.behavioral + weights.historical + weights.recentActivity;

  const saveIcp = async () => {
    if (!config) return;
    setSaving(true);
    setError("");
    try {
      const updated = await saveLeadIcpConfig(session.session.token, workspace.tenantId, {
        companyProfile: { ...config.companyProfile, industries: splitList(industries), employeeBands: splitList(employeeBands), revenueBands: splitList(revenueBands), countries: splitList(countries) },
        buyerProfile: { ...config.buyerProfile, titleKeywords: splitList(titleKeywords) },
        qualificationSignals: config.qualificationSignals,
        historicalSignals: config.historicalSignals,
      });
      setConfig(updated);
      setStatus("ICP saved. Rescoring your leads now.");
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the ICP.");
    } finally {
      setSaving(false);
    }
  };

  const saveWeights = async () => {
    if (weightTotal !== 100) return;
    setSaving(true);
    setError("");
    try {
      const updated = await saveLeadScoringWeights(session.session.token, workspace.tenantId, { weights });
      setConfig(updated);
      setStatus("Scoring weights saved. Rescoring your leads now.");
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the weights.");
    } finally {
      setSaving(false);
    }
  };

  if (!config)
    return (
      <Panel title="ICP &amp; Scoring">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your configuration…" />
      </Panel>
    );

  return (
    <div className="page-stack">
      {error && (
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      )}
      {status && (
        <div className="form-alert success">
          <Check size={16} />
          {status}
        </div>
      )}
      <Panel title="Ideal Customer Profile" kicker={config.icpConfigured ? "Configured" : "Not yet configured"} action={<Target size={18} />}>
        {!config.icpConfigured && <p className="ai-callout">Define your ICP so Clintra can explain how well each lead matches it. Nothing is scored against an ICP until you save one.</p>}
        <div className="lead-config-grid">
          <label>
            Target industries (comma separated)
            <input value={industries} onChange={(event) => setIndustries(event.target.value)} placeholder="e.g. Software, Financial Services" />
          </label>
          <label>
            Target employee bands
            <input value={employeeBands} onChange={(event) => setEmployeeBands(event.target.value)} placeholder="e.g. 51-200, 201-500" />
          </label>
          <label>
            Target revenue bands
            <input value={revenueBands} onChange={(event) => setRevenueBands(event.target.value)} placeholder="e.g. 10M-50M, 50M-250M" />
          </label>
          <label>
            Target countries (ISO-2 codes)
            <input value={countries} onChange={(event) => setCountries(event.target.value)} placeholder="e.g. US, GB" />
          </label>
          <label>
            Buyer title keywords
            <input value={titleKeywords} onChange={(event) => setTitleKeywords(event.target.value)} placeholder="e.g. VP, Director, Head of" />
          </label>
        </div>
        <div className="page-title-action">
          <button className="button primary" disabled={saving} onClick={() => void saveIcp()}>
            {saving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save ICP
          </button>
        </div>
      </Panel>
      <Panel title="Scoring Configuration" kicker="Transparent weighting" action={<SlidersHorizontal size={18} />}>
        <p>Each score component is weighted; weights must total 100%.</p>
        <div className="weight-editor">
          {(
            [
              ["icpFit", "ICP Fit"], ["firmographic", "Firmographic Fit"], ["behavioral", "Behavioral Engagement"],
              ["historical", "Historical Conversion Likelihood"], ["recentActivity", "Recent Activity"],
            ] as const
          ).map(([key, label]) => (
            <div className="weight-row" key={key}>
              <span>{label}</span>
              <input
                type="range" min={0} max={100} value={weights[key]}
                onChange={(event) => setWeights((prev) => ({ ...prev, [key]: Number(event.target.value) }))}
              />
              <b>{weights[key]}%</b>
            </div>
          ))}
        </div>
        <p className={weightTotal === 100 ? "weight-total" : "weight-total invalid"}>
          Total: {weightTotal}% {weightTotal !== 100 && "— weights must total 100%"}
        </p>
        <div className="page-title-action">
          <button className="button primary" disabled={saving || weightTotal !== 100} onClick={() => void saveWeights()}>
            {saving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save Weights
          </button>
        </div>
      </Panel>
    </div>
  );
}

function LeadModelLearningTab({ session, workspace }: { session: AuthSession; workspace: Workspace }) {
  const [stats, setStats] = useState<ModelLearningStats | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    void loadModelLearning(session.session.token, workspace.tenantId)
      .then(setStats)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load model learning."));
  }, []);

  if (error)
    return (
      <Panel title="Model Learning">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (!stats)
    return (
      <Panel title="Model Learning">
        <Empty icon={LoaderCircle} title="Loading" text="Loading model learning…" />
      </Panel>
    );

  return (
    <Panel title="Model Learning" kicker="Won/lost outcomes analysed">
      <div className="metrics">
        <Metric icon={Check} label="Won" value={String(stats.outcomes.won)} meta="Closed-won opportunities" />
        <Metric icon={X} label="Lost" value={String(stats.outcomes.lost)} meta="Closed-lost opportunities" />
        <Metric icon={Activity} label="Open" value={String(stats.outcomes.open)} meta="Still in progress" />
      </div>
      {stats.readiness.ready ? (
        stats.insight ? (
          <p className="ai-callout">{stats.insight}</p>
        ) : (
          <Empty icon={Bot} title="No repeatable patterns identified yet" text="Clintra will surface a pattern here once one segment converts meaningfully better than your overall average." />
        )
      ) : (
        <Empty icon={Bot} title="More historical outcomes are needed" text={stats.readiness.message} />
      )}
    </Panel>
  );
}

function RevenuePlan({ workspace }: { workspace: Workspace }) {
  const c = workspace.commercial,
    currency = String(c.currency ?? "GBP"),
    [target, setTarget] = useState(numberFrom(c.annualRevenueTarget)),
    [deal, setDeal] = useState(numberFrom(c.averageDealValue) || 1),
    [lead, setLead] = useState(numberFrom(c.leadToOpportunityRate) * 100 || 8),
    [win, setWin] = useState(numberFrom(c.opportunityWinRate) * 100 || 25);
  const plan = useMemo(() => {
    const deals = Math.ceil(target / deal),
      opps = Math.ceil(deals / (win / 100)),
      leads = Math.ceil(opps / (lead / 100)),
      pipeline = (deals * deal) / (win / 100);
    return { deals, opps, leads, pipeline };
  }, [target, deal, lead, win]);
  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">Connected commercial model</span>
        <h1>Turn the annual target into required activity.</h1>
        <p>
          Change an assumption and every downstream requirement updates
          immediately.
        </p>
      </div>
      <div className="plan-grid">
        <Panel title="Planning assumptions">
          <div className="form-grid">
            <Field label="Annual target">
              <input
                type="number"
                value={target}
                onChange={(e) => setTarget(Number(e.target.value))}
              />
            </Field>
            <Field label="Average deal value">
              <input
                type="number"
                value={deal}
                onChange={(e) => setDeal(Number(e.target.value))}
              />
            </Field>
            <Field label="Lead → opportunity %">
              <input
                type="number"
                value={lead}
                onChange={(e) => setLead(Number(e.target.value))}
              />
            </Field>
            <Field label="Opportunity win %">
              <input
                type="number"
                value={win}
                onChange={(e) => setWin(Number(e.target.value))}
              />
            </Field>
          </div>
        </Panel>
        <Panel title="Required revenue system">
          <div className="requirement-grid">
            <span>
              <small>Deals</small>
              <b>{plan.deals}</b>
            </span>
            <span>
              <small>Opportunities</small>
              <b>{plan.opps}</b>
            </span>
            <span>
              <small>Qualified leads</small>
              <b>{plan.leads}</b>
            </span>
            <span>
              <small>Pipeline</small>
              <b>{money(plan.pipeline, currency)}</b>
            </span>
          </div>
        </Panel>
      </div>
    </div>
  );
}
// ───────────────────────── Revenue Orchestration ─────────────────────────
// The coordination layer, not a specialist agent -- see the section 44
// principle this was built against: it observes, prioritizes, and
// recommends across the other agents' real outputs, but never performs
// their specialist work itself, and never acts without human approval.

const ORCH_SEVERITY_RANK: Record<OrchestrationSeverity, number> = { critical: 4, high: 3, medium: 2, low: 1 };
const ORCH_SEVERITY_TONE: Record<OrchestrationSeverity, "error" | "pending" | "neutral" | "complete"> = {
  critical: "error",
  high: "pending",
  medium: "neutral",
  low: "complete",
};
const ORCH_SEVERITY_LABEL: Record<OrchestrationSeverity, string> = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };

function healthLabel(health: RevenueHealth): string {
  if (health === "on_track") return "On Track";
  if (health === "watch") return "Watch";
  if (health === "at_risk") return "At Risk";
  if (health === "critical") return "Critical";
  return "Insufficient Data";
}
function healthTone(health: RevenueHealth): "complete" | "pending" | "neutral" | "error" {
  if (health === "on_track") return "complete";
  if (health === "watch") return "neutral";
  if (health === "at_risk") return "pending";
  if (health === "critical") return "error";
  return "neutral";
}
function agentDisplayName(workspace: Workspace, agentId: string): string {
  return workspace.agents.find((agent) => agent.id === agentId)?.name ?? labelFor(agentId);
}
function agentActivityTone(status: AgentActivityStatus): "complete" | "pending" | "neutral" | "error" {
  if (status === "complete") return "complete";
  if (status === "output_available") return "neutral";
  if (status === "running") return "pending";
  if (status === "needs_review") return "pending";
  if (status === "failed") return "error";
  return "neutral";
}
function agentActivityLabel(status: AgentActivityStatus): string {
  if (status === "complete") return "Complete";
  if (status === "output_available") return "Output Available";
  if (status === "running") return "Running";
  if (status === "needs_review") return "Needs Review";
  if (status === "failed") return "Failed";
  if (status === "not_run") return "Not Run";
  return "Waiting";
}

type OrchestrationDrawerContent =
  | { kind: "driver"; driver: GapDriver }
  | { kind: "insight"; insight: CrossAgentInsight }
  | { kind: "recommendation"; recommendation: OrchestrationRecommendation };

const REJECT_REASONS = ["Not relevant", "Too expensive", "Already being addressed", "Insufficient evidence", "Wrong priority", "Other"];

function RevenueOrchestrationView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "orchestrator");
  const [overview, setOverview] = useState<RevenueOrchestrationOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cycleBusy, setCycleBusy] = useState(false);
  const [cycleMessage, setCycleMessage] = useState("");
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState("");
  const [rejectPromptFor, setRejectPromptFor] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<OrchestrationDrawerContent | null>(null);
  const [scenarioLeverType, setScenarioLeverType] = useState<"pipeline" | "winRate">("pipeline");
  const [scenarioPercent, setScenarioPercent] = useState("15");
  const [scenarioResult, setScenarioResult] = useState<ScenarioResult | null>(null);
  const [scenarioBusy, setScenarioBusy] = useState(false);
  const [scenarioError, setScenarioError] = useState("");
  const [agentActivityTab, setAgentActivityTab] = useState<
    "all" | "complete" | "output_available" | "waiting"
  >("all");
  const [activityAgentFilter, setActivityAgentFilter] = useState("all");

  const load = () => {
    setLoading(true);
    void loadRevenueOrchestrationOverview(session.session.token, workspace.tenantId)
      .then((result) => {
        setOverview(result);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Revenue Orchestration."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Revenue Orchestration Agent</span>
          <h1>Revenue Orchestration isn't active yet.</h1>
        </div>
        <Panel title="Activate Revenue Orchestration">
          <Empty icon={Radar} title="Agent not active" text="Activate the Revenue Orchestration agent from Agents & Entitlements to start coordinating your specialist agents toward your revenue target." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  const runCycle = async () => {
    setCycleBusy(true);
    setCycleMessage("");
    try {
      const result = await runRevenueOrchestrationCycle(session.session.token, workspace.tenantId);
      setCycleMessage(`Cycle complete -- ${result.recommendationsGenerated} recommendation${result.recommendationsGenerated === 1 ? "" : "s"} generated.`);
      load();
    } catch (cause) {
      setCycleMessage(cause instanceof Error ? cause.message : "Could not run an orchestration cycle.");
    } finally {
      setCycleBusy(false);
    }
  };

  const decide = async (id: string, decision: "approved" | "rejected", reason?: string) => {
    setDecidingId(id);
    setDecisionError("");
    try {
      const result = await decideRevenueRecommendation(session.session.token, workspace.tenantId, id, decision, reason);
      setOverview((current) =>
        current
          ? {
              ...current,
              recommendations: current.recommendations.map((item) =>
                item.id === id ? { ...item, status: result.status as OrchestrationRecommendation["status"], decidedBy: result.decidedBy, decidedAt: result.decidedAt } : item,
              ),
            }
          : current,
      );
      setRejectPromptFor(null);
      setDrawer(null);
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingId(null);
    }
  };

  const runWhatIf = async () => {
    setScenarioBusy(true);
    setScenarioError("");
    try {
      const result = await runRevenueScenario(session.session.token, workspace.tenantId, { type: scenarioLeverType, percentChange: Number(scenarioPercent) || 0 });
      setScenarioResult(result);
    } catch (cause) {
      setScenarioError(cause instanceof Error ? cause.message : "Could not run that scenario.");
    } finally {
      setScenarioBusy(false);
    }
  };

  if (error)
    return (
      <Panel title="Revenue Orchestration">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading || !overview)
    return (
      <Panel title="Revenue Orchestration">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your revenue orchestration overview…" />
      </Panel>
    );

  const { snapshot } = overview;
  const currency = snapshot.currency;
  const pendingRecommendations = overview.recommendations.filter((item) => item.status === "pending").sort((a, b) => ORCH_SEVERITY_RANK[(b.priority ?? "low") as OrchestrationSeverity] - ORCH_SEVERITY_RANK[(a.priority ?? "low") as OrchestrationSeverity]);
  const approvedActions = overview.recommendations.filter((item) => item.status === "approved");

  const agentActivityCounts = {
    all: overview.agentActivity.length,
    complete: overview.agentActivity.filter((row) => row.status === "complete").length,
    output_available: overview.agentActivity.filter((row) => row.status === "output_available").length,
    waiting: overview.agentActivity.filter((row) => row.status !== "complete" && row.status !== "output_available").length,
  };
  const visibleAgentActivity = overview.agentActivity.filter((row) => {
    if (agentActivityTab === "all") return true;
    if (agentActivityTab === "waiting") return row.status !== "complete" && row.status !== "output_available";
    return row.status === agentActivityTab;
  });

  const activityAgents = Array.from(new Set(overview.activityLog.map((entry) => entry.agent))).sort();
  const visibleActivityLog = activityAgentFilter === "all" ? overview.activityLog : overview.activityLog.filter((entry) => entry.agent === activityAgentFilter);
  const downloadActivityLog = () => {
    const blob = new Blob([JSON.stringify(overview.activityLog, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `revenue-orchestration-activity-${workspace.tenantId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <div className="page-title-action" style={{ marginBottom: 4 }}>
            <span className="agent-pill">Revenue Orchestration Agent</span>
            <em className={`status ${healthTone(snapshot.health)}`}>{healthLabel(snapshot.health)}</em>
          </div>
          <h1>
            Revenue Orchestration
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Coordinates Clintra's specialist agents toward your shared revenue target -- observes, prioritizes, and recommends, but always waits for your approval.</span>
            </span>
          </h1>
        </div>
        <button className="button primary" disabled={cycleBusy} onClick={() => void runCycle()}>
          {cycleBusy ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Run orchestration cycle
        </button>
      </div>
      {cycleMessage && (
        <div className="form-alert info">
          <Info size={16} />
          {cycleMessage}
        </div>
      )}

      <div className="metrics metrics-5">
        <Metric icon={Target} label="Revenue Target" value={snapshot.target !== null ? money(snapshot.target, currency) : "Not set"} meta={snapshot.targetAsOf ? `As of ${new Date(snapshot.targetAsOf).toLocaleDateString()}` : "No target on record"} />
        <Metric icon={CircleDollarSign} label="Current Revenue" value={money(snapshot.currentRevenue, currency)} meta={snapshot.attainmentPct !== null ? `${snapshot.attainmentPct}% of target` : "Won opportunities this year"} />
        <Metric icon={Activity} label="Qualified Pipeline" value={money(snapshot.pipeline, currency)} meta={snapshot.coveragePct !== null ? `${snapshot.coveragePct}% coverage of remaining gap` : "Real open opportunities"} />
        <Metric icon={Radar} label="Forecast" value={snapshot.forecast !== null ? money(snapshot.forecast, currency) : "Not available"} meta={snapshot.forecast !== null ? "AI estimate" : (snapshot.forecastUnavailableReason ?? "")} {...(snapshot.forecast === null ? { valueSize: "sm" as const } : {})} />
        <Metric icon={Gauge} label="Revenue Gap" value={snapshot.gap !== null ? money(snapshot.gap, currency) : "Not available"} meta={snapshot.target === null ? "No target set" : "Target minus forecast"} {...(snapshot.gap === null ? { valueSize: "sm" as const } : {})} />
      </div>

      <Panel title="Revenue Health Diagnosis" kicker="Why this state, not just what it is">
        <em className={`status ${healthTone(snapshot.health)}`}>{healthLabel(snapshot.health)}</em>
        <ul className="score-why" style={{ marginTop: 14 }}>
          {snapshot.healthReasons.map((reason, index) => (
            <li key={index}>{reason}</li>
          ))}
        </ul>
        {snapshot.forecast === null && (
          <div className="ai-callout" style={{ marginTop: 12 }}>
            <Gauge size={14} /> Closed Opportunities Collected: {snapshot.closedOpportunityCount} of {snapshot.minClosedForForecast} needed to project a reliable forecast.
          </div>
        )}
      </Panel>

      <Panel title="Revenue Gap & Root Drivers" kicker={`${overview.gapDrivers.length} driver${overview.gapDrivers.length === 1 ? "" : "s"} identified`}>
        {overview.gapDrivers.length ? (
          <div className="source-list">
            {overview.gapDrivers.map((driver) => (
              <div key={driver.key} className="clickable" onClick={() => setDrawer({ kind: "driver", driver })}>
                <span>
                  <b>{driver.title}</b>
                  <small>
                    {agentDisplayName(workspace, driver.responsibleAgent)}
                    {driver.isEstimate ? " -- AI estimate" : ""}
                  </small>
                </span>
                <em className={`status ${ORCH_SEVERITY_TONE[driver.severity]}`}>{driver.estimatedImpact !== null ? money(driver.estimatedImpact, currency) : ORCH_SEVERITY_LABEL[driver.severity]}</em>
              </div>
            ))}
          </div>
        ) : (
          <Empty icon={ShieldCheck} title="No gap drivers identified" text="Revenue Orchestration hasn't found a real signal pointing to a revenue gap right now." />
        )}
      </Panel>

      <Panel title="Agent Activity & Pipeline Cadence" kicker="Only Lead Intelligence and Website Intelligence run on a recurring, independently-triggerable pipeline">
        <div className="lead-tabs" role="tablist">
          {(
            [
              ["all", `All Agents (${agentActivityCounts.all})`],
              ["complete", `Complete (${agentActivityCounts.complete})`],
              ["output_available", `Output Available (${agentActivityCounts.output_available})`],
              ["waiting", `Waiting (${agentActivityCounts.waiting})`],
            ] as const
          ).map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={agentActivityTab === key} onClick={() => setAgentActivityTab(key)}>
              {label}
            </button>
          ))}
        </div>
        <div className="agent-grid" style={{ marginTop: 14 }}>
          {visibleAgentActivity.map((row) => (
            <article key={row.id}>
              <span>
                <Bot size={22} />
              </span>
              <div>
                <small className="agent-category">{row.category}</small>
                <h3>{row.name}</h3>
                <p>{row.note}</p>
              </div>
              <footer>
                <em className={`status ${agentActivityTone(row.status)}`}>{agentActivityLabel(row.status)}</em>
                <span>{row.lastRunAt ? `Last run ${relativeDay(row.lastRunAt)}` : "Not yet run"}</span>
              </footer>
            </article>
          ))}
        </div>
      </Panel>

      <Panel title="Cross-Agent Insights" kicker="What multiple agents' real data reveals when combined">
        {overview.insights.length ? (
          <div className="website-recommendations">
            {overview.insights.map((insight) => (
              <div key={insight.key} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>
                    <Sparkles size={15} /> {insight.summary}
                  </b>
                  <em className="status neutral">{Math.round(insight.confidence * 100)}% confidence</em>
                </div>
                <p>
                  <b>Contributing agents:</b> {insight.contributingAgents.map((id) => agentDisplayName(workspace, id)).join(" + ")}
                </p>
                <p>
                  <b>Revenue relevance:</b> {insight.revenueRelevance}
                </p>
                <div className="page-title-action">
                  <button className="button secondary" onClick={() => setDrawer({ kind: "insight", insight })}>
                    View evidence
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty icon={Sparkles} title="No cross-agent insights yet" text="Nothing currently combines multiple agents' real data into a new observation." />
        )}
      </Panel>

      <Panel title="Priority Actions & Approval Queue" kicker={`${pendingRecommendations.length} awaiting review -- human-in-the-loop, nothing executes without your approval`}>
        <div className="ai-callout" style={{ marginBottom: 14 }}>
          <ShieldCheck size={14} /> These are AI-generated recommendations, not actions already taken. Review the evidence, then approve, request changes, or reject -- Clintra will not act until you decide.
        </div>
        {decisionError && (
          <div className="form-alert error">
            <X size={16} />
            {decisionError}
          </div>
        )}
        {pendingRecommendations.length ? (
          <div className="website-recommendations">
            {pendingRecommendations.map((recommendation, index) => (
              <div key={recommendation.id} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>
                    {index + 1}. {recommendation.title}
                  </b>
                  <em className={`status ${ORCH_SEVERITY_TONE[(recommendation.priority ?? "low") as OrchestrationSeverity]}`}>{ORCH_SEVERITY_LABEL[(recommendation.priority ?? "low") as OrchestrationSeverity]}</em>
                </div>
                <p>
                  <b>Problem:</b> {recommendation.problem}
                </p>
                <p>
                  <b>Recommendation:</b> {recommendation.recommendation ?? "Not specified."}
                </p>
                <p>
                  <b>Source agents:</b> {recommendation.sourceAgents.map((id) => agentDisplayName(workspace, id)).join(", ") || "Revenue Orchestration"}
                </p>
                <p>
                  <b>Estimated impact:</b>{" "}
                  {recommendation.estimatedImpact !== null ? `${money(recommendation.estimatedImpact, currency)}${recommendation.isEstimate ? " (AI estimate)" : ""}` : "Not quantifiable from available data"}
                  {" -- "}
                  <b>Confidence:</b> {recommendation.confidence !== null ? `${Math.round(recommendation.confidence * 100)}%` : "Not available"}
                  {" -- "}
                  <b>Created:</b> {relativeDay(recommendation.createdAt)}
                </p>
                {rejectPromptFor === recommendation.id ? (
                  <div className="lead-filter-bar" style={{ margin: 0 }}>
                    <select onChange={(event) => event.target.value && void decide(recommendation.id, "rejected", event.target.value)} defaultValue="">
                      <option value="" disabled>
                        Reason…
                      </option>
                      {REJECT_REASONS.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                    <button className="button secondary" onClick={() => setRejectPromptFor(null)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="page-title-action">
                    <button className="button secondary" onClick={() => setDrawer({ kind: "recommendation", recommendation })}>
                      Review evidence
                    </button>
                    <button className="button secondary" disabled={decidingId === recommendation.id} onClick={() => setRejectPromptFor(recommendation.id)}>
                      Reject
                    </button>
                    <button className="button secondary" disabled={decidingId === recommendation.id} onClick={() => void decide(recommendation.id, "rejected", "Changes requested")}>
                      Request Changes
                    </button>
                    <button className="button primary" disabled={decidingId === recommendation.id} onClick={() => void decide(recommendation.id, "approved")}>
                      {decidingId === recommendation.id ? <LoaderCircle className="spin" size={15} /> : "Approve"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <Empty icon={Check} title="You're all caught up" text="No priority actions right now -- run an orchestration cycle to generate recommendations from the current gap drivers." />
        )}
      </Panel>

      <div className="triad-grid">
        <PlanAlignmentPanel snapshot={snapshot} />

        <Panel title="Revenue Drift Alerts">
          {overview.driftAlerts.length ? (
            <div className="source-list">
              {overview.driftAlerts.map((alert) => (
                <div key={alert.key}>
                  <span>
                    <b>{alert.title}</b>
                    <small>{alert.description}</small>
                  </span>
                  <em className={`status ${ORCH_SEVERITY_TONE[alert.severity]}`}>{ORCH_SEVERITY_LABEL[alert.severity]}</em>
                </div>
              ))}
            </div>
          ) : overview.hasPreviousCycle && snapshot.forecast !== null ? (
            <Empty icon={ShieldCheck} title="No drift detected" text="Nothing has moved against plan since the last cycle." />
          ) : (
            <Empty
              icon={Radar}
              title="Drift analysis unavailable"
              text={!overview.hasPreviousCycle ? "Run a second orchestration cycle to start detecting drift against the first one." : "Drift detection needs a computable forecast, which isn't available yet."}
            />
          )}
        </Panel>

        <Panel title="Specialist Agent Tasks" kicker="Delegated by Revenue Orchestration -- derived from the gap drivers above, not a separate execution system">
          {overview.gapDrivers.length ? (
            <div className="source-list">
              {overview.gapDrivers.map((driver) => {
                const matching = overview.recommendations.find((item) => item.actionType === driver.key);
                const taskStatus = !matching ? "Delegated" : matching.status === "pending" ? "Recommendation Generated" : matching.status === "approved" ? "Approved" : matching.status === "rejected" ? "Rejected" : "Expired";
                return (
                  <div key={driver.key} className="clickable" onClick={() => setDrawer({ kind: "driver", driver })}>
                    <span>
                      <b>{driver.title}</b>
                      <small>{agentDisplayName(workspace, driver.responsibleAgent)}</small>
                    </span>
                    <em className={`status ${ORCH_SEVERITY_TONE[driver.severity]}`}>{taskStatus}</em>
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty icon={Bot} title="No open tasks assigned" text="No gap drivers are currently assigned to a specialist agent." />
          )}
        </Panel>
      </div>

      <Panel title="Revenue Action Plan" kicker="Approved actions your team carries out -- Clintra does not launch or execute these automatically">
        {approvedActions.length ? (
          <div className="source-list">
            {approvedActions.map((action, index) => (
              <div key={action.id}>
                <span>
                  <b>
                    Action {String(index + 1).padStart(2, "0")}: {action.title}
                  </b>
                  <small>{action.sourceAgents.map((id) => agentDisplayName(workspace, id)).join(", ")} -- coordinated for your team to execute outside Clintra</small>
                </span>
                <em className="status complete">Approved</em>
              </div>
            ))}
          </div>
        ) : (
          <Empty icon={ShieldCheck} title="No approved actions yet" text="Approved recommendations from the queue above will appear here as coordinated actions for your team to carry out." />
        )}
      </Panel>

      <Panel title="Scenario Simulation" kicker="AI estimate -- assumes all other factors stay constant">
        <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
          <Field label="Metric Dimension">
            <select value={scenarioLeverType} onChange={(event) => setScenarioLeverType(event.target.value as "pipeline" | "winRate")}>
              <option value="pipeline">Qualified pipeline</option>
              <option value="winRate">Win rate</option>
            </select>
          </Field>
          <Field label="Projected Growth / Lift (%)">
            <input type="number" value={scenarioPercent} onChange={(event) => setScenarioPercent(event.target.value)} placeholder="% change" />
          </Field>
          <button className="button primary" style={{ alignSelf: "end" }} disabled={scenarioBusy} onClick={() => void runWhatIf()}>
            {scenarioBusy ? <LoaderCircle className="spin" size={15} /> : "Run scenario"}
          </button>
        </div>
        {scenarioError && (
          <div className="form-alert error">
            <X size={16} />
            {scenarioError}
          </div>
        )}
        {scenarioResult && (
          <div className="ai-callout" style={{ marginTop: 12 }}>
            {scenarioResult.scenarioForecast !== null && scenarioResult.baselineForecast !== null ? (
              <p>
                Estimated forecast: {money(scenarioResult.scenarioForecast, currency)} (vs {money(scenarioResult.baselineForecast, currency)} today, {scenarioResult.delta !== null && scenarioResult.delta >= 0 ? "+" : ""}
                {scenarioResult.delta !== null ? money(scenarioResult.delta, currency) : ""}). {scenarioResult.assumptions.join(" ")}
              </p>
            ) : (
              <p>{scenarioResult.assumptions.join(" ")}</p>
            )}
          </div>
        )}
      </Panel>

      <Panel
        title="Orchestration Activity Feed"
        kicker="Live chronological telemetry of agent outputs, recurring syncs, and orchestration cycles"
        action={
          overview.activityLog.length ? (
            <div className="page-title-action">
              <select value={activityAgentFilter} onChange={(event) => setActivityAgentFilter(event.target.value)}>
                <option value="all">Filter logs: All agents</option>
                {activityAgents.map((agentId) => (
                  <option key={agentId} value={agentId}>
                    {agentDisplayName(workspace, agentId)}
                  </option>
                ))}
              </select>
              <button className="button secondary" onClick={downloadActivityLog}>
                <Download size={14} /> Download Raw Audit JSON
              </button>
            </div>
          ) : undefined
        }
      >
        {visibleActivityLog.length ? (
          <ul className="intel-log">
            {visibleActivityLog.map((entry, index) => (
              <li key={index}>
                <Check size={13} />
                <span>
                  {entry.message} -- {new Date(entry.timestamp).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        ) : overview.activityLog.length ? (
          <Empty icon={Activity} title="No activity for this agent" text="Try a different filter." />
        ) : (
          <Empty icon={Activity} title="No orchestration activity yet" text="Run an orchestration cycle to start building activity history." />
        )}
      </Panel>

      <Drawer open={drawer !== null} onClose={() => setDrawer(null)} title={drawer?.kind === "driver" ? "Gap Driver" : drawer?.kind === "insight" ? "Cross-Agent Insight" : "Recommendation"}>
        {drawer?.kind === "driver" && (
          <div className="page-stack">
            <h3>{drawer.driver.title}</h3>
            <p>{drawer.driver.description}</p>
            <p>
              <b>Responsible agent:</b> {agentDisplayName(workspace, drawer.driver.responsibleAgent)}
            </p>
            <p>
              <b>Severity:</b> <em className={`status ${ORCH_SEVERITY_TONE[drawer.driver.severity]}`}>{ORCH_SEVERITY_LABEL[drawer.driver.severity]}</em>
            </p>
            <p>
              <b>Confidence:</b> {Math.round(drawer.driver.confidence * 100)}%
            </p>
            {drawer.driver.estimatedImpact !== null && (
              <p>
                <b>Estimated impact:</b> {money(drawer.driver.estimatedImpact, currency)} {drawer.driver.isEstimate ? "(AI estimate)" : ""}
              </p>
            )}
            <p>
              <b>Evidence</b>
            </p>
            <ul className="score-why">
              {drawer.driver.evidence.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          </div>
        )}
        {drawer?.kind === "insight" && (
          <div className="page-stack">
            <p>{drawer.insight.summary}</p>
            <p>
              <b>Contributing agents:</b> {drawer.insight.contributingAgents.map((id) => agentDisplayName(workspace, id)).join(", ")}
            </p>
            <p>
              <b>Confidence:</b> {Math.round(drawer.insight.confidence * 100)}%
            </p>
            <p>
              <b>Revenue relevance</b>
            </p>
            <p>{drawer.insight.revenueRelevance}</p>
            <p>
              <b>Evidence</b>
            </p>
            <ul className="score-why">
              {drawer.insight.evidence.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          </div>
        )}
        {drawer?.kind === "recommendation" && (
          <div className="page-stack">
            <h3>{drawer.recommendation.title}</h3>
            <p>
              <b>Problem</b>
            </p>
            <p>{drawer.recommendation.problem}</p>
            <p>
              <b>Recommendation</b>
            </p>
            <p>{drawer.recommendation.recommendation ?? "Not specified."}</p>
            <p>
              <b>Impact</b>
            </p>
            <p>{drawer.recommendation.impact ?? "Not specified."}</p>
            {drawer.recommendation.estimatedImpact !== null && (
              <p>
                <b>Estimated impact:</b> {money(drawer.recommendation.estimatedImpact, currency)} {drawer.recommendation.isEstimate ? "(AI estimate)" : ""}
              </p>
            )}
            <p>
              <b>Confidence:</b> {drawer.recommendation.confidence !== null ? `${Math.round(drawer.recommendation.confidence * 100)}%` : "Not available"}
            </p>
            <p>
              <b>Source agents:</b> {drawer.recommendation.sourceAgents.map((id) => agentDisplayName(workspace, id)).join(", ") || "Revenue Orchestration"}
            </p>
            <p>
              <b>Evidence</b>
            </p>
            <ul className="score-why">
              {drawer.recommendation.evidence.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
            <p>
              <b>Approval status:</b> {drawer.recommendation.status === "pending" ? "Awaiting human approval" : drawer.recommendation.status}
              {drawer.recommendation.decidedBy ? ` by ${drawer.recommendation.decidedBy}` : ""}
            </p>
            {drawer.recommendation.status === "pending" && (
              <div className="page-title-action">
                <button className="button secondary" disabled={decidingId === drawer.recommendation.id} onClick={() => void decide(drawer.recommendation.id, "rejected")}>
                  Reject
                </button>
                <button className="button primary" disabled={decidingId === drawer.recommendation.id} onClick={() => void decide(drawer.recommendation.id, "approved")}>
                  {decidingId === drawer.recommendation.id ? <LoaderCircle className="spin" size={15} /> : "Approve"}
                </button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

// Marketing/Sales alignment has no real backing data anywhere in Clintra
// today (no marketing-vs-sales pipeline-acceptance tracking exists) -- shown
// honestly as unavailable rather than invented. Pipeline/Forecast alignment
// are both real derivations of the snapshot already computed above.
function PlanAlignmentPanel({ snapshot }: { snapshot: RevenueSnapshot }) {
  // Alignment is only ever computed when the underlying numbers exist --
  // missing coverage/forecast data resolves to "Insufficient Data", never to
  // a default "Aligned" or a fabricated "Drifting".
  const pipelineAligned = snapshot.coveragePct === null ? null : snapshot.coveragePct >= 80;
  const forecastAligned = snapshot.target === null || snapshot.forecast === null ? null : snapshot.health === "on_track" || snapshot.health === "watch";
  const alignmentBadge = (aligned: boolean | null) =>
    aligned === null ? <em className="status neutral">Insufficient Data</em> : <em className={`status ${aligned ? "complete" : "pending"}`}>{aligned ? "Aligned" : "Drifting"}</em>;
  return (
    <Panel title="Plan Alignment" kicker="Marketing & Sales Alignment -- only calculated when enough data exists">
      <div className="source-list">
        <div>
          <span>
            <b>Marketing</b>
            <small>No marketing-vs-sales pipeline-acceptance tracking is connected yet.</small>
          </span>
          <em className="status neutral">Insufficient Data</em>
        </div>
        <div>
          <span>
            <b>Sales</b>
            <small>Requires CRM stage-acceptance data Clintra doesn't yet capture.</small>
          </span>
          <em className="status neutral">Insufficient Data</em>
        </div>
        <div>
          <span>
            <b>Pipeline</b>
          </span>
          {alignmentBadge(pipelineAligned)}
        </div>
        <div>
          <span>
            <b>Forecast</b>
          </span>
          {alignmentBadge(forecastAligned)}
        </div>
        <div>
          <span>
            <b>Overall</b>
          </span>
          <em className={`status ${healthTone(snapshot.health)}`}>{healthLabel(snapshot.health)}</em>
        </div>
      </div>
    </Panel>
  );
}

// Business Discovery -- the first agent a tenant meets. Establishes what the
// company sells, who it sells to, its market/category, and a draft ICP
// across six sections (Company, Offering, Market, ICP, Positioning,
// Competitive). Every finding is observed (directly supported by source
// material), inferred (a reasonable conclusion from combining signals,
// above a minimum-evidence bar), or unknown -- never guessed. Nothing here
// answers marketing strategy, lead prioritisation, or website-optimisation
// questions; that's other agents' job. The draft only becomes shared
// context for other agents once a human reviews and approves it.

function factStatusTone(status: FactStatus): "complete" | "neutral" | "pending" {
  if (status === "observed") return "complete";
  if (status === "inferred") return "neutral";
  return "pending";
}
function factStatusLabel(status: FactStatus): string {
  if (status === "observed") return "Observed";
  if (status === "inferred") return "Inferred";
  return "Unknown";
}
function reviewStatusTone(status: ReviewStatus): "complete" | "neutral" | "pending" {
  if (status === "confirmed") return "complete";
  if (status === "ai_identified") return "neutral";
  return "pending";
}
function reviewStatusLabel(status: ReviewStatus): string {
  if (status === "confirmed") return "Confirmed";
  if (status === "ai_identified") return "AI Identified";
  return "Needs Confirmation";
}
function confidenceLabel(level: ConfidenceLevel | undefined): string | null {
  return level ? level.charAt(0).toUpperCase() + level.slice(1) : null;
}
function factValueText(value: FactValue | null): string {
  if (value === null) return "Not available";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "Not available";
  return String(value);
}
const GAP_SEVERITY_TONE: Record<GapSeverity, "error" | "pending" | "neutral"> = { high: "error", medium: "pending", low: "neutral" };
const GAP_SEVERITY_LABEL: Record<GapSeverity, string> = { high: "High", medium: "Medium", low: "Low" };

function findFact(sections: BusinessProfileSections, section: keyof BusinessProfileSections, field: string): BusinessFact | undefined {
  return sections[section].find((fact) => fact.field === field);
}
function editableFactText(sections: BusinessProfileSections | null, section: keyof BusinessProfileSections, field: string): string {
  const value = sections ? findFact(sections, section, field)?.value ?? null : null;
  if (Array.isArray(value)) return value.join(", ");
  return typeof value === "string" ? value : "";
}

// One row per finding, reused for every section panel -- label/value on the
// left, status + (when present) categorical confidence on the right.
function FactRow({ fact, onSelect }: { fact: BusinessFact; onSelect: (fact: BusinessFact) => void }) {
  const confidence = confidenceLabel(fact.confidence);
  return (
    <div className="clickable" onClick={() => onSelect(fact)}>
      <span>
        <b>{fact.label}</b>
        <small>{factValueText(fact.value)}</small>
      </span>
      <em className={`status ${factStatusTone(fact.status)}`}>{factStatusLabel(fact.status)}</em>
      {confidence && <em className="status neutral">{confidence}</em>}
    </div>
  );
}
// Compact label/value/tag row used only inside the Extracted Knowledge Base
// grid -- distinct from FactRow (which stacks label+value and is reused by
// ICP's declared/observed/behavioral lists) so this redesign never touches
// FactRow's other real call sites.
function KnowledgeFactRow({ fact, onSelect }: { fact: BusinessFact; onSelect: (fact: BusinessFact) => void }) {
  return (
    <button type="button" className="knowledge-row" onClick={() => onSelect(fact)}>
      <span>{fact.label}</span>
      <b>{factValueText(fact.value)}</b>
      <em className={`status ${factStatusTone(fact.status)}`}>{factStatusLabel(fact.status)}</em>
    </button>
  );
}
function FactSectionPanel({ title, kicker, facts, onSelect }: { title: string; kicker: string; facts: BusinessFact[]; onSelect: (fact: BusinessFact) => void }) {
  return (
    <Panel title={title} kicker={kicker}>
      <div>
        {facts.map((fact, index) => (
          <KnowledgeFactRow key={`${fact.field}:${index}`} fact={fact} onSelect={onSelect} />
        ))}
      </div>
    </Panel>
  );
}

// Fields whose value is a comma-separated list rather than a single scalar
// -- needed so the drawer's free-text correction box knows whether to split
// on commas when saving.
const LIST_FIELDS = new Set(["geographicMarkets", "targetMarket", "declaredIndustries"]);

function drawerEditableText(value: FactValue | null): string {
  if (value === null) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

function summarizeSection(facts: BusinessFact[], preferredFields: string[]): string {
  for (const field of preferredFields) {
    const fact = facts.find((item) => item.field === field && item.status !== "unknown");
    if (fact) return factValueText(fact.value);
  }
  return "Not yet determined";
}

// A single top-level read of "where is this profile right now" -- distinct
// from any individual fact's status. Running/Needs Attention take priority
// over the raw draft/approved state since they're the states that actually
// need the user's attention right now.
type DiscoveryStatus = "running" | "needs_attention" | "review_required" | "approved";
const DISCOVERY_STATUS_TONE: Record<DiscoveryStatus, "complete" | "pending" | "neutral" | "error"> = {
  running: "neutral",
  needs_attention: "error",
  review_required: "pending",
  approved: "complete",
};
const DISCOVERY_STATUS_LABEL: Record<DiscoveryStatus, string> = {
  running: "Running",
  needs_attention: "Needs Attention",
  review_required: "Review Required",
  approved: "Approved",
};
function computeDiscoveryStatus(record: BusinessProfileRecord | null, runBusy: boolean): DiscoveryStatus {
  if (runBusy) return "running";
  if (!record) return "review_required";
  const allFacts = Object.values(record.profile.sections).flat();
  const openItems = record.profile.gaps.some((gap) => gap.severity === "high") || allFacts.some((fact) => fact.reviewStatus === "needs_confirmation");
  if (record.status === "approved") return openItems ? "needs_attention" : "approved";
  return "review_required";
}

// The seven-step arc from the spec: sources -> facts -> profile -> ICP ->
// gaps -> human review -> approval. Every step's completion is read
// straight off data the profile already carries -- nothing new is tracked.
type StepState = "done" | "warning" | "current" | "pending";
const DISCOVERY_STEP_LABELS = ["Sources connected", "Business facts extracted", "Business profile drafted", "ICP drafted", "Gaps identified", "Human review", "Profile approved"];
function computeDiscoverySteps(record: BusinessProfileRecord | null): Array<{ label: string; state: StepState }> {
  if (!record) return DISCOVERY_STEP_LABELS.map((label, index) => ({ label, state: index === 0 ? "current" : "pending" }));
  const { sources, sections, gaps, completeness } = record.profile;
  const allFacts = Object.values(sections).flat();
  const sourcesConnected = sources.onboardingDataPresent || sources.websiteScanCompleted;
  const factsExtracted = completeness > 0;
  const icpDrafted = sections.icp.some((fact) => fact.status !== "unknown");
  const openItems = gaps.length > 0 || allFacts.some((fact) => fact.reviewStatus === "needs_confirmation");
  const approved = record.status === "approved";
  const states: StepState[] = [
    sourcesConnected ? "done" : "warning",
    factsExtracted ? "done" : "warning",
    "done",
    icpDrafted ? "done" : "warning",
    "done",
    approved ? "done" : openItems ? "current" : "done",
    approved ? "done" : "pending",
  ];
  return DISCOVERY_STEP_LABELS.map((label, index) => ({ label, state: states[index]! }));
}
function StepIcon({ state }: { state: StepState }) {
  if (state === "done") return <Check size={13} />;
  if (state === "warning") return <AlertTriangle size={13} />;
  if (state === "current") return <span className="run-dot queued" />;
  return <Circle size={13} />;
}
function DiscoveryStepper({ steps }: { steps: Array<{ label: string; state: StepState }> }) {
  return (
    <div className="discovery-stepper">
      {steps.map((step) => (
        <div key={step.label} className={`discovery-step ${step.state}`}>
          <StepIcon state={step.state} />
          <span>{step.label}</span>
        </div>
      ))}
    </div>
  );
}

// The six real fact sections, bucketed into the three higher-level
// categories shown in "Needs Your Input" -- a derived grouping label, not a
// field the backend tracks. Company facts fold into Market & Positioning
// since there's no dedicated "company" bucket in that grouping.
const OPEN_ITEM_CATEGORY: Record<keyof BusinessProfileSections, string> = {
  company: "Market & Positioning",
  offering: "Product & Services",
  market: "Market & Positioning",
  icp: "ICP & Persona",
  positioning: "Market & Positioning",
  competitive: "Market & Positioning",
};
function sectionKeyForField(sections: BusinessProfileSections, field: string): keyof BusinessProfileSections | null {
  for (const key of Object.keys(sections) as Array<keyof BusinessProfileSections>)
    if (sections[key].some((fact) => fact.field === field)) return key;
  return null;
}
function categoryForField(sections: BusinessProfileSections, field: string): string {
  const key = sectionKeyForField(sections, field);
  return key ? OPEN_ITEM_CATEGORY[key] : "Market & Positioning";
}

function ConfidenceGauge({ percent, tone }: { percent: number; tone: "success" | "warning" | "danger" }) {
  const radius = 33;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(100, percent)) / 100);
  return (
    <div className="telemetry-gauge">
      <svg viewBox="0 0 78 78">
        <circle cx="39" cy="39" r={radius} className="telemetry-gauge-track" />
        <circle cx="39" cy="39" r={radius} className={`telemetry-gauge-value ${tone}`} strokeDasharray={circumference} strokeDashoffset={offset} />
      </svg>
      <div className="telemetry-gauge-label">{percent}%</div>
    </div>
  );
}

function BusinessDiscoveryView({
  session,
  workspace,
  onNavigateToAgents,
  onNavigateToIntegrations,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
  onNavigateToIntegrations: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "business-discovery");
  const [record, setRecord] = useState<BusinessProfileRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [runBusy, setRunBusy] = useState(false);
  const [runMessage, setRunMessage] = useState("");
  const [drawer, setDrawer] = useState<BusinessFact | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [manualEntryOpen, setManualEntryOpen] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [businessModel, setBusinessModel] = useState("");
  const [valueProposition, setValueProposition] = useState("");
  const [targetMarkets, setTargetMarkets] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [needsInputTab, setNeedsInputTab] = useState("all");
  const [blockerDrafts, setBlockerDrafts] = useState<Record<string, string>>({});
  const [savingBlocker, setSavingBlocker] = useState<string | null>(null);

  const seedForm = (rec: BusinessProfileRecord | null) => {
    const sections = rec?.profile.sections ?? null;
    setCompanyName(editableFactText(sections, "company", "companyName"));
    setIndustry(editableFactText(sections, "company", "industry"));
    setBusinessModel(editableFactText(sections, "company", "businessModel"));
    setValueProposition(editableFactText(sections, "offering", "valueProposition"));
    setTargetMarkets(editableFactText(sections, "company", "geographicMarkets"));
  };

  const load = () => {
    setLoading(true);
    void loadBusinessDiscoveryProfile(session.session.token, workspace.tenantId)
      .then((result) => {
        setRecord(result.profile);
        seedForm(result.profile);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load the Business Profile."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);
  useEffect(() => {
    setEditValue(drawer ? drawerEditableText(drawer.value) : "");
    setManualEntryOpen(false);
  }, [drawer]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Business Discovery Agent</span>
          <h1>Business Discovery isn't active yet.</h1>
        </div>
        <Panel title="Activate Business Discovery">
          <Empty icon={Compass} title="Agent not active" text="Activate the Business Discovery agent from Agents & Entitlements to build the shared business context other agents rely on." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  const runDiscovery = async () => {
    setRunBusy(true);
    setRunMessage("");
    try {
      const result = await runBusinessDiscovery(session.session.token, workspace.tenantId);
      setRecord(result);
      seedForm(result);
      setRunMessage(`Draft generated -- revision ${result.revision}, ${Math.round(result.completeness * 100)}% complete.`);
    } catch (cause) {
      setRunMessage(cause instanceof Error ? cause.message : "Could not run Business Discovery.");
    } finally {
      setRunBusy(false);
    }
  };

  const splitList = (value: string) => value.split(",").map((entry) => entry.trim()).filter(Boolean);

  // Several sections carry the same underlying value under different field
  // names (e.g. declared industry also feeds Market's category comparison
  // and Positioning's category positioning) -- editing fans out to every
  // field name that mirrors it, so a correction doesn't leave stale copies
  // elsewhere until a full re-run.
  const saveChanges = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const geographicMarkets = splitList(targetMarkets);
      const edits: Record<string, FactValue> = {
        companyName,
        industry,
        declaredCategory: industry,
        declaredPositioning: industry,
        businessModel,
        valueProposition,
        primaryValueProposition: valueProposition,
        geographicMarkets,
        targetMarket: geographicMarkets,
        declaredIndustries: geographicMarkets,
      };
      const result = await saveBusinessProfile(session.session.token, workspace.tenantId, edits);
      setRecord(result);
      seedForm(result);
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "Could not save your changes.");
    } finally {
      setSaving(false);
    }
  };

  // Re-submits a finding's own value unchanged -- enough to flip its source
  // to human_edited, which the backend derives as reviewStatus "confirmed".
  const confirmFact = async (fact: BusinessFact) => {
    setConfirming(fact.field);
    setSaveError("");
    try {
      const result = await saveBusinessProfile(session.session.token, workspace.tenantId, { [fact.field]: fact.value ?? "" });
      setRecord(result);
      seedForm(result);
      setDrawer(null);
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "Could not confirm this finding.");
    } finally {
      setConfirming(null);
    }
  };

  // A universal "Edit" for any single finding, independent of the six
  // curated fields in the Human Review form below -- splits on commas only
  // for the fields that are genuinely list-shaped.
  const saveEdit = async (fact: BusinessFact) => {
    setEditingField(fact.field);
    setSaveError("");
    try {
      const value: FactValue = LIST_FIELDS.has(fact.field) ? splitList(editValue) : editValue;
      const result = await saveBusinessProfile(session.session.token, workspace.tenantId, { [fact.field]: value });
      setRecord(result);
      seedForm(result);
      setDrawer(null);
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "Could not save this correction.");
    } finally {
      setEditingField(null);
    }
  };

  // Clears a finding back to "unknown" rather than deleting it -- it
  // reappears in Needs Your Input as an open question instead of silently
  // disappearing.
  const rejectFinding = async (fact: BusinessFact) => {
    setRejecting(fact.field);
    setSaveError("");
    try {
      const result = await saveBusinessProfile(session.session.token, workspace.tenantId, {}, [fact.field]);
      setRecord(result);
      seedForm(result);
      setDrawer(null);
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "Could not reject this finding.");
    } finally {
      setRejecting(null);
    }
  };

  // Same real save path as the drawer's manual-entry flow (saveEdit), just
  // surfaced inline for high-severity gaps so the most urgent questions
  // don't need a drawer round-trip.
  const saveBlockerAnswer = async (gap: Gap, fact: BusinessFact) => {
    const raw = blockerDrafts[gap.key] ?? "";
    if (!raw.trim()) return;
    setSavingBlocker(gap.key);
    setSaveError("");
    try {
      const value: FactValue = LIST_FIELDS.has(fact.field) ? splitList(raw) : raw;
      const result = await saveBusinessProfile(session.session.token, workspace.tenantId, { [fact.field]: value });
      setRecord(result);
      seedForm(result);
      setBlockerDrafts((prev) => {
        const next = { ...prev };
        delete next[gap.key];
        return next;
      });
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "Could not save this answer.");
    } finally {
      setSavingBlocker(null);
    }
  };

  const approve = async () => {
    setApproving(true);
    setApproveError("");
    try {
      const result = await approveBusinessProfile(session.session.token, workspace.tenantId);
      setRecord(result);
    } catch (cause) {
      setApproveError(cause instanceof Error ? cause.message : "Could not approve the Business Profile.");
    } finally {
      setApproving(false);
    }
  };

  if (error)
    return (
      <Panel title="Business Discovery">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Business Discovery">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Business Profile…" />
      </Panel>
    );

  if (!record)
    return (
      <div className="page-stack">
        <div className="page-title">
          <div className="page-title-action" style={{ marginBottom: 4 }}>
            <span className="overline">Business Discovery Agent</span>
            <em className={`status ${DISCOVERY_STATUS_TONE[computeDiscoveryStatus(null, runBusy)]}`}>{DISCOVERY_STATUS_LABEL[computeDiscoveryStatus(null, runBusy)]}</em>
          </div>
          <h1>
            Business Discovery
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Establishes what your company sells, who it sells to, its market/category, and a draft ICP -- the shared context every other Clintra agent builds on.</span>
            </span>
          </h1>
        </div>
        <DiscoveryStepper steps={computeDiscoverySteps(null)} />
        <Panel title="Business Profile">
          <Empty icon={Compass} title="No Business Profile yet" text="Run Business Discovery to build a draft profile from your onboarding data, website scan, and any real customer history." />
          <div className="page-title-action">
            <button className="button primary" disabled={runBusy} onClick={() => void runDiscovery()}>
              {runBusy ? <LoaderCircle className="spin" size={15} /> : <Compass size={15} />} Run Business Discovery
            </button>
          </div>
        </Panel>
      </div>
    );

  const profile = record.profile;
  const isDraft = record.status === "draft";
  const icpDeclared = profile.sections.icp.filter((fact) => fact.field === "declaredIndustries");
  const icpObserved = profile.sections.icp.filter((fact) => fact.field.startsWith("observed"));
  const icpBehavioral = profile.sections.icp.filter((fact) => fact.field !== "declaredIndustries" && !fact.field.startsWith("observed"));

  const allFacts = Object.values(profile.sections).flat();
  const needsConfirmation = allFacts.filter((fact) => fact.reviewStatus === "needs_confirmation");
  const findFactByField = (field: string) => allFacts.find((fact) => fact.field === field);
  const discoveryStatus = computeDiscoveryStatus(record, runBusy);
  const factsIdentified = allFacts.filter((fact) => fact.status !== "unknown").length;
  const highConfidenceCount = allFacts.filter((fact) => fact.confidence === "high").length;
  const mediumConfidenceCount = allFacts.filter((fact) => fact.confidence === "medium").length;
  const openItemsCount = needsConfirmation.length + profile.gaps.length;
  const sectionsNeedingReview = (Object.keys(profile.sections) as Array<keyof BusinessProfileSections>).filter((key) =>
    profile.sections[key].some((fact) => fact.reviewStatus === "needs_confirmation" || fact.status === "unknown"),
  ).length;

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <div className="page-title-action" style={{ marginBottom: 4 }}>
            <span className="agent-pill">Business Discovery Agent</span>
            <em className={`status ${DISCOVERY_STATUS_TONE[discoveryStatus]}`}>{DISCOVERY_STATUS_LABEL[discoveryStatus]}</em>
          </div>
          <h1>
            Business Discovery
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Establishes what your company sells, who it sells to, its market/category, and a draft ICP -- the shared context every other Clintra agent builds on.</span>
            </span>
          </h1>
        </div>
        <button className="button primary" disabled={runBusy} onClick={() => void runDiscovery()}>
          {runBusy ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Refresh Discovery
        </button>
      </div>
      <section className="panel lifecycle-card">
        <span className="overline">Discovery lifecycle progress</span>
        <DiscoveryStepper steps={computeDiscoverySteps(record)} />
      </section>
      {runMessage && (
        <div className="form-alert info">
          <Info size={16} />
          {runMessage}
        </div>
      )}

      <Panel title="Overview" kicker="What Clintra understands so far">
        <div className="business-understanding-grid">
          <div>
            <small>Company</small>
            <b>{summarizeSection(profile.sections.company, ["companyName"])} — {summarizeSection(profile.sections.company, ["industry"])}</b>
          </div>
          <div>
            <small>Offering</small>
            <b>{summarizeSection(profile.sections.offering, ["coreOffering", "valueProposition"])}</b>
          </div>
          <div>
            <small>Market</small>
            <b>{summarizeSection(profile.sections.market, ["targetMarket", "declaredCategory", "inferredCategory"])}</b>
          </div>
          <div>
            <small>Positioning</small>
            <b>{summarizeSection(profile.sections.positioning, ["primaryValueProposition", "declaredPositioning"])}</b>
          </div>
        </div>
        <div className="metrics" style={{ marginTop: 18 }}>
          <Metric icon={FileText} label="Facts identified" value={String(factsIdentified)} meta={`Out of ${allFacts.length} tracked`} />
          <Metric icon={ShieldCheck} label="High confidence" value={String(highConfidenceCount)} meta="Findings you can trust as-is" />
          <Metric icon={Gauge} label="Medium confidence" value={String(mediumConfidenceCount)} meta="Findings worth a second look" />
          <Metric icon={AlertTriangle} label="Open questions" value={String(openItemsCount)} meta={`${sectionsNeedingReview} section${sectionsNeedingReview === 1 ? "" : "s"} need review`} {...(openItemsCount > 0 ? { tone: "warning" as const } : {})} />
        </div>
      </Panel>

      <Panel title="Needs Your Input" kicker={`${openItemsCount} item${openItemsCount === 1 ? "" : "s"} to review -- confirm, edit, or reject each one`}>
        {openItemsCount === 0 ? (
          <Empty icon={ShieldCheck} title="Nothing needs your input right now" text="Every finding is either confirmed, high-confidence, or genuinely unknown with no assumption to review." />
        ) : (
          <>
            {(() => {
              const highGaps = profile.gaps.filter((gap) => gap.severity === "high");
              const otherGaps = profile.gaps.filter((gap) => gap.severity !== "high");
              const categories = Array.from(
                new Set([
                  ...needsConfirmation.map((fact) => categoryForField(profile.sections, fact.field)),
                  ...profile.gaps.map((gap) => categoryForField(profile.sections, gap.relatedField)),
                ]),
              );
              const tabs: Array<{ key: string; label: string }> = [
                { key: "all", label: `All Items (${openItemsCount})` },
                ...(highGaps.length ? [{ key: "high", label: `High Priority (${highGaps.length})` }] : []),
                ...categories.map((category) => ({
                  key: category,
                  label: `${category} (${
                    needsConfirmation.filter((fact) => categoryForField(profile.sections, fact.field) === category).length +
                    profile.gaps.filter((gap) => categoryForField(profile.sections, gap.relatedField) === category).length
                  })`,
                })),
              ];
              const inCategory = (field: string) => needsInputTab === "all" || categoryForField(profile.sections, field) === needsInputTab;
              const visibleFacts = needsConfirmation.filter((fact) => needsInputTab !== "high" && inCategory(fact.field));
              const visibleHighGaps = highGaps.filter((gap) => needsInputTab === "all" || needsInputTab === "high" || categoryForField(profile.sections, gap.relatedField) === needsInputTab);
              const visibleOtherGaps = otherGaps.filter((gap) => needsInputTab !== "high" && inCategory(gap.relatedField));
              const groupedOtherGaps = new Map<string, Gap[]>();
              for (const gap of visibleOtherGaps) {
                const category = categoryForField(profile.sections, gap.relatedField);
                groupedOtherGaps.set(category, [...(groupedOtherGaps.get(category) ?? []), gap]);
              }
              return (
                <div className="page-stack">
                  {tabs.length > 1 && (
                    <div className="lead-tabs" role="tablist">
                      {tabs.map((tab) => (
                        <button key={tab.key} type="button" role="tab" aria-selected={needsInputTab === tab.key} onClick={() => setNeedsInputTab(tab.key)}>
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {visibleFacts.length > 0 && (
                    <div className="website-recommendations">
                      {visibleFacts.map((fact) => (
                        <div key={fact.field} className="website-recommendation-card">
                          <div className="page-title-action">
                            <b>{fact.label}</b>
                            <em className="status neutral">{confidenceLabel(fact.confidence)} confidence</em>
                          </div>
                          <p>
                            <b>AI assumption:</b> {factValueText(fact.value)}
                          </p>
                          <div className="page-title-action">
                            <button className="button secondary" disabled={confirming === fact.field} onClick={() => void confirmFact(fact)}>
                              {confirming === fact.field ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Confirm
                            </button>
                            <button className="button secondary" onClick={() => setDrawer(fact)}>
                              Edit
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {visibleHighGaps.length > 0 && (
                    <div>
                      <span className="overline">High priority blockers ({visibleHighGaps.length})</span>
                      <div className="metrics metrics-2" style={{ marginTop: 10 }}>
                        {visibleHighGaps.map((gap) => {
                          const relatedFact = findFactByField(gap.relatedField);
                          return (
                            <div key={gap.key} className="blocker-card">
                              <div className="page-title-action">
                                <b>{gap.question}</b>
                                <em className="status error">High</em>
                              </div>
                              {relatedFact ? (
                                <form
                                  className="inline-form"
                                  onSubmit={(event) => {
                                    event.preventDefault();
                                    void saveBlockerAnswer(gap, relatedFact);
                                  }}
                                >
                                  <input
                                    placeholder="Type your answer…"
                                    value={blockerDrafts[gap.key] ?? ""}
                                    onChange={(event) => setBlockerDrafts((prev) => ({ ...prev, [gap.key]: event.target.value }))}
                                  />
                                  <button className="button primary" type="submit" disabled={savingBlocker === gap.key || !(blockerDrafts[gap.key] ?? "").trim()}>
                                    {savingBlocker === gap.key ? <LoaderCircle className="spin" size={15} /> : "Save answer"}
                                  </button>
                                </form>
                              ) : (
                                <p>No linked finding to answer -- check the profile once Discovery is refreshed.</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {groupedOtherGaps.size > 0 && (
                    <div>
                      <span className="overline">Other gaps by category</span>
                      <div className="gap-category-grid" style={{ marginTop: 10 }}>
                        {Array.from(groupedOtherGaps.entries()).map(([category, gaps]) => (
                          <div key={category} className="gap-category-card">
                            <header>
                              <b>{category.toUpperCase()}</b>
                              <span className="muted-note">
                                {gaps.length} question{gaps.length === 1 ? "" : "s"}
                              </span>
                            </header>
                            {gaps.map((gap) => {
                              const relatedFact = findFactByField(gap.relatedField);
                              return (
                                <div key={gap.key} className="gap-category-row">
                                  <span>{gap.question}</span>
                                  {relatedFact && (
                                    <button type="button" onClick={() => setDrawer(relatedFact)}>
                                      Provide
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </Panel>

      <Panel
        title="Source Analysis"
        kicker="What real data this profile could draw on"
        action={
          <button type="button" className="button secondary" onClick={onNavigateToIntegrations}>
            <Plug size={14} /> Connect new data source
          </button>
        }
      >
        <div className="metrics">
          <Metric
            icon={FileText}
            label="Onboarding data"
            value={profile.sources.onboardingDataPresent ? "Present" : "Missing"}
            meta="Company details you provided when you connected Clintra"
          />
          <Metric
            icon={Globe2}
            label="Website scan"
            value={profile.sources.websiteScanCompleted ? "Completed" : "Not run"}
            meta={profile.sources.websiteScanAt ? `Last scanned ${new Date(profile.sources.websiteScanAt).toLocaleDateString()}` : "Run a website scan to infer category and positioning"}
          />
          <Metric
            icon={UsersRound}
            label="Observed customer data"
            value={`${profile.sources.observedAccountCount} won account${profile.sources.observedAccountCount === 1 ? "" : "s"}`}
            meta={profile.sources.observedAccountsSufficient ? "Enough real data to infer an observed ICP" : "Not yet enough won deals to validate the ICP against real customers"}
          />
          <Metric
            icon={Sparkles}
            label="AI content extraction"
            value={profile.sources.llmExtractionAvailable ? "Ran" : "Not available"}
            meta={profile.sources.llmExtractionAvailable ? "Offering, ICP, positioning, and competitive findings came from your website content" : "Requires a completed website scan and an AI-enabled plan"}
          />
        </div>
      </Panel>

      <span className="overline">Extracted knowledge base</span>
      <div className="knowledge-grid">
        <FactSectionPanel title="Company & Foundation" kicker="Who you are, as declared and (where evidenced) described" facts={profile.sections.company} onSelect={setDrawer} />
        <FactSectionPanel title="Offering & Solutions" kicker="What you sell -- products, services, and the problems they solve" facts={profile.sections.offering} onSelect={setDrawer} />
        <FactSectionPanel title="Target Market & Category" kicker="Target market, category, and market terminology" facts={profile.sections.market} onSelect={setDrawer} />
      </div>

      <Panel title="ICP" kicker="Declared and observed firmographics, plus buyer behavior inferred from your website">
        <div className="page-title-action" style={{ alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <span className="overline">Declared</span>
            <div className="source-list">
              {icpDeclared.map((fact) => (
                <FactRow key={fact.field} fact={fact} onSelect={setDrawer} />
              ))}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <span className="overline">Observed (from won accounts)</span>
            {!profile.sources.observedAccountsSufficient ? (
              <Empty icon={UsersRound} title="Not enough real data yet" text={icpObserved.find((fact) => fact.note)?.note ?? "Not enough won accounts yet to infer an observed ICP."} />
            ) : (
              <div className="source-list">
                {icpObserved.map((fact) => (
                  <FactRow key={fact.field} fact={fact} onSelect={setDrawer} />
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <span className="overline">Buyer &amp; buying behavior</span>
          <div className="source-list">
            {icpBehavioral.map((fact, index) => (
              <FactRow key={`${fact.field}:${index}`} fact={fact} onSelect={setDrawer} />
            ))}
          </div>
        </div>
      </Panel>

      <div className="knowledge-grid">
        <FactSectionPanel title="Positioning" kicker="Value proposition, category positioning, and differentiators" facts={profile.sections.positioning} onSelect={setDrawer} />
        <FactSectionPanel
          title="Positioning & Competitive Context"
          kicker="Only competitors and alternatives your own website content actually names -- not a full competitive intelligence system"
          facts={profile.sections.competitive}
          onSelect={setDrawer}
        />
      </div>

      <Panel title="Confidence & Completeness Telemetry" kicker="Two separate numbers -- never one flat score for the whole profile">
        <div className="metrics metrics-2">
          {(() => {
            const confidencePercent = profile.overallConfidence !== null ? Math.round(profile.overallConfidence * 100) : null;
            const confidenceTone = confidencePercent === null ? "danger" : confidencePercent >= 80 ? "success" : confidencePercent >= 50 ? "warning" : "danger";
            const confidenceLabelText = confidencePercent === null ? "Not available" : confidencePercent >= 80 ? "High Reliability" : confidencePercent >= 50 ? "Moderate Reliability" : "Low Reliability";
            return (
              <article className="telemetry-card">
                <div>
                  <span className="overline">Overall confidence</span>
                  <em className={`status ${DISCOVERY_STATUS_TONE[confidenceTone === "success" ? "approved" : confidenceTone === "warning" ? "review_required" : "needs_attention"]}`}>{confidenceLabelText}</em>
                  <strong>{confidencePercent === null ? "--" : `${confidencePercent}%`}</strong>
                  <p>Average confidence across everything that isn't unknown</p>
                </div>
                {confidencePercent !== null && <ConfidenceGauge percent={confidencePercent} tone={confidenceTone} />}
              </article>
            );
          })()}
          {(() => {
            const completenessPercent = Math.round(profile.completeness * 100);
            const completenessTone = completenessPercent >= 80 ? "success" : completenessPercent >= 40 ? "warning" : "danger";
            const completenessLabelText = completenessPercent >= 80 ? "Complete" : completenessPercent >= 40 ? "Review Pending" : "Early Draft";
            return (
              <article className="telemetry-card">
                <div>
                  <span className="overline">Completeness</span>
                  <em className={`status ${DISCOVERY_STATUS_TONE[completenessTone === "success" ? "approved" : completenessTone === "warning" ? "review_required" : "needs_attention"]}`}>{completenessLabelText}</em>
                  <strong>{completenessPercent}%</strong>
                  <p>Share of the profile that's observed or inferred, not unknown</p>
                </div>
                <ConfidenceGauge percent={completenessPercent} tone={completenessTone} />
              </article>
            );
          })()}
        </div>
      </Panel>

      <Panel
        title="Human Review & Calibration"
        kicker={isDraft ? "Draft -- review and correct before approving" : `Approved on ${record.approvedAt ? new Date(record.approvedAt).toLocaleDateString() : ""} by ${record.approvedBy ?? "unknown"}`}
      >
        <div className="source-list" style={{ marginBottom: 14 }}>
          <div>
            <span>
              <b>Business Profile v{record.revision}</b>
              <small>
                Created {new Date(record.createdAt).toLocaleString()} · Updated {new Date(record.updatedAt).toLocaleString()}
              </small>
            </span>
            <em className={`status ${isDraft ? "pending" : "complete"}`}>{isDraft ? "Draft" : "Approved"}</em>
          </div>
          {!isDraft && (
            <div>
              <span>
                <b>Reviewed by</b>
                <small>{record.approvedBy ?? "Unknown"}</small>
              </span>
              <em>{record.approvedAt ? new Date(record.approvedAt).toLocaleString() : "—"}</em>
            </div>
          )}
        </div>
        {!isDraft && (
          <p className="ai-callout" style={{ marginBottom: 14 }}>
            <ShieldCheck size={14} /> This profile is approved and is the shared context other agents can read. Start a new revision to make further changes.
          </p>
        )}
        {saveError && (
          <div className="form-alert error">
            <X size={16} />
            {saveError}
          </div>
        )}
        {approveError && (
          <div className="form-alert error">
            <X size={16} />
            {approveError}
          </div>
        )}
        <div className="lead-config-grid">
          <label>
            Company name
            <input value={companyName} onChange={(event) => setCompanyName(event.target.value)} disabled={!isDraft} />
          </label>
          <label>
            Industry / category
            <input value={industry} onChange={(event) => setIndustry(event.target.value)} disabled={!isDraft} />
          </label>
          <label>
            Business model
            <input value={businessModel} onChange={(event) => setBusinessModel(event.target.value)} disabled={!isDraft} />
          </label>
          <label>
            Value proposition
            <input value={valueProposition} onChange={(event) => setValueProposition(event.target.value)} disabled={!isDraft} />
          </label>
          <label>
            Geographic / target markets (comma separated)
            <input value={targetMarkets} onChange={(event) => setTargetMarkets(event.target.value)} disabled={!isDraft} />
          </label>
        </div>
        <div className="page-title-action">
          {isDraft ? (
            <>
              <button className="button secondary" disabled={saving} onClick={() => void saveChanges()}>
                {saving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save changes
              </button>
              <span style={{ flex: 1 }} />
              <button className="button secondary" disabled={runBusy} onClick={() => void runDiscovery()}>
                {runBusy ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Reject / Rerun Discovery
              </button>
              <button className="button primary" disabled={approving} onClick={() => void approve()}>
                {approving ? <LoaderCircle className="spin" size={15} /> : <ShieldCheck size={15} />} Approve Business Profile
              </button>
            </>
          ) : (
            <button className="button primary" disabled={runBusy} onClick={() => void runDiscovery()}>
              {runBusy ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Start new revision
            </button>
          )}
        </div>
      </Panel>

      <Drawer open={drawer !== null} onClose={() => setDrawer(null)} title={drawer?.label ?? "Finding"}>
        {drawer && (
          <div className="page-stack">
            <p>
              <b>Value:</b> {factValueText(drawer.value)}
            </p>
            <p>
              <b>Status:</b> <em className={`status ${factStatusTone(drawer.status)}`}>{factStatusLabel(drawer.status)}</em>
            </p>
            <p>
              <b>Source:</b> {drawer.source}
            </p>
            <p>
              <b>Evidence:</b> {drawer.evidence ?? "Not available."}
            </p>
            <p>
              <b>Confidence:</b> {confidenceLabel(drawer.confidence) ?? "Not available"}
            </p>
            {drawer.reviewStatus && (
              <p>
                <b>Review status:</b> <em className={`status ${reviewStatusTone(drawer.reviewStatus)}`}>{reviewStatusLabel(drawer.reviewStatus)}</em>
              </p>
            )}
            {drawer.note && (
              <p>
                <b>Note:</b> {drawer.note}
              </p>
            )}
            {isDraft &&
              (manualEntryOpen ? (
                <>
                  <label>
                    {drawer.status === "unknown" ? "Enter the correct value" : "Correct this finding"}
                    <input
                      value={editValue}
                      onChange={(event) => setEditValue(event.target.value)}
                      placeholder={LIST_FIELDS.has(drawer.field) ? "Comma separated" : "Type the value"}
                      autoFocus
                    />
                  </label>
                  <div className="page-title-action">
                    <button className="button secondary" onClick={() => setManualEntryOpen(false)}>
                      Cancel
                    </button>
                    <button className="button primary" disabled={editingField === drawer.field || !editValue.trim()} onClick={() => void saveEdit(drawer)}>
                      {editingField === drawer.field ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save correction
                    </button>
                  </div>
                </>
              ) : (
                <div className="page-title-action">
                  {drawer.reviewStatus === "needs_confirmation" && (
                    <button className="button secondary" disabled={confirming === drawer.field} onClick={() => void confirmFact(drawer)}>
                      {confirming === drawer.field ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Accept as-is
                    </button>
                  )}
                  {drawer.status !== "unknown" && (
                    <button className="button secondary" disabled={rejecting === drawer.field} onClick={() => void rejectFinding(drawer)}>
                      {rejecting === drawer.field ? <LoaderCircle className="spin" size={15} /> : <X size={15} />} Reject
                    </button>
                  )}
                  <button className="button primary" onClick={() => setManualEntryOpen(true)}>
                    <Plus size={15} /> Enter manually
                  </button>
                </div>
              ))}
          </div>
        )}
      </Drawer>
    </div>
  );
}

// Market Research -- re-frames Business Discovery's already-approved market/
// ICP/competitive facts as a market-research brief. No independent market
// sizing (TAM/SAM/SOM) or independent competitor discovery exists anywhere
// in Clintra, so `limitation` is a permanent, honest disclosure, never a
// temporary placeholder. Read-only: unlike Business Discovery's own view,
// there is nothing here to edit or approve, so fact rows are plain (not the
// click-to-open-a-drawer FactRow/FactSectionPanel pattern, which would
// misleadingly look editable).
function MarketResearchFactRow({ fact }: { fact: BusinessFact }) {
  const confidence = confidenceLabel(fact.confidence);
  return (
    <div>
      <span>
        <b>{fact.label}</b>
        <small>{factValueText(fact.value)}</small>
      </span>
      <em className={`status ${factStatusTone(fact.status)}`}>{factStatusLabel(fact.status)}</em>
      {confidence && <em className="status neutral">{confidence}</em>}
    </div>
  );
}
function MarketResearchFactPanel({ title, kicker, facts }: { title: string; kicker: string; facts: BusinessFact[] }) {
  return (
    <Panel title={title} kicker={kicker}>
      <div className="source-list">
        {facts.length === 0 ? <p className="ai-callout">Nothing on file yet.</p> : facts.map((fact, index) => <MarketResearchFactRow key={`${fact.field}:${index}`} fact={fact} />)}
      </div>
    </Panel>
  );
}

function MarketResearchView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "market-research");
  const [brief, setBrief] = useState<MarketResearchBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [narrating, setNarrating] = useState(false);
  const [narrateError, setNarrateError] = useState("");
  const [narrative, setNarrative] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    loadMarketResearchBrief(session.session.token, workspace.tenantId)
      .then((result) => {
        setBrief(result);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Market Research."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Market Research Agent</span>
          <h1>Market Research isn't active yet.</h1>
        </div>
        <Panel title="Activate Market Research">
          <Empty icon={Search} title="Agent not active" text="Activate the Market Research agent from Agents & Entitlements to frame your market, ICP, and competitive position from real, already-approved facts." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Market Research">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading || !brief)
    return (
      <Panel title="Market Research">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Market Research workspace…" />
      </Panel>
    );

  if (!brief.available)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Market Research Agent</span>
          <h1>Market Research</h1>
        </div>
        <Panel title="Business Discovery Required">
          <Empty icon={Compass} title="No approved profile yet" text={brief.reason ?? "Approve a Business Discovery profile first."} />
          <div className="page-title-action">
            <button
              className="button primary"
              onClick={() => {
                onNavigateToAgents();
              }}
            >
              Go to Business Discovery <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  const handleNarrate = async () => {
    setNarrating(true);
    setNarrateError("");
    try {
      const result = await narrateMarketResearchBrief(session.session.token, workspace.tenantId);
      if (!result.available) {
        setNarrateError("Narrative synthesis is unavailable right now -- check your AI credits and plan entitlement.");
      } else if (!result.narrative) {
        setNarrateError("The narrative could not be generated. Try again.");
      } else {
        setNarrative(result.narrative);
      }
    } catch (cause) {
      setNarrateError(cause instanceof Error ? cause.message : "The narrative could not be generated.");
    } finally {
      setNarrating(false);
    }
  };

  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">Market Research Agent</span>
        <h1>
          Market Research
          <span className="info-tooltip">
            <Info size={13} />
            <span className="info-tooltip-content">
              Frames your market, ICP, and competitive position from real, already-approved Business Discovery facts -- profile {brief.profileStatus}, last updated {brief.profileUpdatedAt ? new Date(brief.profileUpdatedAt).toLocaleString() : "unknown"}.
            </span>
          </span>
        </h1>
      </div>

      <MarketResearchFactPanel title="Market" kicker="Declared and website-inferred category and target markets" facts={brief.market} />
      <MarketResearchFactPanel title="Ideal Customer Profile" kicker="Declared targets vs. what real won accounts show" facts={brief.icp} />
      <MarketResearchFactPanel title="Competitive Landscape" kicker="Only names your own website content actually names -- never invented from general industry knowledge" facts={brief.competitive} />

      {brief.openQuestions.length > 0 && (
        <Panel title="Open Questions" kicker="Real gaps a human should resolve">
          <ul className="score-why">
            {brief.openQuestions.map((gap) => (
              <li key={gap.key}>
                <em className={`status ${GAP_SEVERITY_TONE[gap.severity]}`}>{GAP_SEVERITY_LABEL[gap.severity]}</em> {gap.question}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="Research Questions" kicker="General methodology -- not specific to your market">
        <ul className="score-why">
          {brief.researchQuestions.map((question, index) => (
            <li key={index}>{question}</li>
          ))}
        </ul>
        <p className="ai-callout">{brief.limitation}</p>
      </Panel>

      <Panel title="Narrative Synthesis" kicker="Optional -- synthesises only the real facts above, never invents market sizing or a new competitor">
        {narrateError && (
          <div className="form-alert error">
            <X size={16} />
            {narrateError}
          </div>
        )}
        {narrative ? (
          <p>{narrative}</p>
        ) : (
          <Empty icon={Sparkles} title="No narrative yet" text="Generate a short, strictly-grounded synthesis of the facts above." />
        )}
        <div className="page-title-action">
          <button className="button primary" disabled={narrating} onClick={() => void handleNarrate()}>
            {narrating ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Generate Narrative Synthesis
          </button>
        </div>
      </Panel>
    </div>
  );
}

// Social Presence -- the bare-minimum honest shell. The only real, queryable
// social-presence fact anywhere in Clintra today is whether the tenant has a
// live LinkedIn sign-in connection (LinkedIn's own integration only verifies
// sign-in, never reads posts/engagement, and no social-listening tool or
// owned-website social-link detector exists). Every declared measure this
// agent might eventually report (cadence, engagement, audience growth,
// traffic) is honestly disclosed as unmeasurable today rather than faked --
// this view is pure read-only status, with no generate/run/decide actions.
function socialPresenceCoverageTone(available: boolean): "complete" | "neutral" {
  return available ? "complete" : "neutral";
}
function socialPresenceSourceLabel(source: SocialPresenceSourceCoverageEntry["source"]): string {
  if (source === "linkedin_connection") return "LinkedIn Connection";
  if (source === "social_listening") return "Social Listening";
  return "Owned Website Social Links";
}

function SocialPresenceView({
  session,
  workspace,
  onNavigateToAgents,
  onNavigateToIntegrations,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
  onNavigateToIntegrations: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "social-presence");
  const [brief, setBrief] = useState<SocialPresenceBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!agent?.active) return;
    setLoading(true);
    loadSocialPresenceBrief(session.session.token, workspace.tenantId)
      .then((result) => {
        setBrief(result);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Social Presence."))
      .finally(() => setLoading(false));
  }, [agent?.active]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Social Presence Agent</span>
          <h1>Social Presence isn't active yet.</h1>
        </div>
        <Panel title="Activate Social Presence">
          <Empty icon={Activity} title="Agent not active" text="Activate the Social Presence agent from Agents & Entitlements to see your real LinkedIn connection status and social data coverage." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Social Presence">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading || !brief)
    return (
      <Panel title="Social Presence">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Social Presence workspace…" />
      </Panel>
    );

  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">Social Presence Agent</span>
        <h1>
          Social Presence
          <span className="info-tooltip">
            <Info size={13} />
            <span className="info-tooltip-content">Reports only what's real: your LinkedIn connection status and an honest disclosure of what social data Clintra cannot measure yet.</span>
          </span>
        </h1>
      </div>

      <Panel title="LinkedIn Connection" kicker={brief.linkedinConnected ? "Connected" : "Not connected"}>
        <div className="source-list">
          <div>
            <span>Status</span>
            <em className={`status ${brief.linkedinConnected ? "complete" : "neutral"}`}>{brief.linkedinConnected ? "Connected" : "Not connected"}</em>
          </div>
          <div>
            <span>Last synced</span>
            <em>{brief.linkedinLastSyncedAt ? new Date(brief.linkedinLastSyncedAt).toLocaleString() : "Never"}</em>
          </div>
        </div>
        {!brief.linkedinConnected && (
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToIntegrations}>
              Connect LinkedIn <ArrowRight size={15} />
            </button>
          </div>
        )}
      </Panel>

      <Panel title="Source Coverage" kicker="A fixed, honest list -- most sources will stay unavailable until a real integration exists">
        <div className="source-list">
          {brief.sourceCoverage.map((entry) => (
            <div key={entry.source}>
              <span>
                <b>{socialPresenceSourceLabel(entry.source)}</b>
                {entry.reason && <small>{entry.reason}</small>}
              </span>
              <em className={`status ${socialPresenceCoverageTone(entry.available)}`}>{entry.available ? "Available" : "Unavailable"}</em>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Unmeasurable Today" kicker="Never fabricated -- these are honestly disclosed, not estimated">
        <ul className="score-why">
          {brief.unmeasurable.map((measure, index) => (
            <li key={index}>{measure}</li>
          ))}
        </ul>
        <p className="ai-callout">{brief.limitation}</p>
        <p className="ai-callout">{brief.recommendation}</p>
      </Panel>
    </div>
  );
}

// Marketing Strategy -- synthesizes verified Business Discovery context,
// Website Intelligence findings, real business goals, and Revenue
// Orchestration signals into a prioritized marketing strategy draft. It
// never executes anything: every initiative requires an explicit human
// accept/reject, and the whole strategy requires explicit approval before
// an initiative can be sent toward Campaign Planning (which isn't a
// separate built feature yet -- see the disclosure shown on that action).

function confidenceTone(confidence: ConfidenceLevel | null): "complete" | "pending" | "neutral" {
  if (confidence === "high") return "complete";
  if (confidence === "medium") return "pending";
  return "neutral";
}
function initiativeStatusTone(status: InitiativeStatus): "complete" | "pending" | "error" {
  if (status === "accepted") return "complete";
  if (status === "rejected") return "error";
  return "pending";
}
function roadmapPhaseTone(phase: RoadmapPhase): "complete" | "pending" | "neutral" {
  if (phase === "now") return "complete";
  if (phase === "next") return "pending";
  return "neutral";
}
function roadmapPhaseLabel(phase: RoadmapPhase): string {
  if (phase === "now") return "Now";
  if (phase === "next") return "Next";
  return "Later";
}

function InitiativeCard({
  initiative,
  isEditing,
  editingFields,
  busy,
  decisionNote,
  sendResult,
  onStartEdit,
  onFieldChange,
  onSaveEdit,
  onCancelEdit,
  onDecide,
  onDecisionNoteChange,
  onSend,
  onNavigateToCampaignPlanning,
}: {
  initiative: InitiativeRecord;
  isEditing: boolean;
  editingFields: Record<string, string>;
  busy: boolean;
  decisionNote: string;
  sendResult: { campaignName: string; disclosure: string } | undefined;
  onStartEdit: () => void;
  onFieldChange: (key: string, value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDecide: (decision: "accepted" | "rejected") => void;
  onDecisionNoteChange: (value: string) => void;
  onSend: () => void;
  onNavigateToCampaignPlanning: () => void;
}) {
  return (
    <div className="website-recommendation-card">
      <div className="page-title-action">
        <b>
          #{initiative.rank} {initiative.name}
        </b>
        <div style={{ display: "flex", gap: 6 }}>
          <em className={`status ${roadmapPhaseTone(initiative.roadmapPhase)}`}>{roadmapPhaseLabel(initiative.roadmapPhase)}</em>
          <em className={`status ${initiativeStatusTone(initiative.status)}`}>{initiative.status === "pending" ? "Pending review" : initiative.status === "accepted" ? "Accepted" : "Rejected"}</em>
        </div>
      </div>
      {isEditing ? (
        <div className="page-stack">
          <label>
            Name
            <input value={editingFields.name ?? ""} onChange={(event) => onFieldChange("name", event.target.value)} />
          </label>
          <label>
            Objective
            <input value={editingFields.objective ?? ""} onChange={(event) => onFieldChange("objective", event.target.value)} />
          </label>
          <label>
            Audience
            <input value={editingFields.audience ?? ""} onChange={(event) => onFieldChange("audience", event.target.value)} />
          </label>
          <label>
            Why this matters
            <textarea rows={2} value={editingFields.rationale ?? ""} onChange={(event) => onFieldChange("rationale", event.target.value)} />
          </label>
          <label>
            Recommended next step
            <input value={editingFields.nextStep ?? ""} onChange={(event) => onFieldChange("nextStep", event.target.value)} />
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="button secondary" onClick={onCancelEdit}>
              Cancel
            </button>
            <button className="button primary" disabled={busy} onClick={onSaveEdit}>
              {busy ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <p>
            <b>Objective:</b> {initiative.objective}
          </p>
          <p>
            <b>Audience:</b> {initiative.audience}
          </p>
          <p>
            <b>Why this matters:</b> {initiative.rationale}
          </p>
          {initiative.evidence.length > 0 && (
            <div>
              <span className="overline">AI-cited evidence</span>
              <ul className="score-why">
                {initiative.evidence.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="source-list">
            <div>
              <span>
                <b>Expected impact</b>
              </span>
              <em>{initiative.impact}</em>
            </div>
            <div>
              <span>
                <b>Effort</b>
              </span>
              <em>{initiative.effort}</em>
            </div>
            <div>
              <span>
                <b>Confidence</b>
              </span>
              <em className={`status ${confidenceTone(initiative.confidence)}`}>{initiative.confidence}</em>
            </div>
          </div>
          {initiative.dependencies.length > 0 && (
            <p>
              <b>Dependencies:</b> {initiative.dependencies.join(", ")}
            </p>
          )}
          <p>
            <b>Recommended next step:</b> {initiative.nextStep}
          </p>
          {initiative.status === "rejected" && initiative.decisionNote && (
            <p className="ai-callout">Rejected: {initiative.decisionNote}</p>
          )}
          {initiative.status === "pending" && (
            <div className="page-stack">
              <input placeholder="Note (required to reject)" value={decisionNote} onChange={(event) => onDecisionNoteChange(event.target.value)} />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button className="button secondary" disabled={busy} onClick={onStartEdit}>
                  Edit
                </button>
                <button className="button secondary" disabled={busy} onClick={() => onDecide("rejected")}>
                  {busy ? <LoaderCircle className="spin" size={15} /> : <X size={15} />} Reject
                </button>
                <button className="button primary" disabled={busy} onClick={() => onDecide("accepted")}>
                  {busy ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Accept
                </button>
              </div>
            </div>
          )}
          {initiative.status === "accepted" && (
            <div className="page-stack">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button className="button secondary" disabled={busy} onClick={onStartEdit}>
                  Edit
                </button>
                <button className="button primary" disabled={busy || initiative.sentToCampaignPlanning} onClick={onSend}>
                  {busy ? <LoaderCircle className="spin" size={15} /> : <ArrowRight size={15} />} {initiative.sentToCampaignPlanning ? "Sent to Campaign Planning" : "Send to Campaign Planning"}
                </button>
              </div>
              {sendResult && (
                <div className="page-stack">
                  <p className="ai-callout">{sendResult.disclosure} Campaign: {sendResult.campaignName}.</p>
                  <div>
                    <button className="button secondary" onClick={onNavigateToCampaignPlanning}>
                      Open Campaign Planning <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MarketingStrategyView({
  session,
  workspace,
  onNavigateToAgents,
  onNavigateToContentGeneration,
  onNavigateToCampaignPlanning,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
  onNavigateToContentGeneration: () => void;
  onNavigateToCampaignPlanning: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "marketing-strategy");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [strategy, setStrategy] = useState<StrategyRecord | null>(null);
  const [initiatives, setInitiatives] = useState<InitiativeRecord[]>([]);
  const [sentElsewhere, setSentElsewhere] = useState<InitiativeRecord[]>([]);
  const [evidenceChecklist, setEvidenceChecklist] = useState<EvidenceChecklistItem[]>([]);

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");

  const [editingInitiativeId, setEditingInitiativeId] = useState<string | null>(null);
  const [editingFields, setEditingFields] = useState<Record<string, string>>({});
  const [busyInitiativeId, setBusyInitiativeId] = useState<string | null>(null);
  const [initiativeError, setInitiativeError] = useState("");
  const [decisionNoteByInitiative, setDecisionNoteByInitiative] = useState<Record<string, string>>({});
  const [sendResultByInitiative, setSendResultByInitiative] = useState<Record<string, { campaignName: string; disclosure: string }>>({});

  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState("");

  const load = () => {
    setLoading(true);
    loadMarketingStrategy(session.session.token, workspace.tenantId)
      .then((result) => {
        setStrategy(result.strategy);
        setInitiatives(result.initiatives);
        setSentElsewhere(result.sentElsewhere);
        setEvidenceChecklist(result.evidenceChecklist);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Marketing Strategy."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Marketing Strategy Agent</span>
          <h1>Marketing Strategy isn't active yet.</h1>
        </div>
        <Panel title="Activate Marketing Strategy">
          <Empty icon={Route} title="Agent not active" text="Activate the Marketing Strategy agent from Agents & Entitlements to turn verified business context and goals into a prioritized marketing strategy." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Marketing Strategy">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Marketing Strategy">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Marketing Strategy workspace…" />
      </Panel>
    );

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError("");
    try {
      const result = await generateMarketingStrategy(session.session.token, workspace.tenantId);
      setStrategy(result.strategy);
      setInitiatives(result.initiatives);
      setEvidenceChecklist(result.evidenceChecklist);
    } catch (cause) {
      setGenerateError(cause instanceof Error ? cause.message : "The marketing strategy could not be generated.");
    } finally {
      setGenerating(false);
    }
  };

  const startEdit = (initiative: InitiativeRecord) => {
    setEditingFields({ name: initiative.name, objective: initiative.objective, audience: initiative.audience, rationale: initiative.rationale, nextStep: initiative.nextStep });
    setEditingInitiativeId(initiative.id);
  };
  const saveEdit = async (initiativeId: string) => {
    setBusyInitiativeId(initiativeId);
    setInitiativeError("");
    try {
      const updated = await editStrategyInitiative(session.session.token, workspace.tenantId, initiativeId, editingFields);
      setInitiatives((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingInitiativeId(null);
    } catch (cause) {
      setInitiativeError(cause instanceof Error ? cause.message : "Could not save this initiative.");
    } finally {
      setBusyInitiativeId(null);
    }
  };
  const handleDecide = async (initiativeId: string, decision: "accepted" | "rejected") => {
    setBusyInitiativeId(initiativeId);
    setInitiativeError("");
    try {
      const updated = await decideStrategyInitiative(session.session.token, workspace.tenantId, initiativeId, decision, decisionNoteByInitiative[initiativeId]);
      setInitiatives((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (cause) {
      setInitiativeError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setBusyInitiativeId(null);
    }
  };
  const handleSend = async (initiativeId: string) => {
    setBusyInitiativeId(initiativeId);
    setInitiativeError("");
    try {
      const result = await sendInitiativeToCampaignPlanning(session.session.token, workspace.tenantId, initiativeId);
      setInitiatives((prev) => prev.map((item) => (item.id === result.initiative.id ? result.initiative : item)));
      setSendResultByInitiative((prev) => ({ ...prev, [initiativeId]: { campaignName: result.campaignName, disclosure: result.disclosure } }));
    } catch (cause) {
      setInitiativeError(cause instanceof Error ? cause.message : "Could not send this initiative to Campaign Planning.");
    } finally {
      setBusyInitiativeId(null);
    }
  };
  const handleApprove = async () => {
    setApproving(true);
    setApproveError("");
    try {
      const updated = await approveMarketingStrategy(session.session.token, workspace.tenantId);
      setStrategy(updated);
    } catch (cause) {
      setApproveError(cause instanceof Error ? cause.message : "Could not approve this strategy.");
    } finally {
      setApproving(false);
    }
  };

  const roadmap: Record<RoadmapPhase, InitiativeRecord[]> = {
    now: initiatives.filter((item) => item.roadmapPhase === "now"),
    next: initiatives.filter((item) => item.roadmapPhase === "next"),
    later: initiatives.filter((item) => item.roadmapPhase === "later"),
  };

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <div className="page-title-action" style={{ marginBottom: 4 }}>
            <span className="overline">Marketing Strategy Agent</span>
            <em className="status complete">Planning Agent · Online</em>
          </div>
          <h1>
            Marketing Strategy
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Turn verified business context and goals into a prioritized marketing strategy.</span>
            </span>
          </h1>
        </div>
      </div>

      {strategy && (
        <div className="metrics metrics-5">
          <Metric icon={Info} label="Strategy status" value={strategy.status === "approved" ? "Approved" : "Draft"} meta={`Revision ${strategy.revision}`} />
          <Metric icon={Target} label="Primary goal" value={strategy.goals.primaryGoal ?? "Not set"} meta="From onboarding" valueSize="sm" />
          <Metric icon={Route} label="Strategic focus" value={strategy.strategySummary.strategicDirection || "Not set"} meta="Strategic direction" valueSize="sm" />
          <Metric icon={Lightbulb} label="Priority initiatives" value={String(initiatives.length)} meta="In this revision" />
          <Metric icon={ShieldCheck} label="Confidence" value={strategy.overallConfidence ?? "Unknown"} meta="Majority across initiatives" />
        </div>
      )}

      {!strategy ? (
        <Panel title="Generate a Marketing Strategy">
          <Empty icon={Route} title="No marketing strategy yet" text="Generate a draft strategy from your verified Business Discovery context, Website Intelligence findings, and business goals." />
          {generateError && (
            <div className="form-alert error">
              <X size={16} />
              {generateError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={generating} onClick={() => void handleGenerate()}>
              {generating ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Generate Strategy
            </button>
          </div>
        </Panel>
      ) : (
        <>
          <Panel title="Strategic Summary" kicker="AI-generated draft · Human review required">
            <p>
              <b>Current situation:</b> {strategy.strategySummary.currentSituation}
            </p>
            <p>
              <b>Primary challenge:</b> {strategy.strategySummary.primaryChallenge}
            </p>
            <p>
              <b>Biggest opportunity:</b> {strategy.strategySummary.biggestOpportunity}
            </p>
            <p>
              <b>Strategic direction:</b> {strategy.strategySummary.strategicDirection}
            </p>
          </Panel>

          <Panel title="Business Goals">
            <div className="source-list">
              <div>
                <span>
                  <b>Primary goal</b>
                </span>
                <em>{strategy.goals.primaryGoal ?? "Not set"}</em>
              </div>
              <div>
                <span>
                  <b>Revenue target</b>
                </span>
                <em>{strategy.goals.revenueTarget ? `${strategy.goals.revenueTarget.currency} ${strategy.goals.revenueTarget.amount.toLocaleString("en-US")} (FY${strategy.goals.revenueTarget.fiscalYear})` : "Not set"}</em>
              </div>
            </div>
          </Panel>

          {strategy.priorityAudiences.length > 0 && (
            <Panel title="Priority Audiences">
              <div className="page-stack">
                {strategy.priorityAudiences.map((audience, index) => (
                  <div key={index} className="website-recommendation-card">
                    <div className="page-title-action">
                      <b>{audience.audience}</b>
                      <em className={`status ${confidenceTone(audience.confidence)}`}>{audience.confidence} confidence</em>
                    </div>
                    <p>
                      <b>Buyer role:</b> {audience.buyerRole}
                    </p>
                    <p>
                      <b>Need:</b> {audience.need}
                    </p>
                    <p>
                      <b>Relevant offering:</b> {audience.relevantOffering}
                    </p>
                    <p>
                      <b>Rationale:</b> {audience.rationale}
                    </p>
                    {audience.evidence.length > 0 && (
                      <ul className="score-why">
                        {audience.evidence.map((item, itemIndex) => (
                          <li key={itemIndex}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel title="Positioning & Messaging Direction">
            <p>
              <b>Core positioning:</b> {strategy.positioning.corePositioning}
            </p>
            {strategy.positioning.messageThemes.length > 0 && (
              <div>
                <span className="overline">Message themes</span>
                <ul className="score-why">
                  {strategy.positioning.messageThemes.map((theme, index) => (
                    <li key={index}>{theme}</li>
                  ))}
                </ul>
              </div>
            )}
            {strategy.positioning.differentiation.length > 0 && (
              <div>
                <span className="overline">Differentiation opportunities</span>
                <ul className="score-why">
                  {strategy.positioning.differentiation.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {strategy.positioning.messagingGaps.length > 0 && (
              <div>
                <span className="overline">Messaging gaps</span>
                <ul className="score-why">
                  {strategy.positioning.messagingGaps.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </Panel>

          {strategy.channelStrategy.length > 0 && (
            <Panel title="Channel Strategy">
              <div className="page-stack">
                {strategy.channelStrategy.map((channel, index) => (
                  <div key={index} className="source-list">
                    <div>
                      <span>
                        <b>{channel.channel}</b>
                      </span>
                      <em className={`status ${confidenceTone(channel.confidence)}`}>{channel.confidence}</em>
                    </div>
                    <div>
                      <span>Purpose</span>
                      <em>{channel.purpose}</em>
                    </div>
                    <div>
                      <span>Audience</span>
                      <em>{channel.audience}</em>
                    </div>
                    <div>
                      <span>Strategic role</span>
                      <em>{channel.strategicRole}</em>
                    </div>
                    <div>
                      <span>Rationale</span>
                      <em>{channel.rationale}</em>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel title="Prioritized Initiatives" kicker="Draft · Human review required">
            {initiativeError && (
              <div className="form-alert error">
                <X size={16} />
                {initiativeError}
              </div>
            )}
            <div className="page-stack">
              {initiatives.map((initiative) => (
                <InitiativeCard
                  key={initiative.id}
                  initiative={initiative}
                  isEditing={editingInitiativeId === initiative.id}
                  editingFields={editingFields}
                  busy={busyInitiativeId === initiative.id}
                  decisionNote={decisionNoteByInitiative[initiative.id] ?? ""}
                  sendResult={sendResultByInitiative[initiative.id]}
                  onStartEdit={() => startEdit(initiative)}
                  onFieldChange={(key, value) => setEditingFields((prev) => ({ ...prev, [key]: value }))}
                  onSaveEdit={() => void saveEdit(initiative.id)}
                  onCancelEdit={() => setEditingInitiativeId(null)}
                  onDecide={(decision) => void handleDecide(initiative.id, decision)}
                  onDecisionNoteChange={(value) => setDecisionNoteByInitiative((prev) => ({ ...prev, [initiative.id]: value }))}
                  onSend={() => void handleSend(initiative.id)}
                  onNavigateToCampaignPlanning={onNavigateToCampaignPlanning}
                />
              ))}
            </div>
          </Panel>

          <Panel title="Roadmap" kicker="Now · Next · Later, based on impact, effort, and confidence">
            <div className="lead-config-grid">
              {(["now", "next", "later"] as const).map((phase) => (
                <div key={phase}>
                  <span className="overline">{roadmapPhaseLabel(phase)}</span>
                  {roadmap[phase].length === 0 ? (
                    <p>Nothing here.</p>
                  ) : (
                    <ul className="score-why">
                      {roadmap[phase].map((initiative) => (
                        <li key={initiative.id}>{initiative.name}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          {sentElsewhere.length > 0 && (
            <Panel title="Sent to Campaign Planning" kicker="From earlier strategy revisions">
              <div className="source-list">
                {sentElsewhere.map((initiative) => (
                  <div key={initiative.id}>
                    <span>
                      <b>{initiative.name}</b>
                    </span>
                    <em>Sent</em>
                  </div>
                ))}
              </div>
              <div className="page-title-action">
                <button className="button secondary" onClick={onNavigateToContentGeneration}>
                  Open Content Generation <ArrowRight size={15} />
                </button>
              </div>
            </Panel>
          )}

          <Panel title="Evidence Panel" kicker="What was actually used to build this strategy">
            <ul className="intel-log">
              {evidenceChecklist.map((item) => (
                <li key={item.key}>
                  {item.done ? <Check size={13} /> : <X size={13} />}
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </Panel>

          {strategy.missingInformation.length > 0 && (
            <Panel title="Missing Information" kicker="Data that would improve confidence">
              <div className="source-list">
                {strategy.missingInformation.map((item, index) => (
                  <div key={index}>
                    <span>{item.text}</span>
                    <em className={item.source === "structural" ? "status neutral" : "status pending"}>{item.source === "structural" ? "In Clintra" : "AI-identified"}</em>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel title="Approval">
            {approveError && (
              <div className="form-alert error">
                <X size={16} />
                {approveError}
              </div>
            )}
            <div className="page-title-action">
              <em className={`status ${strategy.status === "approved" ? "complete" : "neutral"}`}>{strategy.status === "approved" ? "Strategy Approved" : "Draft"}</em>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="button secondary" disabled={generating} onClick={() => void handleGenerate()}>
                  {generating ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Regenerate
                </button>
                {strategy.status === "draft" && (
                  <button className="button primary" disabled={approving} onClick={() => void handleApprove()}>
                    {approving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve Strategy
                  </button>
                )}
              </div>
            </div>
            {strategy.status === "approved" && <p className="ai-callout">Approved -- accepted initiatives can now be sent to Campaign Planning above.</p>}
          </Panel>
        </>
      )}
    </div>
  );
}

// Campaign Planning -- turns an approved Marketing Strategy initiative (or
// an independent, user-created campaign idea) into a structured, multi-
// channel campaign blueprint: channels, phases, timeline, messaging
// pillars, and asset requirements for Content Generation to draft against.
// It never publishes, schedules, or launches anything -- every plan
// requires human approval before it can be sent to Content Generation.

function campaignPlanStatusTone(status: CampaignPlanStatus): "complete" | "pending" | "neutral" {
  if (status === "approved" || status === "sent_to_content_generation") return "complete";
  if (status === "needs_review") return "pending";
  return "neutral";
}
function campaignPlanStatusLabel(status: CampaignPlanStatus): string {
  if (status === "needs_review") return "Needs Review";
  if (status === "approved") return "Approved";
  if (status === "sent_to_content_generation") return "Sent to Content Generation";
  return "Draft";
}
function assetStatusTone(status: CampaignAssetStatus): "complete" | "pending" | "neutral" {
  if (status === "approved" || status === "drafted") return "complete";
  if (status === "ready_for_content_generation" || status === "drafting") return "pending";
  return "neutral";
}
function assetStatusLabel(status: CampaignAssetStatus): string {
  if (status === "ready_for_content_generation") return "Ready for Content Generation";
  if (status === "drafting") return "Drafting";
  if (status === "drafted") return "Drafted";
  if (status === "approved") return "Approved";
  return "Required";
}
// Mirrors services/campaign-planning-service/src/channelSupport.ts's
// isContentGenerationSupported -- an honest, informational check for the UI
// only (the backend independently enforces the same rule server-side).
function isContentGenerationSupportedChannel(channel: string): boolean {
  if (/email/i.test(channel)) return true;
  if (/landing/i.test(channel)) return true;
  if (/outreach/i.test(channel)) return true;
  if (/paid|ad(s|vert)?/i.test(channel)) return true;
  if (/organic|social/i.test(channel)) return false;
  if (/website/i.test(channel)) return false;
  if (/event/i.test(channel)) return false;
  return false;
}

function CampaignPlanningView({
  session,
  workspace,
  onNavigateToAgents,
  onNavigateToContentGeneration,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
  onNavigateToContentGeneration: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "campaign-planning");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [campaigns, setCampaigns] = useState<CampaignPlanSummary[]>([]);
  const [acceptedInitiatives, setAcceptedInitiatives] = useState<AcceptedInitiativeSummary[]>([]);

  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CampaignPlanDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [recommendations, setRecommendations] = useState<CampaignRecommendation[]>([]);

  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [newCampaignObjective, setNewCampaignObjective] = useState("");
  const [newCampaignSegment, setNewCampaignSegment] = useState("");
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [startingInitiativeId, setStartingInitiativeId] = useState<string | null>(null);
  const [startError, setStartError] = useState("");

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [regeneratingSection, setRegeneratingSection] = useState<CampaignRegenerateSection | null>(null);
  const [sectionError, setSectionError] = useState("");

  const [editingOverview, setEditingOverview] = useState(false);
  const [editName, setEditName] = useState("");
  const [editObjective, setEditObjective] = useState("");
  const [editConcept, setEditConcept] = useState("");
  const [savingOverview, setSavingOverview] = useState(false);

  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [assetBusyId, setAssetBusyId] = useState<string | null>(null);
  const [assetError, setAssetError] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([loadCampaignPlans(session.session.token, workspace.tenantId), loadAcceptedInitiatives(session.session.token, workspace.tenantId)])
      .then(([campaignsResult, initiativesResult]) => {
        setCampaigns(campaignsResult.data);
        setAcceptedInitiatives(initiativesResult.data);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Campaign Planning."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  useEffect(() => {
    if (!selectedCampaignId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setDetailError("");
    getCampaignPlan(session.session.token, workspace.tenantId, selectedCampaignId)
      .then((result) => setDetail(result))
      .catch((cause) => setDetailError(cause instanceof Error ? cause.message : "Could not load this campaign plan."))
      .finally(() => setDetailLoading(false));
  }, [selectedCampaignId]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Campaign Planning Agent</span>
          <h1>Campaign Planning isn't active yet.</h1>
        </div>
        <Panel title="Activate Campaign Planning">
          <Empty icon={ClipboardList} title="Agent not active" text="Activate the Campaign Planning agent from Agents & Entitlements to turn approved initiatives into structured, multi-channel campaign plans." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Campaign Planning">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Campaign Planning">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Campaign Planning workspace…" />
      </Panel>
    );

  const handleCreateIndependentCampaign = async () => {
    setCreatingCampaign(true);
    setStartError("");
    try {
      const result = await createCampaignPlan(session.session.token, workspace.tenantId, {
        name: newCampaignName,
        ...(newCampaignObjective.trim() ? { objective: newCampaignObjective.trim() } : {}),
        ...(newCampaignSegment.trim() ? { targetSegment: newCampaignSegment.trim() } : {}),
        pillars: [],
      });
      setCampaigns((prev) => [{ id: result.campaign.id, name: result.campaign.name, objective: result.campaign.objective, targetSegment: result.campaign.targetSegment, planStatus: result.campaign.planStatus, strategicInitiativeId: null, createdAt: result.campaign.createdAt }, ...prev]);
      setDetail(result);
      setSelectedCampaignId(result.campaign.id);
      setShowNewCampaign(false);
      setNewCampaignName("");
      setNewCampaignObjective("");
      setNewCampaignSegment("");
    } catch (cause) {
      setStartError(cause instanceof Error ? cause.message : "Could not create this campaign.");
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleStartFromInitiative = async (initiativeId: string) => {
    setStartingInitiativeId(initiativeId);
    setStartError("");
    try {
      const result = await startCampaignFromInitiative(session.session.token, workspace.tenantId, initiativeId);
      setDetail(result);
      setSelectedCampaignId(result.campaign.id);
      setCampaigns((prev) => (prev.some((item) => item.id === result.campaign.id) ? prev : [{ id: result.campaign.id, name: result.campaign.name, objective: result.campaign.objective, targetSegment: result.campaign.targetSegment, planStatus: result.campaign.planStatus, strategicInitiativeId: result.campaign.strategicInitiativeId, createdAt: result.campaign.createdAt }, ...prev]));
    } catch (cause) {
      setStartError(cause instanceof Error ? cause.message : "Could not start a campaign from this initiative.");
    } finally {
      setStartingInitiativeId(null);
    }
  };

  const handleGenerate = async () => {
    if (!selectedCampaignId) return;
    setGenerating(true);
    setGenerateError("");
    try {
      const result = await generateCampaignPlan(session.session.token, workspace.tenantId, selectedCampaignId);
      setDetail(result);
      setRecommendations(result.recommendations);
    } catch (cause) {
      setGenerateError(cause instanceof Error ? cause.message : "The campaign plan could not be generated.");
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateSection = async (section: CampaignRegenerateSection) => {
    if (!selectedCampaignId) return;
    setRegeneratingSection(section);
    setSectionError("");
    try {
      const result = await regenerateCampaignSection(session.session.token, workspace.tenantId, selectedCampaignId, section);
      setDetail(result);
    } catch (cause) {
      setSectionError(cause instanceof Error ? cause.message : "This section could not be regenerated.");
    } finally {
      setRegeneratingSection(null);
    }
  };

  const startEditOverview = () => {
    if (!detail) return;
    setEditName(detail.campaign.name);
    setEditObjective(detail.campaign.objective ?? "");
    setEditConcept(detail.campaign.concept ?? "");
    setEditingOverview(true);
  };
  const handleSaveOverview = async () => {
    if (!selectedCampaignId) return;
    setSavingOverview(true);
    try {
      const updated = await editCampaignPlan(session.session.token, workspace.tenantId, selectedCampaignId, { name: editName, objective: editObjective, concept: editConcept });
      setDetail((prev) => (prev ? { ...prev, campaign: updated } : prev));
      setEditingOverview(false);
    } catch (cause) {
      setSectionError(cause instanceof Error ? cause.message : "Could not save these changes.");
    } finally {
      setSavingOverview(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedCampaignId) return;
    setApproving(true);
    setApproveError("");
    try {
      const updated = await approveCampaignPlan(session.session.token, workspace.tenantId, selectedCampaignId);
      setDetail((prev) => (prev ? { ...prev, campaign: updated } : prev));
    } catch (cause) {
      setApproveError(cause instanceof Error ? cause.message : "Could not approve this campaign plan.");
    } finally {
      setApproving(false);
    }
  };

  const handleSendToContentGeneration = async () => {
    if (!selectedCampaignId) return;
    setSending(true);
    setSendError("");
    try {
      const result = await sendCampaignToContentGeneration(session.session.token, workspace.tenantId, selectedCampaignId);
      setDetail(result);
    } catch (cause) {
      setSendError(cause instanceof Error ? cause.message : "Could not send this campaign to Content Generation.");
    } finally {
      setSending(false);
    }
  };

  const handleUpdateAssetStatus = async (requirementId: string, status: CampaignAssetStatus) => {
    setAssetBusyId(requirementId);
    setAssetError("");
    try {
      const updated = await updateAssetRequirementStatus(session.session.token, workspace.tenantId, requirementId, status);
      setDetail((prev) => (prev ? { ...prev, assets: prev.assets.map((asset) => (asset.id === updated.id ? updated : asset)) } : prev));
    } catch (cause) {
      setAssetError(cause instanceof Error ? cause.message : "Could not update this asset's status.");
    } finally {
      setAssetBusyId(null);
    }
  };

  if (!selectedCampaignId) {
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Campaign Planning Agent</span>
          <h1>
            Campaign Planning
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Turn an approved marketing initiative into a structured, multi-channel campaign blueprint.</span>
            </span>
          </h1>
        </div>

        {startError && (
          <div className="form-alert error">
            <X size={16} />
            {startError}
          </div>
        )}

        <Panel title="Select a Marketing Initiative">
          {acceptedInitiatives.length === 0 ? (
            <Empty icon={Route} title="No accepted initiatives yet" text="Campaign Planning works best from an approved Marketing Strategy initiative. Accept an initiative there to begin." />
          ) : (
            <div className="page-stack">
              {acceptedInitiatives.map((initiative) => (
                <div key={initiative.id} className="source-list">
                  <div>
                    <span>
                      <b>{initiative.name}</b>
                    </span>
                    <em>{initiative.objective}</em>
                  </div>
                  <button className="button primary" disabled={startingInitiativeId === initiative.id} onClick={() => void handleStartFromInitiative(initiative.id)}>
                    {startingInitiativeId === initiative.id ? <LoaderCircle className="spin" size={15} /> : <ArrowRight size={15} />} {initiative.campaignId ? "Open Campaign" : "Start Campaign"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Existing Campaigns"
          action={
            <button className="button secondary" onClick={() => setShowNewCampaign((value) => !value)} type="button">
              <Plus size={15} /> Independent Campaign
            </button>
          }
        >
          {showNewCampaign && (
            <div className="page-stack" style={{ marginBottom: 14 }}>
              <p className="ai-callout">User-created campaign -- not linked to an approved Marketing Strategy initiative.</p>
              <div className="lead-config-grid">
                <label>
                  Campaign name
                  <input value={newCampaignName} onChange={(event) => setNewCampaignName(event.target.value)} />
                </label>
                <label>
                  Objective
                  <input value={newCampaignObjective} onChange={(event) => setNewCampaignObjective(event.target.value)} />
                </label>
                <label>
                  Target segment
                  <input value={newCampaignSegment} onChange={(event) => setNewCampaignSegment(event.target.value)} />
                </label>
              </div>
              <div className="page-title-action">
                <button className="button primary" disabled={creatingCampaign || !newCampaignName.trim()} onClick={() => void handleCreateIndependentCampaign()}>
                  {creatingCampaign ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Create Campaign
                </button>
              </div>
            </div>
          )}
          {campaigns.length === 0 ? (
            <Empty icon={ClipboardList} title="No campaigns yet" text="Start from an accepted initiative above, or create an independent campaign." />
          ) : (
            <div className="page-stack">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="source-list">
                  <div>
                    <span>
                      <b>{campaign.name}</b>
                    </span>
                    <em>{campaign.objective ?? "No objective set"}</em>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <em className={`status ${campaignPlanStatusTone(campaign.planStatus)}`}>{campaignPlanStatusLabel(campaign.planStatus)}</em>
                    <button className="button secondary" onClick={() => setSelectedCampaignId(campaign.id)}>
                      Open
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    );
  }

  if (detailError)
    return (
      <Panel title="Campaign Planning">
        <div className="form-alert error">
          <X size={16} />
          {detailError}
        </div>
        <button className="button secondary" onClick={() => setSelectedCampaignId(null)}>
          <ArrowLeft size={15} /> Back to campaigns
        </button>
      </Panel>
    );
  if (detailLoading || !detail)
    return (
      <Panel title="Campaign Planning">
        <Empty icon={LoaderCircle} title="Loading" text="Loading this campaign plan…" />
      </Panel>
    );

  const { campaign, pillars, assets } = detail;
  const hasPlan = campaign.channels.length > 0 || campaign.phases.length > 0;
  const assetsByChannel = new Map<string, AssetRequirementRecord[]>();
  for (const asset of assets) {
    const bucket = assetsByChannel.get(asset.channel) ?? [];
    bucket.push(asset);
    assetsByChannel.set(asset.channel, bucket);
  }
  const regenerateButton = (section: CampaignRegenerateSection, label = "Regenerate") => (
    <button className="button secondary" disabled={regeneratingSection !== null} onClick={() => void handleRegenerateSection(section)}>
      {regeneratingSection === section ? <LoaderCircle className="spin" size={14} /> : <RefreshCw size={14} />} {label}
    </button>
  );

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <button className="button secondary" onClick={() => setSelectedCampaignId(null)} style={{ marginBottom: 10 }}>
            <ArrowLeft size={15} /> Back to campaigns
          </button>
          <div className="page-title-action" style={{ marginBottom: 4 }}>
            <span className="overline">Campaign Planning Agent</span>
            <em className={`status ${campaignPlanStatusTone(campaign.planStatus)}`}>{campaignPlanStatusLabel(campaign.planStatus)}</em>
          </div>
          <h1>{campaign.name}</h1>
        </div>
      </div>

      <Panel
        title="Campaign Overview"
        action={
          <button className="button secondary" onClick={startEditOverview} type="button">
            Edit
          </button>
        }
      >
        {editingOverview ? (
          <div className="page-stack">
            <label>
              Campaign name
              <input value={editName} onChange={(event) => setEditName(event.target.value)} />
            </label>
            <label>
              Objective
              <input value={editObjective} onChange={(event) => setEditObjective(event.target.value)} />
            </label>
            <label>
              Campaign concept
              <textarea rows={2} value={editConcept} onChange={(event) => setEditConcept(event.target.value)} />
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="button secondary" onClick={() => setEditingOverview(false)}>
                Cancel
              </button>
              <button className="button primary" disabled={savingOverview} onClick={() => void handleSaveOverview()}>
                {savingOverview ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save
              </button>
            </div>
          </div>
        ) : (
          <div className="source-list">
            <div>
              <span>
                <b>Objective</b>
              </span>
              <em>{campaign.objective ?? "Not set"}</em>
            </div>
            <div>
              <span>
                <b>Audience</b>
              </span>
              <em>{campaign.audience.primary || "Not set"}</em>
            </div>
            <div>
              <span>
                <b>Strategic initiative</b>
              </span>
              <em>{campaign.strategicInitiativeId ? "Linked from Marketing Strategy" : "User-created campaign"}</em>
            </div>
            <div>
              <span>
                <b>Timeframe</b>
              </span>
              <em>{campaign.startsAt ? new Date(campaign.startsAt).toLocaleDateString() : "Not set"}</em>
            </div>
          </div>
        )}
      </Panel>

      {!hasPlan ? (
        <Panel title="Generate a Campaign Plan">
          <Empty icon={ClipboardList} title="No campaign plan yet" text="Generate a channel plan, timeline, messaging pillars, and asset requirements from this campaign's objective and audience." />
          {generateError && (
            <div className="form-alert error">
              <X size={16} />
              {generateError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={generating} onClick={() => void handleGenerate()}>
              {generating ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Generate Campaign Plan
            </button>
          </div>
        </Panel>
      ) : (
        <>
          {sectionError && (
            <div className="form-alert error">
              <X size={16} />
              {sectionError}
            </div>
          )}

          <Panel title="Campaign Summary" action={regenerateButton("concept", "Regenerate Concept")}>
            <p>{campaign.concept ?? "Not generated yet."}</p>
          </Panel>

          <Panel title="Channel Plan" action={regenerateButton("channels")}>
            <div className="page-stack">
              {campaign.channels.map((channel, index) => (
                <div key={index} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{channel.channel}</b>
                    <em>{channel.phase}</em>
                  </div>
                  <p>
                    <b>Role:</b> {channel.role}
                  </p>
                  <p>
                    <b>Purpose:</b> {channel.purpose}
                  </p>
                  <p>
                    <b>Message:</b> {channel.message}
                  </p>
                  <p>
                    <b>CTA:</b> {channel.cta}
                  </p>
                  {channel.requiredAssets.length > 0 && (
                    <p>
                      <b>Assets:</b> {channel.requiredAssets.join(", ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Campaign Timeline" kicker="Recommendation only -- not based on performance data" action={regenerateButton("phases_timeline")}>
            <div className="lead-config-grid">
              {campaign.phases.map((phase, index) => (
                <div key={index}>
                  <span className="overline">{phase.name}</span>
                  <p>{phase.description}</p>
                  <ul className="score-why">
                    {campaign.timeline
                      .filter((activity) => activity.phase === phase.name)
                      .map((activity, activityIndex) => (
                        <li key={activityIndex}>
                          Day {activity.dayOffset}: {activity.label}
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Messaging Pillars" action={regenerateButton("messaging_pillars")}>
            <div className="page-stack">
              {pillars.map((pillar) => (
                <div key={pillar.id} className="website-recommendation-card">
                  <b>{pillar.pillar}</b>
                  {pillar.coreMessage && (
                    <p>
                      <b>Core message:</b> {pillar.coreMessage}
                    </p>
                  )}
                  {pillar.audienceProblem && (
                    <p>
                      <b>Audience problem:</b> {pillar.audienceProblem}
                    </p>
                  )}
                  {pillar.supportingContext && (
                    <p>
                      <b>Supporting context:</b> {pillar.supportingContext}
                    </p>
                  )}
                  {pillar.recommendedUsage && (
                    <p>
                      <b>Recommended usage:</b> {pillar.recommendedUsage}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Asset Requirements" action={regenerateButton("assets")}>
            {assetError && (
              <div className="form-alert error">
                <X size={16} />
                {assetError}
              </div>
            )}
            <div className="page-stack">
              {Array.from(assetsByChannel.entries()).map(([channel, channelAssets]) => (
                <div key={channel}>
                  <span className="overline">
                    {channel} · {channelAssets.length} asset{channelAssets.length === 1 ? "" : "s"}
                  </span>
                  <div className="source-list">
                    {channelAssets.map((asset) => (
                      <div key={asset.id}>
                        <span>
                          <b>{asset.assetType}</b>
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <em className={`status ${assetStatusTone(asset.status)}`}>{assetStatusLabel(asset.status)}</em>
                          {!isContentGenerationSupportedChannel(asset.channel) && asset.status === "required" && <em className="status neutral">Not yet supported by Content Generation</em>}
                          <select value={asset.status} disabled={assetBusyId === asset.id} onChange={(event) => void handleUpdateAssetStatus(asset.id, event.target.value as CampaignAssetStatus)}>
                            <option value="required">Required</option>
                            <option value="ready_for_content_generation">Ready for Content Generation</option>
                            <option value="drafting">Drafting</option>
                            <option value="drafted">Drafted</option>
                            <option value="approved">Approved</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="KPIs">
            <div className="source-list">
              {campaign.kpis.map((kpi, index) => (
                <div key={index}>
                  <span>{kpi.name}</span>
                  <em>{kpi.target ?? "Target not defined."}</em>
                </div>
              ))}
            </div>
          </Panel>

          {campaign.assumptions.length > 0 && (
            <Panel title="Assumptions & Gaps">
              <ul className="score-why">
                {campaign.assumptions.map((assumption, index) => (
                  <li key={index}>{assumption}</li>
                ))}
              </ul>
            </Panel>
          )}

          {recommendations.length > 0 && (
            <Panel title="AI Recommendations">
              <div className="page-stack">
                {recommendations.map((recommendation, index) => (
                  <div key={index} className="website-recommendation-card">
                    <div className="page-title-action">
                      <b>{recommendation.title}</b>
                      <em className={`status ${confidenceTone(recommendation.confidence)}`}>{recommendation.confidence} confidence</em>
                    </div>
                    <ul className="score-why">
                      {recommendation.reasons.map((reason, reasonIndex) => (
                        <li key={reasonIndex}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel title="Approval">
            {approveError && (
              <div className="form-alert error">
                <X size={16} />
                {approveError}
              </div>
            )}
            {sendError && (
              <div className="form-alert error">
                <X size={16} />
                {sendError}
              </div>
            )}
            <div className="page-title-action">
              <em className={`status ${campaignPlanStatusTone(campaign.planStatus)}`}>{campaignPlanStatusLabel(campaign.planStatus)}</em>
              <div style={{ display: "flex", gap: 8 }}>
                {campaign.planStatus === "needs_review" && (
                  <button className="button primary" disabled={approving} onClick={() => void handleApprove()}>
                    {approving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve Campaign Plan
                  </button>
                )}
                {campaign.planStatus === "approved" && (
                  <button className="button primary" disabled={sending} onClick={() => void handleSendToContentGeneration()}>
                    {sending ? <LoaderCircle className="spin" size={15} /> : <ArrowRight size={15} />} Send to Content Generation
                  </button>
                )}
                {campaign.planStatus === "sent_to_content_generation" && (
                  <button className="button secondary" onClick={onNavigateToContentGeneration}>
                    Open Content Generation <ArrowRight size={15} />
                  </button>
                )}
              </div>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

// Sales Planning -- translates an approved revenue target into a
// territory/account coverage plan using real CRM data, Lead Intelligence's
// ICP-fit scoring, and real synced activity mapped into Buying Intent's
// scoring function. Coverage and account tiering are always live-computed
// from current CRM state; only AI recommendations and the territory
// structure are persisted. It never auto-finalizes territory assignments,
// reassigns reps, or changes CRM ownership/quotas/compensation.

type SalesPlanTab = "overview" | "territories" | "accounts" | "gaps" | "risks" | "recommendations";
type AccountSortKey = "tier" | "pipeline" | "name" | "intent";

const TIER_LABELS: Record<AccountTier, string> = { tier_1: "Tier 1 -- Immediate", tier_2: "Tier 2 -- Strategic", tier_3: "Tier 3 -- Develop", tier_4: "Tier 4 -- Low Priority" };
const TIER_ORDER: Record<AccountTier, number> = { tier_1: 0, tier_2: 1, tier_3: 2, tier_4: 3 };
const INTENT_ORDER: Record<IntentBand, number> = { high: 0, medium: 1, low: 2, insufficient_signal: 3 };

function formatNumber(value: number | null): string {
  return value === null ? "Not set" : Math.round(value).toLocaleString("en-US");
}
function tierTone(tier: AccountTier): "complete" | "pending" | "neutral" {
  if (tier === "tier_1") return "complete";
  if (tier === "tier_2") return "pending";
  return "neutral";
}
function intentTone(band: IntentBand): "complete" | "pending" | "neutral" {
  if (band === "high") return "complete";
  if (band === "medium") return "pending";
  return "neutral";
}
function intentLabel(intent: AccountIntent): string {
  if (!intent.available) return "Insufficient signal";
  return `${intent.band.charAt(0).toUpperCase()}${intent.band.slice(1)}${intent.score !== null ? ` (${intent.score})` : ""}`;
}
function gapCertaintyTone(certainty: GapCertainty): "error" | "pending" {
  return certainty === "known" ? "error" : "pending";
}
function gapSeverityTone(severity: CoverageGap["severity"]): "error" | "pending" | "neutral" {
  if (severity === "critical" || severity === "high") return "error";
  if (severity === "medium") return "pending";
  return "neutral";
}
function territoryStatusTone(status: TerritoryStatus): "complete" | "pending" {
  return status === "approved" ? "complete" : "pending";
}

function SalesPlanningView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "sales-planning");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState<SalesPlanResult | null>(null);
  const [tab, setTab] = useState<SalesPlanTab>("overview");

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [decidingRecommendationId, setDecidingRecommendationId] = useState<string | null>(null);
  const [recommendationError, setRecommendationError] = useState("");
  const [noteByRecommendation, setNoteByRecommendation] = useState<Record<string, string>>({});

  const [applyingTerritories, setApplyingTerritories] = useState(false);
  const [territoryError, setTerritoryError] = useState("");
  const [busyTerritoryId, setBusyTerritoryId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [tierFilter, setTierFilter] = useState<AccountTier | "">("");
  const [intentFilter, setIntentFilter] = useState<IntentBand | "">("");
  const [territoryFilter, setTerritoryFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState<"" | "assigned" | "unassigned">("");
  const [sortKey, setSortKey] = useState<AccountSortKey>("tier");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [selectedAccount, setSelectedAccount] = useState<AccountPlanSummary | null>(null);
  const [reassigningAccount, setReassigningAccount] = useState(false);

  const load = () => {
    setLoading(true);
    loadSalesPlan(session.session.token, workspace.tenantId)
      .then((result) => {
        setPlan(result);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Sales Planning."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Sales Planning Agent</span>
          <h1>Sales Planning isn't active yet.</h1>
        </div>
        <Panel title="Activate Sales Planning">
          <Empty icon={MapPinned} title="Agent not active" text="Activate the Sales Planning agent from Agents & Entitlements to turn your revenue target into a territory and account coverage plan." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Sales Planning">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading || !plan)
    return (
      <Panel title="Sales Planning">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Sales Planning workspace…" />
      </Panel>
    );

  const { overview, territories, accounts, coverageGaps, dealRisks, recommendations } = plan;

  const handleGenerateRecommendations = async () => {
    setGenerating(true);
    setGenerateError("");
    try {
      const result = await generateSalesRecommendations(session.session.token, workspace.tenantId);
      setPlan((prev) => (prev ? { ...prev, recommendations: result.data } : prev));
    } catch (cause) {
      setGenerateError(cause instanceof Error ? cause.message : "Recommendations could not be generated.");
    } finally {
      setGenerating(false);
    }
  };
  const handleDecideRecommendation = async (id: string, decision: "approved" | "rejected") => {
    setDecidingRecommendationId(id);
    setRecommendationError("");
    try {
      const updated = await decideSalesRecommendation(session.session.token, workspace.tenantId, id, decision, noteByRecommendation[id]);
      setPlan((prev) => (prev ? { ...prev, recommendations: prev.recommendations.map((item) => (item.id === updated.id ? updated : item)) } : prev));
    } catch (cause) {
      setRecommendationError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingRecommendationId(null);
    }
  };
  const handleApplyProposed = async () => {
    setApplyingTerritories(true);
    setTerritoryError("");
    try {
      await applyProposedTerritoryStructure(session.session.token, workspace.tenantId);
      load();
    } catch (cause) {
      setTerritoryError(cause instanceof Error ? cause.message : "Could not apply the proposed territory structure.");
    } finally {
      setApplyingTerritories(false);
    }
  };
  const handleApproveTerritory = async (territoryId: string) => {
    setBusyTerritoryId(territoryId);
    setTerritoryError("");
    try {
      await approveSalesTerritory(session.session.token, workspace.tenantId, territoryId);
      load();
    } catch (cause) {
      setTerritoryError(cause instanceof Error ? cause.message : "Could not approve this territory.");
    } finally {
      setBusyTerritoryId(null);
    }
  };
  const handleReassignAccount = async (accountId: string, territoryId: string | null) => {
    setReassigningAccount(true);
    try {
      await reassignAccountTerritory(session.session.token, workspace.tenantId, accountId, territoryId);
      load();
      setSelectedAccount(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not reassign this account.");
    } finally {
      setReassigningAccount(false);
    }
  };

  const territoryNames = Array.from(new Set(territories.map((territory) => territory.name)));
  const filteredAccounts = accounts
    .filter((account) => !searchQuery || account.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((account) => !tierFilter || account.tier === tierFilter)
    .filter((account) => !intentFilter || account.intent.band === intentFilter)
    .filter((account) => !territoryFilter || territories.find((territory) => territory.id === account.territoryId)?.name === territoryFilter)
    .filter((account) => !ownerFilter || (ownerFilter === "assigned" ? Boolean(account.ownerUserId) : !account.ownerUserId))
    .sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;
      if (sortKey === "tier") return direction * (TIER_ORDER[a.tier] - TIER_ORDER[b.tier]);
      if (sortKey === "pipeline") return direction * (a.openPipelineAmount - b.openPipelineAmount);
      if (sortKey === "intent") return direction * (INTENT_ORDER[a.intent.band] - INTENT_ORDER[b.intent.band]);
      return direction * a.name.localeCompare(b.name);
    });
  const toggleSort = (key: AccountSortKey) => {
    if (sortKey === key) setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const allProposed = territories.length > 0 && territories.every((territory) => territory.id === null);

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <div className="page-title-action" style={{ marginBottom: 4 }}>
            <span className="overline">Sales Planning Agent</span>
            <em className="status complete">Planning Agent · Online</em>
          </div>
          <h1>
            Sales Planning
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Turn your revenue target into a territory and account coverage plan.</span>
            </span>
          </h1>
        </div>
      </div>

      <div className="metrics metrics-6">
        <Metric icon={Target} label="Revenue target" value={formatNumber(overview.target)} meta="From onboarding / revenue targets" />
        <Metric icon={Gauge} label="Pipeline coverage" value={formatNumber(overview.currentPipeline)} meta="Open qualified pipeline" />
        <Metric icon={Info} label="Coverage %" value={overview.coveragePercent !== null ? `${Math.round(overview.coveragePercent)}%` : "Unknown"} meta="Pipeline / target" />
        <Metric icon={AlertTriangle} label="Revenue gap" value={formatNumber(overview.revenueGap)} meta="Target minus pipeline" />
        <Metric icon={UsersRound} label="Priority accounts" value={String(overview.priorityAccountCount)} meta="Tier 1 + Tier 2" />
        <Metric icon={ShieldCheck} label="Coverage risks" value={String(overview.coverageGapCount)} meta="Known gaps + potential risks" />
      </div>

      {overview.target === null && (
        <Panel title="Revenue Target Required">
          <Empty icon={Target} title="No revenue target set" text="A revenue target is required to generate a target-aligned sales plan. Set one during onboarding or in Plan & Billing." />
        </Panel>
      )}

      <div className="lead-filter-bar">
        {(["overview", "territories", "accounts", "gaps", "risks", "recommendations"] as const).map((key) => (
          <button key={key} className={`button ${tab === key ? "primary" : "secondary"}`} onClick={() => setTab(key)} type="button">
            {key === "overview" ? "Overview" : key === "territories" ? "Territory Plan" : key === "accounts" ? "Priority Accounts" : key === "gaps" ? "Coverage Gaps" : key === "risks" ? "Deal Risks" : "Recommendations"}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <Panel title="Coverage Summary">
            <div className="source-list">
              <div>
                <span>
                  <b>Target</b>
                </span>
                <em>{formatNumber(overview.target)}</em>
              </div>
              <div>
                <span>
                  <b>Current qualified pipeline</b>
                </span>
                <em>{formatNumber(overview.currentPipeline)}</em>
              </div>
              <div>
                <span>
                  <b>Pipeline required (win-rate adjusted)</b>
                </span>
                <em>{formatNumber(overview.pipelineRequired)}</em>
              </div>
              <div>
                <span>
                  <b>Revenue gap</b>
                </span>
                <em>{formatNumber(overview.revenueGap)}</em>
              </div>
            </div>
          </Panel>
          <Panel title="Top Priority Accounts" action={<button className="button secondary" onClick={() => setTab("accounts")}>View all</button>}>
            {accounts.length === 0 ? (
              <Empty icon={UsersRound} title="No accounts available" text="CRM account data is unavailable. Account prioritization is limited." />
            ) : (
              <div className="source-list">
                {[...accounts]
                  .sort((a, b) => TIER_ORDER[a.tier] - TIER_ORDER[b.tier] || b.openPipelineAmount - a.openPipelineAmount)
                  .slice(0, 5)
                  .map((account) => (
                    <div key={account.id}>
                      <span>
                        <b>{account.name}</b>
                      </span>
                      <em className={`status ${tierTone(account.tier)}`}>{TIER_LABELS[account.tier]}</em>
                    </div>
                  ))}
              </div>
            )}
          </Panel>
          <Panel title="Top Coverage Gaps" action={<button className="button secondary" onClick={() => setTab("gaps")}>View all</button>}>
            {coverageGaps.length === 0 ? (
              <Empty icon={ShieldCheck} title="No coverage gaps identified" text="Nothing to flag from the current data." />
            ) : (
              <div className="source-list">
                {coverageGaps.slice(0, 5).map((gap) => (
                  <div key={gap.key}>
                    <span>
                      <b>{gap.title}</b>
                    </span>
                    <em className={`status ${gapCertaintyTone(gap.certainty)}`}>{gap.certainty === "known" ? "Known gap" : "Potential risk"}</em>
                  </div>
                ))}
              </div>
            )}
          </Panel>
          <Panel title="Top Deal Risks" kicker={`${dealRisks.length} identified`} action={<button className="button secondary" onClick={() => setTab("risks")}>View all</button>}>
            {dealRisks.length === 0 ? (
              <Empty icon={ShieldCheck} title="No deal risks identified" text="No open opportunity is missing a close date, overdue, or gone quiet." />
            ) : (
              <div className="source-list">
                {dealRisks.slice(0, 5).map((risk) => (
                  <div key={risk.key}>
                    <span>
                      <b>{risk.opportunityName}</b>
                      <small>{risk.title}</small>
                    </span>
                    <em className={`status ${gapSeverityTone(risk.severity)}`}>{risk.severity}</em>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </>
      )}

      {tab === "territories" && (
        <Panel
          title="Territory Plan"
          kicker={allProposed ? "Proposed structure -- based on account segment, not yet applied" : "Existing structure"}
          action={
            allProposed ? (
              <button className="button primary" disabled={applyingTerritories} onClick={() => void handleApplyProposed()}>
                {applyingTerritories ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Apply proposed structure
              </button>
            ) : undefined
          }
        >
          {territoryError && (
            <div className="form-alert error">
              <X size={16} />
              {territoryError}
            </div>
          )}
          {territories.length === 0 ? (
            <Empty icon={MapPinned} title="No territory data available" text="No accounts are available to propose a territory structure from." />
          ) : (
            <div className="page-inventory-table lead-table">
              <table>
                <thead>
                  <tr>
                    <th>Territory</th>
                    <th>Allocated target</th>
                    <th>Pipeline</th>
                    <th>Priority accounts</th>
                    <th>High-intent accounts</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {territories.map((territory) => (
                    <tr key={territory.id ?? territory.name}>
                      <td>
                        <b>{territory.name}</b>
                        <br />
                        <small>{territory.accountCount} accounts</small>
                      </td>
                      <td>{territory.allocatedTarget !== null ? `${formatNumber(territory.allocatedTarget)} (derived)` : "Not available"}</td>
                      <td>{formatNumber(territory.openPipelineAmount)}</td>
                      <td>{territory.priorityAccountCount}</td>
                      <td>{territory.highIntentAccountCount}</td>
                      <td>
                        <em className={`status ${territoryStatusTone(territory.status)}`}>{territory.status === "approved" ? "Approved" : "Proposed"}</em>
                      </td>
                      <td>
                        {territory.id && territory.status === "proposed" && (
                          <button className="button secondary" disabled={busyTerritoryId === territory.id} onClick={() => void handleApproveTerritory(territory.id!)}>
                            {busyTerritoryId === territory.id ? <LoaderCircle className="spin" size={15} /> : "Approve"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === "accounts" && (
        <Panel title="Priority Accounts">
          <div className="lead-filter-bar">
            <div className="table-search">
              <Search size={15} />
              <input placeholder="Search accounts" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />
            </div>
            <select value={tierFilter} onChange={(event) => setTierFilter(event.target.value as AccountTier | "")}>
              <option value="">All tiers</option>
              <option value="tier_1">Tier 1</option>
              <option value="tier_2">Tier 2</option>
              <option value="tier_3">Tier 3</option>
              <option value="tier_4">Tier 4</option>
            </select>
            <select value={intentFilter} onChange={(event) => setIntentFilter(event.target.value as IntentBand | "")}>
              <option value="">All intent</option>
              <option value="high">High intent</option>
              <option value="medium">Medium intent</option>
              <option value="low">Low intent</option>
              <option value="insufficient_signal">Insufficient signal</option>
            </select>
            <select value={territoryFilter} onChange={(event) => setTerritoryFilter(event.target.value)}>
              <option value="">All territories</option>
              {territoryNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <select value={ownerFilter} onChange={(event) => setOwnerFilter(event.target.value as "" | "assigned" | "unassigned")}>
              <option value="">All owners</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </div>
          {accounts.length === 0 ? (
            <Empty icon={UsersRound} title="No accounts available" text="CRM account data is unavailable. Account prioritization is limited." />
          ) : (
            <div className="page-inventory-table lead-table">
              <table>
                <thead>
                  <tr>
                    <th>
                      <button className="sort-header" onClick={() => toggleSort("tier")}>
                        Priority {sortKey === "tier" && (sortDirection === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                      </button>
                    </th>
                    <th>
                      <button className="sort-header" onClick={() => toggleSort("name")}>
                        Account {sortKey === "name" && (sortDirection === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                      </button>
                    </th>
                    <th>Fit</th>
                    <th>
                      <button className="sort-header" onClick={() => toggleSort("intent")}>
                        Intent {sortKey === "intent" && (sortDirection === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                      </button>
                    </th>
                    <th>
                      <button className="sort-header" onClick={() => toggleSort("pipeline")}>
                        Pipeline {sortKey === "pipeline" && (sortDirection === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                      </button>
                    </th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((account) => (
                    <tr key={account.id} className="clickable" onClick={() => setSelectedAccount(account)}>
                      <td>
                        <em className={`status ${tierTone(account.tier)}`}>{TIER_LABELS[account.tier]}</em>
                      </td>
                      <td>{account.name}</td>
                      <td>{account.fitBand ?? "Insufficient signal"}</td>
                      <td>
                        <em className={`status ${intentTone(account.intent.band)}`}>{intentLabel(account.intent)}</em>
                      </td>
                      <td>{formatNumber(account.openPipelineAmount)}</td>
                      <td>{account.ownerUserId ? "Assigned" : "Unassigned"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === "gaps" && (
        <Panel title="Coverage Gaps">
          {coverageGaps.length === 0 ? (
            <Empty icon={ShieldCheck} title="No coverage gaps identified" text="Nothing to flag from the current data." />
          ) : (
            <div className="page-stack">
              {coverageGaps.map((gap) => (
                <div key={gap.key} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{gap.title}</b>
                    <div style={{ display: "flex", gap: 6 }}>
                      <em className={`status ${gapCertaintyTone(gap.certainty)}`}>{gap.certainty === "known" ? "Known gap" : "Potential risk"}</em>
                      <em className={`status ${gapSeverityTone(gap.severity)}`}>{gap.severity}</em>
                    </div>
                  </div>
                  <p>{gap.reason}</p>
                  <div className="source-list">
                    <div>
                      <span>Target</span>
                      <em>{formatNumber(gap.target)}</em>
                    </div>
                    <div>
                      <span>Current coverage</span>
                      <em>{formatNumber(gap.currentCoverage)}</em>
                    </div>
                    <div>
                      <span>Shortfall</span>
                      <em>{formatNumber(gap.shortfall)}</em>
                    </div>
                  </div>
                  <p>
                    <b>Recommended response:</b> {gap.recommendedResponse}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {tab === "risks" && (
        <Panel title="Deal Risks" kicker="Deterministic -- computed directly from open opportunities and account activity, never LLM-scored">
          {dealRisks.length === 0 ? (
            <Empty icon={ShieldCheck} title="No deal risks identified" text="No open opportunity is missing a close date, overdue, or gone quiet." />
          ) : (
            <div className="page-stack">
              {dealRisks.map((risk) => (
                <div key={risk.key} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{risk.opportunityName}</b>
                    <em className={`status ${gapSeverityTone(risk.severity)}`}>{risk.severity}</em>
                  </div>
                  <p>
                    <b>{risk.title}:</b> {risk.description}
                  </p>
                  <div className="source-list">
                    <div>
                      <span>Account</span>
                      <em>{risk.accountName ?? "No account on file"}</em>
                    </div>
                    <div>
                      <span>Owner</span>
                      <em>{risk.ownerUserId ?? "Unassigned"}</em>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {tab === "recommendations" && (
        <Panel
          title="AI Recommendations"
          kicker="AI-generated draft · Human review required"
          action={
            <button className="button primary" disabled={generating} onClick={() => void handleGenerateRecommendations()}>
              {generating ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Generate Recommendations
            </button>
          }
        >
          {generateError && (
            <div className="form-alert error">
              <X size={16} />
              {generateError}
            </div>
          )}
          {recommendationError && (
            <div className="form-alert error">
              <X size={16} />
              {recommendationError}
            </div>
          )}
          {recommendations.length === 0 ? (
            <Empty icon={Sparkles} title="No recommendations yet" text="Generate recommendations to see the highest-value actions for closing your coverage gap." />
          ) : (
            <div className="page-stack">
              {recommendations.map((recommendation) => (
                <div key={recommendation.id} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{recommendation.title}</b>
                    <em className={`status ${recommendation.status === "approved" ? "complete" : recommendation.status === "rejected" ? "error" : "pending"}`}>
                      {recommendation.status === "pending" ? "Pending review" : recommendation.status}
                    </em>
                  </div>
                  <p>
                    <b>Why:</b> {recommendation.rationale}
                  </p>
                  {recommendation.evidence.length > 0 && (
                    <ul className="score-why">
                      {recommendation.evidence.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                  <div className="source-list">
                    <div>
                      <span>Expected impact</span>
                      <em>{recommendation.expectedImpact}</em>
                    </div>
                    <div>
                      <span>Confidence</span>
                      <em className={`status ${confidenceTone(recommendation.confidence)}`}>{recommendation.confidence}</em>
                    </div>
                  </div>
                  {recommendation.status === "pending" && (
                    <div className="page-stack">
                      <input
                        placeholder="Note (required to adjust)"
                        value={noteByRecommendation[recommendation.id] ?? ""}
                        onChange={(event) => setNoteByRecommendation((prev) => ({ ...prev, [recommendation.id]: event.target.value }))}
                      />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        <button className="button secondary" disabled={decidingRecommendationId === recommendation.id} onClick={() => void handleDecideRecommendation(recommendation.id, "rejected")}>
                          Dismiss
                        </button>
                        <button
                          className="button secondary"
                          disabled={decidingRecommendationId === recommendation.id || !noteByRecommendation[recommendation.id]}
                          onClick={() => void handleDecideRecommendation(recommendation.id, "rejected")}
                        >
                          Adjust
                        </button>
                        <button className="button primary" disabled={decidingRecommendationId === recommendation.id} onClick={() => void handleDecideRecommendation(recommendation.id, "approved")}>
                          {decidingRecommendationId === recommendation.id ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      <Drawer open={selectedAccount !== null} title={selectedAccount?.name ?? "Account"} onClose={() => setSelectedAccount(null)}>
        {selectedAccount && (
          <div className="page-stack">
            <div className="source-list">
              <div>
                <span>Priority</span>
                <em className={`status ${tierTone(selectedAccount.tier)}`}>{TIER_LABELS[selectedAccount.tier]}</em>
              </div>
              <div>
                <span>ICP fit</span>
                <em>{selectedAccount.fitBand ?? "Insufficient signal"}</em>
              </div>
              <div>
                <span>Buying intent</span>
                <em className={`status ${intentTone(selectedAccount.intent.band)}`}>{intentLabel(selectedAccount.intent)}</em>
              </div>
              <div>
                <span>Open pipeline</span>
                <em>{formatNumber(selectedAccount.openPipelineAmount)}</em>
              </div>
              <div>
                <span>Owner</span>
                <em>{selectedAccount.ownerUserId ?? "Unassigned"}</em>
              </div>
              <div>
                <span>Industry</span>
                <em>{selectedAccount.industry ?? "Unknown"}</em>
              </div>
            </div>
            {selectedAccount.intent.unavailableSignalKinds.length > 0 && (
              <p className="ai-callout">
                Buying intent is a prioritisation signal, not guaranteed purchasing behaviour. The following signal kinds are not yet available for this account: {selectedAccount.intent.unavailableSignalKinds.join(", ")}.
              </p>
            )}
            <div>
              <span className="overline">Why this priority</span>
              <ul className="score-why">
                {selectedAccount.tierReasons.map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
            </div>
            <label>
              Territory
              <select
                value={selectedAccount.territoryId ?? ""}
                disabled={reassigningAccount}
                onChange={(event) => void handleReassignAccount(selectedAccount.id, event.target.value || null)}
              >
                <option value="">Unassigned</option>
                {territories
                  .filter((territory) => territory.id !== null)
                  .map((territory) => (
                    <option key={territory.id} value={territory.id ?? ""}>
                      {territory.name}
                    </option>
                  ))}
              </select>
            </label>
          </div>
        )}
      </Drawer>
    </div>
  );
}

// Content Generation -- drafts campaign content against messaging pillars,
// brand voice, and audience/ICP context (read from an approved Business
// Profile). Every asset is a draft until a human explicitly approves it --
// this agent never publishes, sends, or launches anything, and that
// principle is reflected throughout: status is never "Published," and every
// generated asset is labeled "AI-generated draft -- human review required."

const CONTENT_TYPE_LABELS: Record<ContentType, string> = { email: "Email", ad: "Ad Copy", landing_page: "Landing Page", outreach: "Outreach Message" };
const MIN_VARIANTS = 1;
const MAX_VARIANTS = 5;

function contentStatusTone(status: ContentStatus): "complete" | "pending" | "neutral" | "error" {
  if (status === "approved") return "complete";
  if (status === "in_review") return "pending";
  if (status === "changes_requested") return "error";
  return "neutral";
}
function contentStatusLabel(status: ContentStatus): string {
  if (status === "approved") return "Approved";
  if (status === "in_review") return "In Review";
  if (status === "changes_requested") return "Changes Requested";
  return "Draft";
}
function labelizeField(key: string): string {
  const spaced = key.replace(/([A-Z])/g, " $1").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function VariantCard({
  variant,
  selected,
  isEditing,
  editingFields,
  busy,
  isDraft,
  onSelect,
  onStartEdit,
  onFieldChange,
  onSaveEdit,
  onCancelEdit,
  onTransform,
  onDuplicate,
  onUndo,
}: {
  variant: ContentVariantRecord;
  selected: boolean;
  isEditing: boolean;
  editingFields: Record<string, string>;
  busy: boolean;
  isDraft: boolean;
  onSelect: () => void;
  onStartEdit: () => void;
  onFieldChange: (key: string, value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onTransform: (operation: TransformOperation) => void;
  onDuplicate: () => void;
  onUndo: () => void;
}) {
  const entries = Object.entries(variant.content);
  return (
    <div className="website-recommendation-card" style={selected ? { borderColor: "var(--cyan-dark)" } : undefined}>
      <div className="page-title-action">
        <b>
          {variant.label}
          {variant.isEdited ? " (edited)" : ""}
        </b>
        {selected && <em className="status complete">Selected</em>}
      </div>
      {isEditing ? (
        <div className="page-stack">
          {entries.map(([key]) => (
            <label key={key}>
              {labelizeField(key)}
              <input value={editingFields[key] ?? ""} onChange={(event) => onFieldChange(key, event.target.value)} />
            </label>
          ))}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <button className="button secondary" onClick={onCancelEdit}>
              Cancel
            </button>
            <button className="button primary" disabled={busy} onClick={onSaveEdit}>
              {busy ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save
            </button>
          </div>
        </div>
      ) : (
        <>
          {entries.map(([key, value]) => (
            <p key={key}>
              <b>{labelizeField(key)}:</b> {Array.isArray(value) ? value.join(" · ") : String(value)}
            </p>
          ))}
          {isDraft && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              <button className="button secondary" disabled={busy} onClick={onSelect}>
                <Check size={14} /> {selected ? "Selected" : "Select"}
              </button>
              <button className="button secondary" disabled={busy} onClick={onStartEdit}>
                Edit
              </button>
              <button className="button secondary" disabled={busy} onClick={() => onTransform("shorten")}>
                <ArrowDown size={14} /> Shorten
              </button>
              <button className="button secondary" disabled={busy} onClick={() => onTransform("expand")}>
                <ArrowUp size={14} /> Expand
              </button>
              <button className="button secondary" disabled={busy} onClick={() => onTransform("change_tone")}>
                <SlidersHorizontal size={14} /> Change Tone
              </button>
              <button className="button secondary" disabled={busy} onClick={() => onTransform("regenerate")}>
                <RefreshCw size={14} /> Regenerate
              </button>
              <button className="button secondary" disabled={busy} onClick={onDuplicate}>
                <Plus size={14} /> Duplicate
              </button>
              {variant.isEdited && (
                <button className="button secondary" disabled={busy} onClick={onUndo}>
                  <ArrowLeft size={14} /> Undo
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ContentGenerationView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "content-generation");
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [drafts, setDrafts] = useState<ContentDraftRecord[] | null>(null);
  const [brandVoice, setBrandVoice] = useState<BrandVoiceProfile | null>(null);
  const [audience, setAudience] = useState<AudienceSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [campaignDetail, setCampaignDetail] = useState<CampaignDetail | null>(null);
  const [campaignError, setCampaignError] = useState("");

  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [newCampaignObjective, setNewCampaignObjective] = useState("");
  const [newCampaignSegment, setNewCampaignSegment] = useState("");
  const [newPillars, setNewPillars] = useState<string[]>([""]);
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  const [editingBrandVoice, setEditingBrandVoice] = useState(false);
  const [brandTraits, setBrandTraits] = useState("");
  const [brandDo, setBrandDo] = useState("");
  const [brandAvoid, setBrandAvoid] = useState("");
  const [savingBrandVoice, setSavingBrandVoice] = useState(false);
  const [brandVoiceError, setBrandVoiceError] = useState("");

  const [contentType, setContentType] = useState<ContentType>("email");
  const [draftName, setDraftName] = useState("");
  const [channel, setChannel] = useState("");
  const [tone, setTone] = useState("");
  const [length, setLength] = useState("standard");
  const [variantCount, setVariantCount] = useState(3);
  const [cta, setCta] = useState("");
  const [instructions, setInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");

  const [workspaceDraft, setWorkspaceDraft] = useState<ContentDraftRecord | null>(null);
  const [workspaceVariants, setWorkspaceVariants] = useState<ContentVariantRecord[]>([]);
  const [activity, setActivity] = useState<ActivityStep[]>([]);
  const [recommendations, setRecommendations] = useState<ContentRecommendation[]>([]);
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const [editingFields, setEditingFields] = useState<Record<string, string>>({});
  const [busyVariantId, setBusyVariantId] = useState<string | null>(null);
  const [variantError, setVariantError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [reviewFilter, setReviewFilter] = useState<ContentStatus | "all">("in_review");
  const [decisionNoteByDraft, setDecisionNoteByDraft] = useState<Record<string, string>>({});
  const [decidingDraftId, setDecidingDraftId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState("");

  const splitList = (value: string) => value.split(",").map((entry) => entry.trim()).filter(Boolean);

  const load = () => {
    setLoading(true);
    Promise.all([
      loadCampaigns(session.session.token, workspace.tenantId),
      loadBrandVoice(session.session.token, workspace.tenantId),
      loadAudienceContext(session.session.token, workspace.tenantId),
      loadContentDrafts(session.session.token, workspace.tenantId),
    ])
      .then(([campaignsResult, brandVoiceResult, audienceResult, draftsResult]) => {
        setCampaigns(campaignsResult.data);
        setBrandVoice(brandVoiceResult);
        setAudience(audienceResult);
        setDrafts(draftsResult.data);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Content Generation."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  useEffect(() => {
    if (!selectedCampaignId) {
      setCampaignDetail(null);
      return;
    }
    setCampaignError("");
    void getCampaign(session.session.token, workspace.tenantId, selectedCampaignId)
      .then((detail) => setCampaignDetail(detail))
      .catch((cause) => setCampaignError(cause instanceof Error ? cause.message : "Could not load this campaign."));
  }, [selectedCampaignId]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Content Generation Agent</span>
          <h1>Content Generation isn't active yet.</h1>
        </div>
        <Panel title="Activate Content Generation">
          <Empty icon={FileText} title="Agent not active" text="Activate the Content Generation agent from Agents & Entitlements to start drafting on-brand campaign content." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Content Generation">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Content Generation">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Content Generation workspace…" />
      </Panel>
    );

  const handleCreateCampaign = async () => {
    setCreatingCampaign(true);
    setCampaignError("");
    try {
      const pillars = newPillars.map((pillar) => pillar.trim()).filter(Boolean);
      const created = await createCampaign(session.session.token, workspace.tenantId, {
        name: newCampaignName.trim(),
        ...(newCampaignObjective.trim() ? { objective: newCampaignObjective.trim() } : {}),
        ...(newCampaignSegment.trim() ? { targetSegment: newCampaignSegment.trim() } : {}),
        pillars,
      });
      setCampaigns((prev) => [{ id: created.id, name: created.name, objective: created.objective, targetSegment: created.targetSegment, pillarCount: created.pillarCount, createdAt: created.createdAt }, ...prev]);
      setSelectedCampaignId(created.id);
      setCampaignDetail(created);
      setShowNewCampaign(false);
      setNewCampaignName("");
      setNewCampaignObjective("");
      setNewCampaignSegment("");
      setNewPillars([""]);
    } catch (cause) {
      setCampaignError(cause instanceof Error ? cause.message : "Could not create this campaign.");
    } finally {
      setCreatingCampaign(false);
    }
  };

  const startEditBrandVoice = () => {
    setBrandTraits((brandVoice?.traits ?? []).join(", "));
    setBrandDo((brandVoice?.doList ?? []).join(", "));
    setBrandAvoid((brandVoice?.avoidList ?? []).join(", "));
    setEditingBrandVoice(true);
  };
  const handleSaveBrandVoice = async () => {
    setSavingBrandVoice(true);
    setBrandVoiceError("");
    try {
      const updated = await saveBrandVoice(session.session.token, workspace.tenantId, { traits: splitList(brandTraits), doList: splitList(brandDo), avoidList: splitList(brandAvoid) });
      setBrandVoice(updated);
      setEditingBrandVoice(false);
    } catch (cause) {
      setBrandVoiceError(cause instanceof Error ? cause.message : "Could not save the brand voice profile.");
    } finally {
      setSavingBrandVoice(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedCampaignId) return;
    setGenerating(true);
    setGenerateError("");
    try {
      const result = await generateContentDraft(session.session.token, workspace.tenantId, {
        campaignId: selectedCampaignId,
        contentType,
        name: draftName.trim() || `${CONTENT_TYPE_LABELS[contentType]} draft`,
        ...(channel.trim() ? { channel: channel.trim() } : {}),
        ...(tone.trim() ? { tone: tone.trim() } : {}),
        ...(length.trim() ? { length: length.trim() } : {}),
        variantCount,
        ...(cta.trim() ? { cta: cta.trim() } : {}),
        ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
      });
      setWorkspaceDraft(result.draft);
      setWorkspaceVariants(result.variants);
      setActivity(result.activity);
      setRecommendations(result.recommendations);
      setDrafts((prev) => (prev ? [result.draft, ...prev] : [result.draft]));
    } catch (cause) {
      setGenerateError(cause instanceof Error ? cause.message : "Content could not be generated.");
    } finally {
      setGenerating(false);
    }
  };

  const handleTransform = async (variant: ContentVariantRecord, operation: TransformOperation) => {
    setBusyVariantId(variant.id);
    setVariantError("");
    try {
      const updated = await transformContentVariant(session.session.token, workspace.tenantId, variant.id, operation);
      setWorkspaceVariants((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (cause) {
      setVariantError(cause instanceof Error ? cause.message : "Could not update this variant.");
    } finally {
      setBusyVariantId(null);
    }
  };
  const handleDuplicate = async (variant: ContentVariantRecord) => {
    setBusyVariantId(variant.id);
    setVariantError("");
    try {
      const created = await duplicateContentVariant(session.session.token, workspace.tenantId, variant.id);
      setWorkspaceVariants((prev) => [...prev, created]);
    } catch (cause) {
      setVariantError(cause instanceof Error ? cause.message : "Could not duplicate this variant.");
    } finally {
      setBusyVariantId(null);
    }
  };
  const handleUndo = async (variant: ContentVariantRecord) => {
    setBusyVariantId(variant.id);
    setVariantError("");
    try {
      const updated = await undoContentVariant(session.session.token, workspace.tenantId, variant.id);
      setWorkspaceVariants((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (cause) {
      setVariantError(cause instanceof Error ? cause.message : "Could not undo changes to this variant.");
    } finally {
      setBusyVariantId(null);
    }
  };
  const startEditVariant = (variant: ContentVariantRecord) => {
    const fields: Record<string, string> = {};
    for (const [key, value] of Object.entries(variant.content)) fields[key] = Array.isArray(value) ? value.join(", ") : String(value);
    setEditingFields(fields);
    setEditingVariantId(variant.id);
  };
  const saveEditVariant = async (variant: ContentVariantRecord) => {
    setBusyVariantId(variant.id);
    setVariantError("");
    try {
      const content: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(variant.content)) content[key] = Array.isArray(value) ? splitList(editingFields[key] ?? "") : editingFields[key] ?? "";
      const updated = await updateContentVariant(session.session.token, workspace.tenantId, variant.id, content as VariantContentValue);
      setWorkspaceVariants((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingVariantId(null);
    } catch (cause) {
      setVariantError(cause instanceof Error ? cause.message : "Could not save your edit.");
    } finally {
      setBusyVariantId(null);
    }
  };
  const handleSelectVariant = async (variant: ContentVariantRecord) => {
    if (!workspaceDraft) return;
    try {
      const updated = await selectContentVariant(session.session.token, workspace.tenantId, workspaceDraft.id, variant.id);
      setWorkspaceDraft(updated);
    } catch (cause) {
      setVariantError(cause instanceof Error ? cause.message : "Could not select this variant.");
    }
  };
  const handleSubmitForReview = async () => {
    if (!workspaceDraft) return;
    setSubmitting(true);
    setVariantError("");
    try {
      const updated = await submitDraftForReview(session.session.token, workspace.tenantId, workspaceDraft.id);
      setWorkspaceDraft(updated);
      setDrafts((prev) => prev?.map((item) => (item.id === updated.id ? updated : item)) ?? prev);
    } catch (cause) {
      setVariantError(cause instanceof Error ? cause.message : "Could not submit this draft for review.");
    } finally {
      setSubmitting(false);
    }
  };
  const openDraft = async (draftId: string) => {
    try {
      const { draft, variants } = await getContentDraft(session.session.token, workspace.tenantId, draftId);
      setWorkspaceDraft(draft);
      setWorkspaceVariants(variants);
      setActivity([]);
      setRecommendations([]);
      setVariantError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not open this draft.");
    }
  };
  const handleDecide = async (draftId: string, decision: "approved" | "changes_requested") => {
    setDecidingDraftId(draftId);
    setDecisionError("");
    try {
      const updated = await decideContentDraft(session.session.token, workspace.tenantId, draftId, decision, decisionNoteByDraft[draftId]);
      setDrafts((prev) => prev?.map((item) => (item.id === updated.id ? updated : item)) ?? prev);
      if (workspaceDraft?.id === updated.id) setWorkspaceDraft(updated);
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingDraftId(null);
    }
  };

  const draftsGenerated = drafts?.length ?? 0;
  const pendingReview = drafts?.filter((item) => item.status === "in_review").length ?? 0;
  const approvedCount = drafts?.filter((item) => item.status === "approved").length ?? 0;
  const campaignNameById = (campaignId: string) => campaigns.find((item) => item.id === campaignId)?.name ?? "Unknown campaign";
  const filteredDrafts = (drafts ?? []).filter((item) => reviewFilter === "all" || item.status === reviewFilter);
  const isWorkspaceDraftEditable = workspaceDraft?.status === "draft" || workspaceDraft?.status === "changes_requested";

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <div className="page-title-action" style={{ marginBottom: 4 }}>
            <span className="overline">Content Generation Agent</span>
            <em className="status complete">Content Agent · Online</em>
          </div>
          <h1>
            Content Generation
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Create on-brand campaign content and variants for review.</span>
            </span>
          </h1>
        </div>
      </div>

      <div className="metrics metrics-3">
        <Metric icon={FileText} label="Drafts generated" value={String(draftsGenerated)} meta="All content drafts in this workspace" />
        <Metric icon={Info} label="Pending review" value={String(pendingReview)} meta="Awaiting a reviewer decision" />
        <Metric icon={ShieldCheck} label="Approved" value={String(approvedCount)} meta="Ready for your team's own publishing workflow" />
      </div>

      <Panel title="Campaign" kicker="Step 1 -- select or create a campaign">
        {campaigns.length === 0 && !showNewCampaign ? (
          <Empty icon={Target} title="No campaigns yet" text="Create a campaign to attach messaging pillars and generate content against." />
        ) : (
          <div className="lead-filter-bar">
            <select value={selectedCampaignId ?? ""} onChange={(event) => setSelectedCampaignId(event.target.value || null)}>
              <option value="">Select a campaign…</option>
              {campaigns.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <button className="button secondary" onClick={() => setShowNewCampaign((value) => !value)} type="button">
              <Plus size={15} /> New Campaign
            </button>
          </div>
        )}
        {showNewCampaign && (
          <div className="page-stack" style={{ marginTop: 14 }}>
            {campaignError && (
              <div className="form-alert error">
                <X size={16} />
                {campaignError}
              </div>
            )}
            <div className="lead-config-grid">
              <label>
                Campaign name
                <input value={newCampaignName} onChange={(event) => setNewCampaignName(event.target.value)} />
              </label>
              <label>
                Objective
                <input value={newCampaignObjective} onChange={(event) => setNewCampaignObjective(event.target.value)} />
              </label>
              <label>
                Target segment
                <input value={newCampaignSegment} onChange={(event) => setNewCampaignSegment(event.target.value)} placeholder="e.g. Mid-market SaaS" />
              </label>
            </div>
            <div>
              <span className="overline">Messaging pillars</span>
              {newPillars.map((pillar, index) => (
                <div key={index} className="lead-filter-bar" style={{ marginTop: 8 }}>
                  <div className="table-search" style={{ flex: 1 }}>
                    <input value={pillar} onChange={(event) => setNewPillars((prev) => prev.map((item, itemIndex) => (itemIndex === index ? event.target.value : item)))} placeholder={`Pillar ${index + 1}`} />
                  </div>
                  <button className="button secondary" onClick={() => setNewPillars((prev) => prev.filter((_, itemIndex) => itemIndex !== index))} type="button">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button className="button secondary" style={{ marginTop: 8 }} onClick={() => setNewPillars((prev) => [...prev, ""])} type="button">
                <Plus size={14} /> Add pillar
              </button>
            </div>
            <div className="page-title-action">
              <button className="button primary" disabled={creatingCampaign || !newCampaignName.trim()} onClick={() => void handleCreateCampaign()}>
                {creatingCampaign ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Create Campaign
              </button>
            </div>
          </div>
        )}
        {campaignDetail && (
          <>
            <div className="source-list" style={{ marginTop: 14 }}>
              <div>
                <span>
                  <b>Objective</b>
                </span>
                <em>{campaignDetail.objective ?? "Not set"}</em>
              </div>
              <div>
                <span>
                  <b>Messaging pillars</b>
                </span>
                <em>{campaignDetail.pillarCount}</em>
              </div>
              <div>
                <span>
                  <b>Target segment</b>
                </span>
                <em>{campaignDetail.targetSegment ?? "Not set"}</em>
              </div>
            </div>
            {campaignDetail.pillars.length > 0 ? (
              <ul className="score-why" style={{ marginTop: 10 }}>
                {campaignDetail.pillars.map((pillar) => (
                  <li key={pillar.id}>{pillar.pillar}</li>
                ))}
              </ul>
            ) : (
              <Empty icon={Lightbulb} title="This campaign does not have messaging pillars yet" text="Add pillars above so generated content stays on-message." />
            )}
          </>
        )}
      </Panel>

      {recommendations.length > 0 && (
        <Panel title="Agent Recommendations" kicker="Contextual to this campaign and content type">
          <div className="source-list">
            {recommendations.map((rec) => (
              <div key={rec.key}>
                <span>
                  <b>{rec.message}</b>
                </span>
                <em className={`status ${rec.severity === "high" ? "error" : rec.severity === "medium" ? "pending" : "neutral"}`}>{rec.severity}</em>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Content Type" kicker="Step 2 -- choose what to generate">
        <div className="lead-filter-bar">
          {(["email", "ad", "landing_page", "outreach"] as const).map((type) => (
            <button key={type} className={`button ${contentType === type ? "primary" : "secondary"}`} onClick={() => setContentType(type)} type="button">
              {CONTENT_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
      </Panel>

      <Panel
        title="Brand Voice"
        kicker={brandVoice?.configured ? "Configured" : brandVoice?.source === "website_scan" ? "Suggested from your website -- not yet configured" : "Not configured"}
        action={
          <button className="button secondary" onClick={startEditBrandVoice} type="button">
            Edit brand voice
          </button>
        }
      >
        {brandVoiceError && (
          <div className="form-alert error">
            <X size={16} />
            {brandVoiceError}
          </div>
        )}
        {!brandVoice || brandVoice.traits.length === 0 ? (
          <Empty icon={Sparkles} title="No brand voice profile is configured" text="Configure a brand voice so generated content matches how your team writes." />
        ) : (
          <div className="source-list">
            <div>
              <span>
                <b>Traits</b>
              </span>
              <em>{brandVoice.traits.join(", ")}</em>
            </div>
            {brandVoice.doList.length > 0 && (
              <div>
                <span>
                  <b>Do</b>
                </span>
                <em>{brandVoice.doList.join(", ")}</em>
              </div>
            )}
            {brandVoice.avoidList.length > 0 && (
              <div>
                <span>
                  <b>Avoid</b>
                </span>
                <em>{brandVoice.avoidList.join(", ")}</em>
              </div>
            )}
          </div>
        )}
        {editingBrandVoice && (
          <div className="page-stack" style={{ marginTop: 14 }}>
            <div className="lead-config-grid">
              <label>
                Traits (comma separated)
                <input value={brandTraits} onChange={(event) => setBrandTraits(event.target.value)} placeholder="e.g. Clear, Confident, Practical, Concise" />
              </label>
              <label>
                Do (comma separated)
                <input value={brandDo} onChange={(event) => setBrandDo(event.target.value)} placeholder="e.g. Focus on business outcomes" />
              </label>
              <label>
                Avoid (comma separated)
                <input value={brandAvoid} onChange={(event) => setBrandAvoid(event.target.value)} placeholder="e.g. Excessive jargon" />
              </label>
            </div>
            <div className="page-title-action">
              <button className="button secondary" onClick={() => setEditingBrandVoice(false)} type="button">
                Cancel
              </button>
              <button className="button primary" disabled={savingBrandVoice} onClick={() => void handleSaveBrandVoice()}>
                {savingBrandVoice ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save Brand Voice
              </button>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Audience / ICP Context" kicker="Used to personalize generated content -- read from your approved Business Profile">
        {!audience?.available ? (
          <Empty icon={UsersRound} title="No ICP/audience context available" text="Approve a Business Profile to personalize content with real ICP context." />
        ) : (
          <>
            <div className="source-list">
              <div>
                <span>
                  <b>Target segment</b>
                </span>
                <em>{audience.segment ?? "Not available"}</em>
              </div>
              <div>
                <span>
                  <b>Primary persona</b>
                </span>
                <em>{audience.persona ?? "Not available"}</em>
              </div>
            </div>
            {audience.priorities.length > 0 && (
              <ul className="score-why" style={{ marginTop: 10 }}>
                {audience.priorities.map((priority, index) => (
                  <li key={index}>{priority}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </Panel>

      <Panel title="Generation Controls" kicker="Step 3 -- configure this draft">
        <div className="lead-config-grid">
          <label>
            Draft name
            <input value={draftName} onChange={(event) => setDraftName(event.target.value)} placeholder={`${CONTENT_TYPE_LABELS[contentType]} draft`} />
          </label>
          <label>
            Channel
            <input value={channel} onChange={(event) => setChannel(event.target.value)} placeholder="e.g. email, LinkedIn" />
          </label>
          <label>
            Tone
            <input value={tone} onChange={(event) => setTone(event.target.value)} placeholder="e.g. confident" />
          </label>
          <label>
            Length
            <select value={length} onChange={(event) => setLength(event.target.value)}>
              <option value="short">Short</option>
              <option value="standard">Standard</option>
              <option value="long">Long</option>
            </select>
          </label>
          <label>
            Number of variants
            <input type="number" min={MIN_VARIANTS} max={MAX_VARIANTS} value={variantCount} onChange={(event) => setVariantCount(Math.max(MIN_VARIANTS, Math.min(MAX_VARIANTS, Number(event.target.value) || MIN_VARIANTS)))} />
          </label>
          <label>
            Call to action
            <input value={cta} onChange={(event) => setCta(event.target.value)} placeholder="e.g. Book a demo" />
          </label>
        </div>
        <label>
          Additional instructions
          <textarea value={instructions} onChange={(event) => setInstructions(event.target.value)} rows={2} placeholder="e.g. Focus on reducing operational complexity." />
        </label>
        {generateError && (
          <div className="form-alert error">
            <X size={16} />
            {generateError}
          </div>
        )}
        <div className="page-title-action">
          <button className="button primary" disabled={generating || !selectedCampaignId} onClick={() => void handleGenerate()}>
            {generating ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Generate Draft
          </button>
        </div>
      </Panel>

      {activity.length > 0 && (
        <Panel title="Content Agent Activity">
          <ul className="intel-log">
            {activity.map((step) => (
              <li key={step.key}>
                {step.done ? <Check size={13} /> : <X size={13} />}
                <span>{step.label}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {workspaceDraft && (
        <Panel title="Generated Content" kicker="AI-generated draft · Human review required">
          {variantError && (
            <div className="form-alert error">
              <X size={16} />
              {variantError}
            </div>
          )}
          <div className="page-title-action" style={{ alignItems: "flex-start" }}>
            <div style={{ flex: 2 }}>
              <div className="page-stack">
                {workspaceVariants.map((variant) => (
                  <VariantCard
                    key={variant.id}
                    variant={variant}
                    selected={workspaceDraft.selectedVariantId === variant.id}
                    isEditing={editingVariantId === variant.id}
                    editingFields={editingFields}
                    busy={busyVariantId === variant.id}
                    isDraft={Boolean(isWorkspaceDraftEditable)}
                    onSelect={() => void handleSelectVariant(variant)}
                    onStartEdit={() => startEditVariant(variant)}
                    onFieldChange={(key, value) => setEditingFields((prev) => ({ ...prev, [key]: value }))}
                    onSaveEdit={() => void saveEditVariant(variant)}
                    onCancelEdit={() => setEditingVariantId(null)}
                    onTransform={(operation) => void handleTransform(variant, operation)}
                    onDuplicate={() => void handleDuplicate(variant)}
                    onUndo={() => void handleUndo(variant)}
                  />
                ))}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <span className="overline">Generation Context</span>
              <div className="source-list">
                <div>
                  <span>
                    <b>Campaign</b>
                  </span>
                  <em>{workspaceDraft.generationContext.campaignName}</em>
                </div>
                <div>
                  <span>
                    <b>Messaging pillars</b>
                  </span>
                  <em>{workspaceDraft.generationContext.pillarsUsed.length}</em>
                </div>
                <div>
                  <span>
                    <b>Brand voice</b>
                  </span>
                  <em>{workspaceDraft.generationContext.brandVoiceSnapshot.traits.join(", ") || "None"}</em>
                </div>
                <div>
                  <span>
                    <b>Audience</b>
                  </span>
                  <em>{workspaceDraft.generationContext.audienceSnapshot.segment ?? "Not available"}</em>
                </div>
                <div>
                  <span>
                    <b>Content type</b>
                  </span>
                  <em>{CONTENT_TYPE_LABELS[workspaceDraft.contentType]}</em>
                </div>
                <div>
                  <span>
                    <b>Channel</b>
                  </span>
                  <em>{workspaceDraft.generationContext.channel ?? "Not set"}</em>
                </div>
                <div>
                  <span>
                    <b>Variants</b>
                  </span>
                  <em>{workspaceVariants.length}</em>
                </div>
              </div>
            </div>
          </div>
          <div className="page-title-action">
            <em className={`status ${contentStatusTone(workspaceDraft.status)}`}>{contentStatusLabel(workspaceDraft.status)}</em>
            {isWorkspaceDraftEditable && (
              <button className="button primary" disabled={!workspaceDraft.selectedVariantId || submitting} onClick={() => void handleSubmitForReview()}>
                {submitting ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Submit for Review
              </button>
            )}
          </div>
          {isWorkspaceDraftEditable && !workspaceDraft.selectedVariantId && <p className="ai-callout">Select a variant above before submitting this draft for review.</p>}
          {workspaceDraft.status === "approved" && <p className="ai-callout">Approved for publishing -- ready for your team's own publishing workflow. Content Generation does not publish or send anything automatically.</p>}
        </Panel>
      )}

      <Panel title="Review Queue" kicker="Draft · In Review · Changes Requested · Approved">
        {decisionError && (
          <div className="form-alert error">
            <X size={16} />
            {decisionError}
          </div>
        )}
        <div className="lead-filter-bar">
          {(["all", "draft", "in_review", "changes_requested", "approved"] as const).map((status) => (
            <button key={status} className={`button ${reviewFilter === status ? "primary" : "secondary"}`} onClick={() => setReviewFilter(status)} type="button">
              {status === "all" ? "All" : contentStatusLabel(status)}
            </button>
          ))}
        </div>
        {filteredDrafts.length === 0 ? (
          <Empty icon={Check} title="Nothing here" text="No content drafts match this filter." />
        ) : (
          <div className="page-inventory-table lead-table">
            <table>
              <thead>
                <tr>
                  <th>Content</th>
                  <th>Type</th>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDrafts.map((draftItem) => (
                  <tr key={draftItem.id}>
                    <td>{draftItem.name}</td>
                    <td>{CONTENT_TYPE_LABELS[draftItem.contentType]}</td>
                    <td>{campaignNameById(draftItem.campaignId)}</td>
                    <td>
                      <em className={`status ${contentStatusTone(draftItem.status)}`}>{contentStatusLabel(draftItem.status)}</em>
                    </td>
                    <td>{new Date(draftItem.updatedAt).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        <button className="button secondary" onClick={() => void openDraft(draftItem.id)} type="button">
                          Open
                        </button>
                        {draftItem.status === "in_review" && (
                          <>
                            <input
                              placeholder="Note for changes…"
                              value={decisionNoteByDraft[draftItem.id] ?? ""}
                              onChange={(event) => setDecisionNoteByDraft((prev) => ({ ...prev, [draftItem.id]: event.target.value }))}
                              style={{ width: 140 }}
                            />
                            <button className="button secondary" disabled={decidingDraftId === draftItem.id} onClick={() => void handleDecide(draftItem.id, "changes_requested")} type="button">
                              Request Changes
                            </button>
                            <button className="button primary" disabled={decidingDraftId === draftItem.id} onClick={() => void handleDecide(draftItem.id, "approved")} type="button">
                              {decidingDraftId === draftItem.id ? <LoaderCircle className="spin" size={15} /> : "Approve"}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

// Performance Optimisation -- closes the Plan -> Execute -> Measure ->
// Analyze -> Learn -> Recommend loop. There is no real tracked campaign/
// content/channel EXECUTION performance data in Clintra (nothing publishes
// or sends), so this agent is honestly scoped to what's real: pipeline/
// revenue, win-loss and lead-conversion trends, Clintra's own planning-
// pipeline throughput, segment performance, and (when connected) site-wide
// Google Analytics traffic. It only recommends -- humans review and
// approve before anything feeds back into the next planning cycle.

function performanceHealthTone(health: OverallHealth): "complete" | "pending" | "error" | "neutral" {
  if (health === "on_track") return "complete";
  if (health === "needs_attention") return "pending";
  if (health === "at_risk") return "error";
  return "neutral";
}
function performanceHealthLabel(health: OverallHealth): string {
  if (health === "on_track") return "On Track";
  if (health === "needs_attention") return "Needs Attention";
  if (health === "at_risk") return "At Risk";
  return "Insufficient Data";
}
// A Record, not an if-chain -- every DestinationAgent key is required, so a
// value added to the union without a label here is a compile error instead
// of silently rendering as "Unassigned" in production.
const DESTINATION_AGENT_LABELS: Record<DestinationAgent, string> = {
  "marketing-strategy": "Marketing Strategy",
  "campaign-planning": "Campaign Planning",
  "sales-planning": "Sales Planning",
  "revenue-orchestration": "Revenue Orchestration",
  "content-generation": "Content Generation",
  "revenue-forecasting": "Revenue Forecasting",
};
function destinationAgentLabel(agent: DestinationAgent | null): string {
  return agent ? DESTINATION_AGENT_LABELS[agent] : "Unassigned";
}
function recommendationCategoryLabel(category: RecommendationCategory): string {
  return category.charAt(0).toUpperCase() + category.slice(1);
}
function recommendationCategoryTone(category: RecommendationCategory): "complete" | "pending" | "error" | "neutral" {
  if (category === "continue") return "complete";
  if (category === "reconsider") return "error";
  if (category === "test" || category === "improve") return "pending";
  return "neutral";
}
function rootCauseConfidenceTone(confidence: ConfidenceLevel): "complete" | "pending" | "neutral" {
  if (confidence === "high") return "complete";
  if (confidence === "medium") return "pending";
  return "neutral";
}
function performanceRecommendationStatusTone(status: PerformanceRecommendation["status"]): "complete" | "pending" | "error" | "neutral" {
  if (status === "approved") return "complete";
  if (status === "rejected") return "error";
  if (status === "expired") return "neutral";
  return "pending";
}
function experimentRecommendationLabel(recommendation: ExperimentRecommendationType): string {
  if (recommendation === "run_experiment") return "Run experiment";
  if (recommendation === "gather_more_evidence") return "Gather more evidence";
  if (recommendation === "monitor") return "Monitor";
  return "Do not prioritise now";
}
function experimentRecommendationTone(recommendation: ExperimentRecommendationType): "complete" | "pending" | "error" | "neutral" {
  if (recommendation === "run_experiment") return "complete";
  if (recommendation === "gather_more_evidence") return "pending";
  if (recommendation === "do_not_prioritise_now") return "error";
  return "neutral";
}
function priorityBucketLabel(bucket: PriorityBucket): string {
  if (bucket === "run_next") return "Run next";
  if (bucket === "validate_further") return "Validate further";
  if (bucket === "monitor") return "Monitor";
  return "Deprioritise";
}
function priorityBucketTone(bucket: PriorityBucket): "complete" | "pending" | "error" | "neutral" {
  if (bucket === "run_next") return "complete";
  if (bucket === "validate_further") return "pending";
  if (bucket === "deprioritise") return "error";
  return "neutral";
}
function hypothesisOutcomeLabel(outcome: HypothesisOutcome): string {
  if (outcome === "supported") return "Supported";
  if (outcome === "not_supported") return "Not supported";
  return "Inconclusive";
}
function hypothesisOutcomeTone(outcome: HypothesisOutcome): "complete" | "pending" | "error" | "neutral" {
  if (outcome === "supported") return "complete";
  if (outcome === "not_supported") return "error";
  return "neutral";
}
// "iterate"/"retest" are never returned by the backend today (they'd require
// tracking guardrail metrics as real measured KPIs, which Clintra doesn't
// do) -- kept here only so the label is defined if the type is ever
// populated more richly later, matching how Reputation Intelligence's
// "recovering" trend state is handled in its own label lookup.
function recommendedActionLabel(action: RecommendedAction): string {
  if (action === "scale") return "Scale";
  if (action === "iterate") return "Iterate";
  if (action === "retest") return "Retest";
  if (action === "stop") return "Stop";
  return "Investigate further";
}

function PerformanceOptimisationView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "performance-optimisation");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState<PerformanceAnalysisResult | null>(null);
  const [history, setHistory] = useState<PerformanceAnalysisHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showOutcomes, setShowOutcomes] = useState(false);
  const [outcomes, setOutcomes] = useState<RecommendationOutcome[] | null>(null);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState("");
  const [rejectNoteByRecommendation, setRejectNoteByRecommendation] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    loadPerformanceAnalysis(session.session.token, workspace.tenantId)
      .then((result) => setAnalysis(result.hasRun ? result : null))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Performance Optimisation."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  const loadOutcomes = () => {
    setShowOutcomes(true);
    loadRecommendationOutcomes(session.session.token, workspace.tenantId)
      .then((result) => setOutcomes(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load recommendation outcomes."));
  };

  const loadHistory = () => {
    setShowHistory(true);
    loadPerformanceAnalysisHistory(session.session.token, workspace.tenantId)
      .then((result) => setHistory(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load analysis history."));
  };

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Performance Optimisation Agent</span>
          <h1>Performance Optimisation isn't active yet.</h1>
        </div>
        <Panel title="Activate Performance Optimisation">
          <Empty icon={TrendingUp} title="Agent not active" text="Activate the Performance Optimisation agent from Agents & Entitlements to analyze what happened after plans and campaigns were executed and recommend what to change next." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Performance Optimisation">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Performance Optimisation">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Performance Optimisation workspace…" />
      </Panel>
    );

  const handleRun = async () => {
    setRunning(true);
    setRunError("");
    try {
      const result = await runPerformanceAnalysis(session.session.token, workspace.tenantId);
      setAnalysis(result);
      setShowHistory(false);
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : "The performance analysis could not be generated.");
    } finally {
      setRunning(false);
    }
  };

  const handleDecide = async (id: string, decision: "approved" | "rejected") => {
    setDecidingId(id);
    setDecisionError("");
    try {
      const updated = await decidePerformanceRecommendation(session.session.token, workspace.tenantId, id, decision, rejectNoteByRecommendation[id]);
      setAnalysis((prev) => (prev ? { ...prev, recommendations: prev.recommendations.map((rec) => (rec.id === updated.id ? updated : rec)) } : prev));
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingId(null);
    }
  };

  if (!analysis) {
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Performance Optimisation Agent</span>
          <h1>
            Performance Optimisation
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Analyzes what actually happened after plans and campaigns were executed, and recommends what to change in the next planning cycle.</span>
            </span>
          </h1>
        </div>
        <Panel title="Run Analysis">
          <Empty
            icon={TrendingUp}
            title="Not enough performance data yet"
            text="This agent analyzes real pipeline, revenue, lead-conversion, and planning-throughput data -- it does not fabricate campaign or content engagement metrics Clintra doesn't track. Run an analysis to see what it can determine from your current data (campaign results, pipeline outcomes, and revenue outcomes)."
          />
          <p className="ai-callout">Performance Optimisation only recommends -- it never publishes, launches, pauses, or modifies a live campaign, sales plan, or strategy. Every recommendation is reviewed by your team before it's incorporated into the next plan.</p>
          {runError && (
            <div className="form-alert error">
              <X size={16} />
              {runError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={running} onClick={() => void handleRun()}>
              {running ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Run Analysis
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <div className="page-title-action" style={{ marginBottom: 4 }}>
            <span className="overline">Performance Optimisation Agent</span>
            <em className={`status ${performanceHealthTone(analysis.overallHealth)}`}>{performanceHealthLabel(analysis.overallHealth)}</em>
          </div>
          <h1>
            Performance Optimisation
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Last analyzed {new Date(analysis.startedAt).toLocaleString()} -- {analysis.period.current.label} vs {analysis.period.previous.label}{analysis.period.widened ? " (window widened due to limited data)" : ""}</span>
            </span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="button secondary" onClick={loadHistory}>
            View History
          </button>
          <button className="button secondary" onClick={loadOutcomes}>
            View Outcomes
          </button>
          <button className="button primary" disabled={running} onClick={() => void handleRun()}>
            {running ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Run Analysis
          </button>
        </div>
      </div>
      {runError && (
        <div className="form-alert error">
          <X size={16} />
          {runError}
        </div>
      )}

      <div className="metrics metrics-4">
        <Metric icon={TrendingUp} label="Overall health" value={performanceHealthLabel(analysis.overallHealth)} meta="Based on detected underperformance" />
        <Metric icon={AlertTriangle} label="Underperformance" value={String(analysis.underperformance.length)} meta="Findings this run" />
        <Metric icon={Lightbulb} label="Recommendations" value={String(analysis.recommendations.length)} meta={`${analysis.recommendations.filter((r) => r.status === "pending").length} pending review`} />
        <Metric icon={Target} label="Pipeline vs target" value={analysis.pipeline.available ? `${analysis.pipeline.currentRevenue ?? 0} / ${analysis.pipeline.target ?? "not set"}` : "Not available"} meta={analysis.pipeline.available ? String(analysis.pipeline.health) : "Revenue Orchestration hasn't run"} />
      </div>

      <Panel title="Performance Summary">
        <div className="source-list">
          <div>
            <span>
              <b>Win rate trend</b>
            </span>
            <em>{analysis.winLoss.available ? `${analysis.winLoss.currentWinRate?.toFixed(1)}% (prev ${analysis.winLoss.previousWinRate?.toFixed(1) ?? "n/a"}%)` : `Insufficient data -- ${analysis.winLoss.reason}`}</em>
          </div>
          <div>
            <span>
              <b>Lead conversion trend</b>
            </span>
            <em>{analysis.leadConversion.available ? `${analysis.leadConversion.currentRate?.toFixed(1)}% (prev ${analysis.leadConversion.previousRate?.toFixed(1) ?? "n/a"}%)` : `Insufficient data -- ${analysis.leadConversion.reason}`}</em>
          </div>
          <div>
            <span>
              <b>Planning throughput</b>
            </span>
            <em>{analysis.planningThroughput.available ? `${analysis.planningThroughput.initiativesAccepted} initiatives accepted, ${analysis.planningThroughput.coverageGapCount} open coverage gaps` : "Not available"}</em>
          </div>
          <div>
            <span>
              <b>Website traffic (Google Analytics)</b>
            </span>
            <em>{analysis.websiteTraffic.available ? `${analysis.websiteTraffic.sessions} sessions, ${analysis.websiteTraffic.conversions} conversions (site-wide, not campaign-attributed)` : analysis.websiteTraffic.reason}</em>
          </div>
        </div>
        {analysis.segmentPerformance.length > 0 && (
          <>
            <span className="overline" style={{ marginTop: 14, display: "block" }}>
              Segment Performance
            </span>
            <div className="source-list">
              {analysis.segmentPerformance.map((segment, index) => (
                <div key={index}>
                  <span>
                    <b>{segment.segmentLabel}</b> <em>({segment.dimension === "territory" ? "Territory" : "Opportunity Source -- CRM field, not a marketing channel"})</em>
                  </span>
                  <em>{segment.winRate !== null ? `${segment.winRate.toFixed(1)}% win rate, ${segment.closedCount} closed` : "No closed opportunities this period"}</em>
                </div>
              ))}
            </div>
          </>
        )}
      </Panel>

      {analysis.underperformance.length > 0 && (
        <Panel title="Underperformance">
          <div className="page-stack">
            {analysis.underperformance.map((finding) => (
              <div key={finding.key} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>{finding.title}</b>
                  <em className={`status ${finding.certainty === "known" ? "error" : "pending"}`}>{finding.certainty === "known" ? "arithmetic fact" : "hypothesis"}</em>
                </div>
                <p>
                  <b>{finding.metric}:</b> {finding.currentValue} ({finding.change})
                </p>
                <p>
                  <b>Comparison:</b> {finding.comparison}
                </p>
                {finding.quarterOverQuarter && (
                  <p className="ai-callout">
                    Quarter-over-quarter:{" "}
                    {finding.quarterOverQuarter.available
                      ? `${finding.quarterOverQuarter.changePct?.toFixed(1)}% -- ${finding.quarterOverQuarter.sustained ? "consistent with a sustained trend" : "does not confirm a sustained trend, may be a short-term change"}`
                      : `not available -- ${finding.quarterOverQuarter.reason}`}
                  </p>
                )}
                {finding.rootCauseHypotheses.length > 0 ? (
                  <div>
                    <span className="overline">Ranked root-cause hypotheses</span>
                    <ul className="score-why">
                      {finding.rootCauseHypotheses.map((hypothesis, index) => (
                        <li key={index}>
                          <em className={`status ${rootCauseConfidenceTone(hypothesis.confidence)}`}>{hypothesis.confidence}</em> {hypothesis.cause}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="ai-callout">Insufficient data to determine the likely cause.</p>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {analysis.patterns.length > 0 && (
        <Panel title="Patterns">
          <ul className="score-why">
            {analysis.patterns.map((pattern) => (
              <li key={pattern.key}>
                <b>{pattern.title}:</b> {pattern.description}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="Recommendations" kicker="Reviewed by your team before being incorporated into future plans">
        {decisionError && (
          <div className="form-alert error">
            <X size={16} />
            {decisionError}
          </div>
        )}
        <div className="page-stack">
          {analysis.recommendations.map((recommendation) => (
            <div key={recommendation.id} className="website-recommendation-card">
              <div className="page-title-action">
                <b>{recommendation.title}</b>
                <div style={{ display: "flex", gap: 6 }}>
                  {recommendation.category && <em className={`status ${recommendationCategoryTone(recommendation.category)}`}>{recommendationCategoryLabel(recommendation.category)}</em>}
                  <em className={`status ${recommendation.priority === "high" ? "error" : recommendation.priority === "medium" ? "pending" : "neutral"}`}>{recommendation.priority} priority</em>
                  <em className={`status ${performanceRecommendationStatusTone(recommendation.status)}`}>{recommendation.status}</em>
                </div>
              </div>
              <p>{recommendation.description}</p>
              <p>
                <b>Why:</b> {recommendation.reason}
              </p>
              {recommendation.evidence.length > 0 && (
                <ul className="score-why">
                  {recommendation.evidence.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              )}
              <div className="source-list">
                <div>
                  <span>Confidence</span>
                  <em className={`status ${confidenceTone(recommendation.confidence)}`}>{recommendation.confidence}</em>
                </div>
                <div>
                  <span>Send to</span>
                  <em>{destinationAgentLabel(recommendation.destinationAgent)}</em>
                </div>
              </div>
              {recommendation.status === "pending" && (
                <div className="page-stack">
                  <input placeholder="Note (optional, shown on reject)" value={rejectNoteByRecommendation[recommendation.id] ?? ""} onChange={(event) => setRejectNoteByRecommendation((prev) => ({ ...prev, [recommendation.id]: event.target.value }))} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="button secondary" disabled={decidingId === recommendation.id} onClick={() => void handleDecide(recommendation.id, "rejected")}>
                      Reject
                    </button>
                    <button className="button primary" disabled={decidingId === recommendation.id} onClick={() => void handleDecide(recommendation.id, "approved")}>
                      {decidingId === recommendation.id ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Planning Feedback" kicker="Recommendations grouped by the planning agent that should act on them -- only shown when there's a relevant finding">
        <div className="page-stack">
          {(Object.entries(analysis.recommendedAgentFeedback) as Array<[DestinationAgent, AgentFeedbackGroup]>).map(([agent, group]) => (
            <div key={agent} className="source-list">
              <div>
                <span>
                  <b>{destinationAgentLabel(agent)}</b>
                </span>
                <em>{group.summary}</em>
              </div>
            </div>
          ))}
          {analysis.unassignedFeedback && (
            <div className="source-list">
              <div>
                <span>
                  <b>Unassigned</b>
                </span>
                <em>{analysis.unassignedFeedback.summary}</em>
              </div>
            </div>
          )}
          {Object.keys(analysis.recommendedAgentFeedback).length === 0 && !analysis.unassignedFeedback && <p className="ai-callout">No recommendations to route this run.</p>}
        </div>
      </Panel>

      {showOutcomes && (
        <Panel title="Recommendation Outcomes" kicker="Correlational, not causal -- compares real performance before and after each approved recommendation's baseline analysis">
          {!outcomes ? (
            <Empty icon={LoaderCircle} title="Loading" text="Loading recommendation outcomes…" />
          ) : outcomes.length === 0 ? (
            <Empty icon={Lightbulb} title="No approved recommendations yet" text="Outcomes appear here once a recommendation from this agent has been approved." />
          ) : (
            <div className="page-stack">
              {outcomes.map((outcome) => (
                <div key={outcome.recommendationId} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{outcome.title}</b>
                    {outcome.category && <em className={`status ${recommendationCategoryTone(outcome.category)}`}>{recommendationCategoryLabel(outcome.category)}</em>}
                  </div>
                  <p>
                    <b>Baseline analysis:</b> {new Date(outcome.baselineRunAt).toLocaleString()}
                  </p>
                  {!outcome.available ? (
                    <p className="ai-callout">{outcome.reason}</p>
                  ) : (
                    <div className="source-list">
                      <div>
                        <span>Overall health</span>
                        <em>
                          {outcome.overallHealthBefore ? performanceHealthLabel(outcome.overallHealthBefore) : "n/a"} -&gt; {outcome.overallHealthAfter ? performanceHealthLabel(outcome.overallHealthAfter) : "n/a"}
                        </em>
                      </div>
                      <div>
                        <span>Win rate</span>
                        <em>
                          {outcome.winRateBefore?.toFixed(1) ?? "n/a"}% -&gt; {outcome.winRateAfter?.toFixed(1) ?? "n/a"}%
                        </em>
                      </div>
                      <div>
                        <span>Lead conversion</span>
                        <em>
                          {outcome.leadConversionRateBefore?.toFixed(1) ?? "n/a"}% -&gt; {outcome.leadConversionRateAfter?.toFixed(1) ?? "n/a"}%
                        </em>
                      </div>
                    </div>
                  )}
                  {outcome.available && <p className="ai-callout">Performance changed following this decision -- this is a correlation, not a confirmed cause.</p>}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {analysis.dataQualityNotes.length > 0 && (
        <Panel title="Data Quality Notes">
          <ul className="score-why">
            {analysis.dataQualityNotes.map((note, index) => (
              <li key={index}>{note.text}</li>
            ))}
          </ul>
        </Panel>
      )}

      {showHistory && (
        <Panel title="Analysis History">
          {history.length === 0 ? (
            <Empty icon={LoaderCircle} title="Loading" text="Loading analysis history…" />
          ) : (
            <div className="page-stack">
              {history.map((entry) => (
                <div key={entry.runId} className="source-list">
                  <div>
                    <span>
                      <b>{new Date(entry.startedAt).toLocaleString()}</b>
                    </span>
                    <em>{entry.periodLabel}</em>
                  </div>
                  <div>
                    <span>Health</span>
                    <em className={`status ${performanceHealthTone(entry.overallHealth)}`}>{performanceHealthLabel(entry.overallHealth)}</em>
                  </div>
                  <div>
                    <span>Findings</span>
                    <em>{entry.underperformanceCount}</em>
                  </div>
                  <div>
                    <span>Recommendations</span>
                    <em>{entry.recommendations.filter((r) => r.status === "approved").length}/{entry.recommendationCount} approved</em>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

// Conversion Optimisation -- proposes measurable conversion experiments from
// what Clintra can genuinely observe: a real site-wide Google Analytics
// conversion-rate trend (GA is synced one row per day, so a real current-vs-
// previous-period comparison is possible, gated by sufficientHistory so a
// freshly-connected integration never gets a fabricated trend), real
// page-level structural crawl signal from Website Intelligence (never
// visitor behaviour -- Clintra has no click/funnel/form/device telemetry),
// and Performance Optimisation's own CRM trend shown read-only as
// corroborating context. It never re-detects PO's win-rate/lead-conversion
// declines, and it never executes, launches, or declares a winning
// experiment -- every experiment is a proposal for human review.
function ConversionOptimisationView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "conversion-optimisation");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState<ConversionAnalysisResult | null>(null);
  const [history, setHistory] = useState<ConversionAnalysisHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showOutcomes, setShowOutcomes] = useState(false);
  const [outcomes, setOutcomes] = useState<ConversionExperimentOutcome[] | null>(null);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState("");
  const [rejectNoteByExperiment, setRejectNoteByExperiment] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    loadConversionAnalysis(session.session.token, workspace.tenantId)
      .then((result) => setAnalysis(result.hasRun ? result : null))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Conversion Optimisation."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  const loadOutcomes = () => {
    setShowOutcomes(true);
    loadConversionExperimentOutcomes(session.session.token, workspace.tenantId)
      .then((result) => setOutcomes(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load experiment outcomes."));
  };

  const loadHistory = () => {
    setShowHistory(true);
    loadConversionAnalysisHistory(session.session.token, workspace.tenantId)
      .then((result) => setHistory(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load analysis history."));
  };

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Conversion Optimisation Agent</span>
          <h1>Conversion Optimisation isn't active yet.</h1>
        </div>
        <Panel title="Activate Conversion Optimisation">
          <Empty icon={FlaskConical} title="Agent not active" text="Activate the Conversion Optimisation agent from Agents & Entitlements to turn real conversion-rate and website-structural evidence into prioritised experiments." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Conversion Optimisation">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Conversion Optimisation">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Conversion Optimisation workspace…" />
      </Panel>
    );

  const handleRun = async () => {
    setRunning(true);
    setRunError("");
    try {
      const result = await runConversionAnalysis(session.session.token, workspace.tenantId);
      setAnalysis(result);
      setShowHistory(false);
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : "The conversion analysis could not be generated.");
    } finally {
      setRunning(false);
    }
  };

  const handleDecide = async (id: string, decision: "approved" | "rejected") => {
    setDecidingId(id);
    setDecisionError("");
    try {
      const updated = await decideConversionExperiment(session.session.token, workspace.tenantId, id, decision, rejectNoteByExperiment[id]);
      setAnalysis((prev) => (prev ? { ...prev, experiments: prev.experiments.map((experiment) => (experiment.id === updated.id ? updated : experiment)) } : prev));
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingId(null);
    }
  };

  if (!analysis) {
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Conversion Optimisation Agent</span>
          <h1>
            Conversion Optimisation
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Turns your real site-wide conversion-rate trend and website structural signals into a prioritised backlog of measurable experiments.</span>
            </span>
          </h1>
        </div>
        <Panel title="Run Analysis">
          <Empty
            icon={FlaskConical}
            title="Not enough conversion data yet"
            text="This agent analyzes your real Google Analytics conversion-rate trend and Website Intelligence's structural crawl -- it does not fabricate CTA-click, form-start, device, or campaign-attribution data Clintra doesn't track. Run an analysis to see what it can determine from your current data."
          />
          <p className="ai-callout">Conversion Optimisation only proposes experiments -- it never publishes, launches, or declares a winning test. Every experiment is reviewed by your team before it's implemented.</p>
          {runError && (
            <div className="form-alert error">
              <X size={16} />
              {runError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={running} onClick={() => void handleRun()}>
              {running ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Run Analysis
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Conversion Optimisation Agent</span>
          <h1>
            Conversion Optimisation
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Last analyzed {new Date(analysis.startedAt).toLocaleString()}</span>
            </span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="button secondary" onClick={loadHistory}>
            View History
          </button>
          <button className="button secondary" onClick={loadOutcomes}>
            View Outcomes
          </button>
          <button className="button primary" disabled={running} onClick={() => void handleRun()}>
            {running ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Run Analysis
          </button>
        </div>
      </div>
      {runError && (
        <div className="form-alert error">
          <X size={16} />
          {runError}
        </div>
      )}

      <div className="metrics metrics-4">
        <Metric icon={TrendingUp} label="Site-wide conversion rate" value={analysis.conversionRate.available ? `${analysis.conversionRate.currentRatePct?.toFixed(1)}%` : "Not available"} meta={analysis.conversionRate.available ? `${analysis.conversionRate.changePct?.toFixed(1) ?? "n/a"}% vs previous period` : "Insufficient data"} />
        <Metric icon={AlertTriangle} label="Conversion opportunities" value={String(analysis.opportunities.length)} meta="Findings this run" />
        <Metric icon={FlaskConical} label="Experiments" value={String(analysis.experiments.length)} meta={`${analysis.experiments.filter((e) => e.status === "pending").length} pending review`} />
        <Metric icon={Target} label="Structural findings" value={String(analysis.structuralFindings.length)} meta="From the latest website crawl" />
      </div>

      <Panel title="Site-Wide Conversion Rate" kicker="Google Analytics -- site-wide only, never attributed to a page or campaign">
        <div className="source-list">
          <div>
            <span>
              <b>Conversion rate trend</b>
            </span>
            <em>{analysis.conversionRate.available ? `${analysis.conversionRate.currentRatePct?.toFixed(1)}% (prev ${analysis.conversionRate.previousRatePct?.toFixed(1) ?? "n/a"}%)` : `Insufficient data -- ${analysis.conversionRate.reason}`}</em>
          </div>
          <div>
            <span>
              <b>Sessions / conversions</b>
            </span>
            <em>{analysis.conversionRate.currentSessions !== null ? `${analysis.conversionRate.currentSessions} sessions, ${analysis.conversionRate.currentConversions} conversions this period` : "Not available"}</em>
          </div>
        </div>
      </Panel>

      <Panel title="Performance Optimisation Context" kicker="Read-only -- Conversion Optimisation never recomputes or re-scores these numbers">
        {analysis.performanceContext.available ? (
          <div className="source-list">
            <div>
              <span>
                <b>Win rate</b>
              </span>
              <em>{analysis.performanceContext.winLoss ? `${analysis.performanceContext.winLoss.currentWinRate?.toFixed(1)}% (${analysis.performanceContext.winLoss.changePct?.toFixed(1) ?? "n/a"}% change)` : "Not available"}</em>
            </div>
            <div>
              <span>
                <b>Lead conversion</b>
              </span>
              <em>{analysis.performanceContext.leadConversion ? `${analysis.performanceContext.leadConversion.currentRate?.toFixed(1)}% (${analysis.performanceContext.leadConversion.changePct?.toFixed(1) ?? "n/a"}% change)` : "Not available"}</em>
            </div>
          </div>
        ) : (
          <p className="ai-callout">{analysis.performanceContext.reason}</p>
        )}
      </Panel>

      <Panel title="Lead Quality Context" kicker="Read-only downstream-quality signal from Lead Intelligence -- never recomputed here">
        {analysis.leadQualityContext.available ? (
          <div className="source-list">
            <div>
              <span>Average lead score</span>
              <em>{analysis.leadQualityContext.averageScore ?? "n/a"}</em>
            </div>
            <div>
              <span>Band distribution</span>
              <em>
                {analysis.leadQualityContext.highBandCount ?? 0} high / {analysis.leadQualityContext.mediumBandCount ?? 0} medium / {analysis.leadQualityContext.lowBandCount ?? 0} low ({analysis.leadQualityContext.scoredCount ?? 0} scored)
              </em>
            </div>
          </div>
        ) : (
          <p className="ai-callout">{analysis.leadQualityContext.reason}</p>
        )}
      </Panel>

      <Panel title="Buying Intent Context" kicker="Read-only target-audience signal from Buying Intent -- never recomputed here">
        {analysis.buyingIntentContext.available ? (
          <div className="source-list">
            <div>
              <span>High-intent accounts</span>
              <em>
                {analysis.buyingIntentContext.highIntentCount ?? 0} of {analysis.buyingIntentContext.scoredAccountCount ?? 0} scored
              </em>
            </div>
            <div>
              <span>Trend</span>
              <em>
                {analysis.buyingIntentContext.risingCount ?? 0} rising / {analysis.buyingIntentContext.fallingCount ?? 0} falling / {analysis.buyingIntentContext.newlyActiveCount ?? 0} newly active
              </em>
            </div>
          </div>
        ) : (
          <p className="ai-callout">{analysis.buyingIntentContext.reason}</p>
        )}
      </Panel>

      {analysis.opportunities.length > 0 && (
        <Panel title="Conversion Opportunities">
          <div className="page-stack">
            {analysis.opportunities.map((opportunity) => (
              <div key={opportunity.key} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>{opportunity.title}</b>
                  <div style={{ display: "flex", gap: 6 }}>
                    <em className="status neutral">Reach: {opportunity.reach}</em>
                    <em className={`status ${opportunity.certainty === "known" ? "error" : "pending"}`}>{opportunity.certainty === "known" ? "arithmetic fact" : "hypothesis"}</em>
                  </div>
                </div>
                <p>
                  <b>{opportunity.metric}:</b> {opportunity.currentValue} ({opportunity.change})
                </p>
                <p>
                  <b>Comparison:</b> {opportunity.comparison}
                </p>
                {opportunity.evidence.length > 0 && (
                  <ul className="score-why">
                    {opportunity.evidence.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {analysis.structuralFindings.length > 0 && (
        <Panel title="Website Structural Findings" kicker="Technical/crawl signal only -- not visitor behaviour">
          <ul className="score-why">
            {analysis.structuralFindings.map((finding, index) => (
              <li key={index}>
                <b>{finding.title}:</b> {finding.issue}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="Experiments" kicker="Reviewed by your team before implementation">
        {decisionError && (
          <div className="form-alert error">
            <X size={16} />
            {decisionError}
          </div>
        )}
        {analysis.experiments.length === 0 ? (
          <p className="ai-callout">No experiments proposed this run.</p>
        ) : (
          <div className="page-stack">
            {analysis.experiments.map((experiment) => (
              <div key={experiment.id} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>{experiment.title}</b>
                  <div style={{ display: "flex", gap: 6 }}>
                    <em className={`status ${priorityBucketTone(experiment.priorityBucket)}`}>{priorityBucketLabel(experiment.priorityBucket)}</em>
                    <em className={`status ${experimentRecommendationTone(experiment.recommendation)}`}>{experimentRecommendationLabel(experiment.recommendation)}</em>
                    <em className={`status ${performanceRecommendationStatusTone(experiment.status)}`}>{experiment.status}</em>
                  </div>
                </div>
                <p>
                  <b>Problem:</b> {experiment.problem}
                </p>
                <p>
                  <b>Hypothesis:</b> {experiment.hypothesis}
                </p>
                <div className="source-list">
                  <div>
                    <span>Control</span>
                    <em>{experiment.control}</em>
                  </div>
                  <div>
                    <span>Variant</span>
                    <em>{experiment.variant}</em>
                  </div>
                  <div>
                    <span>Target audience</span>
                    <em>{experiment.targetAudience}</em>
                  </div>
                  <div>
                    <span>Primary metric</span>
                    <em>{experiment.primaryMetric}</em>
                  </div>
                  {experiment.baseline && (
                    <div>
                      <span>Baseline</span>
                      <em>{experiment.baseline}</em>
                    </div>
                  )}
                </div>
                {experiment.guardrailMetrics.length > 0 && (
                  <p>
                    <b>Guardrails:</b> {experiment.guardrailMetrics.join(", ")}
                  </p>
                )}
                {experiment.secondaryMetrics.length > 0 && (
                  <p>
                    <b>Secondary metrics:</b> {experiment.secondaryMetrics.join(", ")}
                  </p>
                )}
                <p>
                  <b>Measurement plan:</b> {experiment.measurementPlan}
                </p>
                {experiment.dependencies.length > 0 && (
                  <p>
                    <b>Dependencies:</b> {experiment.dependencies.join(", ")}
                  </p>
                )}
                <div className="source-list">
                  <div>
                    <span>Confidence</span>
                    <em className={`status ${confidenceTone(experiment.confidence)}`}>{experiment.confidence}</em>
                  </div>
                  <div>
                    <span>Expected impact</span>
                    <em>{experiment.expectedImpact}</em>
                  </div>
                  <div>
                    <span>Effort</span>
                    <em>{experiment.effort}</em>
                  </div>
                  <div>
                    <span>Reach</span>
                    <em>{experiment.reach}</em>
                  </div>
                </div>
                {experiment.status === "pending" && (
                  <div className="page-stack">
                    <input placeholder="Note (optional, shown on reject)" value={rejectNoteByExperiment[experiment.id] ?? ""} onChange={(event) => setRejectNoteByExperiment((prev) => ({ ...prev, [experiment.id]: event.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="button secondary" disabled={decidingId === experiment.id} onClick={() => void handleDecide(experiment.id, "rejected")}>
                        Reject
                      </button>
                      <button className="button primary" disabled={decidingId === experiment.id} onClick={() => void handleDecide(experiment.id, "approved")}>
                        {decidingId === experiment.id ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      {showOutcomes && (
        <Panel title="Experiment Outcomes" kicker="Correlational, not causal -- compares the real site-wide conversion rate before and after each approved experiment's baseline analysis">
          {!outcomes ? (
            <Empty icon={LoaderCircle} title="Loading" text="Loading experiment outcomes…" />
          ) : outcomes.length === 0 ? (
            <Empty icon={Lightbulb} title="No approved experiments yet" text="Outcomes appear here once an experiment from this agent has been approved." />
          ) : (
            <div className="page-stack">
              {outcomes.map((outcome) => (
                <div key={outcome.experimentId} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{outcome.title}</b>
                    <em className={`status ${experimentRecommendationTone(outcome.recommendation)}`}>{experimentRecommendationLabel(outcome.recommendation)}</em>
                  </div>
                  <p>
                    <b>Baseline analysis:</b> {new Date(outcome.baselineRunAt).toLocaleString()}
                  </p>
                  {!outcome.available ? (
                    <p className="ai-callout">{outcome.reason}</p>
                  ) : (
                    <div className="source-list">
                      <div>
                        <span>Site-wide conversion rate</span>
                        <em>
                          {outcome.conversionRateBefore?.toFixed(1) ?? "n/a"}% -&gt; {outcome.conversionRateAfter?.toFixed(1) ?? "n/a"}%
                        </em>
                      </div>
                      {outcome.hypothesisSupported && (
                        <div>
                          <span>Hypothesis</span>
                          <em className={`status ${hypothesisOutcomeTone(outcome.hypothesisSupported)}`}>{hypothesisOutcomeLabel(outcome.hypothesisSupported)}</em>
                        </div>
                      )}
                      {outcome.recommendedAction && (
                        <div>
                          <span>Recommended action</span>
                          <em>{recommendedActionLabel(outcome.recommendedAction)}</em>
                        </div>
                      )}
                    </div>
                  )}
                  {outcome.available && <p className="ai-callout">The conversion rate changed following this experiment's approval -- this is a correlation, not a confirmed cause.</p>}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {analysis.dataQualityNotes.length > 0 && (
        <Panel title="Data Quality Notes">
          <ul className="score-why">
            {analysis.dataQualityNotes.map((note, index) => (
              <li key={index}>{note.text}</li>
            ))}
          </ul>
        </Panel>
      )}

      {showHistory && (
        <Panel title="Analysis History">
          {history.length === 0 ? (
            <Empty icon={LoaderCircle} title="Loading" text="Loading analysis history…" />
          ) : (
            <div className="page-stack">
              {history.map((entry) => (
                <div key={entry.runId} className="source-list">
                  <div>
                    <span>
                      <b>{new Date(entry.startedAt).toLocaleString()}</b>
                    </span>
                  </div>
                  <div>
                    <span>Opportunities</span>
                    <em>{entry.opportunityCount}</em>
                  </div>
                  <div>
                    <span>Experiments</span>
                    <em>{entry.experiments.filter((e) => e.status === "approved").length}/{entry.experimentCount} approved</em>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

function reputationSeverityLabel(severity: ReputationSeverity): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}
function reputationSeverityTone(severity: ReputationSeverity): "complete" | "pending" | "error" | "neutral" {
  if (severity === "critical" || severity === "high") return "error";
  if (severity === "medium") return "pending";
  return "neutral";
}
const REPUTATION_ALERT_TYPE_LABELS: Record<ReputationAlertType, string> = {
  emerging_negative_theme: "Emerging Negative Theme",
  reputation_deterioration: "Reputation Deterioration",
  trust_or_credibility_concern: "Trust or Credibility Concern",
  emerging_positive_momentum: "Emerging Positive Momentum",
  conflicting_reputation_signals: "Conflicting Reputation Signals",
};
function reputationAlertTypeLabel(alertType: ReputationAlertType): string {
  return REPUTATION_ALERT_TYPE_LABELS[alertType];
}
const REPUTATION_TREND_LABELS: Record<ReputationTrendState, string> = {
  improving: "Improving",
  stable: "Stable",
  emerging_concern: "Emerging Concern",
  deteriorating: "Deteriorating",
  recovering: "Recovering",
  insufficient_evidence: "Insufficient Evidence",
};
function reputationTrendLabel(trend: ReputationTrendState): string {
  return REPUTATION_TREND_LABELS[trend];
}
function reputationTrendTone(trend: ReputationTrendState): "complete" | "pending" | "error" | "neutral" {
  if (trend === "improving" || trend === "recovering") return "complete";
  if (trend === "emerging_concern") return "pending";
  if (trend === "deteriorating") return "error";
  return "neutral";
}
const REPUTATION_SOURCE_LABELS: Record<ReputationSourceCategory, string> = {
  owned_website: "Owned Website",
  customer_reviews: "Customer Reviews",
  social_discussion: "Social Discussion",
  news_coverage: "News Coverage",
  crm_notes: "CRM / Support Notes",
};
function reputationSourceLabel(source: ReputationSourceCategory): string {
  return REPUTATION_SOURCE_LABELS[source];
}
const REPUTATION_MATURITY_LABELS: Record<ReputationTheme["maturity"], string> = {
  isolated: "Isolated signal",
  emerging: "Emerging pattern",
  established: "Established pattern",
};
function reputationMaturityLabel(maturity: ReputationTheme["maturity"]): string {
  return REPUTATION_MATURITY_LABELS[maturity];
}

// Reputation Intelligence -- a provenance-first agent that is deliberately
// thin today: Clintra has no review-platform, social-listening, or news-
// monitoring integration connected, so the only real signal source is
// whether the business's OWN website presents testimonials/case
// studies/trust signals (self-published evidence, never independent
// corroboration). Themes can only ever classify as "isolated" and alerts
// can only ever be empty until a second, independent source category is
// connected -- that's the honest, correct behaviour given real data, not a
// bug. The Source Coverage panel is the most important panel here.
function ReputationIntelligenceView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "reputation-intelligence");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState<ReputationAnalysisResult | null>(null);
  const [history, setHistory] = useState<ReputationAnalysisHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState("");
  const [rejectNoteByAlert, setRejectNoteByAlert] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    loadReputationAnalysis(session.session.token, workspace.tenantId)
      .then((result) => setAnalysis(result.hasRun ? result : null))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Reputation Intelligence."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  const loadHistory = () => {
    setShowHistory(true);
    loadReputationAnalysisHistory(session.session.token, workspace.tenantId)
      .then((result) => setHistory(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load analysis history."));
  };

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Reputation Intelligence Agent</span>
          <h1>Reputation Intelligence isn't active yet.</h1>
        </div>
        <Panel title="Activate Reputation Intelligence">
          <Empty icon={ShieldAlert} title="Agent not active" text="Activate the Reputation Intelligence agent from Agents & Entitlements to see what reputation signals Clintra can and cannot observe today." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Reputation Intelligence">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Reputation Intelligence">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Reputation Intelligence workspace…" />
      </Panel>
    );

  const handleRun = async () => {
    setRunning(true);
    setRunError("");
    try {
      const result = await runReputationAnalysis(session.session.token, workspace.tenantId);
      setAnalysis(result);
      setShowHistory(false);
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : "The reputation analysis could not be generated.");
    } finally {
      setRunning(false);
    }
  };

  const handleDecide = async (id: string, decision: "approved" | "rejected") => {
    setDecidingId(id);
    setDecisionError("");
    try {
      const updated = await decideReputationAlert(session.session.token, workspace.tenantId, id, decision, rejectNoteByAlert[id]);
      setAnalysis((prev) => (prev ? { ...prev, alerts: prev.alerts.map((alert) => (alert.id === updated.id ? updated : alert)) } : prev));
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingId(null);
    }
  };

  if (!analysis) {
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Reputation Intelligence Agent</span>
          <h1>
            Reputation Intelligence
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Surfaces owned and external reputation signals with provenance -- honestly, including what Clintra cannot see yet.</span>
            </span>
          </h1>
        </div>
        <Panel title="Run Analysis">
          <Empty
            icon={ShieldAlert}
            title="Not analyzed yet"
            text="This agent analyzes real signals only -- today that means your own website's published testimonials, case studies, and trust signals. Clintra has no review-platform, social-listening, or news-monitoring integration connected yet, so external reputation cannot be assessed. Run an analysis to see the honest picture."
          />
          <p className="ai-callout">Reputation Intelligence only surfaces and explains evidence -- it never publishes public responses, contacts customers, or escalates externally. Every alert is reviewed by your team.</p>
          {runError && (
            <div className="form-alert error">
              <X size={16} />
              {runError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={running} onClick={() => void handleRun()}>
              {running ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Run Analysis
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Reputation Intelligence Agent</span>
          <h1>
            Reputation Intelligence
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Last analyzed {new Date(analysis.startedAt).toLocaleString()}</span>
            </span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="button secondary" onClick={loadHistory}>
            View History
          </button>
          <button className="button primary" disabled={running} onClick={() => void handleRun()}>
            {running ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Run Analysis
          </button>
        </div>
      </div>
      {runError && (
        <div className="form-alert error">
          <X size={16} />
          {runError}
        </div>
      )}

      <div className="metrics metrics-4">
        <Metric icon={ShieldAlert} label="Trend" value={reputationTrendLabel(analysis.trend)} meta="Based on negative-signal volume run over run" />
        <Metric icon={AlertTriangle} label="Alerts" value={String(analysis.alerts.length)} meta={`${analysis.alerts.filter((a) => a.status === "pending").length} pending review`} />
        <Metric icon={Target} label="Sources connected" value={`${analysis.sourceCoverage.filter((s) => s.available).length}/${analysis.sourceCoverage.length}`} meta="Real source categories available" />
        <Metric icon={Lightbulb} label="AI narration" value={analysis.llmInvoked ? (analysis.llmAvailable ? "Generated" : "Unavailable") : "Not needed"} meta={analysis.llmInvoked ? "This run" : "No alert-worthy signals this run"} />
      </div>

      <Panel title="Source Coverage" kicker="What Reputation Intelligence can and cannot observe today -- an honest checklist, not a score">
        <div className="source-list">
          {analysis.sourceCoverage.map((entry) => (
            <div key={entry.source}>
              <span>
                <b>{reputationSourceLabel(entry.source)}</b>
              </span>
              <em className={`status ${entry.available ? "complete" : "neutral"}`}>{entry.available ? "Connected" : (entry.reason ?? "Not connected")}</em>
            </div>
          ))}
        </div>
      </Panel>

      {analysis.themes.length > 0 && (
        <Panel title="Reputation Themes">
          <div className="page-stack">
            {analysis.themes.map((theme) => (
              <div key={theme.key} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>{theme.title}</b>
                  <em className="status neutral">{reputationMaturityLabel(theme.maturity)}</em>
                </div>
                <p>
                  <b>Corroboration:</b> {theme.distinctSourceCount} distinct source {theme.distinctSourceCount === 1 ? "category" : "categories"}, {theme.signalCount} signal{theme.signalCount === 1 ? "" : "s"}
                </p>
                {theme.maturity === "isolated" && (
                  <p className="ai-callout">Isolated to a single source category -- not yet independent corroboration of a broader pattern.</p>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {analysis.signals.length > 0 && (
        <Panel title="Signals" kicker="Owned / self-published -- never independent external corroboration">
          <div className="page-stack">
            {analysis.signals.map((signal) => (
              <div key={signal.signalId} className="source-list">
                <div>
                  <span>
                    <b>{signal.sourceName}</b>
                  </span>
                  <em>{reputationSourceLabel(signal.sourceType)}</em>
                </div>
                <div>
                  <span>Evidence</span>
                  <em>{signal.evidence[0]}</em>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {analysis.alerts.length > 0 ? (
        <Panel title="Reputation Alerts" kicker="Reviewed by your team before any action is taken">
          {decisionError && (
            <div className="form-alert error">
              <X size={16} />
              {decisionError}
            </div>
          )}
          <div className="page-stack">
            {analysis.alerts.map((alert) => (
              <div key={alert.id} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>{alert.title}</b>
                  <div style={{ display: "flex", gap: 6 }}>
                    <em className={`status ${reputationSeverityTone(alert.severity)}`}>{reputationSeverityLabel(alert.severity)}</em>
                    <em className="status neutral">{reputationAlertTypeLabel(alert.alertType)}</em>
                    <em className={`status ${performanceRecommendationStatusTone(alert.status)}`}>{alert.status}</em>
                  </div>
                </div>
                <p>
                  <b>What happened:</b> {alert.whatHappened}
                </p>
                <p>
                  <b>Why it matters:</b> {alert.whyItMatters}
                </p>
                {alert.recommendedNextSteps.length > 0 && (
                  <ul className="score-why">
                    {alert.recommendedNextSteps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                )}
                {alert.evidence.length > 0 && (
                  <>
                    <span className="overline">Evidence</span>
                    <ul className="score-why">
                      {alert.evidence.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </>
                )}
                <div className="source-list">
                  <div>
                    <span>Confidence</span>
                    <em className={`status ${confidenceTone(alert.confidence)}`}>{alert.confidence}</em>
                  </div>
                  <div>
                    <span>Source categories involved</span>
                    <em>{alert.sourceCategoriesInvolved.map(reputationSourceLabel).join(", ")}</em>
                  </div>
                </div>
                {alert.status === "pending" && (
                  <div className="page-stack">
                    <input placeholder="Note (optional, shown on reject)" value={rejectNoteByAlert[alert.id] ?? ""} onChange={(event) => setRejectNoteByAlert((prev) => ({ ...prev, [alert.id]: event.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="button secondary" disabled={decidingId === alert.id} onClick={() => void handleDecide(alert.id, "rejected")}>
                        Reject
                      </button>
                      <button className="button primary" disabled={decidingId === alert.id} onClick={() => void handleDecide(alert.id, "approved")}>
                        {decidingId === alert.id ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Acknowledge
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      ) : (
        <Panel title="Reputation Alerts">
          <Empty
            icon={ShieldAlert}
            title="No alerts this run"
            text="An alert requires independent corroboration from a second source category, which isn't connected yet -- a single owned-website signal never escalates into an alert on its own."
          />
        </Panel>
      )}

      {analysis.dataQualityNotes.length > 0 && (
        <Panel title="Data Quality Notes">
          <ul className="score-why">
            {analysis.dataQualityNotes.map((note, index) => (
              <li key={index}>{note.text}</li>
            ))}
          </ul>
        </Panel>
      )}

      {showHistory && (
        <Panel title="Analysis History">
          {history.length === 0 ? (
            <Empty icon={LoaderCircle} title="Loading" text="Loading analysis history…" />
          ) : (
            <div className="page-stack">
              {history.map((entry) => (
                <div key={entry.runId} className="source-list">
                  <div>
                    <span>
                      <b>{new Date(entry.startedAt).toLocaleString()}</b>
                    </span>
                  </div>
                  <div>
                    <span>Trend</span>
                    <em className={`status ${reputationTrendTone(entry.trend)}`}>{reputationTrendLabel(entry.trend)}</em>
                  </div>
                  <div>
                    <span>Alerts</span>
                    <em>{entry.alerts.filter((a) => a.status === "approved").length}/{entry.alertCount} acknowledged</em>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

function contentInitiativeTypeLabel(type: ContentInitiativeType): string {
  if (type === "create_now") return "Create now";
  if (type === "define_further") return "Define further";
  if (type === "monitor") return "Monitor";
  return "Deprioritise";
}
function contentInitiativeTypeTone(type: ContentInitiativeType): "complete" | "pending" | "error" | "neutral" {
  if (type === "create_now") return "complete";
  if (type === "define_further") return "pending";
  if (type === "deprioritise") return "error";
  return "neutral";
}

// Content Strategy -- turns real production-coverage gaps (content-draft
// type mix, asset-requirement status, messaging-pillar linkage) into
// prioritised content-initiative proposals. Marketing Strategy decides WHAT
// audiences/positioning/themes/gaps to pursue; this agent decides WHICH
// specific content assets to create next, using Marketing Strategy's
// themes/audiences/gaps read-only as framing context -- never re-deriving
// or contradicting them, and never claiming any content type or audience
// pairing performs better (Clintra tracks no content-performance data).
function ContentStrategyView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "content-strategy");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState<ContentStrategyAnalysisResult | null>(null);
  const [history, setHistory] = useState<ContentStrategyAnalysisHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState("");
  const [rejectNoteByInitiative, setRejectNoteByInitiative] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    loadContentStrategyAnalysis(session.session.token, workspace.tenantId)
      .then((result) => setAnalysis(result.hasRun ? result : null))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Content Strategy."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  const loadHistory = () => {
    setShowHistory(true);
    loadContentStrategyAnalysisHistory(session.session.token, workspace.tenantId)
      .then((result) => setHistory(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load analysis history."));
  };

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Content Strategy Agent</span>
          <h1>Content Strategy isn't active yet.</h1>
        </div>
        <Panel title="Activate Content Strategy">
          <Empty icon={Newspaper} title="Agent not active" text="Activate the Content Strategy agent from Agents & Entitlements to turn real content-production coverage gaps into a prioritised initiative backlog." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Content Strategy">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Content Strategy">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Content Strategy workspace…" />
      </Panel>
    );

  const handleRun = async () => {
    setRunning(true);
    setRunError("");
    try {
      const result = await runContentStrategyAnalysis(session.session.token, workspace.tenantId);
      setAnalysis(result);
      setShowHistory(false);
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : "The content strategy analysis could not be generated.");
    } finally {
      setRunning(false);
    }
  };

  const handleDecide = async (id: string, decision: "approved" | "rejected") => {
    setDecidingId(id);
    setDecisionError("");
    try {
      const updated = await decideContentInitiative(session.session.token, workspace.tenantId, id, decision, rejectNoteByInitiative[id]);
      setAnalysis((prev) => (prev ? { ...prev, initiatives: prev.initiatives.map((initiative) => (initiative.id === updated.id ? updated : initiative)) } : prev));
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingId(null);
    }
  };

  if (!analysis) {
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Content Strategy Agent</span>
          <h1>
            Content Strategy
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Turns real content-production coverage gaps into a prioritised backlog of content initiatives for your team to review.</span>
            </span>
          </h1>
        </div>
        <Panel title="Run Analysis">
          <Empty
            icon={Newspaper}
            title="Not analyzed yet"
            text="This agent analyzes real content-draft mix, asset-requirement status, and messaging-pillar coverage -- it does not fabricate which content type or audience performs best, since Clintra tracks no content-performance data. Run an analysis to see the honest picture."
          />
          <p className="ai-callout">Content Strategy only recommends -- it never drafts, publishes, or launches content itself. Content Generation drafts content once a human has reviewed and approved a real asset requirement.</p>
          {runError && (
            <div className="form-alert error">
              <X size={16} />
              {runError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={running} onClick={() => void handleRun()}>
              {running ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Run Analysis
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Content Strategy Agent</span>
          <h1>
            Content Strategy
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Last analyzed {new Date(analysis.startedAt).toLocaleString()}</span>
            </span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="button secondary" onClick={loadHistory}>
            View History
          </button>
          <button className="button primary" disabled={running} onClick={() => void handleRun()}>
            {running ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Run Analysis
          </button>
        </div>
      </div>
      {runError && (
        <div className="form-alert error">
          <X size={16} />
          {runError}
        </div>
      )}

      <div className="metrics metrics-4">
        <Metric icon={AlertTriangle} label="Coverage findings" value={String(analysis.findings.length)} meta="Findings this run" />
        <Metric icon={Newspaper} label="Content initiatives" value={String(analysis.initiatives.length)} meta={`${analysis.initiatives.filter((i) => i.status === "pending").length} pending review`} />
        <Metric icon={ClipboardList} label="Asset requirements" value={String(analysis.assetRequirementCoverage.reduce((sum, entry) => sum + entry.count, 0))} meta="Across all campaigns" />
        <Metric icon={Route} label="Messaging pillars" value={`${analysis.pillarCoverage.total - analysis.pillarCoverage.unmapped}/${analysis.pillarCoverage.total}`} meta="Linked to a real asset requirement" />
      </div>

      <Panel title="Content Draft Mix" kicker="Real counts from content_drafts -- production mix, not performance">
        <div className="source-list">
          {analysis.contentDraftMix.length === 0 ? (
            <p className="ai-callout">No content drafts recorded yet.</p>
          ) : (
            analysis.contentDraftMix.map((entry, index) => (
              <div key={index}>
                <span>
                  {entry.contentType} ({entry.status})
                </span>
                <em>{entry.count}</em>
              </div>
            ))
          )}
        </div>
      </Panel>

      <Panel title="Asset Requirement Coverage" kicker="Real counts from campaign_asset_requirements -- human-maintained status, not auto-linked to a draft">
        <div className="source-list">
          {analysis.assetRequirementCoverage.length === 0 ? (
            <p className="ai-callout">No asset requirements recorded yet.</p>
          ) : (
            analysis.assetRequirementCoverage.map((entry, index) => (
              <div key={index}>
                <span>{entry.status}</span>
                <em>{entry.count}</em>
              </div>
            ))
          )}
        </div>
      </Panel>

      <Panel title="Marketing Strategy Context" kicker="Read-only -- Content Strategy never re-derives or contradicts these">
        {analysis.marketingStrategyContext.available ? (
          <div className="source-list">
            <div>
              <span>Status</span>
              <em>{analysis.marketingStrategyContext.approved ? "Approved" : "Not yet approved"}</em>
            </div>
            <div>
              <span>Message themes</span>
              <em>{analysis.marketingStrategyContext.messageThemes.join(", ") || "None"}</em>
            </div>
            <div>
              <span>Messaging gaps already identified</span>
              <em>{analysis.marketingStrategyContext.messagingGaps.join(", ") || "None"}</em>
            </div>
          </div>
        ) : (
          <p className="ai-callout">{analysis.marketingStrategyContext.reason}</p>
        )}
      </Panel>

      <Panel title="Business Discovery Context" kicker="Read-only -- approved ICP facts">
        {analysis.businessContext.available && analysis.businessContext.icpFacts.length > 0 ? (
          <ul className="score-why">
            {analysis.businessContext.icpFacts.map((fact, index) => (
              <li key={index}>
                <b>{fact.label}:</b> {fact.value}
              </li>
            ))}
          </ul>
        ) : (
          <p className="ai-callout">{analysis.businessContext.available ? "No ICP facts recorded yet." : analysis.businessContext.reason}</p>
        )}
      </Panel>

      {analysis.findings.length > 0 && (
        <Panel title="Coverage Findings">
          <div className="page-stack">
            {analysis.findings.map((finding) => (
              <div key={finding.key} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>{finding.title}</b>
                  <em className={`status ${finding.certainty === "known" ? "error" : "pending"}`}>{finding.certainty === "known" ? "arithmetic fact" : "hypothesis"}</em>
                </div>
                <p>
                  <b>{finding.metric}:</b> {finding.currentValue}
                </p>
                <p>
                  <b>Comparison:</b> {finding.comparison}
                </p>
                {finding.evidence.length > 0 && (
                  <ul className="score-why">
                    {finding.evidence.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Content Initiatives" kicker="Reviewed by your team before becoming a real asset requirement">
        {decisionError && (
          <div className="form-alert error">
            <X size={16} />
            {decisionError}
          </div>
        )}
        {analysis.initiatives.length === 0 ? (
          <p className="ai-callout">No initiatives proposed this run.</p>
        ) : (
          <div className="page-stack">
            {analysis.initiatives.map((initiative) => (
              <div key={initiative.id} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>{initiative.title}</b>
                  <div style={{ display: "flex", gap: 6 }}>
                    <em className={`status ${priorityBucketTone(initiative.priorityBucket)}`}>{priorityBucketLabel(initiative.priorityBucket)}</em>
                    <em className={`status ${contentInitiativeTypeTone(initiative.recommendation)}`}>{contentInitiativeTypeLabel(initiative.recommendation)}</em>
                    <em className={`status ${performanceRecommendationStatusTone(initiative.status)}`}>{initiative.status}</em>
                  </div>
                </div>
                <p>
                  <b>Problem:</b> {initiative.problem}
                </p>
                <p>
                  <b>Rationale:</b> {initiative.rationale}
                </p>
                <div className="source-list">
                  <div>
                    <span>Content type</span>
                    <em>{initiative.contentType}</em>
                  </div>
                  <div>
                    <span>Target audience</span>
                    <em>{initiative.targetAudience}</em>
                  </div>
                  <div>
                    <span>Recommended phase</span>
                    <em>{initiative.recommendedPhase}</em>
                  </div>
                </div>
                {initiative.dependencies.length > 0 && (
                  <p>
                    <b>Dependencies:</b> {initiative.dependencies.join(", ")}
                  </p>
                )}
                <div className="source-list">
                  <div>
                    <span>Confidence</span>
                    <em className={`status ${confidenceTone(initiative.confidence)}`}>{initiative.confidence}</em>
                  </div>
                  <div>
                    <span>Impact</span>
                    <em>{initiative.impact}</em>
                  </div>
                  <div>
                    <span>Effort</span>
                    <em>{initiative.effort}</em>
                  </div>
                </div>
                {initiative.status === "pending" && (
                  <div className="page-stack">
                    <input placeholder="Note (optional, shown on reject)" value={rejectNoteByInitiative[initiative.id] ?? ""} onChange={(event) => setRejectNoteByInitiative((prev) => ({ ...prev, [initiative.id]: event.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="button secondary" disabled={decidingId === initiative.id} onClick={() => void handleDecide(initiative.id, "rejected")}>
                        Reject
                      </button>
                      <button className="button primary" disabled={decidingId === initiative.id} onClick={() => void handleDecide(initiative.id, "approved")}>
                        {decidingId === initiative.id ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      {analysis.dataQualityNotes.length > 0 && (
        <Panel title="Data Quality Notes">
          <ul className="score-why">
            {analysis.dataQualityNotes.map((note, index) => (
              <li key={index}>{note.text}</li>
            ))}
          </ul>
        </Panel>
      )}

      {showHistory && (
        <Panel title="Analysis History">
          {history.length === 0 ? (
            <Empty icon={LoaderCircle} title="Loading" text="Loading analysis history…" />
          ) : (
            <div className="page-stack">
              {history.map((entry) => (
                <div key={entry.runId} className="source-list">
                  <div>
                    <span>
                      <b>{new Date(entry.startedAt).toLocaleString()}</b>
                    </span>
                  </div>
                  <div>
                    <span>Findings</span>
                    <em>{entry.findingCount}</em>
                  </div>
                  <div>
                    <span>Initiatives</span>
                    <em>
                      {entry.initiatives.filter((i) => i.status === "approved").length}/{entry.initiativeCount} approved
                    </em>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

function outreachDraftStatusTone(status: OutreachDraftStatus): "complete" | "pending" | "error" | "neutral" {
  if (status === "approved") return "complete";
  if (status === "in_review") return "pending";
  if (status === "changes_requested") return "error";
  return "neutral";
}
function outreachDraftStatusLabel(status: OutreachDraftStatus): string {
  if (status === "in_review") return "In Review";
  if (status === "changes_requested") return "Changes Requested";
  return status.charAt(0).toUpperCase() + status.slice(1);
}
function outreachChannelLabel(channel: OutreachChannel): string {
  return channel === "linkedin_message" ? "LinkedIn Message" : "Email";
}

// Outreach Personalisation -- on-demand, per-lead draft generation (shaped
// like Content Generation's own draft lifecycle, not a periodic tenant-wide
// analysis). Every fact used in a draft is a real DB-derived value or an
// honest "not available" state -- never invented. Distinct from Content
// Generation's own generic 'outreach' content type, which is a campaign-level
// template with no real lead behind it. Always a human-reviewed first draft;
// nothing here is ever sent automatically.
function OutreachDraftCard({
  draft,
  isEditing,
  editSubject,
  editBody,
  busy,
  decisionNote,
  onStartEdit,
  onEditSubjectChange,
  onEditBodyChange,
  onSaveEdit,
  onCancelEdit,
  onSubmit,
  onDecisionNoteChange,
  onDecide,
}: {
  draft: OutreachDraftRecord;
  isEditing: boolean;
  editSubject: string;
  editBody: string;
  busy: boolean;
  decisionNote: string;
  onStartEdit: () => void;
  onEditSubjectChange: (value: string) => void;
  onEditBodyChange: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onSubmit: () => void;
  onDecisionNoteChange: (value: string) => void;
  onDecide: (decision: "approved" | "changes_requested") => void;
}) {
  const context = draft.generationContext;
  return (
    <div className="website-recommendation-card">
      <div className="page-title-action">
        <b>{outreachChannelLabel(draft.channel)}</b>
        <em className={`status ${outreachDraftStatusTone(draft.status)}`}>{outreachDraftStatusLabel(draft.status)}</em>
      </div>
      {isEditing ? (
        <div className="page-stack">
          {draft.channel === "email" && (
            <label>
              Subject
              <input value={editSubject} onChange={(event) => onEditSubjectChange(event.target.value)} />
            </label>
          )}
          <label>
            Message
            <textarea rows={6} value={editBody} onChange={(event) => onEditBodyChange(event.target.value)} />
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="button secondary" disabled={busy} onClick={onCancelEdit}>
              Cancel
            </button>
            <button className="button primary" disabled={busy} onClick={onSaveEdit}>
              {busy ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save
            </button>
          </div>
        </div>
      ) : (
        <>
          {draft.subject && (
            <p>
              <b>Subject:</b> {draft.subject}
            </p>
          )}
          <p style={{ whiteSpace: "pre-wrap" }}>{draft.body}</p>
        </>
      )}

      <div className="source-list">
        <div>
          <span>Lead score</span>
          <em>{context.leadScore.available ? `${context.leadScore.score} (${context.leadScore.band})` : "Not scored"}</em>
        </div>
        <div>
          <span>Buying intent</span>
          <em>{context.buyingIntent.available ? context.buyingIntent.band : "No signal"}</em>
        </div>
        <div>
          <span>Consent</span>
          <em className={`status ${context.consentStatus === "granted" ? "complete" : "neutral"}`}>{context.consentStatus}</em>
        </div>
      </div>

      {!isEditing && !busy && draft.status !== "approved" && (
        <div className="page-stack">
          <div style={{ display: "flex", gap: 8 }}>
            <button className="button secondary" onClick={onStartEdit}>
              Edit
            </button>
            {(draft.status === "draft" || draft.status === "changes_requested") && (
              <button className="button primary" onClick={onSubmit}>
                Submit for Review
              </button>
            )}
          </div>
          {draft.status === "in_review" && (
            <>
              <input
                placeholder="Note (required to request changes)"
                value={decisionNote}
                onChange={(event) => onDecisionNoteChange(event.target.value)}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="button secondary" onClick={() => onDecide("changes_requested")}>
                  Request Changes
                </button>
                <button className="button primary" onClick={() => onDecide("approved")}>
                  <Check size={15} /> Approve
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {busy && (
        <div className="page-title-action">
          <LoaderCircle className="spin" size={15} />
        </div>
      )}
    </div>
  );
}

function OutreachPersonalisationView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "outreach-personalisation");
  const [drafts, setDrafts] = useState<OutreachDraftRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<OutreachDraftStatus | "all">("all");

  const [leadSearchDraft, setLeadSearchDraft] = useState("");
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState<LeadListItem[]>([]);
  const [leadSearchLoading, setLeadSearchLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadListItem | null>(null);

  const [channel, setChannel] = useState<OutreachChannel>("email");
  const [instructions, setInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [generatedDraft, setGeneratedDraft] = useState<OutreachDraftRecord | null>(null);

  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [busyDraftId, setBusyDraftId] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const [decisionNoteByDraft, setDecisionNoteByDraft] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    loadOutreachDrafts(session.session.token, workspace.tenantId)
      .then((result) => {
        setDrafts(result.data);
        setError("");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Outreach Personalisation."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setLeadSearch(leadSearchDraft), 300);
    return () => window.clearTimeout(timeout);
  }, [leadSearchDraft]);

  useEffect(() => {
    if (!agent?.active || selectedLead || !leadSearch.trim()) {
      setLeadResults([]);
      return;
    }
    setLeadSearchLoading(true);
    void loadLeads(session.session.token, workspace.tenantId, { search: leadSearch, pageSize: 10 })
      .then((result) => setLeadResults(result.data))
      .catch(() => setLeadResults([]))
      .finally(() => setLeadSearchLoading(false));
  }, [leadSearch, agent?.active, selectedLead]);

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Outreach Personalisation Agent</span>
          <h1>Outreach Personalisation isn't active yet.</h1>
        </div>
        <Panel title="Activate Outreach Personalisation">
          <Empty icon={Send} title="Agent not active" text="Activate the Outreach Personalisation agent from Agents & Entitlements to draft per-lead, evidence-grounded outreach messages." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Outreach Personalisation">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Outreach Personalisation">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Outreach Personalisation workspace…" />
      </Panel>
    );

  const handleGenerate = async () => {
    if (!selectedLead) return;
    setGenerating(true);
    setGenerateError("");
    try {
      const draft = await generateOutreachDraft(session.session.token, workspace.tenantId, selectedLead.id, {
        channel,
        ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
      });
      setGeneratedDraft(draft);
      setDrafts((prev) => (prev ? [draft, ...prev] : [draft]));
    } catch (cause) {
      setGenerateError(cause instanceof Error ? cause.message : "The draft could not be generated.");
    } finally {
      setGenerating(false);
    }
  };

  const applyUpdatedDraft = (updated: OutreachDraftRecord) => {
    setDrafts((prev) => prev?.map((entry) => (entry.id === updated.id ? updated : entry)) ?? prev);
    setGeneratedDraft((prev) => (prev?.id === updated.id ? updated : prev));
  };

  const handleSaveEdit = async (draft: OutreachDraftRecord) => {
    setBusyDraftId(draft.id);
    setActionError("");
    try {
      const updated = await updateOutreachDraft(session.session.token, workspace.tenantId, draft.id, { subject: editSubject || null, body: editBody });
      applyUpdatedDraft(updated);
      setEditingDraftId(null);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Could not save changes.");
    } finally {
      setBusyDraftId(null);
    }
  };

  const handleSubmit = async (draft: OutreachDraftRecord) => {
    setBusyDraftId(draft.id);
    setActionError("");
    try {
      applyUpdatedDraft(await submitOutreachDraftForReview(session.session.token, workspace.tenantId, draft.id));
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Could not submit this draft for review.");
    } finally {
      setBusyDraftId(null);
    }
  };

  const handleDecide = async (draft: OutreachDraftRecord, decision: "approved" | "changes_requested") => {
    setBusyDraftId(draft.id);
    setActionError("");
    try {
      applyUpdatedDraft(await decideOutreachDraft(session.session.token, workspace.tenantId, draft.id, decision, decisionNoteByDraft[draft.id]));
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setBusyDraftId(null);
    }
  };

  const filteredDrafts = (drafts ?? []).filter((entry) => statusFilter === "all" || entry.status === statusFilter);

  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">Outreach Personalisation Agent</span>
        <h1>
          Outreach Personalisation
          <span className="info-tooltip">
            <Info size={13} />
            <span className="info-tooltip-content">Per-lead, evidence-grounded outreach drafts grounded in real CRM facts, lead score, and buying-intent signals -- distinct from Content Generation's own generic outreach template. Always a human-reviewed first draft, never sent automatically.</span>
          </span>
        </h1>
      </div>

      <Panel title="Generate Draft" kicker="Pick a real lead -- nothing here is invented beyond what's on file">
        <label>
          Find a lead
          <input
            value={leadSearchDraft}
            onChange={(event) => {
              setLeadSearchDraft(event.target.value);
              setSelectedLead(null);
            }}
            placeholder="Search by name, email, or company"
          />
        </label>
        {leadSearchLoading && <p className="ai-callout">Searching…</p>}
        {!selectedLead && leadResults.length > 0 && (
          <div className="source-list">
            {leadResults.map((lead) => (
              <div
                key={lead.id}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedLead(lead);
                  setLeadResults([]);
                  setLeadSearchDraft(lead.name);
                }}
              >
                <span>
                  <b>{lead.name}</b>
                </span>
                <em>{[lead.title, lead.company].filter(Boolean).join(" · ") || "No details on file"}</em>
              </div>
            ))}
          </div>
        )}
        {selectedLead && (
          <div className="source-list">
            <div>
              <span>
                <b>{selectedLead.name}</b>
              </span>
              <em>{[selectedLead.title, selectedLead.company].filter(Boolean).join(" · ") || "No details on file"}</em>
            </div>
          </div>
        )}
        <div className="lead-config-grid">
          <label>
            Channel
            <select value={channel} onChange={(event) => setChannel(event.target.value as OutreachChannel)}>
              <option value="email">Email</option>
              <option value="linkedin_message">LinkedIn Message</option>
            </select>
          </label>
        </div>
        <label>
          Additional instructions
          <textarea rows={2} value={instructions} onChange={(event) => setInstructions(event.target.value)} placeholder="e.g. Mention our new integration." />
        </label>
        {generateError && (
          <div className="form-alert error">
            <X size={16} />
            {generateError}
          </div>
        )}
        <div className="page-title-action">
          <button className="button primary" disabled={!selectedLead || generating} onClick={() => void handleGenerate()}>
            {generating ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Generate Draft
          </button>
        </div>
      </Panel>

      {generatedDraft && (
        <Panel title="Generated Draft" kicker="AI-generated first draft · Human review required">
          <OutreachDraftCard
            draft={generatedDraft}
            isEditing={editingDraftId === generatedDraft.id}
            editSubject={editSubject}
            editBody={editBody}
            busy={busyDraftId === generatedDraft.id}
            decisionNote={decisionNoteByDraft[generatedDraft.id] ?? ""}
            onStartEdit={() => {
              setEditingDraftId(generatedDraft.id);
              setEditSubject(generatedDraft.subject ?? "");
              setEditBody(generatedDraft.body);
            }}
            onEditSubjectChange={setEditSubject}
            onEditBodyChange={setEditBody}
            onSaveEdit={() => void handleSaveEdit(generatedDraft)}
            onCancelEdit={() => setEditingDraftId(null)}
            onSubmit={() => void handleSubmit(generatedDraft)}
            onDecisionNoteChange={(value) => setDecisionNoteByDraft((prev) => ({ ...prev, [generatedDraft.id]: value }))}
            onDecide={(decision) => void handleDecide(generatedDraft, decision)}
          />
        </Panel>
      )}

      <Panel title="Draft Queue" kicker={`${filteredDrafts.length} draft${filteredDrafts.length === 1 ? "" : "s"}`}>
        <div className="lead-config-grid">
          <label>
            Status
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as OutreachDraftStatus | "all")}>
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="in_review">In Review</option>
              <option value="changes_requested">Changes Requested</option>
              <option value="approved">Approved</option>
            </select>
          </label>
        </div>
        {actionError && (
          <div className="form-alert error">
            <X size={16} />
            {actionError}
          </div>
        )}
        {filteredDrafts.length === 0 ? (
          <Empty icon={Send} title="No drafts yet" text="Generate a draft above to see it here." />
        ) : (
          <div className="page-stack">
            {filteredDrafts
              .filter((entry) => entry.id !== generatedDraft?.id)
              .map((draft) => (
                <OutreachDraftCard
                  key={draft.id}
                  draft={draft}
                  isEditing={editingDraftId === draft.id}
                  editSubject={editSubject}
                  editBody={editBody}
                  busy={busyDraftId === draft.id}
                  decisionNote={decisionNoteByDraft[draft.id] ?? ""}
                  onStartEdit={() => {
                    setEditingDraftId(draft.id);
                    setEditSubject(draft.subject ?? "");
                    setEditBody(draft.body);
                  }}
                  onEditSubjectChange={setEditSubject}
                  onEditBodyChange={setEditBody}
                  onSaveEdit={() => void handleSaveEdit(draft)}
                  onCancelEdit={() => setEditingDraftId(null)}
                  onSubmit={() => void handleSubmit(draft)}
                  onDecisionNoteChange={(value) => setDecisionNoteByDraft((prev) => ({ ...prev, [draft.id]: value }))}
                  onDecide={(decision) => void handleDecide(draft, decision)}
                />
              ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// Buying Intent -- account-level buying-activity signal, distinct from Lead
// Intelligence's ICP-fit scoring: this agent tracks whether an account's real
// engagement (synced CRM activity) is currently rising, stable, falling, or
// inactive, using the same deterministic scoreBuyingIntent math Sales
// Planning already relies on for tiering. It never fabricates a website,
// pricing-page, or third-party intent signal Clintra doesn't actually
// collect -- those show as an honest structural gap, not a zero. Alerts only
// fire on a real upward band transition, so an unchanged account is never
// re-alerted.
function buyingIntentBandLabel(band: IntentBand): string {
  if (band === "insufficient_signal") return "Insufficient signal";
  return band.charAt(0).toUpperCase() + band.slice(1);
}
function buyingIntentTrendTone(trend: BuyingIntentTrend): "complete" | "pending" | "error" | "neutral" {
  if (trend === "rising") return "complete";
  if (trend === "falling") return "error";
  if (trend === "stable") return "pending";
  return "neutral";
}
function buyingIntentTrendLabel(trend: BuyingIntentTrend): string {
  if (trend === "insufficient_data") return "Insufficient data";
  return trend.charAt(0).toUpperCase() + trend.slice(1);
}
function buyingIntentTrendIcon(trend: BuyingIntentTrend): typeof Activity {
  if (trend === "rising") return TrendingUp;
  if (trend === "falling") return TrendingDown;
  return Activity;
}

function BuyingIntentAccountDrawerBody({
  session,
  workspace,
  accountId,
  accountName,
}: {
  session: AuthSession;
  workspace: Workspace;
  accountId: string;
  accountName: string;
}) {
  const [detail, setDetail] = useState<BuyingIntentAccountDetail | null>(null);
  const [signals, setSignals] = useState<BuyingIntentSignalTimelineEntry[]>([]);
  const [history, setHistory] = useState<BuyingIntentHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState("");
  const [explanation, setExplanation] = useState<IntentAccountExplanation | null>(null);

  useEffect(() => {
    setLoading(true);
    setExplanation(null);
    setExplainError("");
    Promise.all([
      loadBuyingIntentAccountDetail(session.session.token, workspace.tenantId, accountId),
      loadBuyingIntentAccountSignals(session.session.token, workspace.tenantId, accountId),
      loadBuyingIntentAccountHistory(session.session.token, workspace.tenantId, accountId),
    ])
      .then(([detailResult, signalsResult, historyResult]) => {
        setDetail(detailResult);
        setSignals(signalsResult.data);
        setHistory(historyResult.data);
        if (detailResult.snapshot?.explanation) setExplanation(detailResult.snapshot.explanation);
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load this account."))
      .finally(() => setLoading(false));
  }, [accountId]);

  const handleExplain = async () => {
    setExplainLoading(true);
    setExplainError("");
    try {
      const result = await explainBuyingIntentAccount(session.session.token, workspace.tenantId, accountId);
      if (result.available && result.explanation) setExplanation(result.explanation);
      else setExplainError(result.reason ?? "AI explanation is unavailable right now.");
    } catch (cause) {
      setExplainError(cause instanceof Error ? cause.message : "Could not generate an explanation.");
    } finally {
      setExplainLoading(false);
    }
  };

  if (loading) return <Empty icon={LoaderCircle} title="Loading" text={`Loading ${accountName}…`} />;
  if (error)
    return (
      <div className="form-alert error">
        <X size={16} />
        {error}
      </div>
    );
  const snapshot = detail?.snapshot ?? null;
  if (!snapshot)
    return <Empty icon={Flame} title="Not scored yet" text="This account has real activity but hasn't been scored yet. Run an analysis to score it." />;

  return (
    <div className="page-stack">
      <div className="source-list">
        <div>
          <span>Intent</span>
          <em className={`status ${intentTone(snapshot.band)}`}>
            {buyingIntentBandLabel(snapshot.band)}
            {snapshot.score !== null ? ` (${snapshot.score})` : ""}
          </em>
        </div>
        <div>
          <span>Trend</span>
          <em className={`status ${buyingIntentTrendTone(snapshot.trend)}`}>
            {buyingIntentTrendLabel(snapshot.trend)}
            {snapshot.change !== null ? ` (${snapshot.change > 0 ? "+" : ""}${snapshot.change})` : ""}
          </em>
        </div>
        <div>
          <span>Confidence</span>
          <em>{snapshot.confidence !== null ? snapshot.confidence.toFixed(2) : "n/a"}</em>
        </div>
        <div>
          <span>Signal count</span>
          <em>{snapshot.signalCount}</em>
        </div>
        <div>
          <span>Last scored</span>
          <em>{new Date(snapshot.snapshotAt).toLocaleString()}</em>
        </div>
      </div>

      <p className="ai-callout">Buying intent is a prioritisation signal, not guaranteed purchasing behaviour.</p>

      <div>
        <span className="overline">Source coverage</span>
        <div className="source-list">
          {snapshot.sourceCoverage.map((entry) => (
            <div key={entry.source}>
              <span>{entry.source.replaceAll("_", " ")}</span>
              <em className={`status ${entry.available ? "complete" : "neutral"}`}>{entry.available ? "Connected" : "Not available"}</em>
            </div>
          ))}
        </div>
      </div>

      {snapshot.topSignals.length > 0 && (
        <div>
          <span className="overline">Top signals</span>
          <ul className="score-why">
            {snapshot.topSignals.map((signal, index) => (
              <li key={index}>
                {signal.kind.replaceAll("_", " ")} via {signal.source} -- strength {signal.strength.toFixed(2)}, {new Date(signal.occurredAt).toLocaleDateString()}
              </li>
            ))}
          </ul>
        </div>
      )}

      {signals.length > 0 && (
        <div>
          <span className="overline">Signal timeline</span>
          <ul className="score-why">
            {signals.slice(0, 15).map((signal, index) => (
              <li key={index}>
                {signal.label} -- {new Date(signal.occurredAt).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      )}

      {history.length > 1 && (
        <div>
          <span className="overline">Score history</span>
          <div className="source-list">
            {history.map((entry, index) => (
              <div key={index}>
                <span>{new Date(entry.snapshotAt).toLocaleDateString()}</span>
                <em className={`status ${intentTone(entry.band)}`}>
                  {entry.score !== null ? entry.score : "n/a"} ({buyingIntentBandLabel(entry.band)})
                </em>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="page-title-action">
          <span className="overline">AI Explanation</span>
          <button className="button secondary" disabled={explainLoading} onClick={() => void handleExplain()}>
            {explainLoading ? <LoaderCircle className="spin" size={14} /> : <Sparkles size={14} />} {explanation ? "Regenerate" : "Explain"}
          </button>
        </div>
        {explainError && (
          <div className="form-alert error">
            <X size={16} />
            {explainError}
          </div>
        )}
        {explanation ? (
          <div className="website-recommendation-card">
            <p>{explanation.summary}</p>
            {explanation.drivers.length > 0 && (
              <ul className="score-why">
                {explanation.drivers.map((driver, index) => (
                  <li key={index}>{driver}</li>
                ))}
              </ul>
            )}
            <p>
              <b>Trend:</b> {explanation.trendExplanation}
            </p>
            <p>
              <b>Suggested next step:</b> {explanation.recommendedAction}
            </p>
            <em className={`status ${confidenceTone(explanation.confidence)}`}>{explanation.confidence} confidence</em>
          </div>
        ) : (
          <p className="ai-callout">Not generated yet -- click Explain for an AI narration of this account's already-computed score.</p>
        )}
      </div>
    </div>
  );
}

function BuyingIntentView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "buying-intent");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState<BuyingIntentOverview | null>(null);
  const [accounts, setAccounts] = useState<BuyingIntentAccountSnapshot[]>([]);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [intentLevelFilter, setIntentLevelFilter] = useState<BuyingIntentAccountListQuery["intentLevel"] | "">("");
  const [trendFilter, setTrendFilter] = useState<BuyingIntentAccountListQuery["trend"] | "">("");
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alerts, setAlerts] = useState<BuyingIntentAlert[]>([]);
  const [decidingAlertId, setDecidingAlertId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState("");
  const [rejectNoteByAlert, setRejectNoteByAlert] = useState<Record<string, string>>({});

  const loadAll = () => {
    setLoading(true);
    setError("");
    Promise.all([
      loadBuyingIntentOverview(session.session.token, workspace.tenantId),
      loadBuyingIntentAccounts(session.session.token, workspace.tenantId, {
        ...(intentLevelFilter ? { intentLevel: intentLevelFilter } : {}),
        ...(trendFilter ? { trend: trendFilter } : {}),
      }),
    ])
      .then(([overviewResult, accountsResult]) => {
        setOverview(overviewResult);
        setAccounts(accountsResult.data);
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Buying Intent."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) loadAll();
  }, [agent?.active, intentLevelFilter, trendFilter]);

  const loadAlerts = () => {
    setShowAlerts(true);
    loadBuyingIntentAlerts(session.session.token, workspace.tenantId)
      .then((result) => setAlerts(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load alerts."));
  };

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Buying Intent Agent</span>
          <h1>Buying Intent isn't active yet.</h1>
        </div>
        <Panel title="Activate Buying Intent">
          <Empty icon={Flame} title="Agent not active" text="Activate the Buying Intent agent from Agents & Entitlements to see which accounts are showing real, rising buying activity." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Buying Intent">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Buying Intent">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Buying Intent workspace…" />
      </Panel>
    );

  const handleRun = async () => {
    setRunning(true);
    setRunError("");
    try {
      const result = await runBuyingIntentAnalysis(session.session.token, workspace.tenantId);
      setOverview(result);
      setAccounts(result.accounts);
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : "The buying intent analysis could not be generated.");
    } finally {
      setRunning(false);
    }
  };

  const handleDecideAlert = async (id: string, decision: "approved" | "rejected") => {
    setDecidingAlertId(id);
    setDecisionError("");
    try {
      const updated = await decideBuyingIntentAlert(session.session.token, workspace.tenantId, id, decision, rejectNoteByAlert[id]);
      setAlerts((prev) => prev.map((alert) => (alert.id === updated.id ? updated : alert)));
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingAlertId(null);
    }
  };

  if (!overview?.hasRun && accounts.length === 0) {
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Buying Intent Agent</span>
          <h1>
            Buying Intent
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Scores which accounts are showing real, rising buying activity from your connected sources -- and tracks whether that activity is rising, stable, falling, or inactive over time.</span>
            </span>
          </h1>
        </div>
        <Panel title="Run Analysis">
          <Empty
            icon={Flame}
            title="No scored accounts yet"
            text="This agent scores real, timestamped CRM activity (meetings, calls, emails) -- it never fabricates website, pricing-page, or third-party intent data Clintra doesn't actually collect. Run an analysis to see which accounts already have enough activity to score."
          />
          <p className="ai-callout">Buying Intent only surfaces a prioritisation signal and alerts -- it never sends emails, launches ads, contacts prospects, or changes CRM records. Every alert is reviewed by your team.</p>
          {runError && (
            <div className="form-alert error">
              <X size={16} />
              {runError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={running} onClick={() => void handleRun()}>
              {running ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Run Analysis
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Buying Intent Agent</span>
          <h1>
            Buying Intent
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">{overview?.lastRunAt ? `Last analyzed ${new Date(overview.lastRunAt).toLocaleString()}` : "Not analyzed yet"}</span>
            </span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="button secondary" onClick={loadAlerts}>
            View Alerts
          </button>
          <button className="button primary" disabled={running} onClick={() => void handleRun()}>
            {running ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Run Analysis
          </button>
        </div>
      </div>
      {runError && (
        <div className="form-alert error">
          <X size={16} />
          {runError}
        </div>
      )}

      <div className="metrics metrics-4">
        <Metric icon={Flame} label="High intent accounts" value={String(overview?.highIntentCount ?? 0)} meta="Score-based, not guaranteed to buy" />
        <Metric icon={TrendingUp} label="Rising" value={String(overview?.risingCount ?? 0)} meta="Intent increasing vs previous run" />
        <Metric icon={TrendingDown} label="Falling" value={String(overview?.fallingCount ?? 0)} meta="Intent decreasing vs previous run" />
        <Metric icon={Sparkles} label="Newly active" value={String(overview?.newlyActiveCount ?? 0)} meta="First time reaching high intent" />
      </div>

      <Panel
        title="Accounts"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <select value={intentLevelFilter ?? ""} onChange={(event) => setIntentLevelFilter((event.target.value || "") as typeof intentLevelFilter)}>
              <option value="">All intent levels</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="insufficient_signal">Insufficient signal</option>
            </select>
            <select value={trendFilter ?? ""} onChange={(event) => setTrendFilter((event.target.value || "") as typeof trendFilter)}>
              <option value="">All trends</option>
              <option value="rising">Rising</option>
              <option value="stable">Stable</option>
              <option value="falling">Falling</option>
              <option value="inactive">Inactive</option>
              <option value="insufficient_data">Insufficient data</option>
            </select>
          </div>
        }
      >
        {accounts.length === 0 ? (
          <Empty icon={Flame} title="No accounts match" text="No scored accounts match the current filters." />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Intent</th>
                <th>Trend</th>
                <th>Confidence</th>
                <th>Signals</th>
                <th>Last scored</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => {
                const TrendIcon = buyingIntentTrendIcon(account.trend);
                return (
                  <tr key={account.accountId} className="clickable-row" onClick={() => setSelectedAccount({ id: account.accountId, name: account.accountName })}>
                    <td>{account.accountName}</td>
                    <td>
                      <em className={`status ${intentTone(account.band)}`}>
                        {buyingIntentBandLabel(account.band)}
                        {account.score !== null ? ` (${account.score})` : ""}
                      </em>
                    </td>
                    <td>
                      <em className={`status ${buyingIntentTrendTone(account.trend)}`}>
                        <TrendIcon size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                        {buyingIntentTrendLabel(account.trend)}
                      </em>
                    </td>
                    <td>{account.confidence !== null ? account.confidence.toFixed(2) : "n/a"}</td>
                    <td>{account.signalCount}</td>
                    <td>{new Date(account.snapshotAt).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>

      {showAlerts && (
        <Panel title="Alerts" kicker="Fires only on a real upward band transition -- reviewed by your team">
          {decisionError && (
            <div className="form-alert error">
              <X size={16} />
              {decisionError}
            </div>
          )}
          {alerts.length === 0 ? (
            <Empty icon={AlertTriangle} title="No alerts yet" text="Alerts appear here when an account's buying intent crosses into a higher band." />
          ) : (
            <div className="page-stack">
              {alerts.map((alert) => (
                <div key={alert.id} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{alert.title}</b>
                    <em className={`status ${alert.status === "approved" ? "complete" : alert.status === "rejected" ? "error" : "pending"}`}>{alert.status}</em>
                  </div>
                  <p>
                    <b>Why:</b> {alert.rationale}
                  </p>
                  {alert.evidence.length > 0 && (
                    <ul className="score-why">
                      {alert.evidence.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {alert.status === "pending" && (
                    <div className="page-stack">
                      <input
                        placeholder="Note (optional, shown on reject)"
                        value={rejectNoteByAlert[alert.id] ?? ""}
                        onChange={(event) => setRejectNoteByAlert((prev) => ({ ...prev, [alert.id]: event.target.value }))}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="button secondary" disabled={decidingAlertId === alert.id} onClick={() => void handleDecideAlert(alert.id, "rejected")}>
                          Reject
                        </button>
                        <button className="button primary" disabled={decidingAlertId === alert.id} onClick={() => void handleDecideAlert(alert.id, "approved")}>
                          {decidingAlertId === alert.id ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      <Drawer open={selectedAccount !== null} title={selectedAccount?.name ?? "Account"} onClose={() => setSelectedAccount(null)}>
        {selectedAccount && <BuyingIntentAccountDrawerBody session={session} workspace={workspace} accountId={selectedAccount.id} accountName={selectedAccount.name} />}
      </Drawer>
    </div>
  );
}

// Lead Nurturing -- classifies each lead's lifecycle/engagement state from
// real evidence (Lead Intelligence's score/band, real lead_activities,
// Buying Intent's account-level signal where available), groups leads into
// live-computed segments, proposes nurture tracks for human approval, and
// drafts per-step content for review. It never sends anything -- track_id
// is only ever set by an explicit "Assign to track" action, never by the
// analysis run itself.
function nurtureClassificationTone(classification: NurtureClassification): "complete" | "pending" | "error" | "neutral" {
  if (classification === "sales_ready" || classification === "high_intent") return "complete";
  if (classification === "evaluating" || classification === "engaging") return "pending";
  if (classification === "dormant" || classification === "re_engagement_candidate" || classification === "disqualified_suppress") return "error";
  return "neutral";
}
function nurtureClassificationLabel(classification: NurtureClassification): string {
  return classification
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
function nurtureEngagementTrendTone(trend: NurtureEngagementTrend): "complete" | "pending" | "error" | "neutral" {
  if (trend === "increasing") return "complete";
  if (trend === "declining") return "error";
  if (trend === "stable") return "pending";
  return "neutral";
}
function nurtureEngagementTrendLabel(trend: NurtureEngagementTrend): string {
  if (trend === "insufficient_data") return "Insufficient data";
  return trend.charAt(0).toUpperCase() + trend.slice(1);
}
function nurtureEngagementLevelLabel(level: NurtureEngagementLevel): string {
  return level === "none" ? "None" : level.charAt(0).toUpperCase() + level.slice(1);
}
function nurtureTrackPlanStatusTone(status: NurtureTrack["planStatus"]): "complete" | "pending" | "neutral" {
  if (status === "approved") return "complete";
  if (status === "needs_review") return "pending";
  return "neutral";
}

function LeadNurtureDetailDrawerBody({
  session,
  workspace,
  leadId,
  tracks,
  onAssigned,
}: {
  session: AuthSession;
  workspace: Workspace;
  leadId: string;
  tracks: NurtureTrack[];
  onAssigned: () => void;
}) {
  const [snapshot, setSnapshot] = useState<NurtureLeadSnapshot | null>(null);
  const [history, setHistory] = useState<NurtureLeadHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState("");
  const [explanation, setExplanation] = useState<NurtureLeadExplanation | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");

  useEffect(() => {
    setLoading(true);
    setExplanation(null);
    setExplainError("");
    Promise.all([loadNurtureLeadDetail(session.session.token, workspace.tenantId, leadId), loadNurtureLeadHistory(session.session.token, workspace.tenantId, leadId)])
      .then(([detail, historyResult]) => {
        setSnapshot(detail.snapshot);
        setHistory(historyResult.data);
        if (detail.snapshot?.nextRecommendedAction) setExplanation(detail.snapshot.nextRecommendedAction);
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load this lead."))
      .finally(() => setLoading(false));
  }, [leadId]);

  const handleExplain = async () => {
    setExplainLoading(true);
    setExplainError("");
    try {
      const result = await explainNurtureLead(session.session.token, workspace.tenantId, leadId);
      if (result.available && result.explanation) setExplanation(result.explanation);
      else setExplainError(result.reason ?? "AI explanation is unavailable right now.");
    } catch (cause) {
      setExplainError(cause instanceof Error ? cause.message : "Could not generate an explanation.");
    } finally {
      setExplainLoading(false);
    }
  };

  const handleAssign = async (trackId: string) => {
    setAssigning(true);
    setAssignError("");
    try {
      const detail = await assignNurtureLeadTrack(session.session.token, workspace.tenantId, leadId, trackId || null);
      setSnapshot(detail.snapshot);
      onAssigned();
    } catch (cause) {
      setAssignError(cause instanceof Error ? cause.message : "Could not assign this lead to a track.");
    } finally {
      setAssigning(false);
    }
  };

  if (loading) return <Empty icon={LoaderCircle} title="Loading" text="Loading this lead…" />;
  if (error)
    return (
      <div className="form-alert error">
        <X size={16} />
        {error}
      </div>
    );
  if (!snapshot) return <Empty icon={Workflow} title="Not classified yet" text="This lead hasn't been classified yet. Run an analysis to classify it." />;

  return (
    <div className="page-stack">
      <div className="source-list">
        <div>
          <span>Classification</span>
          <em className={`status ${nurtureClassificationTone(snapshot.classification)}`}>{nurtureClassificationLabel(snapshot.classification)}</em>
        </div>
        <div>
          <span>Lead score band</span>
          <em>{snapshot.currentBand ?? "Unscored"}</em>
        </div>
        <div>
          <span>Account buying intent</span>
          <em>{snapshot.accountIntentBand ? `${snapshot.accountIntentBand} (${snapshot.accountIntentTrend ?? "n/a"})` : "Not available"}</em>
        </div>
        <div>
          <span>Engagement</span>
          <em className={`status ${nurtureEngagementTrendTone(snapshot.engagementTrend)}`}>
            {nurtureEngagementLevelLabel(snapshot.engagementLevel)} -- {nurtureEngagementTrendLabel(snapshot.engagementTrend)}
          </em>
        </div>
        <div>
          <span>Current track</span>
          <em>{snapshot.trackName ?? "Not assigned"}</em>
        </div>
        <div>
          <span>Last classified</span>
          <em>{new Date(snapshot.snapshotAt).toLocaleString()}</em>
        </div>
      </div>

      <div>
        <span className="overline">Why this classification</span>
        <ul className="score-why">
          {snapshot.reasons.map((reason, index) => (
            <li key={index}>{reason}</li>
          ))}
        </ul>
      </div>

      {history.length > 1 && (
        <div>
          <span className="overline">Classification history</span>
          <div className="source-list">
            {history.map((entry, index) => (
              <div key={index}>
                <span>{new Date(entry.snapshotAt).toLocaleDateString()}</span>
                <em className={`status ${nurtureClassificationTone(entry.classification)}`}>{nurtureClassificationLabel(entry.classification)}</em>
              </div>
            ))}
          </div>
        </div>
      )}

      <label>
        Assign to track
        <select value={snapshot.trackId ?? ""} disabled={assigning} onChange={(event) => void handleAssign(event.target.value)}>
          <option value="">Not assigned</option>
          {tracks.map((track) => (
            <option key={track.id} value={track.id}>
              {track.name}
            </option>
          ))}
        </select>
      </label>
      {assignError && (
        <div className="form-alert error">
          <X size={16} />
          {assignError}
        </div>
      )}

      <div>
        <div className="page-title-action">
          <span className="overline">AI Explanation</span>
          <button className="button secondary" disabled={explainLoading} onClick={() => void handleExplain()}>
            {explainLoading ? <LoaderCircle className="spin" size={14} /> : <Sparkles size={14} />} {explanation ? "Regenerate" : "Explain"}
          </button>
        </div>
        {explainError && (
          <div className="form-alert error">
            <X size={16} />
            {explainError}
          </div>
        )}
        {explanation ? (
          <div className="website-recommendation-card">
            <p>{explanation.summary}</p>
            <p>
              <b>Suggested next step:</b> {explanation.nextRecommendedAction}
            </p>
            <em className={`status ${confidenceTone(explanation.confidence)}`}>{explanation.confidence} confidence</em>
          </div>
        ) : (
          <p className="ai-callout">Not generated yet -- click Explain for an AI narration of this lead's already-computed classification.</p>
        )}
      </div>
    </div>
  );
}

function NurtureTrackDetailBody({ session, workspace, track, onUpdated }: { session: AuthSession; workspace: Workspace; track: NurtureTrack; onUpdated: (track: NurtureTrack) => void }) {
  const [decisionError, setDecisionError] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [draftingStep, setDraftingStep] = useState<number | null>(null);
  const [draftError, setDraftError] = useState("");

  const handleDecide = async (decision: "approved" | "rejected") => {
    setDeciding(true);
    setDecisionError("");
    try {
      const updated = await decideNurtureTrack(session.session.token, workspace.tenantId, track.id, decision);
      onUpdated(updated);
    } catch (cause) {
      setDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDeciding(false);
    }
  };

  const handleGenerateDraft = async (stepNumber: number) => {
    setDraftingStep(stepNumber);
    setDraftError("");
    try {
      const updated = await generateNurtureStepDraft(session.session.token, workspace.tenantId, track.id, stepNumber);
      onUpdated(updated);
    } catch (cause) {
      setDraftError(cause instanceof Error ? cause.message : "Could not generate a draft for this step.");
    } finally {
      setDraftingStep(null);
    }
  };

  const handleDecideDraft = async (stepNumber: number, decision: "approved" | "rejected") => {
    try {
      const updated = await decideNurtureStepDraft(session.session.token, workspace.tenantId, track.id, stepNumber, decision);
      onUpdated(updated);
    } catch (cause) {
      setDraftError(cause instanceof Error ? cause.message : "Could not record that draft decision.");
    }
  };

  return (
    <div className="page-stack">
      <div className="source-list">
        <div>
          <span>Status</span>
          <em className={`status ${nurtureTrackPlanStatusTone(track.planStatus)}`}>{track.planStatus.replaceAll("_", " ")}</em>
        </div>
        <div>
          <span>Operational status</span>
          <em>{track.status}</em>
        </div>
        <div>
          <span>Generated by</span>
          <em>{track.generatedBy.replaceAll("_", " ")}</em>
        </div>
      </div>
      <p>
        <b>Audience:</b> {typeof track.audience === "string" ? track.audience : JSON.stringify(track.audience)}
      </p>
      {track.objective && (
        <p>
          <b>Objective:</b> {track.objective}
        </p>
      )}
      {track.messagingApproach && (
        <p>
          <b>Messaging approach:</b> {track.messagingApproach}
        </p>
      )}
      {track.entryCriteria.length > 0 && (
        <div>
          <span className="overline">Entry criteria</span>
          <ul className="score-why">
            {track.entryCriteria.map((criterion, index) => (
              <li key={index}>{criterion}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <span className="overline">Sequence</span>
        <table className="data-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Timing</th>
              <th>Channel</th>
              <th>Topic</th>
              <th>CTA</th>
              <th>Draft</th>
            </tr>
          </thead>
          <tbody>
            {track.stages.map((stage) => (
              <tr key={stage.stepNumber}>
                <td>{stage.stepNumber}</td>
                <td>{stage.timing}</td>
                <td>{stage.channel}</td>
                <td>{stage.topic}</td>
                <td>{stage.cta}</td>
                <td>
                  {stage.draft ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <em className={`status ${stage.draft.status === "approved" ? "complete" : stage.draft.status === "rejected" ? "error" : "pending"}`}>{stage.draft.status}</em>
                      {stage.draft.status === "draft" && (
                        <>
                          <button className="button secondary" onClick={() => void handleDecideDraft(stage.stepNumber, "approved")}>
                            <Check size={13} />
                          </button>
                          <button className="button secondary" onClick={() => void handleDecideDraft(stage.stepNumber, "rejected")}>
                            <X size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <button className="button secondary" disabled={draftingStep === stage.stepNumber} onClick={() => void handleGenerateDraft(stage.stepNumber)}>
                      {draftingStep === stage.stepNumber ? <LoaderCircle className="spin" size={13} /> : <Sparkles size={13} />} Generate Draft
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {track.stages.map(
          (stage) =>
            stage.draft && (
              <p key={stage.stepNumber} className="ai-callout">
                Step {stage.stepNumber} draft ({stage.draft.status}) -- <b>{stage.draft.subject}</b>: {stage.draft.body}
              </p>
            ),
        )}
      </div>

      {draftError && (
        <div className="form-alert error">
          <X size={16} />
          {draftError}
        </div>
      )}
      {decisionError && (
        <div className="form-alert error">
          <X size={16} />
          {decisionError}
        </div>
      )}
      {track.planStatus !== "approved" && (
        <div style={{ display: "flex", gap: 8 }}>
          <button className="button secondary" disabled={deciding} onClick={() => void handleDecide("rejected")}>
            Reject
          </button>
          <button className="button primary" disabled={deciding} onClick={() => void handleDecide("approved")}>
            {deciding ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve
          </button>
        </div>
      )}
    </div>
  );
}

function LeadNurturingView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "lead-nurturing");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState<NurtureOverview | null>(null);
  const [leads, setLeads] = useState<NurtureLeadSnapshot[]>([]);
  const [segments, setSegments] = useState<NurtureSegment[]>([]);
  const [tracks, setTracks] = useState<NurtureTrack[]>([]);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [classificationFilter, setClassificationFilter] = useState<NurtureClassification | "">("");
  const [trendFilter, setTrendFilter] = useState<NurtureEngagementTrend | "">("");
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [proposingSegmentKey, setProposingSegmentKey] = useState<string | null>(null);
  const [proposeError, setProposeError] = useState("");
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [showTracks, setShowTracks] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alerts, setAlerts] = useState<NurtureAlert[]>([]);
  const [decidingAlertId, setDecidingAlertId] = useState<string | null>(null);
  const [alertDecisionError, setAlertDecisionError] = useState("");
  const [rejectNoteByAlert, setRejectNoteByAlert] = useState<Record<string, string>>({});
  const [showPerformance, setShowPerformance] = useState(false);
  const [performance, setPerformance] = useState<NurturePerformanceResult | null>(null);

  const loadAll = () => {
    setLoading(true);
    setError("");
    Promise.all([
      loadNurtureOverview(session.session.token, workspace.tenantId),
      loadNurtureLeads(session.session.token, workspace.tenantId, {
        ...(classificationFilter ? { classification: classificationFilter } : {}),
        ...(trendFilter ? { engagementTrend: trendFilter } : {}),
      }),
      loadNurtureSegments(session.session.token, workspace.tenantId),
      loadNurtureTracks(session.session.token, workspace.tenantId),
    ])
      .then(([overviewResult, leadsResult, segmentsResult, tracksResult]) => {
        setOverview(overviewResult);
        setLeads(leadsResult.data);
        setSegments(segmentsResult.data);
        setTracks(tracksResult.data);
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Lead Nurturing."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) loadAll();
  }, [agent?.active, classificationFilter, trendFilter]);

  const loadAlerts = () => {
    setShowAlerts(true);
    loadNurtureAlerts(session.session.token, workspace.tenantId)
      .then((result) => setAlerts(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load alerts."));
  };
  const loadPerformance = () => {
    setShowPerformance(true);
    loadNurturePerformance(session.session.token, workspace.tenantId)
      .then(setPerformance)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load performance."));
  };

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Lead Nurturing Agent</span>
          <h1>Lead Nurturing isn't active yet.</h1>
        </div>
        <Panel title="Activate Lead Nurturing">
          <Empty icon={Workflow} title="Agent not active" text="Activate the Lead Nurturing agent from Agents & Entitlements to classify leads and get behavior-based nurture recommendations." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Lead Nurturing">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );
  if (loading)
    return (
      <Panel title="Lead Nurturing">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Lead Nurturing workspace…" />
      </Panel>
    );

  const handleRun = async () => {
    setRunning(true);
    setRunError("");
    try {
      const result = await runNurtureAnalysis(session.session.token, workspace.tenantId);
      setOverview(result);
      setLeads(result.leads);
      const segmentsResult = await loadNurtureSegments(session.session.token, workspace.tenantId);
      setSegments(segmentsResult.data);
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : "The lead nurturing analysis could not be generated.");
    } finally {
      setRunning(false);
    }
  };

  const handleProposeTrack = async (segmentKey: string) => {
    setProposingSegmentKey(segmentKey);
    setProposeError("");
    try {
      const track = await proposeNurtureTrack(session.session.token, workspace.tenantId, segmentKey);
      setTracks((prev) => [track, ...prev]);
      setShowTracks(true);
      setSelectedTrackId(track.id);
    } catch (cause) {
      setProposeError(cause instanceof Error ? cause.message : "Could not propose a track for this segment.");
    } finally {
      setProposingSegmentKey(null);
    }
  };

  const handleDecideAlert = async (id: string, decision: "approved" | "rejected") => {
    setDecidingAlertId(id);
    setAlertDecisionError("");
    try {
      const updated = await decideNurtureAlert(session.session.token, workspace.tenantId, id, decision, rejectNoteByAlert[id]);
      setAlerts((prev) => prev.map((alert) => (alert.id === updated.id ? updated : alert)));
    } catch (cause) {
      setAlertDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingAlertId(null);
    }
  };

  const selectedTrack = tracks.find((track) => track.id === selectedTrackId) ?? null;

  if (!overview?.hasRun && leads.length === 0) {
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Lead Nurturing Agent</span>
          <h1>
            Lead Nurturing
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Classifies leads by real lifecycle/engagement evidence, groups them into segments, and proposes behavior-based nurture tracks for your review.</span>
            </span>
          </h1>
        </div>
        <Panel title="Run Analysis">
          <Empty
            icon={Workflow}
            title="No classified leads yet"
            text="This agent classifies leads using real Lead Intelligence scores, real recorded CRM activity, and Buying Intent's account-level signal where available -- it never fabricates email opens, clicks, or webinar attendance Clintra doesn't actually track. Run an analysis to see which leads already have enough signal to classify."
          />
          <p className="ai-callout">Lead Nurturing only recommends and drafts -- it never sends emails, enrolls leads into a live sequence, or changes CRM records. Every track and draft is reviewed by your team.</p>
          {runError && (
            <div className="form-alert error">
              <X size={16} />
              {runError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={running} onClick={() => void handleRun()}>
              {running ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Run Analysis
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Lead Nurturing Agent</span>
          <h1>
            Lead Nurturing
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">{overview?.lastRunAt ? `Last analyzed ${new Date(overview.lastRunAt).toLocaleString()}` : "Not analyzed yet"}</span>
            </span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="button secondary" onClick={() => (showTracks ? setShowTracks(false) : setShowTracks(true))}>
            {showTracks ? "Hide" : "View"} Tracks
          </button>
          <button className="button secondary" onClick={loadAlerts}>
            View Alerts
          </button>
          <button className="button secondary" onClick={loadPerformance}>
            View Performance
          </button>
          <button className="button primary" disabled={running} onClick={() => void handleRun()}>
            {running ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Run Analysis
          </button>
        </div>
      </div>
      {runError && (
        <div className="form-alert error">
          <X size={16} />
          {runError}
        </div>
      )}

      <div className="metrics metrics-4">
        <Metric icon={Workflow} label="Leads in nurture" value={String(overview?.leadsInNurtureCount ?? 0)} meta="Assigned to a track" />
        <Metric icon={Sparkles} label="Sales-ready" value={String(overview?.salesReadyCount ?? 0)} meta="Corroborated by score, intent, or an open opportunity" />
        <Metric icon={AlertTriangle} label="Dormant" value={String(overview?.dormantCount ?? 0)} meta="No recent recorded engagement" />
        <Metric icon={RefreshCw} label="Re-engagement candidates" value={String(overview?.reEngagementCandidateCount ?? 0)} meta="Previously valuable, went quiet" />
      </div>

      <Panel title="Segments" kicker="Live-computed from the latest classification -- not persisted">
        {proposeError && (
          <div className="form-alert error">
            <X size={16} />
            {proposeError}
          </div>
        )}
        {segments.length === 0 ? (
          <Empty icon={Workflow} title="No segments yet" text="Run an analysis to classify leads into segments." />
        ) : (
          <div className="page-stack">
            {segments.map((segment) => (
              <div key={segment.segmentKey} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>
                    {nurtureClassificationLabel(segment.classification)} -- {segment.band ?? "unscored"} band
                  </b>
                  <em className={`status ${nurtureClassificationTone(segment.classification)}`}>{segment.leadCount} leads</em>
                </div>
                {segment.sampleReasons.length > 0 && (
                  <ul className="score-why">
                    {segment.sampleReasons.map((reason, index) => (
                      <li key={index}>{reason}</li>
                    ))}
                  </ul>
                )}
                <button className="button secondary" disabled={proposingSegmentKey === segment.segmentKey} onClick={() => void handleProposeTrack(segment.segmentKey)}>
                  {proposingSegmentKey === segment.segmentKey ? <LoaderCircle className="spin" size={14} /> : <Sparkles size={14} />} Propose Track
                </button>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="Leads"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <select value={classificationFilter} onChange={(event) => setClassificationFilter(event.target.value as NurtureClassification | "")}>
              <option value="">All classifications</option>
              <option value="new">New</option>
              <option value="engaging">Engaging</option>
              <option value="evaluating">Evaluating</option>
              <option value="high_intent">High Intent</option>
              <option value="sales_ready">Sales Ready</option>
              <option value="dormant">Dormant</option>
              <option value="re_engagement_candidate">Re-engagement Candidate</option>
              <option value="disqualified_suppress">Disqualified / Suppress</option>
            </select>
            <select value={trendFilter} onChange={(event) => setTrendFilter(event.target.value as NurtureEngagementTrend | "")}>
              <option value="">All trends</option>
              <option value="increasing">Increasing</option>
              <option value="stable">Stable</option>
              <option value="declining">Declining</option>
              <option value="insufficient_data">Insufficient data</option>
            </select>
          </div>
        }
      >
        {leads.length === 0 ? (
          <Empty icon={Workflow} title="No leads match" text="No classified leads match the current filters." />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Classification</th>
                <th>Engagement</th>
                <th>Track</th>
                <th>Last classified</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.leadId}
                  className="clickable-row"
                  onClick={() => {
                    setSelectedTrackId(null);
                    setSelectedLeadId(lead.leadId);
                  }}
                >
                  <td>{lead.leadName}</td>
                  <td>
                    <em className={`status ${nurtureClassificationTone(lead.classification)}`}>{nurtureClassificationLabel(lead.classification)}</em>
                  </td>
                  <td>
                    <em className={`status ${nurtureEngagementTrendTone(lead.engagementTrend)}`}>
                      {nurtureEngagementLevelLabel(lead.engagementLevel)} -- {nurtureEngagementTrendLabel(lead.engagementTrend)}
                    </em>
                  </td>
                  <td>{lead.trackName ?? "Not assigned"}</td>
                  <td>{new Date(lead.snapshotAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {showTracks && (
        <Panel title="Nurture Tracks">
          {tracks.length === 0 ? (
            <Empty icon={Workflow} title="No tracks yet" text="Propose a track from a segment above to get started." />
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Track</th>
                  <th>Status</th>
                  <th>Steps</th>
                </tr>
              </thead>
              <tbody>
                {tracks.map((track) => (
                  <tr
                    key={track.id}
                    className="clickable-row"
                    onClick={() => {
                      setSelectedLeadId(null);
                      setSelectedTrackId(track.id);
                    }}
                  >
                    <td>{track.name}</td>
                    <td>
                      <em className={`status ${nurtureTrackPlanStatusTone(track.planStatus)}`}>{track.planStatus.replaceAll("_", " ")}</em>
                    </td>
                    <td>{track.stages.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {showAlerts && (
        <Panel title="Alerts" kicker="Fires only on a newsworthy classification transition -- reviewed by your team">
          {alertDecisionError && (
            <div className="form-alert error">
              <X size={16} />
              {alertDecisionError}
            </div>
          )}
          {alerts.length === 0 ? (
            <Empty icon={AlertTriangle} title="No alerts yet" text="Alerts appear here when a lead becomes sales-ready or a tracked lead stalls." />
          ) : (
            <div className="page-stack">
              {alerts.map((alert) => (
                <div key={alert.id} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{alert.title}</b>
                    <em className={`status ${alert.status === "approved" ? "complete" : alert.status === "rejected" ? "error" : "pending"}`}>{alert.status}</em>
                  </div>
                  <p>
                    <b>Why:</b> {alert.rationale}
                  </p>
                  {alert.status === "pending" && (
                    <div className="page-stack">
                      <input
                        placeholder="Note (optional, shown on reject)"
                        value={rejectNoteByAlert[alert.id] ?? ""}
                        onChange={(event) => setRejectNoteByAlert((prev) => ({ ...prev, [alert.id]: event.target.value }))}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="button secondary" disabled={decidingAlertId === alert.id} onClick={() => void handleDecideAlert(alert.id, "rejected")}>
                          Reject
                        </button>
                        <button className="button primary" disabled={decidingAlertId === alert.id} onClick={() => void handleDecideAlert(alert.id, "approved")}>
                          {decidingAlertId === alert.id ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {showPerformance && performance && (
        <Panel title="Performance">
          <table className="data-table">
            <thead>
              <tr>
                <th>Track</th>
                <th>Leads</th>
                <th>Progressed</th>
                <th>Stalled</th>
                <th>Re-engaged</th>
              </tr>
            </thead>
            <tbody>
              {performance.tracks.map((track, index) => (
                <tr key={index}>
                  <td>{track.trackName ?? "Unassigned"}</td>
                  <td>{track.leadCount}</td>
                  <td>{track.progressedCount}</td>
                  <td>{track.stalledCount}</td>
                  <td>{track.reEngagedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {performance.dataQualityNotes.length > 0 && (
            <ul className="score-why">
              {performance.dataQualityNotes.map((note, index) => (
                <li key={index}>{note}</li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      <Drawer open={selectedLeadId !== null} title={leads.find((lead) => lead.leadId === selectedLeadId)?.leadName ?? "Lead"} onClose={() => setSelectedLeadId(null)}>
        {selectedLeadId && <LeadNurtureDetailDrawerBody session={session} workspace={workspace} leadId={selectedLeadId} tracks={tracks} onAssigned={loadAll} />}
      </Drawer>

      <Drawer open={selectedTrack !== null} title={selectedTrack?.name ?? "Track"} onClose={() => setSelectedTrackId(null)}>
        {selectedTrack && (
          <NurtureTrackDetailBody
            session={session}
            workspace={workspace}
            track={selectedTrack}
            onUpdated={(updated) => setTracks((prev) => prev.map((track) => (track.id === updated.id ? updated : track)))}
          />
        )}
      </Drawer>
    </div>
  );
}

// Revenue Forecasting -- a segment-aware, confidence-scored revenue
// projection, additive to (never replacing) Revenue Orchestration's own
// simpler tenant-wide forecast. It never commits to a number on the
// business's behalf: every figure is a projection for leadership review,
// scenarios are always shown as a range, and confidence/data-quality
// limitations are always surfaced alongside the number itself.
const FORECAST_PERIOD_LABELS: Record<ForecastPeriodKind, string> = {
  current_month: "Current Month",
  next_month: "Next Month",
  current_quarter: "Current Quarter",
  next_quarter: "Next Quarter",
  current_year: "Current Year",
};
function forecastTargetStatusTone(status: RevenueForecastResult["targetComparison"]["status"]): "complete" | "pending" | "error" | "neutral" {
  if (status === "on_track" || status === "above_target") return "complete";
  if (status === "at_risk") return "pending";
  if (status === "below_target") return "error";
  return "neutral";
}
function forecastTargetStatusLabel(status: RevenueForecastResult["targetComparison"]["status"]): string {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
function forecastRiskSeverityTone(severity: ForecastRisk["severity"]): "error" | "pending" | "neutral" {
  if (severity === "high") return "error";
  if (severity === "medium") return "pending";
  return "neutral";
}
function formatCurrency(value: number | null): string {
  if (value === null) return "n/a";
  return Math.round(value).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function RevenueForecastingView({
  session,
  workspace,
  onNavigateToAgents,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigateToAgents: () => void;
}) {
  const agent = workspace.agents.find((entry) => entry.id === "revenue-forecasting");
  const [period, setPeriod] = useState<ForecastPeriodKind>("current_quarter");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forecast, setForecast] = useState<RevenueForecastResult | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<RevenueForecastResult[]>([]);
  const [showAccuracy, setShowAccuracy] = useState(false);
  const [accuracy, setAccuracy] = useState<RevenueForecastAccuracyResult | null>(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alerts, setAlerts] = useState<RevenueForecastAlert[]>([]);
  const [decidingAlertId, setDecidingAlertId] = useState<string | null>(null);
  const [alertDecisionError, setAlertDecisionError] = useState("");
  const [rejectNoteByAlert, setRejectNoteByAlert] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    setError("");
    loadRevenueForecast(session.session.token, workspace.tenantId, period)
      .then((result) => setForecast(result.hasRun ? result : null))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load Revenue Forecasting."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (agent?.active) load();
  }, [agent?.active, period]);

  const loadHistory = () => {
    setShowHistory(true);
    loadRevenueForecastHistory(session.session.token, workspace.tenantId, period)
      .then((result) => setHistory(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load forecast history."));
  };
  const loadAccuracy = () => {
    setShowAccuracy(true);
    loadRevenueForecastAccuracy(session.session.token, workspace.tenantId)
      .then(setAccuracy)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load accuracy tracking."));
  };
  const loadAlerts = () => {
    setShowAlerts(true);
    loadRevenueForecastAlerts(session.session.token, workspace.tenantId)
      .then((result) => setAlerts(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load alerts."));
  };

  if (!agent?.active)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Revenue Forecasting Agent</span>
          <h1>Revenue Forecasting isn't active yet.</h1>
        </div>
        <Panel title="Activate Revenue Forecasting">
          <Empty icon={LineChart} title="Agent not active" text="Activate the Revenue Forecasting agent from Agents & Entitlements to get an explainable, segment-aware revenue projection from your live pipeline." />
          <div className="page-title-action">
            <button className="button primary" onClick={onNavigateToAgents}>
              Go to Agents &amp; Entitlements <ArrowRight size={15} />
            </button>
          </div>
        </Panel>
      </div>
    );

  if (error)
    return (
      <Panel title="Revenue Forecasting">
        <div className="form-alert error">
          <X size={16} />
          {error}
        </div>
      </Panel>
    );

  const handleRun = async () => {
    setRunning(true);
    setRunError("");
    try {
      const result = await runRevenueForecast(session.session.token, workspace.tenantId, period);
      setForecast(result);
      setShowHistory(false);
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : "The revenue forecast could not be generated.");
    } finally {
      setRunning(false);
    }
  };

  const handleDecideAlert = async (id: string, decision: "approved" | "rejected") => {
    setDecidingAlertId(id);
    setAlertDecisionError("");
    try {
      const updated = await decideRevenueForecastAlert(session.session.token, workspace.tenantId, id, decision, rejectNoteByAlert[id]);
      setAlerts((prev) => prev.map((alert) => (alert.id === updated.id ? updated : alert)));
    } catch (cause) {
      setAlertDecisionError(cause instanceof Error ? cause.message : "Could not record that decision.");
    } finally {
      setDecidingAlertId(null);
    }
  };

  const periodSelector = (
    <select value={period} onChange={(event) => setPeriod(event.target.value as ForecastPeriodKind)}>
      {(Object.keys(FORECAST_PERIOD_LABELS) as ForecastPeriodKind[]).map((kind) => (
        <option key={kind} value={kind}>
          {FORECAST_PERIOD_LABELS[kind]}
        </option>
      ))}
    </select>
  );

  if (loading)
    return (
      <Panel title="Revenue Forecasting">
        <Empty icon={LoaderCircle} title="Loading" text="Loading your Revenue Forecasting workspace…" />
      </Panel>
    );

  if (!forecast) {
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Revenue Forecasting Agent</span>
          <h1>
            Revenue Forecasting
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">Generates an explainable, data-grounded revenue projection from live CRM pipeline and historical conversion patterns -- never a guaranteed commitment.</span>
            </span>
          </h1>
        </div>
        <Panel title="Run Forecast" action={periodSelector}>
          <Empty icon={LineChart} title="No forecast yet for this period" text="This agent weighs each open opportunity by real CRM probability or historical segment performance -- it never guesses a value it can't ground in data. Run a forecast to see what your current pipeline supports." />
          <p className="ai-callout">Revenue Forecasting only projects and recommends -- it never commits to a number on the business's behalf, changes a target, or modifies CRM records. Final forecast commitments remain a human decision.</p>
          {runError && (
            <div className="form-alert error">
              <X size={16} />
              {runError}
            </div>
          )}
          <div className="page-title-action">
            <button className="button primary" disabled={running} onClick={() => void handleRun()}>
              {running ? <LoaderCircle className="spin" size={15} /> : <Sparkles size={15} />} Run Forecast
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Revenue Forecasting Agent</span>
          <h1>
            Revenue Forecasting
            <span className="info-tooltip">
              <Info size={13} />
              <span className="info-tooltip-content">
                {FORECAST_PERIOD_LABELS[period]} ({forecast.period.start} to {forecast.period.end}) -- last forecast {new Date(forecast.createdAt).toLocaleString()}
              </span>
            </span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {periodSelector}
          <button className="button secondary" onClick={loadHistory}>
            History
          </button>
          <button className="button secondary" onClick={loadAccuracy}>
            Accuracy
          </button>
          <button className="button secondary" onClick={loadAlerts}>
            Alerts
          </button>
          <button className="button primary" disabled={running} onClick={() => void handleRun()}>
            {running ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />} Run Forecast
          </button>
        </div>
      </div>
      {runError && (
        <div className="form-alert error">
          <X size={16} />
          {runError}
        </div>
      )}
      <p className="ai-callout">This is a data-grounded projection for leadership review, not a guaranteed outcome or a committed number. Final forecast commitments remain a human decision.</p>

      <div className="metrics metrics-4">
        <Metric icon={LineChart} label="Expected revenue" value={formatCurrency(forecast.forecastSummary.expectedRevenue)} meta={`Conservative ${formatCurrency(forecast.forecastSummary.conservativeRevenue)} / Upside ${formatCurrency(forecast.forecastSummary.upsideRevenue)}`} />
        <Metric icon={Sparkles} label="Confidence" value={forecast.forecastSummary.confidenceLevel.toUpperCase()} meta={forecast.forecastSummary.confidenceReasons[0] ?? ""} />
        <Metric
          icon={Target}
          label="Target"
          value={formatCurrency(forecast.targetComparison.targetRevenue)}
          meta={forecast.targetComparison.variancePercentage !== null ? `${(forecast.targetComparison.variancePercentage * 100).toFixed(1)}% variance` : "No target configured"}
        />
        <Metric icon={AlertTriangle} label="Status" value={forecastTargetStatusLabel(forecast.targetComparison.status)} meta={`${forecast.forecastRisks.length} risk${forecast.forecastRisks.length === 1 ? "" : "s"} identified`} />
      </div>

      {forecast.narrative && (
        <Panel title="Forecast Narrative">
          <p>{forecast.narrative}</p>
        </Panel>
      )}

      <Panel title="Forecast Change">
        {forecast.forecastChange.previousForecast === null ? (
          <Empty icon={LineChart} title="No prior forecast to compare" text="This is the first forecast run for this period." />
        ) : forecast.forecastChange.primaryDrivers.length === 0 ? (
          <p className="ai-callout">No meaningful change since the previous forecast ({formatCurrency(forecast.forecastChange.previousForecast)}).</p>
        ) : (
          <div className="page-stack">
            <div className="source-list">
              <div>
                <span>Previous forecast</span>
                <em>{formatCurrency(forecast.forecastChange.previousForecast)}</em>
              </div>
              <div>
                <span>Current forecast</span>
                <em>{formatCurrency(forecast.forecastChange.currentForecast)}</em>
              </div>
              <div>
                <span>Change</span>
                <em className={`status ${(forecast.forecastChange.changeAmount ?? 0) >= 0 ? "complete" : "error"}`}>
                  {(forecast.forecastChange.changeAmount ?? 0) >= 0 ? "+" : ""}
                  {formatCurrency(forecast.forecastChange.changeAmount)}
                </em>
              </div>
            </div>
            <ul className="score-why">
              {forecast.forecastChange.primaryDrivers.map((driver, index) => (
                <li key={index}>{driver.description}</li>
              ))}
            </ul>
          </div>
        )}
      </Panel>

      <Panel title="Segment Forecasts">
        {forecast.segmentForecasts.length === 0 ? (
          <Empty icon={LineChart} title="No segments" text="No open pipeline is in scope for this period." />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Probability source</th>
                <th>Expected revenue</th>
                <th>Conversion rate used</th>
                <th>Opportunities</th>
              </tr>
            </thead>
            <tbody>
              {forecast.segmentForecasts.map((segment, index) => (
                <tr key={index}>
                  <td>{segment.segment.replaceAll("_", " ").replaceAll(":", ": ")}</td>
                  <td>{formatCurrency(segment.expectedRevenue)}</td>
                  <td>{segment.conversionRateUsed !== null ? `${(segment.conversionRateUsed * 100).toFixed(0)}%` : "n/a"}</td>
                  <td>{segment.opportunityCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Marketing Contribution">
        {!forecast.marketingContribution.available ? (
          <Empty icon={LineChart} title="Not available" text={forecast.marketingContribution.reason ?? "Marketing contribution could not be computed for this forecast."} />
        ) : (
          <>
            <div className="source-list">
              {forecast.marketingContribution.bySource.map((entry, index) => (
                <div key={index}>
                  <span>{entry.source}</span>
                  <em>
                    {formatCurrency(entry.expectedRevenue)} ({entry.opportunityCount})
                  </em>
                </div>
              ))}
            </div>
            {forecast.marketingContribution.contributionNotes.map((note, index) => (
              <p key={index} className="ai-callout">
                {note}
              </p>
            ))}
          </>
        )}
      </Panel>

      {forecast.forecastRisks.length > 0 && (
        <Panel title="Forecast Risks">
          <div className="page-stack">
            {forecast.forecastRisks.map((risk) => (
              <div key={risk.key} className="website-recommendation-card">
                <div className="page-title-action">
                  <b>{risk.title}</b>
                  <em className={`status ${forecastRiskSeverityTone(risk.severity)}`}>{risk.severity}</em>
                </div>
                <p>{risk.description}</p>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {forecast.dataQualityFlags.length > 0 && (
        <Panel title="Data Quality">
          <div className="page-stack">
            {forecast.dataQualityFlags.map((flag) => (
              <div key={flag.key} className="website-recommendation-card">
                <b>{flag.title}</b>
                <p>{flag.description}</p>
                <p className="ai-callout">{flag.impact}</p>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Forecast Assumptions">
        <ul className="score-why">
          {forecast.forecastAssumptions.map((assumption, index) => (
            <li key={index}>{assumption}</li>
          ))}
        </ul>
      </Panel>

      {showHistory && (
        <Panel title="Forecast History">
          {history.length === 0 ? (
            <Empty icon={LoaderCircle} title="Loading" text="Loading forecast history…" />
          ) : (
            <div className="source-list">
              {history.map((entry, index) => (
                <div key={index}>
                  <span>{new Date(entry.createdAt).toLocaleString()}</span>
                  <em className={`status ${forecastTargetStatusTone(entry.targetComparison.status)}`}>{formatCurrency(entry.forecastSummary.expectedRevenue)}</em>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {showAccuracy && (
        <Panel title="Forecast Accuracy" kicker="Compares past forecasts against real closed-won outcomes for periods that have already elapsed">
          {!accuracy ? (
            <Empty icon={LoaderCircle} title="Loading" text="Loading accuracy tracking…" />
          ) : !accuracy.available ? (
            <Empty icon={LineChart} title="Not enough history yet" text={accuracy.reason ?? "No completed forecast periods yet."} />
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>Error</th>
                  <th>Bias</th>
                </tr>
              </thead>
              <tbody>
                {accuracy.periods.map((entry, index) => (
                  <tr key={index}>
                    <td>
                      {entry.periodStart} to {entry.periodEnd}
                    </td>
                    <td>{formatCurrency(entry.expectedRevenue)}</td>
                    <td>{formatCurrency(entry.actualRevenue)}</td>
                    <td>{entry.percentageError !== null ? `${(entry.percentageError * 100).toFixed(1)}%` : "n/a"}</td>
                    <td>{entry.bias ?? "n/a"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {showAlerts && (
        <Panel title="Recommendations" kicker="Reviewed by your team -- Revenue Forecasting never changes a target, CRM record, or campaign automatically">
          {alertDecisionError && (
            <div className="form-alert error">
              <X size={16} />
              {alertDecisionError}
            </div>
          )}
          {alerts.length === 0 ? (
            <Empty icon={Lightbulb} title="No recommendations yet" text="Recommendations appear here after a forecast run identifies a risk or data quality issue worth reviewing." />
          ) : (
            <div className="page-stack">
              {alerts.map((alert) => (
                <div key={alert.id} className="website-recommendation-card">
                  <div className="page-title-action">
                    <b>{alert.title}</b>
                    <em className={`status ${alert.status === "approved" ? "complete" : alert.status === "rejected" ? "error" : "pending"}`}>{alert.status}</em>
                  </div>
                  <p>{alert.rationale}</p>
                  {alert.status === "pending" && (
                    <div className="page-stack">
                      <input
                        placeholder="Note (optional, shown on reject)"
                        value={rejectNoteByAlert[alert.id] ?? ""}
                        onChange={(event) => setRejectNoteByAlert((prev) => ({ ...prev, [alert.id]: event.target.value }))}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="button secondary" disabled={decidingAlertId === alert.id} onClick={() => void handleDecideAlert(alert.id, "rejected")}>
                          Reject
                        </button>
                        <button className="button primary" disabled={decidingAlertId === alert.id} onClick={() => void handleDecideAlert(alert.id, "approved")}>
                          {decidingAlertId === alert.id ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Approve
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

function IntegrationsView({
  session,
  workspace,
  onUpdate,
}: {
  session: AuthSession;
  workspace: Workspace;
  onUpdate: (workspace: Workspace) => void;
}) {
  const [catalog, setCatalog] = useState<ConnectorManifest[]>([]);
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [activeForm, setActiveForm] = useState<string | null>(null);
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [orgUrlValue, setOrgUrlValue] = useState("");
  const [shopDomainValue, setShopDomainValue] = useState("");
  const [marketoBaseUrlValue, setMarketoBaseUrlValue] = useState("");
  const [marketoClientSecretValue, setMarketoClientSecretValue] = useState("");
  const [syncingAll, setSyncingAll] = useState(false);
  const [callbackStatus, setCallbackStatus] = useState<{
    provider: string;
    status: "connected" | "error";
  } | null>(null);
  useEffect(() => {
    void loadIntegrationsCatalog(session.session.token, workspace.tenantId)
      .then((result) => setCatalog(result.data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("integration");
    const status = params.get("status");
    if (!provider || (status !== "connected" && status !== "error")) return;
    setCallbackStatus({ provider, status });
    window.history.replaceState({}, "", pathForView("Integrations"));
    void loadWorkspace(session.session.token, workspace.tenantId)
      .then(onUpdate)
      .catch(() => {});
  }, []);
  const connect = async (provider: string, extra?: { orgUrl?: string; shopDomain?: string }) => {
    setBusyProvider(provider);
    setError("");
    try {
      const { authorizeUrl } = await startIntegrationConnect(
        session.session.token,
        workspace.tenantId,
        provider,
        extra,
      );
      window.location.assign(authorizeUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not start the connection.");
      setBusyProvider(null);
    }
  };
  const disconnect = async (provider: string) => {
    setBusyProvider(provider);
    setError("");
    try {
      await disconnectIntegration(session.session.token, workspace.tenantId, provider);
      onUpdate(await loadWorkspace(session.session.token, workspace.tenantId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not disconnect.");
    } finally {
      setBusyProvider(null);
    }
  };
  const submitApiKey = async (provider: string) => {
    setBusyProvider(provider);
    setError("");
    try {
      await connectIntegrationWithApiKey(
        session.session.token,
        workspace.tenantId,
        provider,
        apiKeyValue,
        provider === "marketo" ? { baseUrl: marketoBaseUrlValue, clientSecret: marketoClientSecretValue } : undefined,
      );
      setActiveForm(null);
      setApiKeyValue("");
      setMarketoBaseUrlValue("");
      setMarketoClientSecretValue("");
      onUpdate(await loadWorkspace(session.session.token, workspace.tenantId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not connect.");
    } finally {
      setBusyProvider(null);
    }
  };
  const sync = async (provider: string) => {
    setBusyProvider(provider);
    setError("");
    try {
      await syncIntegration(session.session.token, workspace.tenantId, provider);
      onUpdate(await loadWorkspace(session.session.token, workspace.tenantId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not sync.");
    } finally {
      setBusyProvider(null);
    }
  };
  const syncAll = async () => {
    setSyncingAll(true);
    setError("");
    try {
      const { results } = await syncAllIntegrations(session.session.token, workspace.tenantId);
      const failures = results.filter((result) => !result.ok);
      if (failures.length)
        setError(`Some channels failed to sync: ${failures.map((f) => `${labelFor(f.provider)} (${f.message})`).join(", ")}`);
      onUpdate(await loadWorkspace(session.session.token, workspace.tenantId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not sync all channels.");
    } finally {
      setSyncingAll(false);
    }
  };
  const connectedCount = workspace.integrations.filter((item) => item.status === "connected").length;
  const categoryGroups = new Map<string, (typeof integrationOptions)[number][]>();
  for (const entry of integrationOptions) {
    const category = catalog.find((manifest) => manifest.id === entry[0])?.category ?? "Integrations";
    if (!categoryGroups.has(category)) categoryGroups.set(category, []);
    categoryGroups.get(category)!.push(entry);
  }
  const orderedCategories = [
    ...CATEGORY_ORDER.filter((category) => categoryGroups.has(category)),
    ...[...categoryGroups.keys()].filter((category) => !CATEGORY_ORDER.includes(category)),
  ];
  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Composable data layer</span>
          <h1>Connect the systems already running revenue.</h1>
          <p>
            Connect via OAuth where supported, or paste an API key for
            providers that use one. Credentials are encrypted before storage.
          </p>
        </div>
        <button
          className="button primary"
          disabled={syncingAll || connectedCount === 0}
          onClick={() => void syncAll()}
          title={connectedCount === 0 ? "Connect at least one integration first" : "Pull fresh data from every connected channel"}
        >
          {syncingAll ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />}
          {syncingAll ? "Syncing…" : "Sync all connected channels"}
        </button>
      </div>
      {callbackStatus &&
        (() => {
          const providerName =
            integrationOptions.find(([id]) => id === callbackStatus.provider)?.[1] ??
            labelFor(callbackStatus.provider);
          return (
            <div className={`form-alert ${callbackStatus.status === "connected" ? "success" : "error"}`}>
              {callbackStatus.status === "connected" ? <Check size={17} /> : <X size={17} />}
              {callbackStatus.status === "connected"
                ? `${providerName} connected successfully.`
                : `Could not connect ${providerName}. Please try again.`}
            </div>
          );
        })()}
      {error && (
        <div className="form-alert error">
          <X size={17} />
          {error}
        </div>
      )}
      {orderedCategories.map((category) => (
        <section key={category} className="integration-category">
          <h2>{CATEGORY_LABELS[category] ?? category}</h2>
          <div className="integration-grid">
            {categoryGroups.get(category)!.map(([id, name, description]) => {
          const manifest = catalog.find((entry) => entry.id === id);
          const selected = workspace.integrations.find((item) => item.provider === id);
          const connected = selected?.status === "connected";
          const oauthSupported = manifest?.auth === "oauth2";
          const apiKeySupported = manifest?.auth === "api_key";
          const busy = busyProvider === id;
          const formOpen = activeForm === id;
          const brand = PROVIDER_BRAND_ICONS[id];
          return (
            <article key={id}>
              <span>
                {brand ? <brand.Icon size={22} color={brand.color} /> : <Plug size={22} />}
              </span>
              <div>
                <h3>{name}</h3>
                <p>{description}</p>
              </div>
              <em className={`status ${connected ? "complete" : selected ? "pending" : "neutral"}`}>
                {connected ? "Connected" : selected ? labelFor(selected.status) : "Not selected"}
                {connected && manifest && (
                  <span className="info-tooltip">
                    <Info size={13} />
                    <span className="info-tooltip-content">
                      <strong>Data received</strong>
                      <ul>
                        {manifest.capabilities
                          .filter((capability) => capability.implemented)
                          .map((capability) => (
                            <li key={capability.id}>{capability.label}</li>
                          ))}
                      </ul>
                    </span>
                  </span>
                )}
              </em>
              {oauthSupported && !((id === "dynamics" || id === "shopify") && !connected) ? (
                <button
                  className={`button ${connected ? "disconnect" : "connect"}`}
                  disabled={busy}
                  onClick={() => (connected ? disconnect(id) : connect(id))}
                >
                  {busy ? (
                    <LoaderCircle className="spin" size={15} />
                  ) : connected ? (
                    "Disconnect"
                  ) : (
                    "Connect"
                  )}
                </button>
              ) : oauthSupported && id === "dynamics" ? (
                // Every other OAuth provider resolves its own org
                // automatically after login -- Dynamics needs the
                // environment URL up front (Microsoft's Global Discovery
                // Service, which would have avoided this extra step, was
                // retired in June 2026).
                formOpen ? (
                  <form
                    className="integration-token-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void connect(id, { orgUrl: orgUrlValue });
                    }}
                  >
                    <input
                      type="text"
                      value={orgUrlValue}
                      onChange={(event) => setOrgUrlValue(event.target.value)}
                      placeholder="yourorg.crm.dynamics.com"
                      autoFocus
                    />
                    <div className="integration-token-actions">
                      <button type="submit" className="button primary" disabled={busy || !orgUrlValue.trim()}>
                        {busy ? <LoaderCircle className="spin" size={15} /> : "Connect"}
                      </button>
                      <button
                        type="button"
                        className="button secondary"
                        disabled={busy}
                        onClick={() => {
                          setActiveForm(null);
                          setOrgUrlValue("");
                          setError("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    className="button connect"
                    onClick={() => {
                      setActiveForm(id);
                      setOrgUrlValue("");
                      setError("");
                    }}
                  >
                    Connect
                  </button>
                )
              ) : oauthSupported && id === "shopify" ? (
                // The OAuth authorize/token URLs are hosted on the
                // merchant's own shop subdomain, so it has to be known
                // before the redirect happens -- same shape as Dynamics'
                // environment-URL prompt, for the same underlying reason.
                formOpen ? (
                  <form
                    className="integration-token-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void connect(id, { shopDomain: shopDomainValue });
                    }}
                  >
                    <input
                      type="text"
                      value={shopDomainValue}
                      onChange={(event) => setShopDomainValue(event.target.value)}
                      placeholder="yourstore.myshopify.com"
                      autoFocus
                    />
                    <div className="integration-token-actions">
                      <button type="submit" className="button primary" disabled={busy || !shopDomainValue.trim()}>
                        {busy ? <LoaderCircle className="spin" size={15} /> : "Connect"}
                      </button>
                      <button
                        type="button"
                        className="button secondary"
                        disabled={busy}
                        onClick={() => {
                          setActiveForm(null);
                          setShopDomainValue("");
                          setError("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    className="button connect"
                    onClick={() => {
                      setActiveForm(id);
                      setShopDomainValue("");
                      setError("");
                    }}
                  >
                    Connect
                  </button>
                )
              ) : apiKeySupported ? (
                connected ? (
                  <button className="button disconnect" disabled={busy} onClick={() => disconnect(id)}>
                    {busy ? <LoaderCircle className="spin" size={15} /> : "Disconnect"}
                  </button>
                ) : formOpen && id === "marketo" ? (
                  // Marketo has no OAuth consent screen -- each tenant
                  // creates their own Custom Service and pastes its
                  // Client ID/Secret plus their instance's own subdomain,
                  // so this needs three fields instead of every other
                  // api_key provider's one.
                  <form
                    className="integration-token-form integration-token-form-multi"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitApiKey(id);
                    }}
                  >
                    <input
                      type="text"
                      value={marketoBaseUrlValue}
                      onChange={(event) => setMarketoBaseUrlValue(event.target.value)}
                      placeholder="your-instance.mktorest.com"
                      autoFocus
                    />
                    <input
                      type="password"
                      value={apiKeyValue}
                      onChange={(event) => setApiKeyValue(event.target.value)}
                      placeholder="Client ID"
                    />
                    <input
                      type="password"
                      value={marketoClientSecretValue}
                      onChange={(event) => setMarketoClientSecretValue(event.target.value)}
                      placeholder="Client Secret"
                    />
                    <div className="integration-token-actions">
                      <button
                        type="submit"
                        className="button primary"
                        disabled={busy || !marketoBaseUrlValue.trim() || !apiKeyValue.trim() || !marketoClientSecretValue.trim()}
                      >
                        {busy ? <LoaderCircle className="spin" size={15} /> : "Connect"}
                      </button>
                      <button
                        type="button"
                        className="button secondary"
                        disabled={busy}
                        onClick={() => {
                          setActiveForm(null);
                          setApiKeyValue("");
                          setMarketoBaseUrlValue("");
                          setMarketoClientSecretValue("");
                          setError("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : formOpen ? (
                  <form
                    className="integration-token-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitApiKey(id);
                    }}
                  >
                    <input
                      type="password"
                      value={apiKeyValue}
                      onChange={(event) => setApiKeyValue(event.target.value)}
                      placeholder={`Paste your ${name} API key`}
                      autoFocus
                    />
                    <div className="integration-token-actions">
                      <button type="submit" className="button primary" disabled={busy || !apiKeyValue.trim()}>
                        {busy ? <LoaderCircle className="spin" size={15} /> : "Save"}
                      </button>
                      <button
                        type="button"
                        className="button secondary"
                        disabled={busy}
                        onClick={() => {
                          setActiveForm(null);
                          setApiKeyValue("");
                          setError("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    className="button connect"
                    onClick={() => {
                      setActiveForm(id);
                      setApiKeyValue("");
                      setMarketoBaseUrlValue("");
                      setMarketoClientSecretValue("");
                      setError("");
                    }}
                  >
                    Connect
                  </button>
                )
              ) : (
                <button
                  className="button secondary"
                  disabled
                  title="Provider OAuth credentials must be configured before authorisation can begin."
                >
                  {selected ? "Awaiting OAuth setup" : "OAuth setup required"}
                </button>
              )}
              {connected && (
                <footer>
                  <span>
                    {selected?.lastSyncedAt
                      ? `Last synced ${new Date(selected.lastSyncedAt).toLocaleString()}`
                      : "Not yet synced"}
                  </span>
                  {id !== "slack" && (
                    <button className="button secondary" disabled={busy} onClick={() => void sync(id)}>
                      {busy ? <LoaderCircle className="spin" size={13} /> : "Sync now"}
                    </button>
                  )}
                </footer>
              )}
            </article>
          );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
function AgentsView({
  session,
  workspace,
  onUpgrade,
  onUpdate,
}: {
  session: AuthSession;
  workspace: Workspace;
  onUpgrade: () => void;
  onUpdate: (workspace: Workspace) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState("");
  const [error, setError] = useState("");
  const canRefresh = ["owner", "admin"].includes(workspace.role);
  const activeAgentCount = workspace.agents.filter(
    (agent) => agent.active && agent.id !== "orchestrator",
  ).length;
  const activeAgentLimit = workspace.billing.plan.entitlements.activeAgents;
  const atActiveLimit =
    typeof activeAgentLimit === "number" && activeAgentCount >= activeAgentLimit;
  const approvals = workspace.agents.filter(
    (agent) => agent.status === "needs_approval",
  ).length;
  const waiting = workspace.agents.filter((agent) =>
    ["waiting", "not_started", "registered"].includes(agent.status),
  ).length;
  const statusClass = (status: string) =>
    status === "completed"
      ? "complete"
      : status === "failed"
        ? "error"
        : ["needs_approval", "queued", "ready"].includes(status)
          ? "pending"
          : "neutral";
  const refresh = async () => {
    setBusy(true);
    setError("");
    try {
      onUpdate(
        await activateAgentNetwork(session.session.token, workspace.tenantId),
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Unable to refresh the agent network.",
      );
    } finally {
      setBusy(false);
    }
  };
  const changeActivation = async (agentId: string, action: "activate" | "deactivate") => {
    setChanging(agentId);
    setError("");
    try {
      await setAgentActivation(
        session.session.token,
        workspace.tenantId,
        agentId,
        action,
      );
      onUpdate(
        await loadWorkspace(session.session.token, workspace.tenantId),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to change agent activation.");
    } finally {
      setChanging("");
    }
  };
  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Agent network</span>
          <h1>Every specialist agent, in one place.</h1>
          <p>
            All agents available on your plan are listed below — activate,
            monitor, and review what each one is doing. Agents draft, score,
            and recommend; nothing is published or executed without your
            approval.
          </p>
        </div>
        <button
          className="button primary"
          disabled={busy || !canRefresh}
          onClick={refresh}
          title={
            canRefresh
              ? "Re-evaluate agents against the latest connected evidence"
              : "Only workspace owners and administrators can refresh agents"
          }
        >
          {busy ? <LoaderCircle className="spin" /> : <RefreshCw size={18} />}
          {busy ? "Refreshing…" : "Refresh agent network"}
        </button>
      </div>
      {error && <div className="form-alert error">{error}</div>}
      <div className="agent-summary">
        <span>
          <strong>{workspace.agents.length}</strong>
          <small>registered agents</small>
        </span>
        <span>
          <strong>{workspace.agents.filter((agent) => agent.active && agent.id !== "orchestrator").length} / {String(workspace.billing.plan.entitlements.activeAgents ?? "Custom")}</strong>
          <small>active specialist agents</small>
        </span>
        <span>
          <strong>{approvals}</strong>
          <small>drafts need approval</small>
        </span>
        <span>
          <strong>{waiting}</strong>
          <small>waiting for data</small>
        </span>
      </div>
      <div className="agent-grid">
        {workspace.agents.map((agent) => {
          const needsUpgrade =
            agent.id !== "orchestrator" &&
            !agent.active &&
            (!agent.eligible || atActiveLimit);
          return (
          <article
            className={agent.id === "orchestrator" ? "orchestrator" : ""}
            key={agent.id}
          >
            <span>
              <Bot size={22} />
            </span>
            <div>
              <small className="agent-category">{agent.category}</small>
              <h3>{agent.name}</h3>
              <p>{agent.description}</p>
              <div className="agent-capabilities">
                {agent.capabilities.slice(0, 3).map((capability) => (
                  <i key={capability}>{labelFor(capability)}</i>
                ))}
              </div>
              <div className="agent-entitlement">
                <span className={`status ${agent.active ? "complete" : needsUpgrade ? "neutral" : "pending"}`}>
                  {agent.active && agent.id !== "orchestrator" && <Check size={11} />}
                  {agent.id === "orchestrator"
                    ? "Platform core"
                    : agent.active
                      ? "Activated"
                      : needsUpgrade
                        ? !agent.eligible
                          ? "Not in plan"
                          : "Plan limit reached"
                        : "Available in plan"}
                </span>
                {agent.id !== "orchestrator" && canRefresh && (
                  needsUpgrade ? (
                    <button
                      className="button upgrade"
                      onClick={onUpgrade}
                      title={
                        !agent.eligible
                          ? "Upgrade your plan to unlock this agent"
                          : "You've reached your plan's active agent limit — upgrade to activate more"
                      }
                    >
                      <Sparkles size={14} /> Upgrade
                    </button>
                  ) : (
                    <button
                      className="button secondary"
                      disabled={Boolean(changing)}
                      onClick={() => void changeActivation(agent.id, agent.active ? "deactivate" : "activate")}
                    >
                      {changing === agent.id ? <LoaderCircle className="spin" size={15} /> : agent.active ? "Deactivate" : "Activate"}
                    </button>
                  )
                )}
              </div>
            </div>
            <em className={`status ${statusClass(agent.status)}`}>
              {labelFor(agent.status)}
            </em>
            <footer>
              <small>v{agent.version}</small>
              <span>
                {agent.confidence !== null
                  ? `${Math.round(agent.confidence * 100)}% confidence`
                  : agent.blockers.length
                    ? `Waiting for ${agent.blockers
                        .slice(0, 2)
                        .map((blocker) =>
                          labelFor(blocker.replace("agent:", "")),
                        )
                        .join(" + ")}`
                    : "Ready for activation"}
              </span>
            </footer>
          </article>
          );
        })}
      </div>
    </div>
  );
}
const ROLE_LABEL: Record<MembershipRole, string> = {
  owner: "Owner",
  admin: "Admin",
  marketing: "Marketing",
  sales: "Sales",
  analyst: "Analyst",
  viewer: "Viewer",
  reviewer: "Reviewer",
};
const ROLE_OPTIONS: MembershipRole[] = ["owner", "admin", "marketing", "sales", "analyst", "viewer", "reviewer"];
const ROLE_ACCESS_REFERENCE: Array<{ role: MembershipRole; tag: string; access: string }> = [
  { role: "owner", tag: "Full Access", access: "Full access to everything: billing and subscription, integrations, agent activation, team invites and roles, plus every module below." },
  { role: "admin", tag: "Management", access: "Same day-to-day access as Owner, including billing, integrations, agent activation, and team management." },
  { role: "marketing", tag: "Growth Suite", access: "Full access to marketing-led modules (Marketing Strategy, Campaign Planning, Content Generation/Strategy, Reputation Intelligence, Outreach), plus shared reporting on sales data." },
  { role: "sales", tag: "Pipeline Suite", access: "Full access to sales-led modules (Sales Planning, Buying Intent, Lead Nurturing, Revenue Forecasting), plus shared reporting on marketing data." },
  { role: "analyst", tag: "Read & Plan", access: "Read-only reporting across both marketing and sales modules -- can view plans, campaigns, and pipeline data, but can't create or change them." },
  { role: "viewer", tag: "Read Only", access: "Read-only access to dashboards and overview data. Can't take any action anywhere in the workspace." },
  { role: "reviewer", tag: "Restricted Approver", access: "Can approve or reject AI-generated recommendations, drafts and plans across every agent. Can't run agents, create campaigns, or edit data -- a separately-priced seat, not counted against your user limit." },
];
// Must match apps/api/src/team.ts's INVITE_TTL_DAYS.
const INVITE_EXPIRY_DAYS = 14;
const relativeAge = (iso: string): string => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
};

function BusinessProfileView({
  session,
  workspace,
  onUpdate,
}: {
  session: AuthSession;
  workspace: Workspace;
  onUpdate: (workspace: Workspace) => void;
}) {
  const canManage = ["owner", "admin"].includes(workspace.role);
  const seedForm = (ws: Workspace): CompanyProfileUpdate => ({
    company: {
      name: String(ws.company.name ?? ""),
      websiteUrl: String(ws.company.website ?? ""),
      industry: String(ws.company.industry ?? ""),
      countryCode: String(ws.company.countryCode ?? "GB"),
      employeeBand: String(ws.company.employeeBand ?? "1-10"),
      businessModel: String(ws.company.businessModel ?? "B2B services"),
      valueProposition: String(ws.company.valueProposition ?? ""),
      targetMarkets: Array.isArray(ws.company.targetMarkets)
        ? ws.company.targetMarkets.map(String)
        : [],
      primaryGoal: String(ws.company.primaryGoal ?? ""),
    },
    commercial: {
      annualRevenueTarget: Number(ws.commercial.annualRevenueTarget ?? 0),
      currentRevenue: Number(ws.commercial.currentRevenue ?? 0),
      currentPipeline: Number(ws.commercial.currentPipeline ?? 0),
      averageDealValue: Number(ws.commercial.averageDealValue ?? 0),
      leadToOpportunityRate: Number(ws.commercial.leadToOpportunityRate ?? 0),
      opportunityWinRate: Number(ws.commercial.opportunityWinRate ?? 0),
      salesCycleDays: Number(ws.commercial.salesCycleDays ?? 0),
      currency: String(ws.commercial.currency ?? "GBP"),
    },
    revenueSources: ws.revenueSources.length
      ? ws.revenueSources.map((source) => ({
          ...source,
          type: REVENUE_SOURCE_TYPES.includes(
            source.type as CompanyProfileUpdate["revenueSources"][number]["type"],
          )
            ? (source.type as CompanyProfileUpdate["revenueSources"][number]["type"])
            : "other",
        }))
      : [{ name: "", type: "service" as const, annualValue: 0 }],
  });
  const [form, setForm] = useState<CompanyProfileUpdate>(() =>
    seedForm(workspace),
  );
  const [otherIndustry, setOtherIndustry] = useState(
    () => !INDUSTRIES.includes(String(workspace.company.industry ?? "")),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const countryCodeOptions = useMemo(
    () => COUNTRIES.map((country) => ({ value: country.code, label: country.name })),
    [],
  );
  const countryNameOptions = useMemo(
    () => COUNTRIES.map((country) => ({ value: country.name, label: country.name })),
    [],
  );
  const updateCompany = (
    key: keyof CompanyProfileUpdate["company"],
    value: string | string[],
  ) =>
    setForm((current) => ({
      ...current,
      company: { ...current.company, [key]: value },
    }));
  const updateCommercial = (
    key: keyof CompanyProfileUpdate["commercial"],
    value: number | string,
  ) =>
    setForm((current) => ({
      ...current,
      commercial: { ...current.commercial, [key]: value },
    }));
  const sources = form.revenueSources;
  const save = async () => {
    setSaving(true);
    setSaveError("");
    setSaveMessage("");
    try {
      const updated = await updateCompanyProfile(
        session.session.token,
        workspace.tenantId,
        form,
      );
      onUpdate(updated);
      setForm(seedForm(updated));
      setSaveMessage("Business profile updated.");
    } catch (cause) {
      setSaveError(
        cause instanceof Error
          ? cause.message
          : "Could not save the business profile.",
      );
    } finally {
      setSaving(false);
    }
  };
  if (!canManage)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Company & Revenue Profile</span>
          <h1>Business profile editing is restricted.</h1>
          <p>Only workspace owners and admins can edit company and revenue details.</p>
        </div>
      </div>
    );
  return (
    <div className="page-stack">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Company & Revenue Profile</span>
          <h1>Business Profile</h1>
          <p>
            The company and revenue details collected during onboarding --
            every specialist agent uses this as shared context. Edit it any
            time; integrations and website scans are managed from their own
            pages.
          </p>
        </div>
        <button className="button primary" disabled={saving} onClick={() => void save()}>
          {saving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Save changes
        </button>
      </div>
      {saveError && (
        <div className="form-alert error">
          <X size={16} />
          {saveError}
        </div>
      )}
      {saveMessage && (
        <div className="form-alert info">
          <Info size={16} />
          {saveMessage}
        </div>
      )}
      <Panel title="Company" kicker="Who you are and who you serve">
        <div className="form-grid onboarding-form">
          <Field label="Company name" required>
            <input
              value={form.company.name}
              onChange={(e) => updateCompany("name", e.target.value)}
              placeholder="Acme Ltd"
            />
          </Field>
          <Field label="Website" required>
            <input
              type="url"
              value={form.company.websiteUrl}
              onChange={(e) => updateCompany("websiteUrl", e.target.value)}
              placeholder="https://example.com"
            />
          </Field>
          <Field label="Industry" required>
            <select
              value={otherIndustry ? "Other" : form.company.industry}
              onChange={(e) => {
                const selected = e.target.value;
                if (selected === "Other") {
                  setOtherIndustry(true);
                  updateCompany("industry", "");
                } else {
                  setOtherIndustry(false);
                  updateCompany("industry", selected);
                }
              }}
            >
              <option value="" disabled>
                Select an industry
              </option>
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
            {otherIndustry && (
              <input
                style={{ marginTop: 8 }}
                value={form.company.industry}
                onChange={(e) => updateCompany("industry", e.target.value)}
                placeholder="Enter your industry"
              />
            )}
          </Field>
          <Field label="Country" required>
            <SearchableSelect
              value={form.company.countryCode}
              onChange={(value) => updateCompany("countryCode", value)}
              options={countryCodeOptions}
              placeholder="Select a country"
            />
          </Field>
          <Field label="Team size" required>
            <select
              value={form.company.employeeBand}
              onChange={(e) => updateCompany("employeeBand", e.target.value)}
            >
              {["1-10", "11-50", "51-200", "201-500", "501+"].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </Field>
          <Field label="Business model" required>
            <select
              value={form.company.businessModel}
              onChange={(e) => updateCompany("businessModel", e.target.value)}
            >
              {[
                "B2B services",
                "B2B SaaS",
                "B2C",
                "Ecommerce",
                "Marketplace",
                "Mixed product and services",
              ].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Value proposition"
            required
            hint={`Minimum 10 characters (${form.company.valueProposition.trim().length}/10)`}
          >
            <textarea
              value={form.company.valueProposition}
              onChange={(e) => updateCompany("valueProposition", e.target.value)}
              placeholder="What value do you create, and for whom?"
            />
          </Field>
          <Field label="Primary revenue goal" required>
            <textarea
              value={form.company.primaryGoal}
              onChange={(e) => updateCompany("primaryGoal", e.target.value)}
              placeholder="Grow recurring revenue by 30% while improving win rate."
            />
          </Field>
          <Field label="Target markets" required hint="Search and select one or more countries">
            <SearchableMultiSelect
              values={form.company.targetMarkets}
              onChange={(values) => updateCompany("targetMarkets", values)}
              options={countryNameOptions}
              placeholder="Search countries..."
            />
          </Field>
        </div>
      </Panel>
      <Panel title="Revenue Target" kicker="Targets, pipeline and conversion">
        <div className="form-grid onboarding-form numeric">
          <Field label="Annual revenue target">
            <input
              type="number"
              value={form.commercial.annualRevenueTarget}
              onChange={(e) => updateCommercial("annualRevenueTarget", Number(e.target.value))}
            />
          </Field>
          <Field label="Revenue achieved so far">
            <input
              type="number"
              value={form.commercial.currentRevenue}
              onChange={(e) => updateCommercial("currentRevenue", Number(e.target.value))}
            />
          </Field>
          <Field label="Current qualified pipeline">
            <input
              type="number"
              value={form.commercial.currentPipeline}
              onChange={(e) => updateCommercial("currentPipeline", Number(e.target.value))}
            />
          </Field>
          <Field label="Average deal value">
            <input
              type="number"
              value={form.commercial.averageDealValue}
              onChange={(e) => updateCommercial("averageDealValue", Number(e.target.value))}
            />
          </Field>
          <Field label="Lead to opportunity %">
            <input
              type="number"
              value={form.commercial.leadToOpportunityRate * 100}
              onChange={(e) =>
                updateCommercial("leadToOpportunityRate", Number(e.target.value) / 100)
              }
            />
          </Field>
          <Field label="Opportunity win rate %">
            <input
              type="number"
              value={form.commercial.opportunityWinRate * 100}
              onChange={(e) =>
                updateCommercial("opportunityWinRate", Number(e.target.value) / 100)
              }
            />
          </Field>
          <Field label="Average sales cycle (days)">
            <input
              type="number"
              value={form.commercial.salesCycleDays}
              onChange={(e) => updateCommercial("salesCycleDays", Number(e.target.value))}
            />
          </Field>
          <Field label="Currency">
            <select
              value={form.commercial.currency}
              onChange={(e) => updateCommercial("currency", e.target.value)}
            >
              {["GBP", "USD", "EUR", "CAD", "AUD"].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </Field>
        </div>
      </Panel>
      <Panel title="Revenue Sources" kicker="How your company earns revenue">
        <div className="sources-editor">
          {sources.map((item, index) => (
            <div className="source-row" key={index}>
              <Field label="Revenue source">
                <input
                  value={item.name}
                  onChange={(e) =>
                    setForm((c) => ({
                      ...c,
                      revenueSources: c.revenueSources.map((s, i) =>
                        i === index ? { ...s, name: e.target.value } : s,
                      ),
                    }))
                  }
                />
              </Field>
              <Field label="Type">
                <select
                  value={item.type}
                  onChange={(e) =>
                    setForm((c) => ({
                      ...c,
                      revenueSources: c.revenueSources.map((s, i) =>
                        i === index
                          ? { ...s, type: e.target.value as typeof s.type }
                          : s,
                      ),
                    }))
                  }
                >
                  {REVENUE_SOURCE_TYPES.map((x) => (
                    <option key={x} value={x}>
                      {labelFor(x)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Annual value">
                <input
                  type="number"
                  value={item.annualValue}
                  onChange={(e) =>
                    setForm((c) => ({
                      ...c,
                      revenueSources: c.revenueSources.map((s, i) =>
                        i === index
                          ? { ...s, annualValue: Number(e.target.value) }
                          : s,
                      ),
                    }))
                  }
                />
              </Field>
              <button
                className="icon-action"
                aria-label="Remove revenue source"
                disabled={sources.length === 1}
                onClick={() =>
                  setForm((c) => ({
                    ...c,
                    revenueSources: c.revenueSources.filter((_, i) => i !== index),
                  }))
                }
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
          <button
            className="button secondary"
            onClick={() =>
              setForm((c) => ({
                ...c,
                revenueSources: [
                  ...c.revenueSources,
                  { name: "", type: "service", annualValue: 0 },
                ],
              }))
            }
          >
            <Plus size={17} />
            Add revenue source
          </button>
        </div>
      </Panel>
    </div>
  );
}

function TeamView({
  session,
  workspace,
}: {
  session: AuthSession;
  workspace: Workspace;
}) {
  const canManage = ["owner", "admin"].includes(workspace.role);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [loading, setLoading] = useState(canManage);
  const [error, setError] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<MembershipRole>("viewer");
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(true);
  const [memberFilter, setMemberFilter] = useState("");
  const reload = () => {
    if (!canManage) return;
    setLoading(true);
    setError("");
    return Promise.all([
      loadTeamMembers(session.session.token, workspace.tenantId),
      loadTeamInvites(session.session.token, workspace.tenantId),
    ])
      .then(([membersResult, invitesResult]) => {
        setMembers(membersResult.data);
        setInvites(invitesResult.data);
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load the team."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    void reload();
  }, [canManage, session.session.token, workspace.tenantId]);
  if (!canManage)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Team & Roles</span>
          <h1>Team management is restricted.</h1>
          <p>Only workspace owners and admins can invite teammates or change roles.</p>
        </div>
      </div>
    );
  const ownerCount = members.filter((member) => member.role === "owner").length;
  const filteredMembers = memberFilter.trim()
    ? members.filter((member) =>
        `${member.name ?? ""} ${member.email}`.toLowerCase().includes(memberFilter.trim().toLowerCase()),
      )
    : members;
  const submitInvite = async (event: FormEvent) => {
    event.preventDefault();
    setCreatingInvite(true);
    setError("");
    try {
      await createTeamInvite(session.session.token, workspace.tenantId, inviteEmail.trim(), inviteRole);
      setInviteEmail("");
      setInviteRole("viewer");
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create the invite.");
    } finally {
      setCreatingInvite(false);
    }
  };
  const changeRole = async (memberId: string, role: MembershipRole) => {
    setBusyId(memberId);
    setError("");
    try {
      await updateTeamMemberRole(session.session.token, workspace.tenantId, memberId, role);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not change the role.");
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (memberId: string) => {
    setBusyId(memberId);
    setError("");
    try {
      await removeTeamMember(session.session.token, workspace.tenantId, memberId);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not remove this member.");
    } finally {
      setBusyId(null);
    }
  };
  const revoke = async (inviteId: string) => {
    setBusyId(inviteId);
    setError("");
    try {
      await revokeTeamInvite(session.session.token, workspace.tenantId, inviteId);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not revoke this invite.");
    } finally {
      setBusyId(null);
    }
  };
  const copyLink = async (id: string, link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
    } catch {
      setError("Could not copy the link -- copy it manually instead.");
    }
  };
  const resendEmail = async (inviteId: string) => {
    setBusyId(inviteId);
    setError("");
    try {
      await resendTeamInviteEmail(session.session.token, workspace.tenantId, inviteId);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not resend the invite email.");
    } finally {
      setBusyId(null);
    }
  };
  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">Team & access management</span>
        <h1>Invite teammates and manage access.</h1>
      </div>
      {showBanner && (
        <div className="team-banner">
          <span>
            <Clock size={17} />
          </span>
          <p>
            <strong>Direct link invites enabled:</strong> Clintra generates direct shareable
            invitation links. Copy and send the link to your teammate. Links automatically expire
            after {INVITE_EXPIRY_DAYS} days.
          </p>
          <button type="button" aria-label="Dismiss" onClick={() => setShowBanner(false)}>
            <X size={16} />
          </button>
        </div>
      )}
      {error && (
        <div className="form-alert error">
          <X size={17} />
          {error}
        </div>
      )}
      <section className="panel">
        <header className="team-invite-header">
          <span>
            <UserPlus size={19} />
          </span>
          <h2>Invite a teammate</h2>
          <em>Instant onboarding via custom link</em>
        </header>
        <form className="inline-form" onSubmit={(event) => void submitInvite(event)}>
          <label className="team-email-field">
            <Mail size={15} />
            <input
              type="email"
              required
              placeholder="teammate@company.com"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
            />
          </label>
          <select value={inviteRole} onChange={(event) => setInviteRole(event.target.value as MembershipRole)}>
            {ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABEL[role]} {role === "viewer" ? "(Read-only)" : role === "reviewer" ? "(Approvals only)" : ""}
              </option>
            ))}
          </select>
          <button className="button primary" type="submit" disabled={creatingInvite}>
            {creatingInvite ? <LoaderCircle className="spin" size={15} /> : <UserPlus size={15} />}
            {creatingInvite ? "Creating…" : "Create invite link"}
          </button>
        </form>
        <p className="team-invite-hint">
          <Info size={13} />
          You can review and revoke active invite links at any time below.
        </p>
      </section>
      <section className="panel">
        <header>
          <div className="team-section-heading">
            <em className="status pending">{invites.length} pending</em>
            <h2>Pending invites</h2>
          </div>
          {invites.length > 0 && <span className="muted-note">Awaiting recipient link registration</span>}
        </header>
        {loading ? (
          <Empty icon={LoaderCircle} title="Loading" text="Loading invites…" />
        ) : invites.length === 0 ? (
          <Empty icon={UserPlus} title="No pending invites" text="Invite a teammate above to get started." />
        ) : (
          <div className="team-table-scroll">
            <table className="team-table">
              <thead>
                <tr>
                  <th>User / email</th>
                  <th>Assigned role</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => (
                  <tr key={invite.id}>
                    <td>
                      <div className="team-user-cell">
                        <span className="team-avatar">{invite.email.slice(0, 2).toUpperCase()}</span>
                        <div>
                          <b>{invite.email}</b>
                          <small>Created {relativeAge(invite.createdAt)}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <em className={`role-badge ${invite.role}`}>{ROLE_LABEL[invite.role]}</em>
                    </td>
                    <td>
                      {invite.emailStatus === "sent" ? (
                        <em className="status complete">Sent</em>
                      ) : invite.emailStatus === "failed" ? (
                        <em className="status pending">
                          Failed to send
                          {invite.emailError && (
                            <span className="info-tooltip">
                              <Info size={13} />
                              <span className="info-tooltip-content">{invite.emailError}</span>
                            </span>
                          )}
                        </em>
                      ) : (
                        <em className="status neutral">Not configured</em>
                      )}
                    </td>
                    <td>{new Date(invite.expiresAt).toLocaleDateString()}</td>
                    <td className="team-actions">
                      {invite.link ? (
                        <button
                          type="button"
                          className="button secondary"
                          onClick={() => void copyLink(invite.id, invite.link!)}
                        >
                          <Copy size={14} />
                          {copiedId === invite.id ? "Copied" : "Copy link"}
                        </button>
                      ) : (
                        <em>Link unavailable</em>
                      )}
                      {invite.emailStatus !== "not_configured" && (
                        <button
                          type="button"
                          className="button secondary"
                          disabled={busyId === invite.id}
                          onClick={() => void resendEmail(invite.id)}
                        >
                          {busyId === invite.id ? <LoaderCircle className="spin" size={15} /> : "Resend email"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="button disconnect"
                        disabled={busyId === invite.id}
                        onClick={() => void revoke(invite.id)}
                      >
                        {busyId === invite.id ? <LoaderCircle className="spin" size={15} /> : "Revoke"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="panel">
        <header>
          <div className="team-section-heading">
            <em className="status info">{members.length} member{members.length === 1 ? "" : "s"}</em>
            <h2>Active members</h2>
          </div>
          <div className="table-search">
            <Search size={15} />
            <input
              type="search"
              placeholder="Filter members…"
              value={memberFilter}
              onChange={(event) => setMemberFilter(event.target.value)}
            />
          </div>
        </header>
        {loading ? (
          <Empty icon={LoaderCircle} title="Loading" text="Loading team members…" />
        ) : filteredMembers.length === 0 ? (
          <Empty icon={UsersRound} title="No members match" text="Try a different search term." />
        ) : (
          <div className="team-table-scroll">
            <table className="team-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Role</th>
                  <th>Joined date</th>
                  <th>Manage</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => {
                  const isSelf = member.userId === session.user.id;
                  const isLastOwner = member.role === "owner" && ownerCount <= 1;
                  return (
                    <tr key={member.id}>
                      <td>
                        <div className="team-user-cell">
                          <span className="team-avatar">
                            {(member.name || member.email).slice(0, 2).toUpperCase()}
                          </span>
                          <div>
                            <b>{member.name ?? "—"}</b>
                            {member.role === "owner" && <small>Primary account</small>}
                          </div>
                        </div>
                      </td>
                      <td>{member.email}</td>
                      <td>
                        {member.emailVerified === null ? (
                          <em className="status neutral">Unknown</em>
                        ) : member.emailVerified ? (
                          <em className="status complete">Verified</em>
                        ) : (
                          <em className="status pending">Not verified</em>
                        )}
                      </td>
                      <td>
                        <select
                          value={member.role}
                          disabled={isSelf || isLastOwner || busyId === member.id}
                          onChange={(event) => void changeRole(member.id, event.target.value as MembershipRole)}
                        >
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABEL[role]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{new Date(member.joinedAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          type="button"
                          className="team-icon-button"
                          disabled={isSelf || isLastOwner || busyId === member.id}
                          title={isSelf ? "You can't remove yourself" : isLastOwner ? "A workspace needs at least one owner" : "Remove member"}
                          onClick={() => void remove(member.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="panel">
        <header>
          <div className="team-section-heading">
            <ShieldCheck size={18} />
            <h2>What can each role access?</h2>
          </div>
        </header>
        <p className="panel-intro" style={{ marginTop: -8, marginBottom: 16 }}>
          Granular workspace permissions and access rights across all Clintra revenue modules.
        </p>
        <div className="role-reference-grid">
          {ROLE_ACCESS_REFERENCE.map((entry) => (
            <article key={entry.role}>
              <header>
                <em className={`role-badge ${entry.role}`}>{ROLE_LABEL[entry.role]}</em>
                <span className="role-tag">{entry.tag}</span>
              </header>
              <p>{entry.access}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
function ApprovalsView({
  session,
  workspace,
  onNavigate,
}: {
  session: AuthSession;
  workspace: Workspace;
  onNavigate: (view: View) => void;
}) {
  const [items, setItems] = useState<ApprovalItem[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void loadApprovals(session.session.token, workspace.tenantId)
      .then((result) => setItems(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load approvals."));
  }, [session.session.token, workspace.tenantId]);
  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">Human-governed</span>
        <h1>
          {items?.length
            ? `${items.length} item${items.length === 1 ? "" : "s"} need${items.length === 1 ? "s" : ""} your review.`
            : "Review evidence before changing the business."}
        </h1>
        <p>Every recommendation, draft, or plan an agent produces waits here until a human decides -- nothing acts on its own.</p>
      </div>
      {error && (
        <div className="form-alert error">
          <X size={17} />
          {error}
        </div>
      )}
      {items === null ? (
        <Empty icon={LoaderCircle} title="Loading" text="Loading pending approvals…" />
      ) : items.length === 0 ? (
        <Empty
          icon={ClipboardCheck}
          title="Nothing waiting on you"
          text="Every agent recommendation, draft, and plan has been reviewed. New ones will appear here as agents produce them."
        />
      ) : (
        <div className="recommendations">
          {items.map((item) => {
            const targetView = APPROVAL_AGENT_VIEW[item.agentId];
            return (
              <article key={`${item.entityType}-${item.id}`}>
                <span>
                  <ClipboardCheck size={21} />
                </span>
                <small>
                  {labelFor(item.agentId)} · {new Date(item.createdAt).toLocaleDateString()}
                </small>
                <h3>{item.title}</h3>
                {item.summary && <p>{item.summary}</p>}
                <footer>
                  <button
                    className="button primary"
                    disabled={!targetView}
                    title={targetView ? undefined : "This item's review page isn't available yet"}
                    onClick={() => targetView && onNavigate(targetView)}
                  >
                    Review <ArrowRight size={15} />
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
function Generic({ view, workspace }: { view: View; workspace: Workspace }) {
  const recommendations = workspace.websiteScan?.opportunities ?? [];
  if (view === "Recommendations")
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">Human-governed optimisation</span>
          <h1>Review evidence before changing the business.</h1>
        </div>
        <div className="recommendations">
          {recommendations.length ? (
            recommendations.map((item) => (
              <article key={item}>
                <span>
                  <Lightbulb size={21} />
                </span>
                <small>Website Intelligence</small>
                <h3>{item}</h3>
                <p>
                  Based on the public website scan. Validate against conversion
                  analytics before acting.
                </p>
                <footer>
                  <button className="button secondary">Reject</button>
                  <button className="button primary">Approve</button>
                </footer>
              </article>
            ))
          ) : (
            <Panel title="Recommendations">
              <Empty
                icon={Lightbulb}
                title="Recommendations need more evidence"
                text="Connect analytics and CRM sources to move beyond the initial website baseline."
              />
            </Panel>
          )}
        </div>
      </div>
    );
  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">{view}</span>
        <h1>{view} is ready for connected data.</h1>
        <p>
          Complete the relevant integration to populate this workspace with
          authorised tenant data.
        </p>
      </div>
      <Panel title={`Prepare ${view}`}>
        <Empty
          icon={BarChart3}
          title="No live records yet"
          text="Clintra will populate this module after the selected systems complete their first synchronisation."
        />
      </Panel>
    </div>
  );
}

function ApiKeysView({
  session,
  workspace,
  onUpgrade,
}: {
  session: AuthSession;
  workspace: Workspace;
  onUpgrade: () => void;
}) {
  const canManage = ["owner", "admin"].includes(workspace.role);
  const entitled = Boolean(workspace.billing.plan.entitlements.apiAccess);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(canManage);
  const [error, setError] = useState("");
  const [keyName, setKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState<{ prefix: string; secret: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const reload = () => {
    if (!canManage) return;
    setLoading(true);
    setError("");
    return loadApiKeys(session.session.token, workspace.tenantId)
      .then((result) => setKeys(result.data))
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Could not load API keys."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    void reload();
  }, [canManage, session.session.token, workspace.tenantId]);
  if (!canManage)
    return (
      <div className="page-stack">
        <div className="page-title">
          <span className="overline">API</span>
          <h1>API key management is restricted.</h1>
          <p>Only workspace owners and admins can generate or revoke API keys.</p>
        </div>
      </div>
    );
  const submitCreate = async (event: FormEvent) => {
    event.preventDefault();
    setCreating(true);
    setError("");
    try {
      const result = await createApiKey(session.session.token, workspace.tenantId, keyName.trim());
      setKeyName("");
      setNewSecret({ prefix: result.key.keyPrefix, secret: result.secret });
      setCopied(false);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create the API key.");
    } finally {
      setCreating(false);
    }
  };
  const revoke = async (keyId: string) => {
    setBusyId(keyId);
    setError("");
    try {
      await revokeApiKey(session.session.token, workspace.tenantId, keyId);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not revoke this key.");
    } finally {
      setBusyId(null);
    }
  };
  const copySecret = async () => {
    if (!newSecret) return;
    try {
      await navigator.clipboard.writeText(newSecret.secret);
      setCopied(true);
    } catch {
      setError("Could not copy the key -- copy it manually instead.");
    }
  };
  return (
    <div className="page-stack">
      <div className="page-title">
        <span className="overline">Read-only data export API</span>
        <h1>Generate and manage API keys.</h1>
        <p>
          Keys authenticate read-only requests to <code>/api/v1/export/*</code> (leads, campaigns,
          usage) for pulling your workspace's data into external tools.
        </p>
      </div>
      {!entitled && (
        <div className="form-alert error">
          <Info size={17} />
          <span>Your current plan doesn't include API access.</span>
          <button type="button" className="button secondary" onClick={onUpgrade}>
            Upgrade plan
          </button>
        </div>
      )}
      {error && (
        <div className="form-alert error">
          <X size={17} />
          {error}
        </div>
      )}
      {newSecret && (
        <div className="team-banner">
          <span>
            <LockKeyhole size={17} />
          </span>
          <p>
            <strong>Copy this key now -- it won't be shown again:</strong>{" "}
            <code>{newSecret.secret}</code>
          </p>
          <button type="button" className="button secondary" onClick={() => void copySecret()}>
            <Copy size={14} />
            {copied ? "Copied" : "Copy"}
          </button>
          <button type="button" aria-label="Dismiss" onClick={() => setNewSecret(null)}>
            <X size={16} />
          </button>
        </div>
      )}
      <section className="panel">
        <header className="team-invite-header">
          <span>
            <KeyRound size={19} />
          </span>
          <h2>Generate a new key</h2>
        </header>
        <form className="inline-form" onSubmit={(event) => void submitCreate(event)}>
          <label className="team-email-field">
            <FileText size={15} />
            <input
              type="text"
              required
              placeholder="e.g. Analytics warehouse sync"
              value={keyName}
              onChange={(event) => setKeyName(event.target.value)}
              disabled={!entitled}
            />
          </label>
          <button className="button primary" type="submit" disabled={creating || !entitled}>
            {creating ? <LoaderCircle className="spin" size={15} /> : <KeyRound size={15} />}
            {creating ? "Generating…" : "Generate key"}
          </button>
        </form>
      </section>
      <section className="panel">
        <header>
          <div className="team-section-heading">
            <em className="status pending">{keys.filter((key) => !key.revokedAt).length} active</em>
            <h2>API keys</h2>
          </div>
        </header>
        {loading ? (
          <Empty icon={LoaderCircle} title="Loading" text="Loading API keys…" />
        ) : keys.length === 0 ? (
          <Empty icon={KeyRound} title="No API keys yet" text="Generate a key above to start pulling your data via the export API." />
        ) : (
          <div className="team-table-scroll">
            <table className="team-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Created</th>
                  <th>Last used</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id}>
                    <td>
                      <b>{key.name}</b>
                    </td>
                    <td>
                      <code>{key.keyPrefix}…</code>
                    </td>
                    <td>{new Date(key.createdAt).toLocaleDateString()}</td>
                    <td>{key.lastUsedAt ? relativeAge(key.lastUsedAt) : "Never"}</td>
                    <td>
                      {key.revokedAt ? (
                        <em className="status neutral">Revoked</em>
                      ) : (
                        <em className="status complete">Active</em>
                      )}
                    </td>
                    <td className="team-actions">
                      {!key.revokedAt && (
                        <button
                          type="button"
                          className="button disconnect"
                          disabled={busyId === key.id}
                          onClick={() => void revoke(key.id)}
                        >
                          {busyId === key.id ? <LoaderCircle className="spin" size={15} /> : "Revoke"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const entitlementNumber = (summary: CommercialSummary, key: string) => {
  const value = summary.plan.entitlements[key];
  return typeof value === "number" ? value : null;
};
const planEntitlementNumber = (plan: PublicPlan, key: string) => {
  const value = plan.entitlements[key];
  return typeof value === "number" ? value : null;
};

function BillingView({
  session,
  workspace,
}: {
  session: AuthSession;
  workspace: Workspace;
}) {
  const billing = workspace.billing;
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [interval, setInterval] = useState<"monthly" | "annual">(
    billing.subscription.interval,
  );
  const [invoices, setInvoices] = useState<Array<{
    number: string | null;
    currency: string;
    amountDueMinor: number;
    amountPaidMinor: number;
    status: string;
    hostedInvoiceUrl: string | null;
    invoicePdfUrl: string | null;
    createdAt: string;
  }>>([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const canManage = ["owner", "admin"].includes(workspace.role);
  useEffect(() => {
    void loadPublicPlans().then((result) => setPlans(result.plans));
    if (canManage)
      void loadInvoices(session.session.token, workspace.tenantId)
        .then((result) => setInvoices(result.data))
        .catch(() => undefined);
  }, [canManage, session.session.token, workspace.tenantId]);
  const redirect = async (key: string, action: () => Promise<{ url: string }>) => {
    setBusy(key);
    setError("");
    try {
      const result = await action();
      window.location.assign(result.url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Billing action failed.");
      setBusy("");
    }
  };
  const formatPrice = (plan: PublicPlan) => {
    const price = plan.prices[interval];
    if (!price || price.amountMinor === null) return "Contact sales";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: price.currency,
      maximumFractionDigits: 0,
    }).format(price.amountMinor / 100);
  };
  // Enterprise's fullUsers/monthlyCredits/integrations are 0/null in the
  // catalogue because those are contracted per-deal, not real zero limits --
  // shown as Unlimited/Custom rather than the literal stored value.
  const planStats = (plan: PublicPlan) => {
    const users = planEntitlementNumber(plan, "fullUsers");
    const agents = planEntitlementNumber(plan, "activeAgents");
    const credits = planEntitlementNumber(plan, "monthlyCredits");
    const integrations = planEntitlementNumber(plan, "integrations");
    return [
      ["Users", users === null || users === 0 ? "Unlimited" : `Up to ${users}`],
      ["AI agents", agents === null ? "Custom" : `${agents} agents`],
      ["AI credits", credits === null ? "Custom" : `${credits.toLocaleString()} / mo`],
      ["Integrations", integrations === null ? "Unlimited" : String(integrations)],
    ] as const;
  };
  return (
    <div className="page-stack commercial-page">
      <div className="page-title page-title-action">
        <div>
          <span className="overline">Settings · Plan and billing</span>
          <h1>A clear path from trial to paid value.</h1>
          <p>Plan state and allowances come from the backend catalogue. Stripe hosts all card entry and sensitive payment workflows.</p>
        </div>
        {canManage && billing.billingConfigured && (
          <button className="button secondary" disabled={Boolean(busy)} onClick={() => void redirect("portal", () => createBillingPortal(session.session.token, workspace.tenantId))}>
            <CircleDollarSign size={18} />
            Manage subscription
          </button>
        )}
      </div>
      {error && <div className="form-alert error"><X size={17} />{error}</div>}
      {billing.trial && (
        <section className="trial-banner">
          <div>
            <span className="overline">30-day no-card trial</span>
            <h2>{billing.trial.daysRemaining} days remaining on {billing.plan.name}</h2>
            <p>Your trial ends {new Date(billing.trial.endsAt).toLocaleDateString()}. It will not charge or convert automatically without your explicit confirmation.</p>
          </div>
          <span className="status pending">{labelFor(billing.trial.status)}</span>
        </section>
      )}
      <div className="metrics">
        <Metric label="Current plan" value={billing.plan.name} meta={`${labelFor(billing.subscription.status)} · ${billing.subscription.interval}`} icon={CircleDollarSign} />
        <Metric label="Full users" value={`${billing.usage.fullUsers} / ${entitlementNumber(billing, "fullUsers") ?? "Custom"}`} meta="Reviewer access is restricted and separate" icon={UsersRound} />
        <Metric label="Active agents" value={`${billing.usage.agents} / ${entitlementNumber(billing, "activeAgents") ?? "Custom"}`} meta="Execution also consumes AI credits" icon={Bot} />
        <Metric label="AI credits" value={billing.usage.credits.remaining.toLocaleString()} meta={`${billing.usage.credits.consumed.toLocaleString()} consumed`} icon={Activity} />
      </div>
      <div className="overview-grid">
        <Panel title="Payment method" kicker="Stripe secured">
          <div className="payment-state">
            <span><LockKeyhole size={22} /></span>
            <div>
              <h3>{billing.paymentMethod.present ? `${billing.paymentMethod.brand ?? "Card"} ending ${billing.paymentMethod.last4}` : "No payment method added"}</h3>
              <p>{billing.paymentMethod.present ? `Expires ${billing.paymentMethod.expiryMonth}/${billing.paymentMethod.expiryYear}` : "Your no-card trial cannot charge automatically."}</p>
            </div>
          </div>
          <button className="button primary" disabled={!canManage || !billing.billingConfigured || Boolean(busy)} onClick={() => void redirect("setup", () => createSetupSession(session.session.token, workspace.tenantId))}>
            {busy === "setup" ? <LoaderCircle className="spin" /> : <CircleDollarSign size={17} />}
            {billing.paymentMethod.present ? "Replace payment method" : "Add payment method"}
          </button>
          {!billing.billingConfigured && <p className="configuration-note">Stripe credentials and Price IDs are not configured for this environment. Billing actions fail closed.</p>}
        </Panel>
        <Panel title="Current allowances" kicker={`Plan version ${billing.plan.version}`}>
          <div className="allowance-list">
            {[
              ["Reviewer seats", billing.usage.reviewers, "reviewerSeats"],
              ["Active leads", billing.usage.leads, "activeLeads"],
              ["Integrations", billing.usage.integrations, "integrations"],
              ["Campaigns", billing.usage.campaigns, "activeCampaigns"],
              ["Nurture journeys", billing.usage.nurtureJourneys, "nurtureJourneys"],
            ].map(([label, used, key]) => (
              <div key={String(key)}><span>{String(label)}</span><strong>{Number(used).toLocaleString()} / {entitlementNumber(billing, String(key))?.toLocaleString() ?? "Contracted"}</strong></div>
            ))}
          </div>
        </Panel>
      </div>
      <Panel title="Compare plans" kicker="Versioned backend catalogue" action={
        <div className="page-title-action" style={{ gap: 12 }}>
          <div className="interval-toggle">
            <button className={interval === "monthly" ? "active" : ""} onClick={() => setInterval("monthly")}>Monthly</button>
            <button className={interval === "annual" ? "active" : ""} onClick={() => setInterval("annual")}>Annual</button>
          </div>
          <em className="status complete">Save ~17%</em>
        </div>
      }>
        <div className="commercial-plan-grid">
          {plans.map((plan) => {
            const current = plan.slug === billing.plan.slug;
            return (
              <article className={current ? "current" : ""} key={plan.slug}>
                {plan.slug === "growth" && <span className="plan-popular-badge">Most popular</span>}
                <span className="overline">{current ? "Current plan" : "Available plan"}</span>
                <h3>{plan.name}</h3>
                <strong>{formatPrice(plan)}{plan.prices[interval]?.amountMinor !== null ? <small> / {interval === "monthly" ? "month" : "year"}</small> : null}</strong>
                <p>{plan.description}</p>
                <div className="plan-stat-grid">
                  {planStats(plan).map(([label, value]) => (
                    <div key={label}>
                      <span>{label}</span>
                      <b>{value}</b>
                    </div>
                  ))}
                </div>
                <ul>{plan.keyFeatures.slice(0, 5).map((feature) => <li key={feature}><Check size={14} />{feature}</li>)}</ul>
                {plan.slug === "enterprise" ? (
                  <a className="button secondary" href="https://clintra.com/contact">Contact sales</a>
                ) : billing.subscription.status === "trialing" ? (
                  <div className="plan-actions">
                    <button className="button primary" disabled={!canManage || !billing.billingConfigured || Boolean(busy)} onClick={() => void redirect(`now-${plan.slug}`, () => createSubscriptionCheckout(session.session.token, workspace.tenantId, { plan: plan.slug, billing: interval, conversion: "immediate" }))}>Start paid now</button>
                    <button className="button secondary" disabled={!canManage || !billing.billingConfigured || Boolean(busy)} onClick={() => void redirect(`later-${plan.slug}`, () => createSubscriptionCheckout(session.session.token, workspace.tenantId, { plan: plan.slug, billing: interval, conversion: "trial_end" }))}>Activate at trial end</button>
                  </div>
                ) : (
                  <button className="button secondary" disabled={!canManage || !billing.billingConfigured || Boolean(busy)} onClick={() => void redirect("portal-plan", () => createBillingPortal(session.session.token, workspace.tenantId))}>{current ? "Manage plan" : "Review change"}</button>
                )}
              </article>
            );
          })}
        </div>
      </Panel>
      <Panel title="Invoices" kicker="Trusted webhook records">
        {invoices.length ? <div className="invoice-list">{invoices.map((invoice) => (
          <a href={invoice.hostedInvoiceUrl ?? invoice.invoicePdfUrl ?? "#"} target="_blank" rel="noreferrer" key={`${invoice.number}-${invoice.createdAt}`}>
            <span><b>{invoice.number ?? "Stripe invoice"}</b><small>{new Date(invoice.createdAt).toLocaleDateString()}</small></span>
            <span><b>{new Intl.NumberFormat("en-US", { style: "currency", currency: invoice.currency }).format(invoice.amountDueMinor / 100)}</b><small>{labelFor(invoice.status)}</small></span>
          </a>
        ))}</div> : <Empty icon={CircleDollarSign} title="No invoices yet" text="Invoices appear only after Stripe confirms a trusted billing event." />}
      </Panel>
    </div>
  );
}

function UsageView({
  session,
  workspace,
  onUpdate,
}: {
  session: AuthSession;
  workspace: Workspace;
  onUpdate: (workspace: Workspace) => void;
}) {
  const billing = workspace.billing;
  const canManage = ["owner", "admin"].includes(workspace.role);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [creditPurchases, setCreditPurchases] = useState<
    Array<{ credits: number; amountMinor: number; currency: string; status: string; expiresAt: string | null; createdAt: string }>
  >([]);
  const [usageRecords, setUsageRecords] = useState<UsageRecordEntry[]>([]);
  const [historyTab, setHistoryTab] = useState<"purchases" | "usage">("purchases");
  const [autoRecharge, setAutoRecharge] = useState(billing.autoRecharge);
  const [autoRechargeSaved, setAutoRechargeSaved] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponMessage, setCouponMessage] = useState("");
  useEffect(() => {
    if (canManage) {
      void loadCreditPurchases(session.session.token, workspace.tenantId)
        .then((result) => setCreditPurchases(result.data))
        .catch(() => undefined);
      void loadUsageRecords(session.session.token, workspace.tenantId)
        .then((result) => setUsageRecords(result.data))
        .catch(() => undefined);
    }
  }, [canManage, session.session.token, workspace.tenantId]);
  const redeem = async () => {
    if (!couponCode.trim()) return;
    setCouponBusy(true);
    setError("");
    setCouponMessage("");
    try {
      const result = await redeemCoupon(session.session.token, workspace.tenantId, couponCode.trim());
      setCouponMessage(`Redeemed ${result.credits.toLocaleString()} credits.`);
      setCouponCode("");
      onUpdate(await loadWorkspace(session.session.token, workspace.tenantId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not redeem that coupon.");
    } finally {
      setCouponBusy(false);
    }
  };
  const purchase = async (pack: string) => {
    setBusy(pack);
    setError("");
    try {
      const result = await createCreditCheckout(session.session.token, workspace.tenantId, pack);
      window.location.assign(result.url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Credit checkout failed.");
      setBusy("");
    }
  };
  const exportCsv = async () => {
    setBusy("export");
    try {
      const blob = await downloadUsageCsv(session.session.token, workspace.tenantId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "clintra-usage.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Usage export failed.");
    } finally {
      setBusy("");
    }
  };
  const saveAutoRecharge = async () => {
    setBusy("auto-recharge");
    setError("");
    setAutoRechargeSaved(false);
    try {
      const result = await updateAutoRecharge(session.session.token, workspace.tenantId, {
        enabled: autoRecharge.enabled,
        creditPack: autoRecharge.creditPack,
        thresholdCredits: autoRecharge.thresholdCredits,
        maximumMonthlyPurchases: autoRecharge.maximumMonthlyPurchases,
      });
      setAutoRecharge((current) => ({
        ...current,
        enabled: result.enabled,
        creditPack: result.creditPack,
        thresholdCredits: result.thresholdCredits,
        maximumMonthlyPurchases: result.maximumMonthlyPurchases,
      }));
      setAutoRechargeSaved(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to update auto-recharge.");
    } finally {
      setBusy("");
    }
  };
  const projectedDays = billing.usage.credits.consumed > 0 && billing.trial
    ? Math.max(1, Math.round(billing.usage.credits.remaining / (billing.usage.credits.consumed / Math.max(1, 30 - billing.trial.daysRemaining))))
    : null;
  const nextExpiration = billing.usage.credits.nextExpiration;
  return (
    <div className="page-stack commercial-page">
      <div className="page-title page-title-action">
        <div><span className="overline">Settings · AI credits and usage</span><h1>Understand every allowance and AI operation.</h1><p>Credits remain separated by source. Provider cost and rate-card details stay internal while customer consumption remains auditable.</p></div>
        <button className="button secondary" disabled={Boolean(busy)} onClick={() => void exportCsv()}><Database size={17} />Export CSV</button>
      </div>
      {error && <div className="form-alert error"><X size={17} />{error}</div>}
      <div className="metrics metrics-5">
        <Metric label="Credits remaining" value={billing.usage.credits.remaining.toLocaleString()} meta={projectedDays ? `Projected ${projectedDays} days at current use` : `${billing.usage.credits.consumed.toLocaleString()} consumed to date`} icon={Activity} />
        <Metric label="Promotional" value={billing.usage.credits.promotional.toLocaleString()} meta={nextExpiration ? `${nextExpiration.amount.toLocaleString()} expire ${new Date(nextExpiration.expiresAt).toLocaleDateString()}` : "Expires with the trial"} icon={Sparkles} />
        <Metric label="Subscription" value={billing.usage.credits.subscription.toLocaleString()} meta="Allocated by paid billing cycle" icon={CircleDollarSign} />
        <Metric label="Purchased" value={billing.usage.credits.purchased.toLocaleString()} meta="Separate 12-month balance" icon={Database} />
        <Metric label="Adjustment" value={billing.usage.credits.adjustment.toLocaleString()} meta="Manual corrections by Clintra support" icon={SlidersHorizontal} />
      </div>
      <div className="overview-grid thirds">
        {[
          ["Usage by agent", billing.usage.byAgent],
          ["Usage by user", billing.usage.byUser],
          ["Usage by workflow", billing.usage.byWorkflow],
        ].map(([title, rows]) => (
          <Panel title={String(title)} key={String(title)}>
            {(rows as CommercialSummary["usage"]["byAgent"]).length ? <div className="usage-breakdown">{(rows as CommercialSummary["usage"]["byAgent"]).map((row) => <div key={row.name}><span>{labelFor(row.name)}</span><b>{row.credits} credits</b><small>{row.operations} operations</small></div>)}</div> : <Empty icon={Activity} title="No metered operations" text="Usage will appear after an agent executes through the central LLM gateway." />}
          </Panel>
        ))}
      </div>
      <Panel title="Purchase AI credits" kicker="One-time Stripe checkout">
        <p className="panel-intro">Credits are granted only after Clintra verifies Stripe’s signed payment event. A success redirect alone never changes the balance.</p>
        <div className="credit-pack-grid">{billing.creditPacks.map((pack) => <article key={pack.slug}><strong>{pack.credits.toLocaleString()}</strong><span>AI credits</span><b>{new Intl.NumberFormat("en-US", { style: "currency", currency: pack.currency, maximumFractionDigits: 0 }).format(pack.priceMinor / 100)}</b><button className="button primary" disabled={!billing.billingConfigured || !pack.configured || Boolean(busy)} onClick={() => void purchase(pack.slug)}>{busy === pack.slug ? <LoaderCircle className="spin" /> : "Purchase"}</button></article>)}</div>
        <form
          className="inline-form"
          style={{ marginTop: 18 }}
          onSubmit={(event) => {
            event.preventDefault();
            void redeem();
          }}
        >
          <input type="text" placeholder="Have a coupon code?" value={couponCode} onChange={(event) => setCouponCode(event.target.value)} />
          <button className="button secondary" type="submit" disabled={couponBusy || !couponCode.trim()}>
            {couponBusy ? <LoaderCircle className="spin" size={15} /> : "Redeem"}
          </button>
        </form>
        {couponMessage && (
          <div className="form-alert info" style={{ marginTop: 12 }}>
            <Info size={16} />
            {couponMessage}
          </div>
        )}
        {!billing.billingConfigured && (
          <p className="configuration-note">Stripe credentials and Price IDs are not configured for this environment. Purchases fail closed -- use a coupon code above instead.</p>
        )}
      </Panel>
      {canManage && (
        <Panel
          title="Purchase & Usage History"
          kicker={historyTab === "purchases" ? "Stripe-confirmed credit pack purchases" : "Most recent 100 metered AI operations, newest first"}
          action={
            <div className="interval-toggle">
              <button className={historyTab === "purchases" ? "active" : ""} onClick={() => setHistoryTab("purchases")}>Purchases</button>
              <button className={historyTab === "usage" ? "active" : ""} onClick={() => setHistoryTab("usage")}>Usage</button>
            </div>
          }
        >
          {historyTab === "purchases" ? (
            creditPurchases.length ? (
              <div className="invoice-list">
                {creditPurchases.map((purchaseRow, index) => (
                  <div key={`${purchaseRow.createdAt}-${index}`}>
                    <span>
                      <b>{purchaseRow.credits.toLocaleString()} credits</b>
                      <small>{new Date(purchaseRow.createdAt).toLocaleDateString()}</small>
                    </span>
                    <span>
                      <b>{new Intl.NumberFormat("en-US", { style: "currency", currency: purchaseRow.currency }).format(purchaseRow.amountMinor / 100)}</b>
                      <small>{labelFor(purchaseRow.status)}</small>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon={Database} title="No purchases yet" text="Credit pack purchases appear here once Stripe confirms a trusted payment event." />
            )
          ) : usageRecords.length ? (
            <div className="team-table-scroll">
              <table className="team-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Agent</th>
                    <th>Operation</th>
                    <th>Model</th>
                    <th>Credits</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {usageRecords.map((record, index) => (
                    <tr key={`${record.createdAt}-${index}`}>
                      <td>{record.userName}</td>
                      <td>{labelFor(record.agentId)}</td>
                      <td>{labelFor(record.operation)}</td>
                      <td>{record.model}</td>
                      <td>{record.creditsConsumed.toLocaleString()}</td>
                      <td>
                        <em className={`status ${record.status === "completed" ? "complete" : "error"}`}>
                          {labelFor(record.status)}
                        </em>
                      </td>
                      <td>{new Date(record.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty icon={Activity} title="No metered operations yet" text="Usage appears here as soon as an agent executes through the central LLM gateway." />
          )}
        </Panel>
      )}
      <Panel title="Auto-recharge" kicker="Billing administrator control">
        {billing.trial ? (
          <div className="auto-recharge-row">
            <div>
              <h3>Unavailable during the no-card trial</h3>
              <p>Add a payment method and activate a paid subscription before enabling auto-recharge.</p>
            </div>
          </div>
        ) : (
          <>
            <p className="panel-intro">
              Auto-recharge is <b>{autoRecharge.enabled ? "on" : "off"}</b>
              {autoRecharge.enabled
                ? ` -- uses the ${autoRecharge.creditPack ?? "selected"} pack once your balance reaches ${autoRecharge.thresholdCredits.toLocaleString()} credits, up to ${autoRecharge.maximumMonthlyPurchases}/month (${autoRecharge.purchasesThisMonth} used this month).`
                : "."}
            </p>
            <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr auto" }}>
              <Field label="Enabled">
                <select value={autoRecharge.enabled ? "on" : "off"} onChange={(event) => setAutoRecharge((current) => ({ ...current, enabled: event.target.value === "on" }))}>
                  <option value="off">Off</option>
                  <option value="on">On</option>
                </select>
              </Field>
              <Field label="Credit pack">
                <select value={autoRecharge.creditPack ?? ""} onChange={(event) => setAutoRecharge((current) => ({ ...current, creditPack: event.target.value || null }))}>
                  <option value="" disabled>
                    Select a pack
                  </option>
                  {billing.creditPacks.map((pack) => (
                    <option key={pack.slug} value={pack.slug}>
                      {pack.credits.toLocaleString()} credits
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Threshold credits">
                <input type="number" value={autoRecharge.thresholdCredits} onChange={(event) => setAutoRecharge((current) => ({ ...current, thresholdCredits: Number(event.target.value) }))} />
              </Field>
              <Field label="Max purchases / month">
                <input type="number" value={autoRecharge.maximumMonthlyPurchases} onChange={(event) => setAutoRecharge((current) => ({ ...current, maximumMonthlyPurchases: Number(event.target.value) }))} />
              </Field>
              <button className="button primary" style={{ alignSelf: "end" }} disabled={!canManage || busy === "auto-recharge"} onClick={() => void saveAutoRecharge()}>
                {busy === "auto-recharge" ? <LoaderCircle className="spin" size={15} /> : "Save"}
              </button>
            </div>
            {autoRechargeSaved && (
              <div className="form-alert info">
                <Info size={16} />
                Auto-recharge settings saved.
              </div>
            )}
            {!billing.paymentMethod.present && <p className="configuration-note">A payment method is required before auto-recharge can be turned on.</p>}
            {!canManage && <p className="configuration-note">Only workspace owners and admins can change auto-recharge settings.</p>}
          </>
        )}
      </Panel>
    </div>
  );
}

function ReadinessView({ session, workspace }: { session: AuthSession; workspace: Workspace }) {
  const [data, setData] = useState<{ generatedAt: string; capabilities: Array<Record<string, unknown>> } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void loadPlatformReadiness(session.session.token, workspace.tenantId)
      .then(setData)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Readiness metadata is unavailable."));
  }, [session.session.token, workspace.tenantId]);
  return <div className="page-stack commercial-page">
    <div className="page-title"><span className="overline">Administrator only</span><h1>Platform readiness from actual metadata.</h1><p>Configuration, migrations and successful tenant activity are reported independently from visible UI.</p></div>
    {error && <div className="form-alert error"><X size={17} />{error}</div>}
    {!data ? <LoadingScreen label="Reading platform metadata…" /> : <Panel title="Capability status" kicker={`Generated ${new Date(data.generatedAt).toLocaleString()}`}><div className="readiness-table">
      {data.capabilities.map((item) => {
        const missing = Array.isArray(item.missingCredentials) ? item.missingCredentials.map(String) : [];
        const limitations = Array.isArray(item.knownLimitations) ? item.knownLimitations.map(String) : [];
        return <article key={String(item.capability)}><div><h3>{String(item.capability)}</h3><p>{String(item.configurationStatus ?? "Unknown")}</p></div><span className={`status ${item.health === "healthy" ? "complete" : item.health === "blocked" ? "error" : "pending"}`}>{labelFor(String(item.health ?? "unknown"))}</span><dl><div><dt>Implementation</dt><dd>{labelFor(String(item.implementationStatus ?? "unknown"))}</dd></div><div><dt>Last agent run / test</dt><dd>{item.lastSuccessfulAgentRun || item.lastSuccessfulTest ? new Date(String(item.lastSuccessfulAgentRun ?? item.lastSuccessfulTest)).toLocaleString() : "No successful record"}</dd></div><div><dt>Missing configuration</dt><dd>{missing.join(", ") || "None detected"}</dd></div><div><dt>Known limitations</dt><dd>{limitations.join(", ") || "None recorded"}</dd></div></dl></article>;
      })}
    </div></Panel>}
  </div>;
}

function WorkspaceApp({
  session,
  workspace,
  onSignOut,
  onWorkspaceUpdated,
}: {
  session: AuthSession;
  workspace: Workspace;
  onSignOut: () => void;
  onWorkspaceUpdated: (workspace: Workspace) => void;
}) {
  const initialView: View = window.location.pathname.startsWith("/settings/billing")
    ? "Plan & Billing"
    : window.location.pathname.startsWith("/settings/usage")
      ? "AI Credits & Usage"
      : window.location.pathname.startsWith("/settings/agents")
        ? "Agent Centre"
      : window.location.pathname.startsWith("/integrations")
        ? "Integrations"
      : window.location.pathname.startsWith("/settings/team")
        ? "Team"
      : window.location.pathname.startsWith("/settings/api-keys")
        ? "API"
      : window.location.pathname.startsWith("/settings/business-profile")
        ? "Business Profile"
      : window.location.pathname.startsWith("/admin/platform-readiness")
        ? "Platform Readiness"
      : window.location.pathname.startsWith("/approvals")
        ? "Approvals"
      : window.location.pathname.startsWith("/website-intelligence")
        ? "Website Intelligence"
      : window.location.pathname.startsWith("/lead-intelligence")
        ? "Lead Intelligence"
      : window.location.pathname.startsWith("/revenue-orchestration")
        ? "Revenue Orchestration"
      : window.location.pathname.startsWith("/business-discovery")
        ? "Business Discovery"
      : window.location.pathname.startsWith("/marketing-strategy")
        ? "Marketing Strategy"
      : window.location.pathname.startsWith("/campaign-planning")
        ? "Campaign Planning"
      : window.location.pathname.startsWith("/sales-planning")
        ? "Sales Planning"
      : window.location.pathname.startsWith("/content-generation")
        ? "Content Generation"
      : window.location.pathname.startsWith("/performance-optimisation")
        ? "Performance Optimisation"
      : window.location.pathname.startsWith("/buying-intent")
        ? "Buying Intent"
      : window.location.pathname.startsWith("/lead-nurturing")
        ? "Lead Nurturing"
      : window.location.pathname.startsWith("/revenue-forecasting")
        ? "Revenue Forecasting"
      : window.location.pathname.startsWith("/conversion-optimisation")
        ? "Conversion Optimisation"
      : window.location.pathname.startsWith("/reputation-intelligence")
        ? "Reputation Intelligence"
      : window.location.pathname.startsWith("/content-strategy")
        ? "Content Strategy"
      : window.location.pathname.startsWith("/outreach-personalisation")
        ? "Outreach Personalisation"
      : window.location.pathname.startsWith("/market-research")
        ? "Market Research"
      : window.location.pathname.startsWith("/social-presence")
        ? "Social Presence"
        : "Overview";
  const [view, setView] = useState<View>(initialView),
    [open, setOpen] = useState(false),
    [sidebarCollapsed, setSidebarCollapsed] = useState(false),
    [openMenus, setOpenMenus] = useState<Set<View>>(() => {
      const root = familyRootOf(initialView);
      return root ? new Set([root]) : new Set();
    });
  const groupRefs = useRef<Partial<Record<View, HTMLDivElement | null>>>({});
  const [flyout, setFlyout] = useState<{ parent: View; top: number; left: number } | null>(null);
  const [approvalsCount, setApprovalsCount] = useState<number | null>(null);
  useEffect(() => {
    void loadApprovals(session.session.token, workspace.tenantId)
      .then((result) => setApprovalsCount(result.data.length))
      .catch(() => {});
  }, [session.session.token, workspace.tenantId]);
  useEffect(() => {
    const root = familyRootOf(view);
    setOpenMenus(root ? new Set([root]) : new Set());
  }, [view]);
  useEffect(() => {
    setFlyout(null);
  }, [sidebarCollapsed]);
  const toggleMenu = (name: View) =>
    setOpenMenus((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const openFlyout = (parent: View) => {
    if (!sidebarCollapsed) return;
    const rect = groupRefs.current[parent]?.getBoundingClientRect();
    if (rect) setFlyout({ parent, top: rect.top, left: rect.right + 6 });
  };
  const closeFlyout = () => setFlyout(null);
  let content: ReactNode;
  if (view === "Overview")
    content = (
      <Overview
        workspace={workspace}
        onNavigate={(target) => {
          setView(target);
          window.history.pushState(
            {},
            "",
            target === "Agent Centre" ? "/settings/agents" : "/",
          );
        }}
      />
    );
  else if (view === "Revenue Plan")
    content = <RevenuePlan workspace={workspace} />;
  else if (view === "Website Intelligence")
    content = (
      <WebsiteView
        session={session}
        workspace={workspace}
        onUpdate={onWorkspaceUpdated}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Lead Intelligence")
    content = (
      <LeadIntelligenceView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
        onNavigateToIntegrations={() => {
          setView("Integrations");
          window.history.pushState({}, "", pathForView("Integrations"));
        }}
      />
    );
  else if (view === "Revenue Orchestration")
    content = (
      <RevenueOrchestrationView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Business Discovery")
    content = (
      <BusinessDiscoveryView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
        onNavigateToIntegrations={() => {
          setView("Integrations");
          window.history.pushState({}, "", pathForView("Integrations"));
        }}
      />
    );
  else if (view === "Marketing Strategy")
    content = (
      <MarketingStrategyView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
        onNavigateToContentGeneration={() => {
          setView("Content Generation");
          window.history.pushState({}, "", "/content-generation");
        }}
        onNavigateToCampaignPlanning={() => {
          setView("Campaign Planning");
          window.history.pushState({}, "", "/campaign-planning");
        }}
      />
    );
  else if (view === "Campaign Planning")
    content = (
      <CampaignPlanningView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
        onNavigateToContentGeneration={() => {
          setView("Content Generation");
          window.history.pushState({}, "", "/content-generation");
        }}
      />
    );
  else if (view === "Sales Planning")
    content = (
      <SalesPlanningView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Content Generation")
    content = (
      <ContentGenerationView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Performance Optimisation")
    content = (
      <PerformanceOptimisationView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Buying Intent")
    content = (
      <BuyingIntentView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Lead Nurturing")
    content = (
      <LeadNurturingView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Revenue Forecasting")
    content = (
      <RevenueForecastingView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Conversion Optimisation")
    content = (
      <ConversionOptimisationView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Reputation Intelligence")
    content = (
      <ReputationIntelligenceView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Content Strategy")
    content = (
      <ContentStrategyView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Outreach Personalisation")
    content = (
      <OutreachPersonalisationView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Market Research")
    content = (
      <MarketResearchView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
      />
    );
  else if (view === "Social Presence")
    content = (
      <SocialPresenceView
        session={session}
        workspace={workspace}
        onNavigateToAgents={() => {
          setView("Agent Centre");
          window.history.pushState({}, "", "/settings/agents");
        }}
        onNavigateToIntegrations={() => {
          setView("Integrations");
          window.history.pushState({}, "", pathForView("Integrations"));
        }}
      />
    );
  else if (view === "Integrations")
    content = (
      <IntegrationsView
        session={session}
        workspace={workspace}
        onUpdate={onWorkspaceUpdated}
      />
    );
  else if (view === "Agent Centre")
    content = (
      <AgentsView
        session={session}
        workspace={workspace}
        onUpgrade={() => {
          setView("Plan & Billing");
          window.history.pushState({}, "", "/settings/billing");
        }}
        onUpdate={onWorkspaceUpdated}
      />
    );
  else if (view === "Team")
    content = <TeamView session={session} workspace={workspace} />;
  else if (view === "API")
    content = (
      <ApiKeysView
        session={session}
        workspace={workspace}
        onUpgrade={() => {
          setView("Plan & Billing");
          window.history.pushState({}, "", "/settings/billing");
        }}
      />
    );
  else if (view === "Business Profile")
    content = (
      <BusinessProfileView
        session={session}
        workspace={workspace}
        onUpdate={onWorkspaceUpdated}
      />
    );
  else if (view === "Plan & Billing")
    content = <BillingView session={session} workspace={workspace} />;
  else if (view === "AI Credits & Usage")
    content = <UsageView session={session} workspace={workspace} onUpdate={onWorkspaceUpdated} />;
  else if (view === "Platform Readiness")
    content = <ReadinessView session={session} workspace={workspace} />;
  else if (view === "Approvals")
    content = (
      <ApprovalsView
        session={session}
        workspace={workspace}
        onNavigate={(target) => {
          setView(target);
          window.history.pushState({}, "", pathForView(target));
        }}
      />
    );
  else content = <Generic view={view} workspace={workspace} />;
  // Platform Readiness is role-gated, every agent workspace view is gated
  // on that specialist actually being active -- group headers themselves
  // carry neither restriction, so they pass straight through.
  const isNavItemVisible = (name: View) => {
    if (name === "Platform Readiness" && !["owner", "admin"].includes(workspace.role))
      return false;
    const requiredAgentId = AGENT_GATED_VIEWS[name];
    if (requiredAgentId)
      return workspace.agents.some(
        (agent) => agent.id === requiredAgentId && agent.active,
      );
    return true;
  };
  // A group header only shows once at least one of its children would --
  // otherwise a fresh workspace with no agents active yet would show
  // (say) "Intelligence" expanding to an empty submenu.
  const visibleNavigation = navigation.filter(([name]) => {
    const children = NAV_CHILDREN[name];
    if (children) return children.some(([childName]) => isNavItemVisible(childName));
    return isNavItemVisible(name);
  });
  const creditsRemaining = workspace.billing.usage.credits.remaining;
  const creditTone = creditsRemaining > 100 ? "green" : creditsRemaining < 30 ? "red" : "yellow";
  const goToCredits = () => {
    setView("AI Credits & Usage");
    window.history.pushState({}, "", "/settings/usage");
  };
  return (
    <div className={`workspace-shell ${sidebarCollapsed ? "sidebar-rail" : ""}`}>
      <aside className={`sidebar ${open ? "open" : ""} ${sidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-brand">
          <Brand light />
          <button onClick={() => setOpen(false)}>
            <X />
          </button>
        </div>
        <button
          type="button"
          className="sidebar-rail-toggle"
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!sidebarCollapsed}
          onClick={() => setSidebarCollapsed((value) => !value)}
        >
          {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
        <div className="workspace-chip">
          <span>{workspace.organisationName.slice(0, 2).toUpperCase()}</span>
          <div>
            <small>Revenue workspace</small>
            <b>{workspace.organisationName}</b>
          </div>
        </div>
        <nav>
          {visibleNavigation.map(([name, Icon, label]) => {
            const renderButton = (
              itemName: View,
              ItemIcon: typeof LayoutDashboard,
              itemLabel: string | undefined,
              sub?: boolean,
              extraClass?: string,
            ) => (
              <button
                key={itemName}
                className={`${view === itemName ? "active" : ""} ${
                  ["Plan & Billing", "AI Credits & Usage", "Agent Centre"].includes(
                    itemName,
                  )
                    ? "commercial-nav-item"
                    : ""
                } ${sub ? "sidebar-subitem" : ""} ${extraClass ?? ""}`}
                onClick={() => {
                  setView(itemName);
                  setOpen(false);
                  window.history.pushState({}, "", pathForView(itemName));
                }}
              >
                <ItemIcon size={sub ? 16 : 19} />
                <span>{itemLabel ?? itemName}</span>
                {itemName === "Plan & Billing" ? (
                  <em>{workspace.billing.trial ? "Trial" : workspace.billing.plan.name}</em>
                ) : null}
                {itemName === "Recommendations" &&
                workspace.websiteScan?.opportunities.length ? (
                  <em>{workspace.websiteScan.opportunities.length}</em>
                ) : null}
                {itemName === "Approvals" && approvalsCount ? <em>{approvalsCount}</em> : null}
              </button>
            );
            const children = NAV_CHILDREN[name]?.filter(([childName]) => isNavItemVisible(childName));
            if (!children || children.length === 0)
              return (
                <div key={name} className="sidebar-nav-group">
                  {renderButton(name, Icon, label)}
                </div>
              );
            const menuOpen = openMenus.has(name);
            const childActive = children.some(([childName]) => childName === view);
            return (
              <div
                key={name}
                className="sidebar-nav-group"
                ref={(el) => {
                  groupRefs.current[name] = el;
                }}
                onMouseEnter={() => openFlyout(name)}
                onMouseLeave={closeFlyout}
              >
                <div className="sidebar-nav-row">
                  {renderButton(name, Icon, label, false, childActive ? "section-active" : "")}
                  <button
                    type="button"
                    className={`sidebar-chevron ${menuOpen ? "open" : ""}`}
                    aria-label={
                      menuOpen ? `Collapse ${name} submenu` : `Expand ${name} submenu`
                    }
                    aria-expanded={menuOpen}
                    onClick={() => toggleMenu(name)}
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>
                {menuOpen && !sidebarCollapsed ? (
                  <div className="sidebar-submenu">
                    {children.map(([childName, ChildIcon, childLabel]) =>
                      renderButton(childName, ChildIcon, childLabel, true),
                    )}
                  </div>
                ) : null}
                {sidebarCollapsed && flyout?.parent === name
                  ? createPortal(
                      <div
                        className="sidebar-flyout"
                        style={{ top: flyout.top, left: flyout.left }}
                        onMouseEnter={() => openFlyout(name)}
                        onMouseLeave={closeFlyout}
                      >
                        {children.map(([childName, ChildIcon, childLabel]) =>
                          renderButton(childName, ChildIcon, childLabel, true),
                        )}
                      </div>,
                      document.body,
                    )
                  : null}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div>
            <span>
              {(session.user.name || session.user.email)
                .slice(0, 2)
                .toUpperCase()}
            </span>
            <p>
              <b>{session.user.name || session.user.email}</b>
              <small>{workspace.role}</small>
            </p>
          </div>
          <button aria-label="Sign out" onClick={onSignOut}>
            <LogOut size={19} />
          </button>
        </div>
      </aside>
      <main className="workspace-main">
        <header className="topbar">
          <button className="mobile-menu" onClick={() => setOpen(true)}>
            <Menu />
          </button>
          <div>
            <span className="overline">Revenue workspace</span>
            {!VIEWS_WITH_OWN_PAGE_TITLE.has(view) && (
              <h1>{view === "Agent Centre" ? "Agents & Entitlements" : view}</h1>
            )}
          </div>
          <div className="top-actions">
            <button type="button" className={`credit-pill ${creditTone}`} onClick={goToCredits}>
              <Zap size={13} />
              {creditsRemaining.toLocaleString()} credits
            </button>
            {creditTone !== "green" && (
              <button className="button primary" onClick={goToCredits}>
                <Plus size={15} />
                Add Credit
              </button>
            )}
            <span className="system-online">
              <i />
              Revenue system active
            </span>
            <button className="button primary">
              <Sparkles size={17} />
              Ask Clintra
            </button>
          </div>
        </header>
        <div className="workspace-content">{content}</div>
      </main>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<AuthSession | null>(null),
    [bootstrap, setBootstrap] = useState<Bootstrap | null>(null),
    [workspace, setWorkspace] = useState<Workspace | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [invitePreview, setInvitePreview] = useState<PublicInvitePreview | null>(null);
  useEffect(() => {
    const match = window.location.pathname.match(/^\/invite\/([^/]+)/);
    if (!match) return;
    const token = match[1]!;
    window.sessionStorage.setItem("clintra-invite-token", token);
    void loadPublicInvitePreview(token)
      .then(setInvitePreview)
      .catch(() => setInvitePreview({ valid: false }));
  }, []);
  const initialise = async (current: AuthSession) => {
    setLoading(true);
    setError("");
    setSession(current);
    try {
      const selectionToken = window.sessionStorage.getItem(
        "clintra-plan-selection",
      ) ?? undefined;
      const inviteToken = window.sessionStorage.getItem(
        "clintra-invite-token",
      ) ?? undefined;
      const boot = await bootstrapWorkspace(
        current.session.token,
        selectionToken,
        inviteToken,
      );
      if (selectionToken)
        window.sessionStorage.removeItem("clintra-plan-selection");
      if (inviteToken)
        window.sessionStorage.removeItem("clintra-invite-token");
      setBootstrap(boot);
      if (boot.onboardingCompleted)
        setWorkspace(await loadWorkspace(current.session.token, boot.tenantId));
      else setWorkspace(null);
      const strandedOnAuthPage = ["/sign-in", "/sign-up", "/onboarding"].some(
        (path) => window.location.pathname.startsWith(path),
      );
      window.history.replaceState(
        {},
        "",
        boot.onboardingCompleted
          ? strandedOnAuthPage
            ? "/"
            : `${window.location.pathname}${window.location.search}`
          : "/onboarding",
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Unable to initialise the workspace.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void getSession()
      .then((current) => (current ? initialise(current) : setLoading(false)))
      .catch((cause) => {
        setError(
          cause instanceof Error
            ? cause.message
            : "Unable to check your session.",
        );
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    registerSessionRefresh(async () => {
      try {
        const current = await getSession();
        if (!current) return null;
        setSession(current);
        return current.session.token;
      } catch {
        return null;
      }
    });
  }, []);
  // The global header's credit pill reads straight off `workspace`, but
  // most credit-consuming actions (an agent run in particular) never call
  // setWorkspace themselves -- only specific views that already fetch a
  // fresh workspace for their own reasons (an integration connect, a plan
  // change) happen to update it as a side effect. Without this, the pill
  // only ever catches up on a hard reload. Poll rather than wire every
  // agent view individually -- there are 18 of them, and any of Clintra's
  // background agent runs can spend credits at any time regardless of
  // which page is open.
  useEffect(() => {
    if (!session || !workspace) return;
    const token = session.session.token;
    const tenantId = workspace.tenantId;
    const interval = window.setInterval(() => {
      void loadWorkspace(token, tenantId).then(setWorkspace).catch(() => {});
    }, 30000);
    return () => window.clearInterval(interval);
  }, [session, workspace?.tenantId]);
  const logout = async () => {
    try {
      await signOut();
    } finally {
      setSession(null);
      setBootstrap(null);
      setWorkspace(null);
      setError("");
      window.history.replaceState({}, "", "/sign-in");
    }
  };
  if (loading) return <LoadingScreen />;
  if (!session)
    return (
      <>
        {invitePreview && (
          <div className={`form-alert ${invitePreview.valid ? "info" : "error"}`}>
            {invitePreview.valid ? (
              <>
                You've been invited to join <b>{invitePreview.organisationName}</b> as{" "}
                {invitePreview.role}. Sign in or create an account with{" "}
                <b>{invitePreview.email}</b> to accept.
              </>
            ) : (
              "This invite link is invalid or has expired."
            )}
          </div>
        )}
        <AuthPage onAuthenticated={initialise} invitePreview={invitePreview} />
        {error && <div className="global-error">{error}</div>}
      </>
    );
  if (error)
    return (
      <main className="fatal">
        <Brand />
        <h1>We couldn’t prepare your workspace.</h1>
        <p>{error}</p>
        <button className="button primary" onClick={() => initialise(session)}>
          Try again
        </button>
        <button className="button secondary" onClick={logout}>
          Sign out
        </button>
      </main>
    );
  if (!bootstrap) return <LoadingScreen />;
  if (!workspace)
    return (
      <Onboarding
        session={session}
        bootstrap={bootstrap}
        onComplete={(state) => {
          setWorkspace(state);
          setBootstrap({ ...bootstrap, onboardingCompleted: true });
          window.history.replaceState({}, "", "/");
        }}
        onSignOut={logout}
      />
    );
  return (
    <WorkspaceApp
      session={session}
      workspace={workspace}
      onSignOut={logout}
      onWorkspaceUpdated={setWorkspace}
    />
  );
}
